import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
  closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Star,
  Filter as FilterIcon,
  Settings2,
  Plus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  LayoutGrid,
  GanttChart,
  Eye,
  Layers,
  GitBranch,
  Flag,
  GripVertical,
  Calendar,
  Trash2,
  Copy,
  Check,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  Search,
  UserPlus,
  RefreshCcw,
  X,
  Pencil,
  Paperclip,
  Repeat,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError, isPlanLimitError } from "../../lib/api";
import {
  formatDate,
  formatDateTime,
  formatPattern,
  formatTime,
  formatNumber,
  dayOffsetFromToday,
  dayKey,
  datePattern,
  timePattern,
  weekStartJsDay,
  getDisplayTimeZone,
  getCalendarTimeZone,
} from "../../lib/format";
import { DateTime } from "luxon";
import { lengthError } from "../../lib/fieldLength";
import { smoothScrollLeftTo } from "../../lib/smooth-scroll";
import { invalidateGamification } from "../../lib/gamification-refresh";
import { useSpaceAccess } from "../../lib/useSpaceAccess";
import { flashElement, scrollAndFlash } from "../../lib/focusFlash";
import { CONTAINS_EMOJI } from "../../lib/validators";
import { useAuthStore } from "../../stores/authStore";
import {
  useFavoriteLists,
  useFavoriteListMeta,
  useFavoriteSpaces,
  useGanttColumns,
  useTaskColumns,
  useTaskFilters,
} from "../../lib/space-prefs";
import {
  space as spaceSchema,
  listSummary,
  listListResponse,
  folderListResponse,
  taskListResponse,
  workspaceMembersResponse,
  spaceMembersResponse,
  TASK_PRIORITIES,
} from "../../lib/types";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { SearchField } from "../../components/ui/Field";
import { Breadcrumb } from "../../components/Breadcrumb";
import { TaskAttachmentsCell } from "../../components/TaskAttachmentsCell";
import { TaskActivity } from "../../components/TaskActivity";
import { SpaceFilesView } from "../../components/SpaceFilesView";
import { TaskStatusEditor } from "../../components/TaskStatusEditor";
import { SopUploadDialog } from "../../components/SopUploadDialog";
import { SopPreviewDialog } from "../../components/SopPreviewDialog";
import { CreateTaskDialog } from "../../components/CreateTaskDialog";
import { DuplicateTaskModal } from "../../components/DuplicateTaskModal";
import { RecurrenceDialog } from "../../components/RecurrenceDialog";
import { summariseSchedule } from "../../components/recurrenceSchedule";
import { isPastDueNow } from "../../lib/team-stats";
import { rangeIdsBetween, applyRange } from "../../lib/useRangeSelection";
import {
  collectDescendants,
  groupCheckState,
  prunedSelection,
  selectionCounts,
} from "../../lib/taskSelection";
import { recurringRulesResponse } from "../../lib/types";
// Custom Fields were removed from the task form (and from the Company Admin
// panel — see the note in AdminLayout.jsx). `components/TaskCustomFields.jsx`
// is kept on disk so re-enabling is just this import plus the hook call and
// the render block below. Stored `task.customFields` values are untouched.
import { WorkspacePeopleDialog } from "../../components/WorkspacePeopleDialog";
import { SpaceShareDialog } from "../../components/SpaceShareDialog";
import { StatusDot, isUnstartedStatus } from "../../components/StatusDot";
import { CommentsCell, TaskComments } from "../../components/TaskComments";
import { DEFAULT_SPACE_TASK_STATUSES } from "../../lib/types";
import {
  SpaceOverviewKpiStrip,
  SpaceOverviewBreakdowns,
  SpaceOverviewWorkload,
  SpaceOverviewLists,
  SpaceOverviewProjects,
  SpaceOverviewActivity,
} from "../../components/space-overview";

const PRIORITY_META = {
  urgent: { label: "Urgent", color: "var(--color-priority-urgent)" },
  high: { label: "High", color: "var(--color-priority-high)" },
  normal: { label: "Medium", color: "var(--color-priority-normal)" },
  low: { label: "Low", color: "var(--color-priority-low)" },
};

const AVATAR_PALETTE = [
  "#02367B",
  "#34D399",
  "#F87171",
  "#FBBF24",
  "#A78BFA",
  "#F472B6",
  "#0a2349",
  "#FB923C",
];

function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}
function initials(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** True when a task / subtask name carries real content — at least one
 *  Unicode letter or digit — so pure-punctuation junk like "@@@@" is
 *  rejected. Mirrors the server rule in `tasks.dto.js` / the
 *  `subtaskName` schema so the client and API agree (bug 0013372). */
function isMeaningfulTaskName(value) {
  // Must contain a real LETTER — rejects numbers-only ("12345") and
  // symbols-only ("@@@@") names. Mirrors the server `tasks.dto` rule.
  return /\p{L}/u.test(String(value ?? ""));
}

/** Longest task / subtask name the API will store — mirrors `.max(100)` on
 *  the `name` schema in `tasks.dto.js`. */
const TASK_NAME_MAX = 100;

/**
 * Over-length message for a task / subtask name, or null when it fits.
 *
 * Every task-name editor in this file already mirrors the server's OTHER name
 * rules (at least 2 characters, no emoji, must contain a letter or digit) so
 * the user gets the real reason instantly. The length cap was the one rule
 * nobody mirrored: an over-long name went to the API and came back as a 400
 * whose top-level message is the generic "Validation failed." The per-field
 * text sits in `error.fields`, which the toast never reads — so the user was
 * told only that something was wrong, never what or by how much (QA
 * 2026-09-12).
 *
 * These inputs are SOFT-limited by design: no DOM `maxLength`, so pasting an
 * over-long name shows it and lets the user edit it down instead of silently
 * truncating at 100. `lengthError` is the shared helper that convention is
 * built on (see `lib/fieldLength.js`, which spells out the same bargain and
 * carries the unit tests for the message itself) — this just supplies the
 * task-name cap and trims first, since the server compares the trimmed value.
 */
function taskNameLengthError(value, label = "Task Name") {
  return lengthError(String(value ?? "").trim(), undefined, TASK_NAME_MAX, label);
}

/**
 * Initials-style avatar with a custom on-hover name tooltip. Used for
 * task assignees on the List view, Board cards, Task Detail dialog and
 * the Member panel — anywhere we render a small coloured circle and
 * need the full name on hover for disambiguation (initials alone are
 * ambiguous when two members share them, e.g. "AM" Aarav Mehta vs Asha
 * Mishra).
 *
 * Implementation note: we use Tailwind's `group` + `group-hover`
 * pattern instead of the browser-native `title` attribute, because:
 *   - the native tooltip has a ~700ms delay before it appears (no
 *     control on Mac), which doesn't match how snappy the rest of the
 *     UI feels;
 *   - the native tooltip is unstyled; ours uses the design-system
 *     dark-navy chip so it reads as part of the product.
 * `aria-label` covers screen readers; we deliberately do NOT also set
 * a `title` attribute on the inner chip, because the OS would then
 * render its own tooltip on top of the custom one (you'd see the
 * name twice).
 */
function Avatar({ name, showTooltip = true }) {
  if (!name)
    return (
      <span
        title="Unassigned"
        className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-dashed border-[color:var(--color-rule-strong)] text-[color:var(--color-ink-subtle)] text-[10px]"
        aria-label="Unassigned"
      >
        —
      </span>
    );
  // When the name is ALREADY displayed next to the avatar (e.g.
  // inside the assignee-picker's people list), the floating tooltip
  // is redundant and visually overlaps the row label — render the
  // bare initials circle in those contexts. Default stays `true` so
  // standalone avatars (Board cards, Task Detail, bar overlays) keep
  // their disambiguating tooltip.
  if (!showTooltip) {
    return (
      <span
        className="inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-semibold text-white"
        style={{ backgroundColor: avatarColor(name) }}
        aria-label={name}
      >
        {initials(name)}
      </span>
    );
  }
  return (
    <AvatarBubbleWithTooltip name={name}>
      <span
        className="inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-semibold text-white"
        style={{ backgroundColor: avatarColor(name) }}
        aria-label={name}
      >
        {initials(name)}
      </span>
    </AvatarBubbleWithTooltip>
  );
}

/**
 * Hover-tooltip wrapper for the SpacePage's local Avatar bubble.
 * Renders the chip via `createPortal(document.body, ...)` so no
 * ancestor's `overflow: hidden` / sticky column header / row
 * stacking context can clip it — the previous CSS-only
 * `absolute bottom-full` chip got truncated on the very first row
 * of the list view (the column header sits right above it, the
 * chip extended upward, and the eye read the header text as
 * cutting through the chip).
 *
 * Mirrors the portal pattern landed on the shared
 * `components/Avatar.jsx`. Default position is BELOW the avatar
 * (first-row rows have empty space beneath them but a column
 * header right above); only flips ABOVE when the avatar is in the
 * very bottom slice of the viewport and the chip wouldn't fit.
 */
function AvatarBubbleWithTooltip({ name, children }) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const measure = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Default position is ABOVE the avatar (user preference). The
    // portal + fixed positioning means no ancestor can clip the
    // chip, so the original concern (column header truncating it)
    // doesn't apply once the chip lives in document.body.
    //
    // Auto-flip BELOW only when the avatar sits near the very top
    // of the viewport — the chip would otherwise extend above the
    // browser frame and get clipped by the viewport itself.
    const ESTIMATED_H = 36;
    const PADDING = 6;
    const flipBelow = r.top < ESTIMATED_H + PADDING;
    setCoords({
      top: flipBelow ? r.bottom + PADDING : r.top - PADDING,
      left: r.left + r.width / 2,
      flipBelow,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // `capture: true` so nested scroll containers (the list view's
    // horizontal-scroll wrapper, the page itself) all fire.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <span
        ref={wrapperRef}
        className="inline-flex"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open && coords
        ? createPortal(
            <span
              role="tooltip"
              aria-label={name}
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                transform: coords.flipBelow
                  ? "translate(-50%, 0)"
                  : "translate(-50%, -100%)",
                zIndex: 1000,
              }}
              className="pointer-events-none whitespace-nowrap rounded-md bg-[color:var(--color-ink-strong)] text-[color:var(--color-canvas)] text-[12px] font-medium px-2 py-1 shadow-md"
            >
              {name}
              {/* Caret — points DOWN at the avatar when the chip
               *  sits above it (default), UP when flipped below. */}
              <span
                aria-hidden="true"
                className={
                  coords.flipBelow
                    ? "absolute bottom-full left-1/2 -translate-x-1/2 h-0 w-0 border-x-4 border-x-transparent border-b-4 border-b-[color:var(--color-ink-strong)]"
                    : "absolute top-full left-1/2 -translate-x-1/2 h-0 w-0 border-x-4 border-x-transparent border-t-4 border-t-[color:var(--color-ink-strong)]"
                }
              />
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

function StatusPill({ status }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold uppercase tracking-wider"
      style={{ color: status.color, backgroundColor: `${status.color}1f` }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: status.color }}
        aria-hidden="true"
      />
      {status.name}
    </span>
  );
}

/**
 * Bulk-selection checkbox for a List-view row (and the group header).
 *
 * All toggling lives in `onClick` with a no-op `onChange`, matching the
 * Task Monitor's checkbox: React's onChange would otherwise re-toggle the row
 * and cancel a Shift+click range. `indeterminate` is a DOM property with no
 * JSX attribute, so it goes on via ref.
 *
 * The wrapper is a FIXED `w-5` with a negative left margin tuned per depth, so
 * the checkbox lands in the row's existing indent gutter and every depth's
 * content shifts right by the same 1rem — the parent row's status dot stays
 * vertically aligned with its subtasks', which is the whole reason the drag
 * handles carry negative margins in the first place.
 */
function RowSelectCheckbox({
  checked,
  indeterminate = false,
  disabled = false,
  onClick,
  ariaLabel,
  title,
  className = "",
}) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <span
      className={
        "shrink-0 w-5 flex items-center justify-center self-stretch " + className
      }
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => {}}
        onClick={onClick}
        aria-label={ariaLabel}
        title={title}
        className={
          "h-4 w-4 accent-[color:var(--color-primary)] align-middle " +
          (disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer")
        }
      />
    </span>
  );
}

function PriorityChip({ priority }) {
  if (!priority)
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-subtle)]">
        <Flag size={12} />
      </span>
    );
  const meta = PRIORITY_META[priority];
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs"
      style={{ color: meta.color }}
    >
      <Flag size={12} fill={meta.color} strokeWidth={0} />
      {meta.label}
    </span>
  );
}

/** The urgency colour for a due date — overdue = red, due today = amber,
 *  completed-on-time = green, otherwise neutral muted ink. Shared by the
 *  List/Board `DueChip` and the task-detail `DueDateCell` so both convey the
 *  same cue. */
function dueDateColor(iso, completedAt) {
  // Day offset in the tenant display timezone, not the browser zone, so a
  // date-only due date is classified on the calendar day everyone sees.
  const diffDays = dayOffsetFromToday(iso) ?? 0;
  if (completedAt) {
    // Green only when the task was completed on or before its due calendar day
    // (display timezone) — the "completed on time" cue. A late completion drops
    // back to muted (no longer red, the task is done).
    if ((dayKey(completedAt) ?? "") <= (dayKey(iso) ?? "")) {
      return "var(--color-status-complete, #16a34a)";
    }
    return "var(--color-ink-muted)";
  }
  if (diffDays < 0) return "var(--color-priority-urgent)";
  if (diffDays === 0) return "var(--color-priority-high)";
  return "var(--color-ink-muted)";
}

function DueChip({ iso, completedAt }) {
  if (!iso)
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-subtle)]">
        <Calendar size={12} />
      </span>
    );
  const due = new Date(iso);
  // Always show the ACTUAL due date (DD/MM/YYYY), the same format the Start
  // date column uses — QA flagged the previous relative wording ("Today" /
  // "Yesterday" / "2 days ago" / "In 4 days") for hiding the real date and
  // reading inconsistently next to the absolute Start date. Urgency is still
  // conveyed by COLOUR (overdue = red, due today = amber, completed-on-time =
  // green). The time, when set, lives in the separate "Due time" column, so
  // this cell shows only the calendar day.
  const label = formatDate(due);
  const color = dueDateColor(iso, completedAt);

  return (
    <span className="inline-flex items-center gap-1 text-xs" style={{ color }}>
      <Calendar size={12} />
      {label}
    </span>
  );
}

// ---------- Popover plumbing ----------
// Anchor a fixed-position popover under the trigger and shift it up if it
// would otherwise overflow the viewport. Returns nothing — caller renders the
// menu via createPortal to <body> using the coords the hook supplies.
/** Viewport-y of the top edge of the trigger's nearest VERTICAL scroll
 *  container. Portaled popovers must never render above this — it sits below
 *  the app's fixed 64px header, so clamping to it stops a popover sliding over
 *  the header when its trigger scrolls up underneath. Returns 0 (viewport top)
 *  when the page scrolls with the window (no fixed header to clear). */
function scrollParentTop(el) {
  let p = el?.parentElement;
  while (p) {
    const oy = window.getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) {
      return p.getBoundingClientRect().top;
    }
    p = p.parentElement;
  }
  return 0;
}

function usePortalCoords(open, triggerRef, setCoords, popoverW = 280) {
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    function update() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Defensive: a 0×0 rect means the trigger is detached / hidden — the
      // rect's `top/left` are both 0, which would slam every popover to the
      // top-left corner of the viewport. Skip the read and retry next frame
      // when the element actually has layout. Symptom this guards against:
      // on the Board view, clicking a card's StatusCell briefly during a
      // dnd-kit transform / scroll re-flow could yield a zero rect and
      // strand the popover at (0,0).
      if (r.width === 0 && r.height === 0) return;
      const margin = 8;
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      // Largest comfortable height for any picker we render.
      const desired = 480;
      // Minimum below-space needed to even consider opening downward; smaller
      // than this and we always flip above the trigger.
      const minBelow = 200;
      // Never render above the trigger's scroll container (which sits below the
      // app's fixed header) — otherwise a below-opening popover slides over the
      // header when the trigger scrolls up underneath it.
      const minTop = scrollParentTop(el) + 4;
      const top = Math.max(r.bottom + 4, minTop);
      // Clamp `left` so the popover never escapes the viewport. Without this
      // a trigger near the right edge could push the popover off-screen. The
      // caller passes the popover's real width (`popoverW`, default 280) so a
      // wide picker (e.g. the 480px assignee menu) is clamped by its ACTUAL
      // width instead of a too-small lower bound that let it overflow right.
      const left = Math.max(
        margin,
        Math.min(r.left, viewportW - popoverW - margin),
      );
      const availableBelow = viewportH - top - margin;
      if (availableBelow >= minBelow) {
        // Open below — clamp menu height to whatever fits in the viewport.
        setCoords({
          top,
          bottom: undefined,
          left,
          maxHeight: Math.min(desired, availableBelow),
        });
        return;
      }
      // Flip above the trigger. Anchor the popup's BOTTOM to just above the
      // trigger (via `bottom`) instead of deriving `top` from the height
      // CAP — the cap (480px) is usually taller than the actual content, so
      // top-anchoring pinned the popup ~480px up and left a big gap between
      // it and the field (the "shifted to the upper side" bug). Bottom-
      // anchoring keeps it glued to the trigger no matter the content height.
      // Bound the flipped height to the gap between the container top (below
      // the header) and the trigger, so a flipped popover can't creep over the
      // header either.
      const availableAbove = r.top - minTop - 8;
      const maxHeight = Math.max(120, Math.min(desired, availableAbove));
      setCoords({
        top: undefined,
        bottom: viewportH - r.top + 4,
        left,
        maxHeight,
      });
    }
    // Schedule the FIRST measurement on the next animation frame so the DOM
    // has fully painted before we read the trigger's rect. Reading it
    // synchronously inside useLayoutEffect can race with surrounding
    // layouts (dnd-kit transforms, scroll-snap, virtualized lists), which
    // is the root cause of the "popover lands at top-left on Board view"
    // bug: the rect briefly reads as zero immediately after click, so we
    // anchor at (0,0) and never recompute.
    let rafId = window.requestAnimationFrame(update);
    function rafUpdate() {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(update);
    }
    // Keep the popup glued to the trigger while the page scrolls / resizes.
    window.addEventListener("resize", rafUpdate);
    window.addEventListener("scroll", rafUpdate, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", rafUpdate);
      window.removeEventListener("scroll", rafUpdate, true);
    };
  }, [open, triggerRef, setCoords]);
}

// Close on outside-click + Escape.
function useDismissOn(open, triggerRef, menuRef, close) {
  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      const t = e.target;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t))
        return;
      close();
    }
    function onKey(e) {
      if (e.key === "Escape") {
        // Claim the keystroke so an ENCLOSING overlay (e.g. the task detail
        // dialog, which also closes on Escape) doesn't dismiss itself in the
        // same keypress. Innermost overlay wins; outer ones skip a
        // `defaultPrevented` event.
        e.preventDefault();
        close();
      }
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, triggerRef, menuRef, close]);
}

// ---------- Inline editors ----------

/** Picker grouping order — covers both the tenant Workflow scheme
 *  (`backlog/active/review/done/cancelled`, returned by the backend
 *  from the default Workflow) and the legacy per-Space scheme so
 *  unmigrated data still groups sensibly. */
const STATUS_CATEGORY_ORDER = [
  "backlog",
  "not_started",
  "active",
  "review",
  "done",
  "closed",
  "cancelled",
];
/** Colour-coded status dot that opens a picker grouped by category. Replaces
 *  the static dot the row used to show — clicking it commits a new status to
 *  the task. The SVG rendering now lives in `components/StatusDot.jsx` so
 *  Team Tasks / Task Monitor / any other list page can use the same dot. */
// Permission-denied messages mirror the backend's editDeniedMessage so the
// click-toast matches what a blocked write would return.
const STATUS_DENIED_MSG =
  "You can only change the status of tasks assigned to you.";
const PLANNING_DENIED_MSG =
  "Start date, due date, priority and assignee can only be changed by a Team Leader or Manager for their team.";
// A View-only member can't change ANY task's status — it's their access level,
// not assignment — so the "assigned to you" wording would mislead them. The
// message shown for a denied status change is therefore access-aware: SpacePage
// provides it via context from the viewer's effective Space access
// (`myAccessLevel`); StatusCell reads it. The default keeps the assignment
// message, which is correct for Comment / Edit members who simply aren't
// assigned to the task.
const VIEW_STATUS_DENIED_MSG =
  "You have View only access to this Space, so you can't change a task's status. Ask the Space owner for higher access.";
const StatusDeniedMsgContext = createContext(STATUS_DENIED_MSG);

/** Map of userId → { userId, fullName, avatarUrl } for EVERY assignee across
 *  the loaded tasks, resolved server-side. AssigneeCell falls back to this when
 *  an assignee isn't in the Space's scoped assignable roster, so an
 *  out-of-scope assignee (a cross-department member, or one who lost Space
 *  access) still renders their avatar/name instead of an anonymous "+1". */
const AssigneeDisplayContext = createContext(new Map());

/** Shared Sonner toast id for EVERY "you can't edit/assign this" denial —
 *  the frontend planning/status/rank guards AND the backend 403 that the
 *  update mutation surfaces all reuse it. Sonner updates (not stacks) a
 *  toast when the id repeats, so a blocked action shows exactly ONE toast
 *  that stays visible for its full duration, instead of two near-identical
 *  messages (frontend pre-check + backend response) piling up. */
const EDIT_DENIED_TOAST_ID = "edit-denied";

/** Locked planning field. Per UX request the picker still OPENS (so the
 *  user — e.g. an Employee — can see and explore it), but committing a
 *  change is blocked with this toast instead of saving. Deduped by id so a
 *  picker that fires onCommit more than once shows a single toast. */
function notifyPlanningDenied() {
  toast.error(PLANNING_DENIED_MSG, { id: EDIT_DENIED_TOAST_ID });
}

/** Priority (and other planning fields) can't be changed once a task is
 *  finished — the work is done, so the priority no longer means anything.
 *  Reopening the task unlocks it again. */
const COMPLETED_LOCKED_MSG =
  "Priority cannot be changed on a completed task. Reopen the task first.";
function notifyCompletedLocked() {
  toast.error(COMPLETED_LOCKED_MSG, { id: EDIT_DENIED_TOAST_ID });
}

/** Terminal status categories — mirrors the backend `TERMINAL_STATUS_CATEGORIES`
 *  used by its priority-lock guard (`isClosedStatus`). */
const TERMINAL_STATUS_CATEGORIES = new Set(["done", "cancelled", "closed"]);

/** Whether a task's CURRENT status locks priority — the task sits in a terminal
 *  status (done / cancelled / closed). Deriving the lock from the live status
 *  (not `completedAt`, which the client can hold stale after a reopen /
 *  optimistic status move) keeps the cell in step with the server guard, so a
 *  task reopened to In Progress is no longer treated as completed. */
function isTerminalStatusName(statuses, statusName) {
  const s = (statuses ?? []).find((x) => x.name === statusName);
  return s ? TERMINAL_STATUS_CATEGORIES.has(s.category) : false;
}

/** A field control the user lacks permission to edit. Per UX request we do
 *  NOT disable it — it stays clickable and surfaces a toast explaining why
 *  the edit isn't allowed, instead of silently doing nothing. */
function DeniedFieldButton({ message, title, ariaLabel, className, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toast.error(message, { id: EDIT_DENIED_TOAST_ID });
      }}
      className={className}
    >
      {children}
    </button>
  );
}

function StatusCell({
  current,
  statuses,
  onCommit,
  onManageStatuses,
  disabled = false,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  usePortalCoords(open, triggerRef, setCoords);
  useDismissOn(open, triggerRef, menuRef, () => setOpen(false));

  const currentStatus = statuses.find((s) => s.name === current) ?? null;
  const dotColor = currentStatus?.color ?? "#94a3b8";
  // ClickUp pattern: pre-work statuses (Backlog / Not started / TO DO)
  // render as an outlined dashed ring — once the task has moved past
  // "not started" the dot is FILLED with the status colour so the
  // user can see at a glance which rows are actually in motion.
  //
  // Two ways a row is treated as "unstarted":
  //   1. The status row in the Space catalogue has `category` set to
  //      one of the pre-work categories. This is the canonical path
  //      for spaces seeded from the default template.
  //   2. Name-based heuristic — the status name LOOKS like a "not
  //      started" state. Belt-and-braces fallback for spaces whose
  //      `category` field is missing / wrong (custom workflows
  //      imported without category metadata, legacy data, etc.).
  //      Without this, "TO DO" rows on those spaces silently render
  //      as solid grey discs because the category lookup returns
  //      undefined, which is the exact symptom seen on the List view.
  const isUnstarted = isUnstartedStatus(currentStatus, current);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const s of statuses) {
      const arr = m.get(s.category) ?? [];
      arr.push(s);
      m.set(s.category, arr);
    }
    return m;
  }, [statuses]);

  // Access-aware denied message (View-only access vs simply not being
  // assigned), supplied by SpacePage via context.
  const statusDeniedMsg = useContext(StatusDeniedMsgContext);

  // No permission to change status — stays clickable but explains why via
  // a toast on click (UX request: don't disable, surface the reason).
  if (disabled) {
    return (
      <DeniedFieldButton
        message={statusDeniedMsg}
        title={`Status: ${current}`}
        ariaLabel={`Status: ${current}. You don't have permission to change it.`}
        className="shrink-0 p-0.5 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
      >
        <StatusDot color={dotColor} unstarted={isUnstarted} size={14} />
      </DeniedFieldButton>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={`Status: ${current}`}
        aria-label={`Status: ${current}. Click to change.`}
        className="shrink-0 p-0.5 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
      >
        <StatusDot color={dotColor} unstarted={isUnstarted} size={14} />
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: coords.top,
                bottom: coords.bottom,
                left: coords.left,
                maxHeight: coords.maxHeight,
                overflowY: "auto",
                zIndex: 60,
              }}
              className="w-56 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] max-h-[28rem] overflow-auto"
            >
              {/* Flat list — statuses are still ordered by category
               *  internally (Backlog → Active → Review → Done →
               *  Cancelled, plus legacy buckets) via STATUS_CATEGORY_ORDER,
               *  but the per-category headers and dividers are gone so
               *  the picker reads as one continuous list. */}
              <div className="py-1">
                {STATUS_CATEGORY_ORDER.flatMap(
                  (cat) => grouped.get(cat) ?? [],
                ).map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => {
                      onCommit(s.name);
                      setOpen(false);
                    }}
                    className={
                      "w-full flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold cursor-pointer " +
                      (s.name === current
                        ? "bg-[color:var(--color-accent-bg)]"
                        : "hover:bg-[color:var(--color-card-hover)]")
                    }
                  >
                    <StatusDot
                      color={s.color}
                      unstarted={isUnstartedStatus(s, s.name)}
                      size={12}
                    />
                    <span
                      style={{ color: s.color }}
                      className="flex-1 text-left truncate"
                    >
                      {s.name}
                    </span>
                    {s.name === current ? (
                      <Check
                        size={12}
                        className="text-[color:var(--color-accent)]"
                      />
                    ) : null}
                  </button>
                ))}
              </div>
              {onManageStatuses ? (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onManageStatuses();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer border-t border-[color:var(--color-rule)]"
                >
                  <Plus size={12} />
                  Manage statuses
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function NameCell({ value, onCommit, onOpenDetail }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed.length === 0 || trimmed === value) {
      setDraft(value);
      return;
    }
    if (trimmed.length < 2) {
      // Mirror the Create Task dialog's 2-character rule client-side so a
      // short rename shows that message — not the backend's generic
      // "Validation failed." — and reverts without a wasted round-trip.
      toast.error("Task Name must be at least 2 characters.");
      setDraft(value);
      return;
    }
    const tooLong = taskNameLengthError(trimmed);
    if (tooLong) {
      // NOT reverted to `value`, unlike the guards either side of this one.
      // Those discard a name that is too SHORT or holds an emoji — a keystroke
      // or two to retype. This one fires only once the user has typed more
      // than 100 characters, and throwing that away to make them start over
      // is worse than the mistake. `draft` is left as typed (the
      // `setDraft(value)` effect above only runs when the SAVED name changes,
      // which a rejected rename never does), so reopening the cell returns
      // their text ready to trim.
      toast.error(tooLong);
      return;
    }
    if (CONTAINS_EMOJI.test(trimmed)) {
      toast.error("Task Name cannot contain emojis.");
      setDraft(value);
      return;
    }
    onCommit(trimmed);
  }

  return (
    // `flex-1` removed — when it was here NameCell expanded to consume
    // ALL remaining space in the sticky cell, pushing the subtask-count
    // badge ("↳ 2") to the far right edge of the 560px Name column and
    // leaving an awkward gap between the task name and the badge. With
    // `min-w-0` + `shrink` (default) the wrapper sizes to its content,
    // and the inner button's `truncate` + parent sticky cell's
    // `overflow-hidden` still ellipses very long names cleanly.
    <div className="group/name flex items-center gap-1 min-w-0 shrink">
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") {
              setDraft(value);
              setEditing(false);
            }
          }}
          /* Soft limit: no DOM maxLength — accepts input past 100; the rename
             save is capped + rejected by the server DTO. */
          className="flex-1 min-w-0 bg-transparent border border-[color:var(--color-accent)] rounded px-1.5 py-0.5 text-sm text-[color:var(--color-ink-strong)] outline-none"
        />
      ) : (
        <>
          <button
            type="button"
            onClick={onOpenDetail}
            title={value}
            className="truncate flex-1 min-w-0 text-left text-[color:var(--color-ink-strong)] cursor-pointer"
          >
            {value}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            title="Rename task"
            aria-label="Rename task"
            className="hidden group-hover/name:inline-flex p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer shrink-0"
          >
            <Pencil size={12} />
          </button>
        </>
      )}
    </div>
  );
}

function AssigneeCell({
  assigneeIds,
  members,
  currentUserId,
  workspaceId,
  workspaceName,
  spaceId,
  spaceName,
  taskId,
  onCommit,
  disabled = false,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [query, setQuery] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [profileMember, setProfileMember] = useState(null);

  // "Invite people" is a membership-management action — it opens the Share
  // dialog to add someone to the Space. Only surface it to someone who can
  // actually invite: the Space owner or a platform admin (Company Admin / Sub
  // Admin with workspaces.manage), never an external. This MIRRORS
  // SpaceShareDialog's `canAddMembers`, so the CTA appears exactly when the
  // dialog it opens will let them add. Without it, a cross-department member
  // (who can edit the task but NOT manage members) saw "Invite people" and it
  // opened a read-only "People with access" dialog — an invite CTA that can't
  // invite. The `['space', spaceId]` query is already populated by SpacePage,
  // so this is a cache hit here.
  const actor = useAuthStore((s) => s.user);
  const isExternalMember = useAuthStore(
    (s) => s.user?.membershipType === "external",
  );
  const hasWorkspacesManageGrant = useAuthStore(
    (s) => s.hasSubAdminOperation?.("workspaces.manage") ?? false,
  );
  const spaceOwnerQuery = useQuery({
    queryKey: ["space", spaceId],
    queryFn: async () => (await api.get(`/spaces/${spaceId}`)).data,
    enabled: Boolean(spaceId),
    staleTime: 60_000,
    retry: false,
  });
  const canInviteMembers = useMemo(() => {
    if (!actor || isExternalMember) return false;
    // Server-resolved flag is authoritative (covers department-scoped
    // cross-department managers). Fall back to the owner/admin heuristic only
    // when the space payload hasn't supplied it yet.
    const flag = spaceOwnerQuery.data?.myCanManageMembers;
    if (typeof flag === "boolean") return flag;
    if (spaceOwnerQuery.data?.createdById === actor.id) return true;
    return actor.role === "company_admin" || hasWorkspacesManageGrant;
  }, [actor, isExternalMember, spaceOwnerQuery.data, hasWorkspacesManageGrant]);
  // A member may not assign a task UP the Work-Role hierarchy. The viewer's
  // own rank comes from the roster (rank 1 = most senior); anyone ranked
  // strictly more senior is blocked from being added OR removed here. null
  // ranks (unranked viewer or member) impose no constraint. The backend
  // enforces the same rule — this just gives immediate feedback.
  const viewerRank = (() => {
    const me = members.find((m) => m.userId === currentUserId);
    return typeof me?.rank === "number" ? me.rank : null;
  })();
  const isSeniorToViewer = (userId) => {
    if (viewerRank == null) return false;
    const m = members.find((x) => x.userId === userId);
    if (!m || m.userId === currentUserId) return false;
    return typeof m.rank === "number" && m.rank < viewerRank;
  };
  const notifyAssignSeniorDenied = () =>
    toast.error(
      "You can't assign a task to someone above you in the hierarchy.",
      { id: EDIT_DENIED_TOAST_ID },
    );

  function reassign(userId) {
    if (disabled) {
      notifyPlanningDenied();
      return;
    }
    if (isSeniorToViewer(userId)) {
      notifyAssignSeniorDenied();
      return;
    }
    // Replace the whole assignee set with just this user. We deliberately
    // leave the popover open so the user sees the selection state flip from
    // their old picks to the single new one when the refetch lands.
    onCommit([userId]);
    // Unique toast id so rapid double-clicks (or React StrictMode's
    // double-invoke in dev) collapse into one notification instead of
    // stacking two identical "Assignees replaced." toasts.
    toast.success("Assignees replaced.", { id: "assignees-replaced" });
  }
  // 480 = the picker's w-[30rem] width, so the right-edge clamp matches it.
  usePortalCoords(open, triggerRef, setCoords, 480);
  useDismissOn(open, triggerRef, menuRef, () => setOpen(false));

  // Reset search and focus the input each time the popover opens.
  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    // Defer focus until after the portal mounts. `preventScroll: true` so
    // the focus call doesn't trigger the browser's auto-scroll-into-view —
    // on the Board view the search input sits inside a portal at viewport
    // coordinates; auto-scroll could shift the page beneath the popover
    // and make the picker visually drift away from the trigger.
    const id = window.setTimeout(
      () => searchRef.current?.focus({ preventScroll: true }),
      0,
    );
    return () => window.clearTimeout(id);
  }, [open]);

  const assigneeDisplay = useContext(AssigneeDisplayContext);
  // Resolve each assignee from the Space's scoped roster first, then fall back
  // to the server-supplied display map so an assignee OUTSIDE that roster still
  // renders a named avatar instead of collapsing into a "+1" overflow.
  const current = assigneeIds
    .map((id) => members.find((m) => m.userId === id) ?? assigneeDisplay.get(id))
    .filter((m) => m);
  const titleText =
    current.length === 0
      ? "Unassigned"
      : current.map((m) => m.fullName).join(", ");

  // Locked (e.g. an Employee): the picker still opens so they can see who's
  // assigned, but changing the assignees is blocked with the explanatory
  // toast instead of saving.
  function toggleMember(userId) {
    if (disabled) {
      notifyPlanningDenied();
      return;
    }
    if (isSeniorToViewer(userId)) {
      notifyAssignSeniorDenied();
      return;
    }
    if (assigneeIds.includes(userId)) {
      onCommit(assigneeIds.filter((id) => id !== userId));
    } else {
      onCommit([...assigneeIds, userId]);
    }
    // No success toast here. The commit is OPTIMISTIC — the task-update
    // mutation flips the avatar instantly and, on failure, rolls back and
    // shows exactly one error toast. Firing "…added successfully" synchronously
    // was premature: it showed even when the request then failed (e.g. the API
    // was briefly unavailable), so the user saw a success AND an error at once.
    // This now matches every other inline cell (status / priority / dates),
    // none of which toast on success.
  }

  // Filter by name OR Email ID — the "Search or enter Email ID" pattern.
  const q = query.trim().toLowerCase();
  const matches = (m) =>
    q === "" ||
    m.fullName.toLowerCase().includes(q) ||
    m.emailId.toLowerCase().includes(q);

  // Split into Assignees (already selected) vs People (everyone else). The
  // current user always sits at the top of whichever section they're in,
  // labelled "Me".
  const me = currentUserId
    ? (members.find((m) => m.userId === currentUserId) ?? null)
    : null;
  const isMeSelected = me ? assigneeIds.includes(me.userId) : false;

  const selectedOthers = members
    .filter((m) => assigneeIds.includes(m.userId) && m.userId !== currentUserId)
    .filter(matches);
  const unselectedOthers = members
    .filter(
      (m) => !assigneeIds.includes(m.userId) && m.userId !== currentUserId,
    )
    // No department filter: a Space can host several departments collaborating,
    // so the picker offers every eligible Space member the backend returns. The
    // backend (`assertAssigneesCanAccessSpace`) is the real gate; unrelated
    // departments never appear because they aren't in the assignable roster.
    .filter(matches);
  const meVisible = me ? matches(me) : false;

  // "Select all" — every currently-visible member the viewer may actually
  // assign, skipping anyone senior in the Work-Role hierarchy (they can't be
  // assigned). Seniors already placed by someone else are preserved by the
  // union/difference in `toggleSelectAll` below.
  const selectableIds = [
    ...(me && meVisible ? [me.userId] : []),
    ...selectedOthers.map((m) => m.userId),
    ...unselectedOthers.map((m) => m.userId),
  ].filter((id) => !isSeniorToViewer(id));
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => assigneeIds.includes(id));
  const someSelected = selectableIds.some((id) => assigneeIds.includes(id));

  function toggleSelectAll() {
    if (disabled) {
      notifyPlanningDenied();
      return;
    }
    if (allSelected) {
      // Deselect only the selectable ones — a senior placed by someone else
      // stays assigned (toggling them individually is blocked anyway).
      onCommit(assigneeIds.filter((id) => !selectableIds.includes(id)));
    } else {
      onCommit(Array.from(new Set([...assigneeIds, ...selectableIds])));
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        // No native `title` here — each individual avatar inside
        // `AvatarStack` has its own custom hover chip. Adding a
        // `title` to the button would make BOTH fire on hover
        // (native + custom), and the user would see two tooltips
        // stacked. `aria-label` keeps assistive tech informed.
        aria-label={
          titleText === "Unassigned"
            ? "Unassigned — click to assign"
            : `Assigned to ${titleText}. Click to edit assignees.`
        }
        className="cursor-pointer"
      >
        <AvatarStack members={current} totalCount={assigneeIds.length} />
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                // Either `top` (opens down) or `bottom` (flipped up) is set
                // by usePortalCoords — applying both lets whichever is
                // present anchor the popup; the undefined one is ignored.
                top: coords.top,
                bottom: coords.bottom,
                left: coords.left,
                maxHeight: coords.maxHeight,
                zIndex: 60,
              }}
              className="w-[30rem] max-w-[calc(100vw-1rem)] rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] flex flex-col"
            >
              <div className="p-2 border-b border-[color:var(--color-rule)]">
                <div className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
                  >
                    <Search size={14} />
                  </span>
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    // Same 200 cap as every other search field in the app — a
                    // search box has no reason to accept an unbounded paste.
                    maxLength={200}
                    placeholder="Search by Name or Email ID"
                    className="fd-search-pill w-full pl-9 pr-8 py-1.5 rounded-full text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] focus:outline-none"
                  />
                  {/* Clear (X) — consistent with the app's other search fields,
                   *  which all offer a one-click reset. */}
                  {query ? (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      aria-label="Clear search"
                      title="Clear search"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
                    >
                      <X size={14} />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="overflow-auto flex-1">
                {!disabled && selectableIds.length > 0 ? (
                  <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]">
                    <input
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && someSelected;
                      }}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="shrink-0 accent-[color:var(--color-accent)]"
                    />
                    <span className="font-medium text-[color:var(--color-ink)]">
                      Select all
                    </span>
                    {assigneeIds.length > 0 ? (
                      <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
                        {assigneeIds.length} selected
                      </span>
                    ) : null}
                  </label>
                ) : null}
                {(isMeSelected && meVisible) || selectedOthers.length > 0 ? (
                  <p className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                    Assignees
                  </p>
                ) : null}
                {me && isMeSelected && meVisible ? (
                  <MemberRow
                    member={me}
                    label="Me"
                    selected
                    onClick={() => toggleMember(me.userId)}
                    onReassign={() => reassign(me.userId)}
                    onProfile={() => setProfileMember(me)}
                  />
                ) : null}
                {selectedOthers.map((m) => (
                  <MemberRow
                    key={m.userId}
                    member={m}
                    selected
                    onClick={() => toggleMember(m.userId)}
                    onReassign={() => reassign(m.userId)}
                    onProfile={() => setProfileMember(m)}
                  />
                ))}

                {(me && !isMeSelected && meVisible) ||
                unselectedOthers.length > 0 ? (
                  <p className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                    People
                  </p>
                ) : null}
                {me && !isMeSelected && meVisible ? (
                  <MemberRow
                    member={me}
                    label="Me"
                    onClick={() => toggleMember(me.userId)}
                    onReassign={() => reassign(me.userId)}
                    onProfile={() => setProfileMember(me)}
                  />
                ) : null}
                {unselectedOthers.map((m) => (
                  <MemberRow
                    key={m.userId}
                    member={m}
                    onClick={() => toggleMember(m.userId)}
                    onReassign={() => reassign(m.userId)}
                    onProfile={() => setProfileMember(m)}
                  />
                ))}

                {/* No match for either section → empty state. */}
                {q !== "" &&
                !meVisible &&
                selectedOthers.length === 0 &&
                unselectedOthers.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                    No matches found.
                  </p>
                ) : null}

                {!disabled && canInviteMembers ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!spaceId) {
                        toast.error("Space not loaded yet — try again.");
                        return;
                      }
                      setOpen(false);
                      setShareOpen(true);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer border-t border-[color:var(--color-rule)]"
                  >
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
                      <UserPlus size={12} />
                    </span>
                    Invite people
                  </button>
                ) : null}
              </div>

              {assigneeIds.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (disabled) {
                      notifyPlanningDenied();
                      return;
                    }
                    onCommit([]);
                    toast.success("Assignees removed successfully.");
                    setOpen(false);
                  }}
                  className="w-full px-3 py-2 text-xs text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] cursor-pointer border-t border-[color:var(--color-rule)] text-left"
                >
                  Clear all
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
      <WorkspacePeopleDialog
        open={inviteOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        taskId={taskId}
        onClose={() => setInviteOpen(false)}
        onInvited={(addedUserIds) => {
          // Auto-assign the freshly-added members to the current task —
          // the "Invite people" → assignee flow.
          if (addedUserIds.length === 0) return;
          const merged = Array.from(new Set([...assigneeIds, ...addedUserIds]));
          onCommit(merged);
        }}
      />
      <SpaceShareDialog
        open={shareOpen}
        spaceId={spaceId}
        workspaceId={workspaceId}
        spaceName={spaceName}
        onClose={() => setShareOpen(false)}
      />
      <MemberProfileDialog
        member={profileMember}
        onClose={() => setProfileMember(null)}
      />
    </>
  );
}

/** Resolved Work Role label for a member — the catalogue name, else the
 *  baseline tier slug's label, else null. */
function workRoleLabelOf(member) {
  return (
    member.workRoleName ??
    { manager: "Manager", team_leader: "Team Leader", employee: "Employee" }[
      member.workRole
    ] ??
    null
  );
}
/** Work-role badge + plain Department text shown under a member's name in
 *  the assignee picker so same-named teammates can be told apart. The Work
 *  Role is a small accent pill; the Department is plain muted text. */
function MemberRoleTags({ member }) {
  const role = workRoleLabelOf(member);
  const dept = member.departmentName ?? null;
  const isExternal = Boolean(member.external);
  if (!role && !dept && !isExternal) return null;
  return (
    <span className="mt-0.5 flex min-w-0 flex-wrap items-start gap-1.5">
      {/* Role and department are always shown IN FULL — never truncated.
       *  The line wraps (and long unbroken tokens break with `break-all`)
       *  so however long the values are, both stay completely readable;
       *  the row just grows taller. */}
      {role ? (
        <span className="min-w-0 break-all text-xs text-[color:var(--color-ink-muted)]">
          {role}
        </span>
      ) : null}
      {isExternal ? (
        <span className="inline-flex min-w-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)] border border-[color:var(--color-rule)]">
          External
        </span>
      ) : null}
      {dept ? (
        <span className="inline-flex min-w-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-[color:var(--color-card-hover)] text-[color:var(--color-ink)] border border-[color:var(--color-rule)]">
          <span className="break-all">{dept}</span>
        </span>
      ) : null}
    </span>
  );
}

function MemberRow({
  member,
  label,
  selected,
  onClick,
  onReassign,
  onProfile,
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={
        "group w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer " +
        (selected
          ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]"
          : "")
      }
    >
      {/* Checkbox mirrors the row's selected state so multi-select reads
       *  clearly. The row's own onClick is the single toggle handler —
       *  the checkbox is visual only (pointer-events-none), so clicking
       *  anywhere on the row, checkbox included, toggles once. */}
      <input
        type="checkbox"
        checked={Boolean(selected)}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none shrink-0 accent-[color:var(--color-accent)]"
      />
      {/* In a list row the name is rendered next to the avatar, so
       *  the floating tooltip would just duplicate it (and visually
       *  overlap the row). Suppress it here — the bare initials
       *  circle is enough. */}
      <Avatar name={member.fullName} showTooltip={false} />
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate">
          {label ? (
            <span className="font-medium">{label}</span>
          ) : (
            member.fullName
          )}
        </span>
        <MemberRoleTags member={member} />
      </span>
      {/* Hover-only actions. stopPropagation so clicking these doesn't toggle
       *  assignment. The highlighted row background already conveys selected
       *  state — no separate check icon needed.
       *  Kept in flow at all times (faded out until hover) so their width is
       *  permanently reserved — otherwise appearing on hover would squeeze the
       *  role/department line and make the work role truncate. */}
      <div className="flex items-center gap-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onProfile();
          }}
          title="Profile"
          className="px-1.5 py-0.5 rounded text-sm border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          Profile
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReassign();
          }}
          title="Remove assignees and reassign"
          aria-label="Remove assignees and reassign"
          className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <RefreshCcw size={12} />
        </button>
      </div>
    </div>
  );
}

/** Single-select "Checker" picker — the Accountability twin of AssigneeCell.
 *  Same searchable, avatar-rich popover the assignee field uses, but selects
 *  exactly ONE verifier (or none). The stored value is `verifierId`
 *  (userId | null). Roster + department scoping come from the same `members`
 *  list the assignee picker uses, so both stay consistent. */
function CheckerCell({ verifierId, members, onCommit, disabled = false }) {
  // A task's Checker may be any member of this Space — an internal member OR an
  // external collaborator whose grant already covers the Space (policy change
  // 2026-09-29). Externals used to be filtered out here (defect DF_008 / Mantis
  // 22111) because the server rejected them; that server rule has been lifted,
  // so the shared Space roster — already scoped to people who can open this
  // Space, externals included only when their grant covers it — is offered
  // as-is. Each row is tagged (incl. "External") via MemberRoleTags so the
  // picker stays honest about who is being named as sign-off authority.
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [query, setQuery] = useState("");
  usePortalCoords(open, triggerRef, setCoords, 384);
  useDismissOn(open, triggerRef, menuRef, () => setOpen(false));

  // Reset search + focus the input each time the popover opens (mirrors the
  // assignee picker, incl. preventScroll so the portal doesn't drift).
  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const id = window.setTimeout(
      () => searchRef.current?.focus({ preventScroll: true }),
      0,
    );
    return () => window.clearTimeout(id);
  }, [open]);

  // Resolve the CURRENT checker against the Space roster; fall back to the raw
  // id when the named checker isn't in this Space's roster (e.g. their grant
  // was later revoked) so the field still shows something rather than blank.
  const selected =
    (members ?? []).find((m) => m.userId === verifierId) ??
    (verifierId ? { userId: verifierId, fullName: verifierId } : null);
  const q = query.trim().toLowerCase();
  const filtered =
    q === ""
      ? members
      : members.filter((m) =>
          `${m.fullName ?? ""} ${m.emailId ?? ""}`.toLowerCase().includes(q),
        );

  const pick = (userId) => {
    if (disabled) {
      notifyPlanningDenied();
      return;
    }
    onCommit(userId);
    setOpen(false);
  };

  return (
    <>
      {/* Trigger mirrors the assignee cell: a bare, clickable avatar — the
       *  selected checker's avatar, or the dashed "unassigned" placeholder when
       *  there's none — not a dropdown-styled pill. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-label={
          selected
            ? `Checker: ${selected.fullName}. Click to change.`
            : "No checker — click to set a checker"
        }
        className="cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Avatar name={selected ? selected.fullName : null} />
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: coords.top,
                bottom: coords.bottom,
                left: coords.left,
                maxHeight: coords.maxHeight,
                zIndex: 60,
              }}
              className="w-[24rem] max-w-[calc(100vw-1rem)] rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] flex flex-col"
            >
              <div className="p-2 border-b border-[color:var(--color-rule)]">
                <div className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
                  >
                    <Search size={14} />
                  </span>
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    // Same 200 cap as every other search field in the app — a
                    // search box has no reason to accept an unbounded paste.
                    maxLength={200}
                    placeholder="Search by Name or Email ID"
                    className="fd-search-pill w-full pl-9 pr-8 py-1.5 rounded-full text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] focus:outline-none"
                  />
                  {/* Clear (X) — consistent with the app's other search fields,
                   *  which all offer a one-click reset. */}
                  {query ? (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      aria-label="Clear search"
                      title="Clear search"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
                    >
                      <X size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="overflow-auto flex-1">
                {/* "No checker" clears the verifier. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => pick(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      pick(null);
                    }
                  }}
                  className={
                    "w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] " +
                    (!verifierId
                      ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]"
                      : "")
                  }
                >
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] text-[10px]">
                    —
                  </span>
                  <span className="min-w-0 flex-1 text-left">No checker</span>
                  {!verifierId ? <Check size={14} className="shrink-0" /> : null}
                </div>
                {filtered.map((m) => {
                  const isSel = m.userId === verifierId;
                  return (
                    <div
                      key={m.userId}
                      role="button"
                      tabIndex={0}
                      onClick={() => pick(m.userId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          pick(m.userId);
                        }
                      }}
                      className={
                        "w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--color-card-hover)] " +
                        (isSel
                          ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]"
                          : "")
                      }
                    >
                      <Avatar name={m.fullName} showTooltip={false} />
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block truncate">{m.fullName}</span>
                        <MemberRoleTags member={m} />
                      </span>
                      {isSel ? <Check size={14} className="shrink-0" /> : null}
                    </div>
                  );
                })}
                {filtered.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                    No matches found.
                  </p>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Verification status → [chip classes, label]. Mirrors the task-detail popup
 *  so the List column reads identically. */
const ACCOUNTABILITY_CHIP = {
  none: [
    "bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]",
    "Not submitted",
  ],
  pending: [
    "bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)]",
    "Awaiting checker",
  ],
  approved: [
    "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]",
    "Approved",
  ],
  rejected: [
    "bg-[#FEE2E2] text-[#b91c1c] dark:bg-white/[0.06] dark:text-red-300",
    "Changes requested",
  ],
};

/** Proof-required column cell (List view) — a compact toggle switch. When on,
 *  a task can't be completed / submitted for verification until at least one
 *  proof (file or link) is attached. Commits via the shared task-update path
 *  (optimistic), so it flips in place. */
function ProofRequiredCell({ value, onToggle, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(value)}
      aria-label="Proof Required"
      disabled={disabled}
      title={
        value
          ? "Proof required to complete — click to make optional"
          : "Proof optional — click to require proof before completion"
      }
      onClick={() => onToggle(!value)}
      className={
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed " +
        (value
          ? "bg-[color:var(--color-accent)]"
          : "bg-[color:var(--color-rule-strong)]")
      }
    >
      <span
        className={
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
          (value ? "translate-x-4" : "translate-x-0.5")
        }
      />
    </button>
  );
}

/** Accountability column cell (List view) — the FULL inline verification
 *  workflow: the checker picker, the status chip, and the Submit / Approve /
 *  Reject actions, so a supervisor runs verification without opening the task.
 *  Reuses the SAME endpoints + cache key (`['tasks', listId]`) as the popup.
 *  The API is the real gate — these controls only surface the affordance. */
function AccountabilityCell({
  task,
  members,
  currentUserId,
  listId,
  onUpdate,
  disabled = false,
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const status = task.approvalStatus ?? "none";
  const isChecker =
    Boolean(task.verifierId) &&
    String(task.verifierId) === String(currentUserId);
  const [chipCls, chipLabel] =
    ACCOUNTABILITY_CHIP[status] ?? ACCOUNTABILITY_CHIP.none;

  // Patch THIS task inside the cached `['tasks', listId]` list so the row's
  // chip/buttons flip instantly — no reload, no waiting on the refetch. The
  // subsequent invalidate reconciles with the authoritative server state
  // (e.g. an approved task completing + dropping out of the list).
  function patchTask(patch) {
    qc.setQueryData(["tasks", listId], (old) =>
      old?.data
        ? {
            ...old,
            data: old.data.map((t) =>
              t.id === task.id ? { ...t, ...patch } : t,
            ),
          }
        : old,
    );
  }
  async function submit() {
    setBusy(true);
    try {
      await api.post(`/lists/${listId}/tasks/${task.id}/submit-verification`);
      patchTask({
        approvalStatus: "pending",
        approvalRequestedAt: new Date().toISOString(),
        approvalNote: null,
      });
      toast.success("Submitted for verification.");
      void qc.invalidateQueries({ queryKey: ["tasks", listId] });
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setBusy(false);
    }
  }
  async function verify(decision, note) {
    setBusy(true);
    try {
      await api.post(`/lists/${listId}/tasks/${task.id}/verify`, {
        decision,
        note,
      });
      patchTask(
        decision === "approve"
          ? { approvalStatus: "approved" }
          : { approvalStatus: "rejected", approvalNote: note },
      );
      toast.success(
        decision === "approve" ? "Task approved." : "Changes requested.",
      );
      setRejectOpen(false);
      setRejectNote("");
      void qc.invalidateQueries({ queryKey: ["tasks", listId] });
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  // Reject-note popover — portaled + anchored to the reject icon so entering a
  // note NEVER grows the row (the whole cell stays one line tall).
  const rejectBtnRef = useRef(null);
  const rejectPopRef = useRef(null);
  const [rejectCoords, setRejectCoords] = useState(null);
  usePortalCoords(rejectOpen, rejectBtnRef, setRejectCoords, 260);
  useDismissOn(rejectOpen, rejectBtnRef, rejectPopRef, () => {
    setRejectOpen(false);
    setRejectNote("");
  });

  // Shared 24px icon-button chrome — matches the row's other icon actions
  // (duplicate / delete) so the column reads as part of the same table.
  const iconBtn =
    "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md cursor-pointer transition-colors hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    // ONE horizontal line — checker avatar + status chip + compact icon
    // actions — so the row height matches every other column.
    <div className="flex items-center gap-1.5 min-w-0 w-full">
      <CheckerCell
        verifierId={task.verifierId ?? null}
        members={members}
        onCommit={(verifierId) => onUpdate({ verifierId })}
        disabled={disabled}
      />
      <span
        className={
          "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap " +
          chipCls
        }
      >
        {chipLabel}
      </span>

      {/* Submit / Resubmit — a compact pill, small enough to sit on the chip's
       *  line without adding height. Shown only once a checker is named. */}
      {(status === "none" || status === "rejected") && task.verifierId ? (
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          title={
            status === "rejected"
              ? "Resubmit for verification"
              : "Submit for verification"
          }
          className="inline-flex items-center rounded-md bg-[color:var(--color-primary)] px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50 cursor-pointer whitespace-nowrap"
        >
          {status === "rejected" ? "Resubmit" : "Submit"}
        </button>
      ) : null}

      {/* Approve (check) / Reject (✕) — icon buttons for the named checker. */}
      {status === "pending" && isChecker ? (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={() => verify("approve", "")}
            title="Approve"
            aria-label="Approve"
            className={iconBtn + " text-[color:var(--color-success)]"}
          >
            <Check size={15} />
          </button>
          <button
            ref={rejectBtnRef}
            type="button"
            disabled={busy}
            onClick={() => setRejectOpen((v) => !v)}
            title="Request changes"
            aria-label="Request changes"
            className={iconBtn + " text-[color:var(--color-priority-urgent)]"}
          >
            <X size={15} />
          </button>
        </>
      ) : null}

      {rejectOpen && rejectCoords
        ? createPortal(
            <div
              ref={rejectPopRef}
              style={{
                position: "fixed",
                top: rejectCoords.top,
                bottom: rejectCoords.bottom,
                left: rejectCoords.left,
                zIndex: 60,
              }}
              className="w-64 max-w-[calc(100vw-1rem)] rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl p-2"
            >
              <textarea
                autoFocus
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={3}
                placeholder="Reason for the changes (required)"
                className="w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2 py-1 text-xs text-[color:var(--color-ink)]"
              />
              <div className="mt-2 flex items-center justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setRejectOpen(false);
                    setRejectNote("");
                  }}
                  className="rounded-md border border-[color:var(--color-rule)] px-2 py-1 text-xs text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={busy || !rejectNote.trim()}
                  onClick={() => verify("reject", rejectNote.trim())}
                  className="rounded-md bg-[color:var(--color-priority-urgent)] px-2 py-1 text-xs font-semibold text-white disabled:opacity-50 cursor-pointer"
                >
                  Send Back
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// Friendly access-level labels — mirror the Sharing & Permissions dialog
// (SpaceShareDialog) so the member-profile panel reads "View only" /
// "Full edit" rather than the raw enum. Keep in sync with that dialog.
const ACCESS_LEVEL_LABEL = {
  full: "Full edit",
  edit: "Edit",
  comment: "Comment",
  view: "View only",
};

/** Right-side drawer showing a workspace member's profile — avatar, name,
 *  Email ID, work role, department, access level, and join date. */
function MemberProfileDialog({ member, onClose }) {
  if (!member) return null;

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/40 flex justify-end"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[420px] h-full bg-[color:var(--color-card)] border-l border-[color:var(--color-rule)] shadow-2xl text-[color:var(--color-ink)] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-5 py-5 border-b border-[color:var(--color-rule)] flex items-start gap-4">
          <span
            className="h-16 w-16 rounded-full inline-flex items-center justify-center text-lg font-semibold text-white shrink-0"
            style={{ backgroundColor: avatarColor(member.fullName) }}
          >
            {initials(member.fullName)}
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base text-[color:var(--color-ink-strong)] truncate">
              {member.fullName}
            </p>
            <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
              {member.emailId}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — member details */}
        <dl className="px-5 py-4 space-y-3 text-sm">
            <div>
              <dt className="text-[color:var(--color-ink-muted)] text-xs mb-1">
                Email ID
              </dt>
              <dd className="text-[color:var(--color-ink)]">
                {member.emailId}
              </dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-ink-muted)] text-xs mb-1">
                Work role
              </dt>
              <dd className="text-[color:var(--color-ink)]">
                {workRoleLabelOf(member) ?? "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-ink-muted)] text-xs mb-1">
                Department
              </dt>
              <dd className="text-[color:var(--color-ink)]">
                {member.departmentName ?? "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-ink-muted)] text-xs mb-1">
                Access level
              </dt>
              <dd className="text-[color:var(--color-ink)]">
                {ACCESS_LEVEL_LABEL[member.accessLevel] ?? "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-ink-muted)] text-xs mb-1">
                Joined this workspace
              </dt>
              <dd className="text-[color:var(--color-ink)]">
                {fmtDateShort(member.addedAt, true)}
              </dd>
            </div>
          </dl>
      </div>
    </div>,
    document.body,
  );
}

function AvatarStack({ members }) {
  // Every resolvable assignee is drawn as its own avatar — no "+N"
  // overflow chip. `members` comes from the Space's scoped assignable
  // roster, so an assignee outside that roster (another department, or
  // one who lost Space access) simply isn't drawn here. (`totalCount`
  // was previously used to render an overflow badge; the badge was
  // removed per request, so the prop is no longer read.)
  const list = Array.isArray(members) ? members : [];
  if (list.length === 0) return <Avatar name={null} />;
  return (
    <div className="inline-flex items-center -space-x-2">
      {list.map((m, i) => (
        <span
          key={m.userId}
          className="ring-2 ring-[color:var(--color-card)] rounded-full"
          style={{ zIndex: list.length - i }}
        >
          <Avatar name={m.fullName} />
        </span>
      ))}
    </div>
  );
}

/** Read-only task detail panel opened from clicking a task name in the list.
 *  Shows the full name (no truncation), status, dates, priority, assignees,
 *  and an editable description. Inline edits route through `onUpdate`. */
function TaskDetailDialog({
  task,
  members,
  statuses,
  currentUserId,
  onClose,
  onUpdate,
  /** Parent Space id — forwarded to the comment thread so it can gate
   *  the compose box on the viewer's per-Space access level. */
  spaceId,
  /** Parent List id — needed to fetch the task's Activity timeline. */
  listId,
  /** Space / workspace context threaded through to the inline field
   *  editors below. AssigneeCell needs these for its invite / share /
   *  profile sub-dialogs; forwarding them lets the detail modal's
   *  Assignee / Priority / date fields open the same editors as the
   *  List and Board rows (they were previously display-only). */
  workspaceId,
  workspaceName,
  spaceName,
  /** Personal (owner-only) Space — hides the Created by + Assignees fields,
   *  which carry no meaning when the Space has a single owner. */
  isPersonal = false,
  allTasks,
}) {
  const [description, setDescription] = useState(task?.description ?? "");
  // Escape closes the detail dialog, matching the shared Modal primitive and
  // every other dialog in the app (QA-100 / D-UI-002: it previously closed only
  // via its X button). A nested field popover (Assignee / Priority / dates) that
  // is open calls `preventDefault()` on the same keystroke, so Escape peels the
  // overlays off one layer at a time instead of collapsing both at once.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Recurrence ("Repeat") — only offered on top-level tasks (a template that
  // spawns occurrences). The rule query drives the "Repeats" badge; the dialog
  // owns create/edit/pause/stop. Shares the ['recurring', taskId] cache key.
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const isTopLevel = !task?.parentTaskId;
  const recurringQuery = useQuery({
    queryKey: ["recurring", task?.id],
    enabled: Boolean(task?.id) && isTopLevel,
    queryFn: async () => {
      const res = await api.get(`/tasks/${task.id}/recurring`);
      return recurringRulesResponse.parse(res.data);
    },
  });
  const recurringRuleRow = recurringQuery.data?.data?.[0] ?? null;
  // May this viewer actually rename the task? The heading below is
  // contentEditable, and it used to be so UNCONDITIONALLY — so a member with
  // `view` / `comment` access could type into the title even though the
  // backend refuses a `name` write at those levels (`can('task.update', …)`),
  // making the edit silently revert (QA). Same hook the attachments cell uses;
  // it fails open while loading, which is safe because the API is the real
  // gate — this only decides whether we offer the affordance.
  const { canModify: canRenameTask } = useSpaceAccess(spaceId);

  // [Accountability · Phase 2] Checker verification actions. Self-invalidates
  // the list query (realtime skips the acting tab). The API is the real gate —
  // these buttons only offer the affordance.
  const verifyQc = useQueryClient();
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  // [AI · Phase 4B] Advisory AI proof review for this submission (null when the
  // feature is off / no review yet). Fetched when the task has a checker and is
  // in a verification state; realtime task changes refetch it.
  const proofReviewQuery = useQuery({
    queryKey: ["task-proof-review", task?.id],
    enabled: Boolean(task?.verifierId) && (task?.approvalStatus ?? "none") !== "none",
    queryFn: async () => {
      const res = await api.get(`/lists/${listId}/tasks/${task.id}/proof-review`);
      return res.data; // may be null
    },
  });
  const proofReview = proofReviewQuery.data;
  async function submitForVerification() {
    setVerifyBusy(true);
    try {
      await api.post(`/lists/${listId}/tasks/${task.id}/submit-verification`);
      toast.success("Submitted for verification.");
      await verifyQc.invalidateQueries({ queryKey: ["tasks", listId] });
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setVerifyBusy(false);
    }
  }
  async function runVerify(decision, note) {
    setVerifyBusy(true);
    try {
      await api.post(`/lists/${listId}/tasks/${task.id}/verify`, {
        decision,
        note,
      });
      toast.success(
        decision === "approve" ? "Task approved." : "Changes requested.",
      );
      setRejectOpen(false);
      setRejectNote("");
      await verifyQc.invalidateQueries({ queryKey: ["tasks", listId] });
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setVerifyBusy(false);
    }
  }
  // Editable <h2> ref — see commitName / the heading element below for
  // the contentEditable pattern. We don't keep the title in React
  // state because contentEditable + React reconciliation fight each
  // other on every keystroke; uncontrolled-via-ref is the standard
  // workaround.
  const titleRef = useRef(null);

  // Re-sync local state whenever a different task is opened. The
  // heading text is set imperatively via ref to keep React out of the
  // contentEditable element's render loop.
  useEffect(() => {
    setDescription(task?.description ?? "");
    if (titleRef.current) {
      titleRef.current.textContent = task?.name ?? "";
    }
  }, [task?.id]);

  // Deep-link focus — a notification link of the form
  // `?task=<taskId>&focusComment=<commentId>` (or `&focusAttachment=<id>`)
  // opens this dialog via the page's `?task` handler, then scrolls + flashes
  // the specific comment / attachment once it renders (they load async, so the
  // shared helper polls). Reuses lib/focusFlash — new item types adopt it by
  // emitting a `focus…` param + tagging their element with `data-focus-id`.
  const [focusParams, setFocusParams] = useSearchParams();
  const focusDoneRef = useRef("");
  useEffect(() => {
    if (!task) return undefined;
    const target =
      focusParams.get("focusComment") || focusParams.get("focusAttachment");
    // Reset the guard when the param is absent (after a flash strips it) so
    // re-clicking the SAME comment/attachment notification fires again.
    if (!target) {
      focusDoneRef.current = "";
      return undefined;
    }
    const fireKey = `${task.id}:${target}`;
    // Fire ONCE per (task, target). Set the guard synchronously and do NOT
    // return scrollAndFlash's cancel-cleanup: React StrictMode double-invokes
    // effects (run → cleanup → run), and returning the cleanup would cancel
    // the first run while the guard blocks the second, so the flash never
    // happens. scrollAndFlash is self-bounded (≤5s poll, then stops) and the
    // WAAPI flash self-ends, so nothing lingers or leaks.
    if (focusDoneRef.current === fireKey) return undefined;
    focusDoneRef.current = fireKey;
    // Strip the transient focus params ONLY AFTER the element is located
    // (via onFound) — an immediate strip would clear `autoFocusAttachmentId`
    // before the attachments popover auto-opens. Keep `task` so the panel
    // stays open. Replace — no history entry — so a refresh / Back nav
    // doesn't re-flash and the URL stays clean.
    scrollAndFlash(target, {
      onFound: () => {
        const stripped = new URLSearchParams(focusParams);
        stripped.delete("focusComment");
        stripped.delete("focusAttachment");
        setFocusParams(stripped, { replace: true });
      },
    });
    return undefined;
  }, [task?.id, focusParams]);
  if (!task) return null;

  const status =
    statuses.find((s) => s.name === task.status) ?? statuses[0] ?? null;

  /** Put the caret in the title so the hover Pencil actually starts an edit
   *  (a contentEditable node needs focus + an explicit range; without the
   *  collapse the whole title would be selected and the next keystroke would
   *  wipe it). Same range dance the `onInput` truncation uses. */
  function focusTitleForEdit() {
    const el = titleRef.current;
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false); // caret at the END, nothing selected
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  function commitName() {
    const raw = titleRef.current?.textContent ?? "";
    const trimmed = raw.trim();
    if (trimmed.length < 2 || trimmed === task?.name) {
      // Too short (or unchanged) — restore the previous text so the
      // heading never renders blank/invalid. Flag the 2-character rule
      // (matching the Create Task dialog) when the user typed something.
      if (trimmed.length > 0 && trimmed.length < 2) {
        toast.error("Task Name must be at least 2 characters.");
      }
      if (titleRef.current) titleRef.current.textContent = task?.name ?? "";
      return;
    }
    const tooLong = taskNameLengthError(trimmed);
    if (tooLong) {
      // The heading is contentEditable, so a DOM `maxLength` could not apply
      // here even if these fields were hard-limited. The text is left on
      // screen to be trimmed rather than restored.
      toast.error(tooLong);
      return;
    }
    if (CONTAINS_EMOJI.test(trimmed)) {
      toast.error("Task Name cannot contain emojis.");
      if (titleRef.current) titleRef.current.textContent = task?.name ?? "";
      return;
    }
    // Reject a rename to a junk, content-free name (bug 0013372) and
    // restore the previous title so the heading stays valid.
    if (!isMeaningfulTaskName(trimmed)) {
      toast.error("Task Name must include at least one letter or number.");
      if (titleRef.current) titleRef.current.textContent = task?.name ?? "";
      return;
    }
    onUpdate({ name: trimmed });
  }
  function commitDescription() {
      if (description === (task?.description ?? "")) return;
      onUpdate({ description: description || null });
    }

    return createPortal(
      <div
        onClick={onClose}
        // z-50 = the shared Modal primitive's layer. It MUST sit BELOW the task
        // field popovers (Assignee / Priority / Start + Due date), which portal
        // to the document body at zIndex 60 so they float above any z-50 modal —
        // that's how they already work in List / Board. This modal used to be
        // z-[70] (ABOVE the popover layer), which stranded those popovers behind
        // the backdrop once the detail-modal fields were made editable. The
        // member profile drawer opened from the Assignee picker stays z-[70], so
        // the stack reads modal (50) < popover (60) < profile drawer (70).
        className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-y-auto"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          // Dialog semantics so assistive tech announces this as a modal and
          // names it from the task heading below (QA-100 / D-UI-002).
          role="dialog"
          aria-modal="true"
          aria-labelledby="task-detail-title"
          // Fixed-height modal: cap to the viewport (minus the top margin +
          // backdrop padding) and let the BODY scroll internally. Without
          // this the card grew past the viewport and the whole pop-up
          // scrolled inside the backdrop instead of its content scrolling.
          className="w-full max-w-4xl mt-6 max-h-[calc(100vh-3rem)] flex flex-col overflow-hidden rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
        >
          <div className="shrink-0 flex items-start gap-3 px-5 py-4 border-b border-[color:var(--color-rule)]">
            {/* Status now lives in the details grid below (Status row), so
             *  it's no longer duplicated here in the header. */}
            {/* The task title is a semantic <h3> rendered contentEditable
             *  so the dialog has a proper heading in the document
             *  outline (better for screen readers + assistive tech)
             *  while still allowing inline rename. The text is set
             *  imperatively via `titleRef` to keep React's virtual DOM
             *  out of every keystroke.
             *
             *  Editing is gated on `canRenameTask` (edit/full access) — a
             *  view/comment member gets a plain, non-editable heading instead
             *  of one that accepts keystrokes the API will refuse.
             *
             *  When it IS editable the styling advertises it (hover surface +
             *  "Click to rename" tooltip). Previously it looked like static
             *  text with no affordance, so testers reported the title as
             *  "editable without an edit option" — the capability was fine,
             *  the discoverability wasn't. */}
            <span className="group/title flex-1 flex items-start gap-1 min-w-0">
            <h3
              id="task-detail-title"
              ref={titleRef}
              contentEditable={canRenameTask}
              suppressContentEditableWarning
              spellCheck={canRenameTask}
              role={canRenameTask ? "textbox" : undefined}
              aria-label="Task Name"
              title={canRenameTask ? "Click to rename" : undefined}
              onBlur={canRenameTask ? commitName : undefined}
              onKeyDown={(e) => {
                // Enter (without Shift) commits and blurs — task titles
                // are single-line. Shift+Enter still inserts a newline
                // for users who need it, then it'll be trimmed on commit.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                // Escape cancels: revert text to the saved task name.
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.currentTarget.textContent = task?.name ?? "";
                  e.currentTarget.blur();
                }
              }}
              // Cap user-entered text at 200 chars — same limit the old
              // textarea enforced via maxLength. We can't use the
              // attribute here, so trim on input.
              onInput={(e) => {
                const text = e.currentTarget.textContent ?? "";
                if (text.length > 100) {
                  e.currentTarget.textContent = text.slice(0, 100);
                  // Restore caret to end after the truncation rewrite.
                  const range = document.createRange();
                  range.selectNodeContents(e.currentTarget);
                  range.collapse(false);
                  const sel = window.getSelection();
                  sel?.removeAllRanges();
                  sel?.addRange(range);
                }
              }}
              className={
                // NOT `flex-1` — the heading sizes to its TEXT so the hover
                // Pencil sits immediately after the title instead of being
                // pushed out to the far right edge of the header (QA).
                // `min-w-0` keeps `break-words` working so a long title still
                // wraps inside the dialog rather than overflowing.
                "min-w-0 bg-transparent text-xl font-semibold leading-snug text-[color:var(--color-ink-strong)] outline-none rounded px-1.5 py-1 break-words " +
                (canRenameTask
                  ? // Signals "this is a field": a hover surface + focus ring
                    // and a text caret, so a rename is a deliberate act rather
                    // than a surprise.
                    "cursor-text hover:bg-[color:var(--color-card-hover)] focus:bg-[color:var(--color-card-hover)] focus:ring-1 focus:ring-[color:var(--color-accent)] transition-colors"
                  : "cursor-default")
              }
            />
            {/* Hover-only Pencil — the SAME affordance the List / Board rows
             *  use for "Edit task", so the rename entry point is discoverable
             *  here too instead of the title looking like static text (QA).
             *  Clicking it drops the caret at the end of the title. Only
             *  rendered when the viewer may actually rename. */}
            {canRenameTask ? (
              <button
                type="button"
                onClick={focusTitleForEdit}
                title="Rename task"
                aria-label="Rename task"
                className="mt-1.5 shrink-0 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer opacity-0 group-hover/title:opacity-100 focus-visible:opacity-100 transition-opacity"
              >
                <Pencil size={12} />
              </button>
            ) : null}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              title="Close"
              className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Scrollable body — everything below the pinned header scrolls
           *  here, so the pop-up stays put and only its content moves. */}
          <div className="flex-1 overflow-y-auto">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 px-5 py-4 text-sm">
              {isPersonal ? null : (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-[color:var(--color-ink-muted)]">
                    Created by
                  </dt>
                  <dd className="min-w-0">
                    <CreatedByCell name={task.createdByName ?? null} />
                  </dd>
                </div>
              )}
              {isPersonal ? null : (
                <div className="flex items-center justify-between">
                  <dt className="text-[color:var(--color-ink-muted)]">
                    Assignees
                  </dt>
                  <dd>
                    <AssigneeCell
                      assigneeIds={task.assigneeIds ?? []}
                      members={members}
                      currentUserId={currentUserId}
                      workspaceId={workspaceId}
                      workspaceName={workspaceName}
                      spaceId={spaceId}
                      spaceName={spaceName}
                      taskId={task.id}
                      allTasks={allTasks}
                      statuses={statuses}
                      onCommit={(assigneeIds) => onUpdate({ assigneeIds })}
                      disabled={task.canEditAssignee === false}
                    />
                  </dd>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <dt className="text-[color:var(--color-ink-muted)]">Status</dt>
                <dd>{status ? <StatusPill status={status} /> : "—"}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-[color:var(--color-ink-muted)]">
                  Priority
                </dt>
                <dd>
                  <PriorityCell
                    priority={task.priority ?? null}
                    onCommit={(priority) => onUpdate({ priority })}
                    disabled={task.canEditPlanning === false}
                    completed={isTerminalStatusName(statuses, task.status)}
                  />
                </dd>
              </div>
              {/* Start / Due / Created / Updated — use `fmtIsoDateTime`
               *  so the time component renders when the user set one
               *  ("26/05/2026 04:30 PM"), and quietly omits it for
               *  date-only tasks ("26/05/2026"). `fmtDateShort` was
               *  date-only across the board, which hid the start/due
               *  times the date picker explicitly lets users set. */}
              <div className="flex items-center justify-between">
                <dt className="text-[color:var(--color-ink-muted)]">
                  Start Date and Time
                </dt>
                <dd>
                  <StartDateCell
                    startDate={task.startDate ?? null}
                    dueDate={task.dueDate ?? null}
                    startDateHasTime={task.startDateHasTime ?? false}
                    dueDateHasTime={task.dueDateHasTime ?? false}
                    onCommit={(patch) => onUpdate(patch)}
                    disabled={task.canEditPlanning === false}
                    showTime
                  />
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-[color:var(--color-ink-muted)]">
                  Due Date and Time
                </dt>
                <dd>
                  <DueDateCell
                    startDate={task.startDate ?? null}
                    dueDate={task.dueDate ?? null}
                    startDateHasTime={task.startDateHasTime ?? false}
                    dueDateHasTime={task.dueDateHasTime ?? false}
                    dueEndOfDay={task.dueEndOfDay ?? false}
                    completedAt={task.completedAt ?? null}
                    onCommit={(patch) => onUpdate(patch)}
                    disabled={task.canEditPlanning === false}
                    showTime
                  />
                </dd>
              </div>
              <div className="flex items-center justify-between">
                {/* "Date Created" / "Date Updated" — match the canonical
                 *  List/Board/Gantt column labels (COLUMN_ORDER) so the task
                 *  date labels read the same everywhere. */}
                <dt className="text-[color:var(--color-ink-muted)]">Date Created</dt>
                <dd className="text-sm text-[color:var(--color-ink)]">
                  {fmtIsoDateTime(task.createdAt, true)}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-[color:var(--color-ink-muted)]">Date Updated</dt>
                <dd className="text-sm text-[color:var(--color-ink)]">
                  {fmtIsoDateTime(task.updatedAt, true)}
                </dd>
              </div>
            </dl>

            {isTopLevel ? (
              <div className="px-5 py-3 border-t border-[color:var(--color-rule)] flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Repeat size={15} className="text-[color:var(--color-ink-muted)]" />
                  <span className="text-[color:var(--color-ink-muted)]">Repeat</span>
                  {recurringRuleRow ? (
                    <span
                      className={
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                        (recurringRuleRow.isActive
                          ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]"
                          : "bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]")
                      }
                    >
                      {recurringRuleRow.isActive ? "Recurring" : "Paused"}
                    </span>
                  ) : null}
                  {/* The schedule itself, next to the state badge — the panel
                   *  used to show only "Recurring"/"Paused", so which days the
                   *  series ran on was invisible until you opened the editor
                   *  (DF_006 / Mantis 22086). */}
                  {recurringRuleRow &&
                  summariseSchedule(recurringRuleRow.schedule) ? (
                    <span className="text-xs text-[color:var(--color-ink-muted)]">
                      {summariseSchedule(recurringRuleRow.schedule)}
                    </span>
                  ) : null}
                </div>
                {/* Gated on planning access like the other schedule controls —
                 *  a view / comment member can see the Recurring/Paused badge
                 *  but can't open the editor to change the schedule. */}
                <button
                  type="button"
                  onClick={() => setRecurrenceOpen(true)}
                  disabled={task.canEditPlanning === false}
                  title={
                    task.canEditPlanning === false
                      ? "You don't have permission to change the schedule"
                      : undefined
                  }
                  className="rounded-lg border border-[color:var(--color-rule)] px-3 py-1 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  {recurringRuleRow ? "Edit" : "Set repeat"}
                </button>
              </div>
            ) : null}

            {/* The tenant-defined Custom Fields section used to render here.
             *  Removed with the Company Admin Custom Fields page; the server
             *  still stores `task.customFields` and only validates keys that
             *  are actually submitted, so dropping the inputs changes nothing
             *  about create/update. */}

            {/* [Accountability] Optional per-task checker, expected output, and
             *  proof requirement. Auto-commit like the rest of this card. Hidden
             *  on personal Spaces (no cross-person verification there). All
             *  fields default off, so tasks that never set them are unchanged. */}
            {isPersonal ? null : (
              <div className="px-5 py-4 border-t border-[color:var(--color-rule)]">
                {/* Header — icon + title + one-line purpose so a first-time
                 *  user immediately understands what this section is for. */}
                <div className="flex items-start gap-2.5 mb-3">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]">
                    <ShieldCheck size={16} />
                  </span>
                  <div className="min-w-0">
                    <h4 className="m-0 text-sm font-semibold text-[color:var(--color-ink-strong)]">
                      Task Checker
                    </h4>
                    <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                      Decide who signs off this task and what proof counts as
                      done. Every field here is optional.
                    </p>
                  </div>
                </div>

                {/* Each control is a labelled row: bold name + a plain-English
                 *  helper on the left, the control on the right — so the
                 *  purpose of each is obvious without prior knowledge. */}
                <div className="rounded-lg border border-[color:var(--color-rule)] divide-y divide-[color:var(--color-rule)]">
                  <div className="flex items-start justify-between gap-3 px-3 py-3">
                    <div className="min-w-0">
                      <p className="m-0 text-sm font-medium text-[color:var(--color-ink)]">
                        Checker
                      </p>
                      <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                        Person who reviews and approves this task once it&apos;s
                        marked done.
                      </p>
                    </div>
                    <div className="shrink-0">
                      <CheckerCell
                        verifierId={task.verifierId ?? null}
                        members={members}
                        onCommit={(verifierId) => onUpdate({ verifierId })}
                        disabled={task.canEditAssignee === false}
                      />
                    </div>
                  </div>

                  <label className="flex items-start justify-between gap-3 px-3 py-3 cursor-pointer">
                    <div className="min-w-0">
                      <p className="m-0 text-sm font-medium text-[color:var(--color-ink)]">
                        Proof Required
                      </p>
                      <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                        Block completion until the assignee attaches proof (a
                        file or link).
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={Boolean(task.proofRequired)}
                      onChange={(e) =>
                        onUpdate({ proofRequired: e.target.checked })
                      }
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--color-accent)] cursor-pointer"
                    />
                  </label>

                  <div className="px-3 py-3">
                    <p className="m-0 text-sm font-medium text-[color:var(--color-ink)]">
                      Expected output
                    </p>
                    <p className="mt-0.5 mb-2 text-xs text-[color:var(--color-ink-muted)]">
                      Describe what a finished task should produce — the checker
                      verifies the work against this.
                    </p>
                    <textarea
                      key={task.id}
                      defaultValue={task.expectedOutput ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (task.expectedOutput ?? ""))
                          onUpdate({ expectedOutput: v });
                      }}
                      rows={2}
                      placeholder="e.g. A signed PDF report uploaded to Files"
                      className="w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm text-[color:var(--color-ink)]"
                    />
                  </div>
                </div>
                {/* Verification status + checker actions — only for tasks that
                 *  name a checker. The API enforces who may act; these controls
                 *  just surface the affordance. */}
                {task.verifierId ? (
                  <div className="mt-4 border-t border-[color:var(--color-rule)] pt-3">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-sm text-[color:var(--color-ink-muted)]">
                        Verification
                      </dt>
                      <dd>
                        {(() => {
                          const s = task.approvalStatus ?? "none";
                          const chip = (cls, label) => (
                            <span
                              className={
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                                cls
                              }
                            >
                              {label}
                            </span>
                          );
                          if (s === "pending")
                            return chip(
                              "bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)]",
                              "Awaiting checker",
                            );
                          if (s === "approved")
                            return chip(
                              "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]",
                              "Approved",
                            );
                          if (s === "rejected")
                            return chip(
                              "bg-[#FEE2E2] text-[#b91c1c] dark:bg-white/[0.06] dark:text-red-300",
                              "Changes requested",
                            );
                          return chip(
                            "bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]",
                            "Not submitted",
                          );
                        })()}
                      </dd>
                    </div>
                    {task.approvalStatus === "rejected" && task.approvalNote ? (
                      <p className="mt-2 rounded-lg bg-[#FEF2F2] px-3 py-2 text-xs text-[#b91c1c] dark:bg-white/[0.04] dark:text-red-300">
                        <span className="font-semibold">Checker note:</span>{" "}
                        {task.approvalNote}
                      </p>
                    ) : null}
                    {proofReview ? (
                      <div className="mt-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-accent-strong)]">
                            AI proof check
                          </span>
                          {(() => {
                            const v = proofReview.verdict;
                            const cls =
                              v === "looks_correct"
                                ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]"
                                : v === "needs_human_review"
                                  ? "bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)]"
                                  : "bg-[#FEE2E2] text-[#b91c1c] dark:bg-white/[0.06] dark:text-red-300";
                            const label = String(v || "")
                              .replace(/_/g, " ")
                              .replace(/^\w/, (c) => c.toUpperCase());
                            return (
                              <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold " + cls}>
                                {label || "Reviewed"}
                              </span>
                            );
                          })()}
                          {typeof proofReview.confidence === "number" ? (
                            <span className="text-[11px] text-[color:var(--color-ink-muted)] tabular-nums">
                              {proofReview.confidence}%
                            </span>
                          ) : null}
                        </div>
                        {proofReview.summary ? (
                          <p className="mt-2 text-xs text-[color:var(--color-ink)]">
                            {proofReview.summary}
                          </p>
                        ) : null}
                        {Array.isArray(proofReview.findings) &&
                        proofReview.findings.length > 0 ? (
                          <ul className="mt-2 list-disc pl-4 text-[11px] text-[color:var(--color-ink-muted)]">
                            {proofReview.findings.map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        ) : null}
                        {proofReview.humanReviewRequired ? (
                          <p className="mt-2 text-[11px] font-medium text-[color:var(--color-warning)]">
                            Flagged for human review.
                          </p>
                        ) : null}
                        <p className="mt-2 text-[10px] italic text-[color:var(--color-ink-muted)]">
                          AI-assisted guidance — you make the final decision.
                        </p>
                      </div>
                    ) : null}
                    {task.approvalStatus === "none" ||
                    task.approvalStatus === "rejected" ? (
                      <button
                        type="button"
                        disabled={verifyBusy}
                        onClick={submitForVerification}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--color-primary)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50 cursor-pointer"
                      >
                        Submit for Verification
                      </button>
                    ) : null}
                    {task.approvalStatus === "pending" &&
                    String(task.verifierId) === String(currentUserId) ? (
                      <div className="mt-3">
                        {rejectOpen ? (
                          <div className="flex flex-col gap-2">
                            <textarea
                              value={rejectNote}
                              onChange={(e) => setRejectNote(e.target.value)}
                              rows={2}
                              placeholder="Reason for the changes requested (required)"
                              className="w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm text-[color:var(--color-ink)]"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={verifyBusy || !rejectNote.trim()}
                                onClick={() => runVerify("reject", rejectNote.trim())}
                                className="rounded-lg bg-[color:var(--color-priority-urgent)] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 cursor-pointer"
                              >
                                Send Back
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRejectOpen(false);
                                  setRejectNote("");
                                }}
                                className="rounded-lg border border-[color:var(--color-rule)] px-3 py-1.5 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={verifyBusy}
                              onClick={() => runVerify("approve", "")}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 cursor-pointer"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={verifyBusy}
                              onClick={() => setRejectOpen(true)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] px-3 py-1.5 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}

            <div className="px-5 py-4 border-t border-[color:var(--color-rule)]">
              <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2 mt-0">
                Description
              </h3>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={commitDescription}
                rows={3}
                placeholder="Enter a Description"
                className={
                  "w-full resize-y bg-[color:var(--color-canvas)] border rounded px-2 py-1.5 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] " +
                  ((description ?? "").length > 5000
                    ? "border-[color:var(--color-priority-urgent)]"
                    : "border-[color:var(--color-rule)]")
                }
              />
              {(description ?? "").length > 5000 ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  Description must be at most 5000 characters.
                </p>
              ) : null}
            </div>

            {/* Files & Links — the modal previously had no way to see or reach
             *  a task's attachments even though the list view shows a count.
             *  Reuse the exact same TaskAttachmentsCell (button + popover: list,
             *  download/open, upload, add-link, remove) so the modal and the
             *  list column stay in lockstep. */}
            <div className="px-5 py-4 border-t border-[color:var(--color-rule)]">
              <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2 mt-0">
                Files &amp; Links
              </h3>
              <TaskAttachmentsCell
                taskId={task.id}
                spaceId={spaceId}
                autoFocusAttachmentId={focusParams.get("focusAttachment")}
              />
            </div>

            <TaskComments
              taskId={task.id}
              currentUserId={currentUserId}
              spaceId={spaceId}
            />

            {/* Activity / audit log — who changed what (old → new) and when. */}
            <TaskActivity taskId={task.id} listId={listId} />
          </div>
        </div>

        {/* Repeat / recurrence editor (portals itself to body). React
         *  synthetic events bubble through the REACT tree, not the DOM — so a
         *  click inside this portaled modal would otherwise reach the detail
         *  dialog's outer `onClick={onClose}` backdrop above and close the whole
         *  task detail (that was the "clicking Custom closes everything" bug).
         *  Stop propagation here so the Repeat modal's clicks stay contained. */}
        {isTopLevel ? (
          <span
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <RecurrenceDialog
              open={recurrenceOpen}
              taskId={task.id}
              taskName={task.name}
              onClose={() => setRecurrenceOpen(false)}
            />
          </span>
        ) : null}
      </div>,
      document.body,
    );
  }

  /* Per-task comment thread + list-view comment cell are now shared
   * between SpacePage, TaskMonitorPage, and TeamTasksPage. See the
   * imports at the top of this file:
   *   import { CommentsCell, TaskComments } from '../../components/TaskComments'
   *
   * Any visual / behavioral tweaks should land in that one file so all
   * three pages stay in lockstep — the local copies that used to live
   * here have been removed.
   *
   * SpacePage's task fetcher doesn't include a `commentCount` on each
   * row, so SpacePage's `<CommentsCell>` instances pass
   * `alwaysFetchCount` to opt into the eager-count behaviour (the
   * heavier views feed counts via `initialCount` to avoid N-per-row
   * GETs on mount). */
  // ---------- Unified Start + Due date popover ----------
  //
  // Dual pills (Start / Due), preset shortcuts, calendar grid with range
  // highlight, and a per-pill "Add time" column for 15-minute time slots.

  function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  }
  function addMonths(d, n) {
    const x = new Date(d);
    x.setMonth(x.getMonth() + n);
    return x;
  }
  /** Returns the next occurrence of the given day-of-week (0=Sun..6=Sat),
   *  strictly *after* `from` (or `from` itself if `inclusive` is true). */
  function nextDow(from, dow, inclusive = false) {
    const x = startOfDay(from);
    const cur = x.getDay();
    let delta = (dow - cur + 7) % 7;
    if (delta === 0 && !inclusive) delta = 7;
    return addDays(x, delta);
  }
  // The popover edits WALL-CLOCK Dates: a stored instant is turned into the
  // wall-clock it shows as (toWall) and back into a UTC instant on commit
  // (fromWall). A date with a time uses the person's display zone; a
  // date-only deadline uses the company's calendar (the zone formatDate /
  // dayKey read days in), so the day picked is the day everyone sees.
  function wallZone(hasTime) {
    return (hasTime ? getDisplayTimeZone() : getCalendarTimeZone()) || "local";
  }
  function toWall(value, hasTime) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const dt = DateTime.fromJSDate(d).setZone(wallZone(hasTime));
    return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond);
  }
  function fromWall(d, hasTime) {
    return DateTime.fromObject(
      {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
        hour: d.getHours(),
        minute: d.getMinutes(),
        second: d.getSeconds(),
        millisecond: d.getMilliseconds(),
      },
      { zone: wallZone(hasTime) },
    )
      .toUTC()
      .toISO();
  }
  /** Today (00:00) as a wall-clock Date on the company's calendar. */
  function todayWall() {
    const t = DateTime.now().setZone(wallZone(false));
    return new Date(t.year, t.month - 1, t.day);
  }
  /** A STORED date (ISO / instant): a date-only value is the company's
   *  calendar day (`formatDate`), a value with a time is the day it falls on
   *  in the person's zone — so the date cell agrees with its time. */
  function fmtDateShort(value, hasTime = false) {
    return hasTime ? formatPattern(value, datePattern()) : formatDate(value);
  }
  /** "12:00 AM" / "11:59 PM" (or "00:00" / "23:59") — the start / end of a
   *  date-only day, in the person's clock. */
  function startOfDayLabel() {
    return fmtTime(new Date(2000, 0, 1, 0, 0));
  }
  function endOfDayLabel() {
    return fmtTime(new Date(2000, 0, 1, 23, 59));
  }
  // Wall-clock Dates (the date popover) are formatted by their parts (never
  // zone-shifted again), in the person's date format and 12/24-hour clock.
  function fmtWallDate(d) {
    return DateTime.fromObject(
      { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() },
      { locale: "en-GB" },
    ).toFormat(datePattern());
  }
  function fmtTime(d) {
    return DateTime.fromObject({ hour: d.getHours(), minute: d.getMinutes() }, { locale: "en-GB" })
      .toFormat(timePattern())
      .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
  }
  function fmtDow(d) {
    return d.toLocaleDateString("en-GB", { weekday: "short" });
  }
  function presetDefs() {
    const today = todayWall();
    return [
      {
        key: "today",
        label: "Today",
        date: () => today,
        hint: () => fmtDow(today),
      },
      {
        key: "later",
        label: "Later",
        date: () => {
          const x = new Date(today);
          x.setHours(18, 0, 0, 0);
          return x;
        },
        hint: () => fmtTime(new Date(2000, 0, 1, 18, 0)),
      },
      {
        key: "tomorrow",
        label: "Tomorrow",
        date: () => addDays(today, 1),
        hint: () => fmtDow(addDays(today, 1)),
      },
      {
        key: "thisWeekend",
        label: "This weekend",
        date: () => nextDow(today, 6, true), // Saturday
        hint: () => fmtDow(nextDow(today, 6, true)),
      },
      {
        key: "nextWeek",
        label: "Next week",
        date: () => nextDow(today, 1), // next Monday
        hint: () => fmtWallDate(nextDow(today, 1)),
      },
      {
        key: "nextWeekend",
        label: "Next weekend",
        date: () => addDays(nextDow(today, 6, true), 7),
        hint: () => fmtWallDate(addDays(nextDow(today, 6, true), 7)),
      },
      {
        key: "twoWeeks",
        label: "2 weeks",
        date: () => addDays(today, 14),
        hint: () => fmtWallDate(addDays(today, 14)),
      },
      {
        key: "fourWeeks",
        label: "4 weeks",
        date: () => addDays(today, 28),
        hint: () => fmtWallDate(addDays(today, 28)),
      },
    ];
  }

  /** Build the 6×7 day matrix for the given month, starting on the
   *  person's first day of the week (Monday or Sunday). */
  function monthMatrix(viewMonth) {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const lead = (first.getDay() - weekStartJsDay() + 7) % 7;
    const start = addDays(first, -lead);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }

  /** 15-minute time slots from 12:00 am to 11:45 pm. */
  const TIME_SLOTS = (() => {
    const out = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const d = new Date();
        d.setHours(h, m, 0, 0);
        out.push({ hh: h, mm: m, label: fmtTime(d) });
      }
    }
    return out;
  })();

  function TaskDatePopover({
    open,
    triggerRef,
    startDate,
    dueDate,
    /** Whether the user explicitly set a time of day on each date.
     *  Threaded in so picking a different DAY preserves an intentional
     *  time (incl. "12:00 AM"), and the pill chips show that time. */
    startDateHasTime = false,
    dueDateHasTime = false,
    initialField,
    /** When true, the time-slot column opens immediately for the
     *  `initialField` — used by the dedicated Start time / Due time
     *  cells in the table so the user lands on the time picker
     *  without an extra click. */
    initialTimeOpen = false,
    onCommit,
    onClose,
  }) {
    const menuRef = useRef(null);
    const [coords, setCoords] = useState(null);
    const [active, setActive] = useState(initialField);
    /** Which pill currently has its "Add time" panel expanded. */
    const [timeFor, setTimeFor] = useState(null);

    const startObj = startDate ? toWall(startDate, startDateHasTime) : null;
    const dueObj = dueDate ? toWall(dueDate, dueDateHasTime) : null;

    const initialView =
      (active === "start" ? startObj : dueObj) ??
      (active === "start" ? dueObj : startObj) ??
      todayWall();
    const [viewMonth, setViewMonth] = useState(
      new Date(initialView.getFullYear(), initialView.getMonth(), 1),
    );

    // Position the popover relative to the trigger and KEEP it there while
    // the page scrolls / resizes. Without the scroll listener the
    // `position: fixed` popover stayed frozen at its open-time coordinates
    // while the trigger scrolled away, leaving the calendar stranded
    // mid-page. We also flip it above the trigger when there isn't enough
    // room below, and always cap its height to the available space (the
    // `maxHeight` is applied to the element below + `overflow-y: auto`) so
    // a calendar that opens near the bottom of the viewport stays fully
    // scrollable instead of being clipped off-screen.
    useLayoutEffect(() => {
      if (!open) return;
      let raf = 0;
      function update(attempt = 0) {
        const r = triggerRef.current?.getBoundingClientRect();
        // Trigger not laid out yet — a null OR zero-size rect. Retry for a
        // few frames rather than positioning off a (0,0,0,0) rect, which
        // slams the popover to the top-left corner detached from the icon
        // that opened it (QA: Gantt date cell opened the calendar at the
        // top of the page).
        if (!r || (r.width === 0 && r.height === 0)) {
          if (attempt < 30) raf = requestAnimationFrame(() => update(attempt + 1));
          return;
        }
        const popoverW = 600;
        const leftGutter = 8;
        const gap = 6;
        const margin = 12;
        // RIGHT-align the popover's right edge to the date trigger so it
        // opens LEFTWARD and never spills across the columns to the right
        // of the date field (Priority / Comments / Files). The popover is
        // ~600px — far wider than the narrow date columns — so left-
        // anchoring made it sprawl rightward and read as "misaligned".
        // Fall back to left-pinning only if right-aligning would push the
        // popover past the left viewport edge.
        let left;
        let right;
        if (r.right - popoverW < leftGutter) {
          left = leftGutter;
          right = undefined;
        } else {
          right = Math.max(leftGutter, window.innerWidth - r.right);
          left = undefined;
        }
        const spaceBelow = window.innerHeight - r.bottom - gap - margin;
        const spaceAbove = r.top - gap - margin;
        // Natural height of the popover (preset list + month calendar). We cap
        // the measured space to this so the popover HUGS the trigger instead of
        // ballooning to fill the whole gap. The old code, when it flipped up,
        // set maxHeight to the FULL space above and positioned `top = r.top -
        // maxHeight`, which shot the popover to the very top of the viewport —
        // visually detached from the icon that opened it.
        const naturalH = 440;
        // Prefer opening BELOW the trigger so the popover stays adjacent to the
        // icon. Only flip up when below genuinely can't fit the popover's
        // MINIMUM height (220px — the `Math.max(220, …)` floor below) AND above
        // has more room. The popover scrolls internally (maxHeight +
        // overflow-y:auto), so as long as ≥220px sits below the field it opens
        // there rather than jumping to the top of the page. The old 260px
        // threshold flipped up while ~230px was still available below, which
        // shot the calendar to the top, detached from the date field
        // (QA-reported).
        const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
        const space = openUp ? spaceAbove : spaceBelow;
        const maxHeight = Math.max(220, Math.floor(Math.min(space, naturalH)));
        const top = openUp
          ? Math.max(margin, r.top - gap - maxHeight)
          : r.bottom + gap;
        setCoords({ top, left, right, maxHeight });
      }
      update();
      const onEvt = () => update();
      window.addEventListener("resize", onEvt);
      window.addEventListener("scroll", onEvt, true);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", onEvt);
        window.removeEventListener("scroll", onEvt, true);
      };
    }, [open, triggerRef]);

    useDismissOn(open, triggerRef, menuRef, () => {
      onClose();
      setTimeFor(null);
    });

    // Sync the active field whenever the popover (re-)opens. If
    // `initialTimeOpen` is set, pre-expand the time-slot column for
    // that field too — that's the path the Start time / Due time
    // table cells use.
    useEffect(() => {
      if (open) {
        setActive(initialField);
        setTimeFor(initialTimeOpen ? initialField : null);
      }
    }, [open, initialField, initialTimeOpen]);

    if (!open || !coords) return null;

    function pickDate(d) {
      // Preserve the existing time on the active field if there is one.
      const current = active === "start" ? startObj : dueObj;
      const currentHasTime =
        active === "start" ? startDateHasTime : dueDateHasTime;
      const next = new Date(d);
      if (current) {
        next.setHours(
          current.getHours(),
          current.getMinutes(),
          current.getSeconds(),
          current.getMilliseconds(),
        );
      }
      // The "time set" flag follows the time that lands on `next`: keep
      // an explicit time the user already chose (so changing the DAY
      // doesn't silently drop a "12:00 AM"), and treat a preset that
      // itself carries a time (e.g. "Later" = 6:00 PM) as explicit too.
      const nextHasTime =
        currentHasTime || next.getHours() !== 0 || next.getMinutes() !== 0;
      onCommit({
        [active === "start" ? "startDate" : "dueDate"]: fromWall(next, nextHasTime),
        [active === "start" ? "startDateHasTime" : "dueDateHasTime"]:
          nextHasTime,
      });
      // Auto-advance from start → due so the user can pick the second date
      // without an extra click.
      if (active === "start" && !dueObj) setActive("due");
    }

    function pickTime(field, hh, mm) {
      const current = field === "start" ? startObj : dueObj;
      const base = current ?? todayWall();
      const next = new Date(base);
      next.setHours(hh, mm, 0, 0);
      // An explicit slot click is always an explicit time — even the
      // 12:00 AM slot, which is the whole point of the flag.
      onCommit({
        [field === "start" ? "startDate" : "dueDate"]: fromWall(next, true),
        [field === "start" ? "startDateHasTime" : "dueDateHasTime"]: true,
      });
    }

    const matrix = monthMatrix(viewMonth);
    const today = todayWall();
    const presets = presetDefs();

    return createPortal(
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          top: coords.top,
          // Either `left` or `right` is set by the positioner — right-
          // anchoring opens the popover leftward so it doesn't overflow
          // the columns to the right of the date field.
          left: coords.left,
          right: coords.right,
          zIndex: 60,
          // 600px on desktop, but never wider than the viewport on mobile —
          // otherwise the calendar half is clipped off-screen. The body below
          // stacks presets above the calendar under `sm` so both fit.
          width: "min(600px, calc(100vw - 16px))",
          // Cap to the space the positioner measured and scroll internally
          // so a calendar opened near a viewport edge is always fully
          // reachable. Inline overflowY beats the class's `overflow-hidden`
          // on the vertical axis; horizontal stays hidden for rounding.
          maxHeight: coords.maxHeight,
          overflowY: "auto",
        }}
        className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] overflow-hidden"
      >
        {/* Pills */}
        <div className="flex items-center gap-2 p-2 border-b border-[color:var(--color-rule)]">
          <DatePill
            icon={<Calendar size={12} />}
            label={startObj ? fmtWallDate(startObj) : "Start date"}
            time={
              startObj &&
              (startDateHasTime ||
                startObj.getHours() !== 0 ||
                startObj.getMinutes() !== 0)
                ? fmtTime(startObj)
                : null
            }
            active={active === "start"}
            empty={!startObj}
            onSelect={() => {
              setActive("start");
              setTimeFor(null);
            }}
            onClear={() =>
              onCommit({ startDate: null, startDateHasTime: false })
            }
            onAddTime={() => {
              setActive("start");
              setTimeFor((cur) => (cur === "start" ? null : "start"));
            }}
          />
          <DatePill
            icon={<Calendar size={12} />}
            label={dueObj ? fmtWallDate(dueObj) : "Due date"}
            time={
              dueObj &&
              (dueDateHasTime ||
                dueObj.getHours() !== 0 ||
                dueObj.getMinutes() !== 0)
                ? fmtTime(dueObj)
                : null
            }
            active={active === "due"}
            empty={!dueObj}
            onSelect={() => {
              setActive("due");
              setTimeFor(null);
            }}
            onClear={() => onCommit({ dueDate: null, dueDateHasTime: false })}
            onAddTime={() => {
              setActive("due");
              setTimeFor((cur) => (cur === "due" ? null : "due"));
            }}
          />
        </div>

        {/* End-of-day hint — a due date with no explicit time is scheduled
         *  for 23:59 (in the company timezone) so shorter reminders still
         *  fire. Shown only while that's the case; picking a time hides it. */}
        {dueObj && !dueDateHasTime ? (
          <div className="px-3 py-2 text-[11px] leading-snug text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
            No due time selected. Task will be scheduled for end of day (11:59
            PM).
          </div>
        ) : null}

        {/* Body — presets beside the calendar on desktop; stacked (presets
         *  above calendar) on mobile so the 600px two-column layout doesn't
         *  overflow a phone viewport. */}
        <div className="flex flex-col sm:flex-row">
          {/* Presets — wider than the label-only minimum because longer
           *  hints like "Next weekend 16/05/2026" need ~200px to fit on
           *  one line. Without the extra width the `whitespace-nowrap`
           *  text used to overflow the column and visually overlap the
           *  calendar grid to its right. */}
          <div className="w-full sm:w-80 shrink-0 overflow-hidden border-b sm:border-b-0 sm:border-r border-[color:var(--color-rule)] py-1">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => pickDate(p.date())}
                className="w-full flex items-center justify-between gap-2 px-3 py-1 text-sm hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                <span className="text-[color:var(--color-ink)] truncate">
                  {p.label}
                </span>
                <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
                  {p.hint()}
                </span>
              </button>
            ))}
          </div>

          {/* Calendar */}
          <div className="flex-1 p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                {viewMonth.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setViewMonth(
                      (m) => new Date(m.getFullYear(), m.getMonth(), 1),
                    )
                  }
                  className="text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] px-2 py-0.5 cursor-pointer"
                  title="Jump to today's month"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                  aria-label="Previous month"
                  className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                  aria-label="Next month"
                  className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-sm text-[color:var(--color-ink-muted)] mb-0.5">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
                .map((_, i, all) => all[(weekStartJsDay() + i) % 7])
                .map((d) => (
                <div key={d} className="text-center py-0.5">
                  {d}
                </div>
              ))}
            </div>
            {(() => {
              // Pre-snap start/due/today to midnight so the per-cell
              // boolean comparisons use clean equality rather than
              // "same calendar day" math repeated 42 times.
              const startMs = startObj ? startOfDay(startObj).getTime() : null;
              const dueMs = dueObj ? startOfDay(dueObj).getTime() : null;
              const rangeLow =
                startMs !== null && dueMs !== null
                  ? Math.min(startMs, dueMs)
                  : null;
              const rangeHigh =
                startMs !== null && dueMs !== null
                  ? Math.max(startMs, dueMs)
                  : null;
              const todayMs = today.getTime();
              return (
                <div className="grid grid-cols-7 gap-1">
                  {matrix.map((d) => {
                    const dayMs = startOfDay(d).getTime();
                    // Past dates ARE selectable — back-dating a Start / Due is
                    // allowed (e.g. logging work that was already due), and the
                    // API accepts it. `isPast` is kept only to style past days
                    // as muted + label them, never to block selection.
                    const isPast = dayMs < todayMs;
                    // Range-order guard: when editing DUE, dates before the
                    // chosen START are invalid; when editing START, dates after
                    // the chosen DUE are invalid. Keeps due >= start.
                    const violatesOrder =
                      (active === "due" &&
                        startMs !== null &&
                        dayMs < startMs) ||
                      (active === "start" && dueMs !== null && dayMs > dueMs);
                    const isBlocked = violatesOrder;
                    const isToday = dayMs === todayMs;
                    const isStart = startMs !== null && dayMs === startMs;
                    const isDue = dueMs !== null && dayMs === dueMs;
                    // Strictly BETWEEN start and due (both inclusive
                    // endpoints handled by isStart / isDue above so the
                    // endpoint cells get the strong style).
                    const isInRange =
                      rangeLow !== null &&
                      rangeHigh !== null &&
                      dayMs > rangeLow &&
                      dayMs < rangeHigh;
                    // Days bleeding in from the prev/next month — render
                    // them dim so the current month reads cleanly.
                    const isOtherMonth = d.getMonth() !== viewMonth.getMonth();

                    // Range fill (soft accent) for in-between days.
                    // Start / Due endpoints get a solid accent on top.
                    // Today (when it isn't an endpoint) gets a ring so
                    // the user can spot it in the grid.
                    let cellClass =
                      "h-7 rounded text-sm transition-colors relative " +
                      (isBlocked ? "cursor-not-allowed " : "cursor-pointer ");
                    if (isStart || isDue) {
                      cellClass +=
                        "bg-[color:var(--color-accent)] text-white font-semibold hover:brightness-110 ";
                    } else if (isInRange) {
                      cellClass +=
                        "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] hover:bg-[color:var(--color-accent-bg)] ";
                    } else if (isToday) {
                      cellClass +=
                        "ring-1 ring-inset ring-[color:var(--color-accent)] text-[color:var(--color-accent)] font-semibold hover:bg-[color:var(--color-card-hover)] ";
                    } else if (isOtherMonth) {
                      cellClass +=
                        "text-[color:var(--color-ink-subtle)] hover:bg-[color:var(--color-card-hover)] ";
                    } else if (isPast) {
                      cellClass +=
                        "text-[color:var(--color-ink-subtle)] hover:bg-[color:var(--color-card-hover)] ";
                    } else {
                      cellClass +=
                        "text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] ";
                    }
                    // Out-of-order picks can't be selected (the button is
                    // `disabled` below). Dim the WHOLE cell uniformly — range
                    // fill, accent endpoint, plain text — with no hover restore.
                    if (isBlocked) {
                      cellClass += "opacity-40 ";
                    }

                    // Squared inner edge between adjacent in-range
                    // cells, rounded only on the outer endpoints — so
                    // the range reads as a continuous band, not 5
                    // disconnected pills.
                    if (isStart && dueMs !== null && dayMs < dueMs) {
                      cellClass = cellClass.replace("rounded ", "rounded-l ");
                    } else if (isDue && startMs !== null && dayMs > startMs) {
                      cellClass = cellClass.replace("rounded ", "rounded-r ");
                    } else if (isInRange) {
                      cellClass = cellClass.replace(
                        "rounded ",
                        "rounded-none ",
                      );
                    }

                    const ariaLabel = isStart
                      ? `Start date ${fmtWallDate(d)}`
                      : isDue
                        ? `Due date ${fmtWallDate(d)}`
                        : isInRange
                          ? `In range ${fmtWallDate(d)}`
                          : isPast
                            ? `Past date ${fmtWallDate(d)}`
                            : fmtWallDate(d);

                    return (
                      <button
                        key={d.toISOString()}
                        type="button"
                        aria-label={ariaLabel}
                        aria-pressed={isStart || isDue}
                        disabled={isBlocked}
                        title={
                          violatesOrder
                            ? active === "due"
                              ? "Due date can’t be before the start date."
                              : "Start date can’t be after the due date."
                            : undefined
                        }
                        onClick={() => {
                          // Block out-of-order picks — never commit one, and
                          // never auto-advance the field on a bad pick.
                          if (!isBlocked) pickDate(d);
                        }}
                        className={cellClass}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Time column (Phase 2) */}
          {timeFor ? (
            <div className="w-32 border-l border-[color:var(--color-rule)] py-1 overflow-auto max-h-[320px]">
              <p className="px-3 py-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                {timeFor === "start" ? "Start time" : "Due time"}
              </p>
              {TIME_SLOTS.map((t) => {
                const cur = timeFor === "start" ? startObj : dueObj;
                const selected =
                  cur && cur.getHours() === t.hh && cur.getMinutes() === t.mm;
                return (
                  <button
                    key={`${t.hh}:${t.mm}`}
                    type="button"
                    onClick={() => pickTime(timeFor, t.hh, t.mm)}
                    className={
                      "w-full text-left px-3 py-1 text-sm cursor-pointer " +
                      (selected
                        ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]"
                        : "")
                    }
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>,
      document.body,
    );
  }

  function DatePill({
    icon,
    label,
    time,
    active,
    empty,
    onSelect,
    onClear,
    onAddTime,
  }) {
    return (
      <div
        role="button"
        onClick={onSelect}
        className={
          "flex-1 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer " +
          (active
            ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]"
            : "")
        }
      >
        <span className="flex items-center gap-1.5 min-w-0 truncate">
          {icon}
          <span className="truncate">{label}</span>
          {time ? (
            <span className="text-xs opacity-70 ml-1">{time}</span>
          ) : null}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {empty ? null : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
              aria-label="Clear"
            >
              <X size={12} />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddTime();
            }}
            className="text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
          >
            {time ? "Edit time" : "Add time"}
          </button>
        </span>
      </div>
    );
  }

  /** Read-only date display used by the new "Date created", "Date updated", and
   *  "Date done" columns in Customize. Always shows a short date ("27 Apr") —
   *  no time-of-day swap for today, since that confused users into reading the
   *  Date Updated column as a time field. */
  function ReadOnlyDateCell({ iso }) {
    if (!iso)
      return (
        <span className="text-xs text-[color:var(--color-ink-subtle)]">—</span>
      );
    return (
      <span className="text-xs text-[color:var(--color-ink-muted)]">
        {fmtDateShort(iso, true)}
      </span>
    );
  }

  /** Read-only "Created by" cell — initials avatar + creator name. Falls
   *  back to a neutral placeholder when the creator can't be resolved
   *  (e.g. the user was deleted). Name is resolved server-side
   *  (`createdByName`). Uses the local initials Avatar for visual parity
   *  with the assignee column. */
  function CreatedByCell({ name }) {
    if (!name) {
      return (
        <span className="text-xs text-[color:var(--color-ink-subtle)]">—</span>
      );
    }
    return (
      // `flex` + `min-w-0` bounds the cell to its fixed grid track so a long
      // name truncates with ellipsis instead of overflowing into the adjacent
      // Date column. The name span needs `min-w-0` too — `truncate` sets
      // white-space:nowrap, so without it the name's min-content is the whole
      // string and it can't shrink. Full name stays on the `title` tooltip.
      <span className="flex items-center gap-1.5 min-w-0">
        <Avatar name={name} showTooltip={false} />
        <span
          className="min-w-0 text-xs text-[color:var(--color-ink-muted)] truncate"
          title={name}
        >
          {name}
        </span>
      </span>
    );
  }

  /** "Repeat" cell for the list. `state` is the server-resolved
   *  'active' | 'paused' | null from the list serializer.
   *
   *  When `onOpen` is provided the cell is a BUTTON that opens the recurrence
   *  editor — click the repeat icon to set a schedule, or the chip to edit an
   *  existing one. Only TOP-LEVEL tasks can repeat (a parent's recurrence
   *  clones its subtasks), so `onOpen` is wired for top-level rows only. A
   *  non-recurring row shows a "Set repeat" affordance. Without `onOpen`
   *  (subtasks) it's read-only: a chip if recurring, else a neutral dash. */
  function RecurringCell({ state, schedule, onOpen, canEdit = true }) {
    const paused = state === "paused";
    const active = state === "active";
    const isRecurring = active || paused;
    // Which days the series actually runs on — "Weekly · Mon, Wed, Fri". The
    // cell used to say only "Recurring", so the schedule was invisible until
    // you opened the Repeat dialog (DF_006 / Mantis 22086). Falls back to the
    // plain state word for a rule whose shape we can't summarise.
    const summary = isRecurring ? summariseSchedule(schedule) : null;

    // Read-only variant — subtasks (no click target) OR a user who lacks
    // planning-edit rights on this task (e.g. Space "view"/"comment" access).
    // The recurring status still shows for context, but a non-recurring task
    // shows a dash instead of an actionable "Set repeat" the backend rejects.
    if (!onOpen || !canEdit) {
      if (!isRecurring) {
        return (
          <span className="text-xs text-[color:var(--color-ink-subtle)]">
            —
          </span>
        );
      }
      return (
        // `flex` + `min-w-0` so the chip is bounded by its fixed grid track —
        // same pattern as CreatedByCell. A weekday list ("Weekly · Mon, Tue,
        // Wed, Thu, Fri, Sat") is far wider than the 130px column, and a grid
        // item defaults to min-width:auto, so without this it overflowed into
        // the neighbouring Comments column instead of clipping.
        <span
          className={
            "flex items-center gap-1 min-w-0 text-xs " +
            (paused
              ? "text-[color:var(--color-ink-subtle)]"
              : "text-[color:var(--color-ink-muted)]")
          }
          title={
            (paused ? "Recurring (paused)" : "Recurring task") +
            (summary ? ` — ${summary}` : "")
          }
        >
          <Repeat size={13} className="shrink-0" />
          <span className="min-w-0 truncate">
            {paused ? "Paused" : (summary ?? "Recurring")}
          </span>
        </span>
      );
    }

    // Interactive variant (top-level tasks) — opens the recurrence editor.
    // stopPropagation so the click never bubbles to a row/detail handler.
    const label = active
      ? (summary ?? "Recurring")
      : paused
        ? "Paused"
        : "Set repeat";
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        title={
          active
            ? `Edit recurrence${summary ? ` — ${summary}` : ""}`
            : paused
              ? `Recurrence paused${summary ? ` (${summary})` : ""} — click to edit`
              : "Set a repeat schedule"
        }
        aria-label={label}
        className={
          // Clearly legible in every state (the faint low-opacity style read
          // as "disabled / missing"). Active recurrence is the strongest;
          // "Set repeat" and paused sit at the normal body-text weight.
          // `flex` + `min-w-0` (not `inline-flex`) so the button is bounded by
          // its fixed grid track. As an inline-flex grid item with the default
          // min-width:auto it sized to its content, so a full weekday list
          // ("Weekly · Mon, Tue, Wed, Thu, Fri, Sat") spilled over the 130px
          // column and collided with Comments — and the `truncate` below never
          // engaged, because a flex item can't shrink under min-width:auto.
          "group/recur flex items-center gap-1 min-w-0 max-w-full rounded px-1.5 py-0.5 text-xs cursor-pointer " +
          "hover:bg-[color:var(--color-card-hover)] " +
          (active
            ? "text-[color:var(--color-ink-strong)] font-medium"
            : "text-[color:var(--color-ink)]")
        }
      >
        <Repeat size={13} className="shrink-0" />
        {/* truncate: the schedule summary ("Every 2 weeks · Mon, Wed, Fri") is
            longer than the old one-word badge, so it must clip inside the
            column rather than widen it. The full text stays in `title`. */}
        <span className="min-w-0 truncate">{label}</span>
      </button>
    );
  }

  function StartDateCell({
    startDate,
    dueDate,
    startDateHasTime = false,
    dueDateHasTime = false,
    onCommit,
    disabled = false,
    // When true (task-detail modal), append the time to the date. A
    // date-only start reads as start of day ("12:00 AM"); the List/Board
    // keep this false since they carry a separate Start Time column.
    showTime = false,
  }) {
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const commit = disabled
      ? () => {
          notifyPlanningDenied();
          setOpen(false);
        }
      : onCommit;
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="cursor-pointer inline-flex items-center gap-1"
          aria-label="Set start date"
        >
          {startDate ? (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)]">
              <Calendar size={12} />
              {fmtDateShort(startDate, startDateHasTime)}
              {showTime
                ? ` ${startDateHasTime ? fmtIsoTime(startDate, true) : startOfDayLabel()}`
                : ""}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-subtle)]">
              <Calendar size={12} />
            </span>
          )}
        </button>
        <TaskDatePopover
          open={open}
          triggerRef={triggerRef}
          startDate={startDate}
          dueDate={dueDate}
          startDateHasTime={startDateHasTime}
          dueDateHasTime={dueDateHasTime}
          initialField="start"
          onCommit={commit}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  function DueDateCell({
    startDate,
    dueDate,
    startDateHasTime = false,
    dueDateHasTime = false,
    completedAt,
    onCommit,
    disabled = false,
    // When true (task-detail modal), append the time to the date. A
    // date-only due reads as end of day ("11:59 PM"); the List/Board keep
    // this false since they carry a separate Due Time column.
    showTime = false,
  }) {
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const commit = disabled
      ? () => {
          notifyPlanningDenied();
          setOpen(false);
        }
      : onCommit;
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="cursor-pointer inline-flex items-center gap-1"
          aria-label="Set due date"
        >
          {/* In the task-detail modal (`showTime`) render the due date+time as
           *  a SINGLE inline run — icon, date, then time — exactly like
           *  `StartDateCell`, so the two fields wrap and align identically on a
           *  narrow (mobile) column. Urgency is still conveyed by colour. The
           *  List/Board keep the compact `DueChip` (their own Due Time column
           *  carries the time). */}
          {showTime ? (
            dueDate ? (
              <span
                className="inline-flex items-center gap-1 text-xs"
                style={{ color: dueDateColor(dueDate, completedAt) }}
              >
                <Calendar size={12} />
                {fmtDateShort(dueDate, dueDateHasTime)}
                {` ${dueDateHasTime ? fmtIsoTime(dueDate, true) : endOfDayLabel()}`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-subtle)]">
                <Calendar size={12} />
              </span>
            )
          ) : (
            <span className="inline-flex items-center gap-1">
              <DueChip iso={dueDate} completedAt={completedAt} />
            </span>
          )}
        </button>
        <TaskDatePopover
          open={open}
          triggerRef={triggerRef}
          startDate={startDate}
          dueDate={dueDate}
          startDateHasTime={startDateHasTime}
          dueDateHasTime={dueDateHasTime}
          initialField="due"
          onCommit={commit}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  /** Read-only-looking time cell — clicking it opens the same date
   *  popover the StartDateCell uses, but with the time-slot column
   *  pre-expanded for the relevant field (start or due). Renders the
   *  task's time-of-day on that field, or an em-dash when the field is
   *  unset or midnight (where there's no meaningful time to display).
   *
   *  Shared by StartTimeCell and DueTimeCell via the `field` prop, so
   *  there's one implementation to maintain for both columns. */
  function TimeCell({
    field,
    startDate,
    dueDate,
    startDateHasTime = false,
    dueDateHasTime = false,
    onCommit,
    disabled = false,
  }) {
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const iso = field === "start" ? startDate : dueDate;
    const hasTime = field === "start" ? startDateHasTime : dueDateHasTime;
    // Time-of-day label. When a task carries an explicit time we show it.
    // When it doesn't but the task HAS a date, we surface the effective
    // default so the column never reads "—" for a dated task: the DUE
    // column shows "11:59 PM" (end of day — the deadline) and the START
    // column shows "12:00 AM" (start of day). Both match the task-detail
    // modal. A cell with no date at all stays blank.
    const timeLabel = iso
      ? hasTime
        ? fmtIsoTime(iso, hasTime)
        : field === "due"
          ? endOfDayLabel()
          : startOfDayLabel()
      : null;
    const commit = disabled
      ? () => {
          notifyPlanningDenied();
          setOpen(false);
        }
      : onCommit;
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="cursor-pointer inline-flex items-center gap-1"
          aria-label={field === "start" ? "Set start time" : "Set due time"}
        >
          {timeLabel ? (
            <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums">
              {timeLabel}
            </span>
          ) : (
            <span className="text-xs text-[color:var(--color-ink-subtle)]">
              —
            </span>
          )}
        </button>
        <TaskDatePopover
          open={open}
          triggerRef={triggerRef}
          startDate={startDate}
          dueDate={dueDate}
          startDateHasTime={startDateHasTime}
          dueDateHasTime={dueDateHasTime}
          initialField={field}
          initialTimeOpen
          onCommit={commit}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  function PriorityCell({
    priority,
    onCommit,
    disabled = false,
    completed = false,
  }) {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    usePortalCoords(open, triggerRef, setCoords);
    useDismissOn(open, triggerRef, menuRef, () => setOpen(false));

    // Locked: the dropdown still opens, but choosing a priority is blocked
    // with the explanatory toast instead of saving. A COMPLETED task locks
    // priority for everyone (the work is finished); otherwise the planning
    // permission gate applies. Completion is checked first so a finished task
    // shows the "reopen first" message rather than the permission one.
    const commit =
      completed || disabled
        ? () => {
            if (completed) notifyCompletedLocked();
            else notifyPlanningDenied();
            setOpen(false);
          }
        : onCommit;

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="cursor-pointer"
        >
          <PriorityChip priority={priority ?? null} />
        </button>
        {open && coords
          ? createPortal(
              <div
                ref={menuRef}
                style={{
                  position: "fixed",
                  top: coords.top,
                  bottom: coords.bottom,
                  left: coords.left,
                  maxHeight: coords.maxHeight,
                  overflowY: "auto",
                  zIndex: 60,
                }}
                className="w-44 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
              >
                {TASK_PRIORITIES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      commit(p);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    <Flag
                      size={12}
                      fill={PRIORITY_META[p].color}
                      strokeWidth={0}
                    />
                    <span style={{ color: PRIORITY_META[p].color }}>
                      {PRIORITY_META[p].label}
                    </span>
                  </button>
                ))}
                <div className="border-t border-[color:var(--color-rule)]" />
                <button
                  type="button"
                  onClick={() => {
                    commit(null);
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)] cursor-pointer"
                >
                  <Flag size={12} />
                  Clear
                </button>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  }

  // ---------- Status section (List view) ----------

  /** Stable column order — header row, body row, and Customize popover all
   *  iterate this list so they never drift apart. Widths are tuned to the cell
   *  content (avatars, short dates, priority chip) plus a small breathing
   *  margin, not a uniform value, so wide tables don't waste horizontal space. */
  /** Subset of column keys the Board view supports as on-card chips.
   *  The Customize pill filters itself to this set whenever the Board
   *  is active — toggles for fields the Board can't render (Start time,
   *  Due time, timestamps, Task ID) stay invisible there but remain
   *  available on the List view (which renders the full catalogue). */
  const BOARD_ALLOWED_COLUMN_KEYS = new Set([
    "assignee",
    "startDate",
    "dueDate",
    "priority",
    "recurring",
    "comments",
  ]);

  /** Subset of column keys the Gantt's left pane supports. The right
   *  pane already carries every task's start / due / colour cue on its
   *  bar, so the left pane only needs the four interactive cells that
   *  make sense as a table: Assignee, Start date, Due date, Priority.
   *  Time / Comments / timestamps / Task ID aren't rendered there, so
   *  their toggles get filtered out of the Customize panel when Gantt
   *  is the active view. */
  const GANTT_ALLOWED_COLUMN_KEYS = new Set([
    "assignee",
    "startDate",
    "dueDate",
    "priority",
    "recurring",
    "createdBy",
  ]);

  // Column widths bumped across the board — the old set produced a
  // cramped, hard-to-scan table where assignee names truncated at the
  // first space, priority pills clipped, and "Files & Links" overlapped
  // the trailing action column. Wider columns give each cell real
  // breathing room and naturally push the table past most viewport
  // widths so the horizontal scrollbar mirror appears reliably.
  const COLUMN_ORDER = [
    // Task ID sits FIRST (immediately after the sticky Name cell). It's
    // off by default; when a user enables it in Customize they're usually
    // looking a specific task up, so the id earns the leading scrollable
    // slot rather than being buried at the tail where the whole row had
    // to be scrolled to read it. Product call (2026-07-27) — users who
    // leave it off (the majority) see no change. NOTE: the body rows
    // render cells as hardcoded JSX (three nesting levels), NOT by
    // iterating this array, so any reorder here must be mirrored in each
    // row's cell sequence or the header and body drift apart.
    { key: "taskId", label: "Task ID", width: "120px" },
    // Status pill sits immediately after Task ID because it's the single
    // most-glanced piece of info on the row alongside the task name. Even
    // though tasks are grouped by status by default, the column is useful
    // when the admin regroups (by assignee / priority / etc.) — the row's
    // status is then no longer implied by its group header.
    { key: "status", label: "Status", width: "150px" },
    { key: "assignee", label: "Assignee", width: "160px" },
    { key: "startDate", label: "Start Date", width: "140px" },
    // Start time sits IMMEDIATELY after Start date so when an admin
    // toggles it on in Customize, the pair reads as one unit
    // ("Start date | Start time") rather than being shoved to the end
    // of the row.
    { key: "startTime", label: "Start Time", width: "110px" },
    { key: "dueDate", label: "Due Date", width: "150px" },
    // Same logic for Due time — pairs visually with Due date.
    { key: "dueTime", label: "Due Time", width: "110px" },
    { key: "priority", label: "Priority", width: "130px" },
    // Repeat — recurrence indicator/control. Sits right after Priority.
    // Cell shows a repeat icon + "Recurring" / "Paused" chip when the task
    // has a recurring rule, else a "Set repeat" affordance; clicking opens
    // the recurrence editor. Only TOP-LEVEL tasks can repeat (a parent's
    // recurrence clones its subtasks), so subtask rows are read-only here.
    { key: "recurring", label: "Repeat", width: "130px" },
    { key: "comments", label: "Comments", width: "130px" },
    // Created by — task author (avatar + name). Read-only. Toggleable in
    // Customize. Lives after Comments so it sits with the metadata columns.
    { key: "createdBy", label: "Created By", width: "180px" },
    { key: "dateCreated", label: "Date Created", width: "150px" },
    { key: "dateUpdated", label: "Date Updated", width: "150px" },
    { key: "dateDone", label: "Date Done", width: "140px" },
    // Files & Links — per-task attachments. Cell renders a paperclip
    // icon + count if attachments exist; clicking opens a popover with
    // the full list + Upload + Add-link controls. Sits at the end of
    // the row so the column reads as a trailing action affordance
    // rather than competing with the core task metadata columns.
    { key: "attachments", label: "Files & Links", width: "160px" },
    // Task Checker — the checker + verification workflow inline (status chip +
    // Submit / Approve / Reject). Sits BEFORE Proof required. `key` stays
    // "accountability" (internal) so stored column prefs keep working; only the
    // user-facing label reads "Task Checker".
    { key: "accountability", label: "Task Checker", width: "240px" },
    // Proof required — a standalone toggle column: block completion until proof
    // is attached. Off by default; every row body renders its cell in this slot.
    { key: "proof", label: "Proof Required", width: "130px" },
  ];

  /** Floor for the Name column — wide enough to hold the chevron + status
   *  dot + subtask count + most task names without truncation. Other
   *  columns are fixed; this one is too (no `1fr`, see `gridTemplate`).
   *  Reduced from 560 → 400 so the list view feels less left-heavy and
   *  more of the metadata columns (Assignee / dates / priority) land
   *  inside the visible viewport before horizontal scroll kicks in. A
   *  400px floor still fits chevron + status dot + subtask count + ~30
   *  characters of name; long names truncate with ellipsis and the user
   *  can hover for the title tooltip or click into the task. */
  // Desktop keeps the roomy 400px name column. On phones (< 640px) that is
  // WIDER than the whole screen, so the sticky Task Name column — pinned at
  // left:0 — would cover the entire viewport and hide every other column,
  // making the horizontal scroll read as "not working". Narrow it so the
  // pinned column fits and the other columns stay visible while scrolling.
  // Read at render (NOT a hook: this sits after an early return, so a hook
  // here would break the rules of hooks). The value feeds BOTH gridTemplate
  // and gridMinPx, so the columns and the scroll width stay aligned.
  const NAME_COL_MIN =
    typeof window !== "undefined" && window.innerWidth < 640 ? 168 : 400;

  /** Small blank gutter column that sits between Task Name and the
   *  first metadata column (Status / Assignee). Adds visual
   *  separation so the sticky Name cell doesn't bleed into the
   *  metadata block — the grid `gap-3` alone reads as too tight on
   *  wide rows. Used by `gridTemplate` and `gridMinPx` below; every
   *  rendered grid row needs a matching empty cell after its Name
   *  cell so the columns line up. */
  const NAME_GUTTER_W = 24;

  function gridTemplate(cols) {
    // Name column is a fixed pixel width, not `1fr`. With `1fr`, when the
    // viewport is wider than the table's natural width the Name column would
    // stretch and leave a big empty gap before the next column. A fixed width
    // keeps the table tightly packed and visually structured like a real
    // table — extra horizontal space sits to the right of the table instead.
    //
    // After the Name column we slot in a small blank gutter (NAME_GUTTER_W)
    // so the sticky Name cell has visual breathing room before the
    // metadata block starts. Every rendered row body needs an empty cell
    // in this position to keep the columns aligned with the header.
    const parts = [`${NAME_COL_MIN}px`, `${NAME_GUTTER_W}px`];
    for (const c of COLUMN_ORDER) {
      if (cols[c.key]) parts.push(c.width);
    }
    // Trailing action column — holds the hover-only Trash button. `minmax(56px,
    // 1fr)` (was a fixed 56px): it keeps the 56px hit target but GROWS to eat
    // any horizontal slack, so the table always fills its container and the
    // row/box border reaches the right edge instead of stopping short (which
    // showed as a missing border when few columns are visible, e.g. personal
    // Spaces). The Trash button stays `justify-self-end`, pinned to the edge.
    parts.push("minmax(56px, 1fr)");
    return parts.join(" ");
  }

  /** Pixel sum of the column template's minimum widths — used as `min-width`
   *  on each section so the shared horizontal scrollbar at the bottom of the
   *  list view kicks in instead of the columns squashing past their fixed sizes.
   *  Includes the row's horizontal padding (`px-4` → 32) and the inter-cell
   *  gap (`gap-3` → 12 × gap-count); without those the grid overflows its box
   *  by ~32px, making the box's right border cut into the last column. */
  function gridMinPx(cols) {
    let total = NAME_COL_MIN;
    let columnCount = 1; // Name
    // Blank gutter between Name and the first metadata column. Counts
    // as a real grid column for both width AND the gap-3 inter-cell
    // spacing — keeping the two in sync prevents the row's overflow
    // edge from drifting (which would make the bottom scrollbar shift
    // a few px every time Customize toggles a column on/off).
    total += NAME_GUTTER_W;
    columnCount += 1;
    for (const c of COLUMN_ORDER) {
      if (cols[c.key]) {
        total += parseInt(c.width, 10);
        columnCount += 1;
      }
    }
    total += 56; // trailing action column (matches gridTemplate)
    columnCount += 1;
    total += 32; // px-4 on the row (16 left + 16 right)
    total += 12 * (columnCount - 1); // gap-3 between cells
    return total;
  }

  /**
   * Sparse-numbering helper for drag-and-drop reorder.
   *
   * Given the in-bucket tasks array AFTER the user's drop has been
   * applied locally (i.e. the move has already shifted into place),
   * compute the smallest set of `{taskId, sortOrder}` updates needed
   * to make the new order persist.
   *
   * The dropped task always gets a new sortOrder equal to the average
   * of its two neighbours. If neighbours are absent (top / bottom of
   * the bucket) we step by `STEP` so subsequent inserts at the same
   * edge keep finding room without colliding.
   *
   * We only renumber the WHOLE bucket if the computed average collides
   * with an existing neighbour (i.e. they're already touching at the
   * float resolution). That's the rare path — typical drag → 1 write.
   */
  const REORDER_STEP = 1000;
  function computeReorderUpdates(reorderedTasks, movedTaskId) {
    if (!Array.isArray(reorderedTasks) || reorderedTasks.length === 0)
      return [];
    const idx = reorderedTasks.findIndex((t) => t.id === movedTaskId);
    if (idx === -1) return [];

    const before =
      idx > 0 ? Number(reorderedTasks[idx - 1].sortOrder ?? 0) : null;
    const after =
      idx < reorderedTasks.length - 1
        ? Number(reorderedTasks[idx + 1].sortOrder ?? 0)
        : null;

    let newSortOrder;
    if (before == null && after == null) {
      newSortOrder = REORDER_STEP;
    } else if (before == null) {
      newSortOrder = after - REORDER_STEP;
    } else if (after == null) {
      newSortOrder = before + REORDER_STEP;
    } else {
      newSortOrder = (before + after) / 2;
    }

    // Collision path — neighbours are touching at the float resolution
    // (e.g. they're both 500.5000001 because the bucket has had dozens
    // of inserts between two values). Renumber the whole bucket to a
    // clean 1000-step grid so future drags have room again.
    const collides =
      (before != null && newSortOrder <= before) ||
      (after != null && newSortOrder >= after);
    if (collides) {
      return reorderedTasks.map((t, i) => ({
        taskId: t.id,
        sortOrder: (i + 1) * REORDER_STEP,
      }));
    }

    return [{ taskId: movedTaskId, sortOrder: newSortOrder }];
  }

  /**
   * Order tasks/subtasks exactly the way the backend list query does —
   * `sortOrder` asc, then `createdAt`, then `id` as stable tie-breakers
   * (mirrors `tasks.service.list`'s `.sort({ sortOrder, createdAt, _id })`).
   *
   * The list-view groups MUST sort by this so a drag's OPTIMISTIC update
   * — which only rewrites `sortOrder` VALUES, not array order — actually
   * repositions the row. Without it the optimistic change was invisible
   * and the row snapped back to its original slot on drop.
   */
  function bySortOrder(a, b) {
    return (
      Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0) ||
      new Date(a.createdAt ?? 0).getTime() -
        new Date(b.createdAt ?? 0).getTime() ||
      String(a.id ?? "").localeCompare(String(b.id ?? ""))
    );
  }

  /**
   * dnd-kit modifier — locks the drag to the vertical axis so a row (and
   * its floating preview) can only slide up / down, never drift sideways
   * or pull the sticky Name column out of alignment. Inline so we don't
   * add the @dnd-kit/modifiers package for a one-liner.
   */
  function restrictToVerticalAxis({ transform }) {
    return { ...transform, x: 0 };
  }

  /**
   * Force dnd-kit to re-measure droppable rects continuously during a
   * drag. The default strategy measures each droppable ONCE at drag
   * start — fine for a static list, but the List view has sticky
   * columns, a horizontally-scrolled grid, and rows whose height changes
   * as subtasks expand/collapse. A rect measured once goes stale, so
   * `closestCenter` resolves to nothing (or to the dragged row itself,
   * `active.id === over.id`) — which reads to the user as "the row lifts
   * but the neighbours never slide and it won't drop / reorder".
   * `Always` keeps the rects in sync every frame; negligible cost for
   * the few-dozen rows a status group holds.
   */
  const DND_MEASURING = { droppable: { strategy: MeasuringStrategy.Always } };

  /**
   * Render-prop wrapper that makes a single task row sortable via
   * `useSortable` without forcing the caller to restructure the
   * existing row JSX. The row content is passed as a function so it
   * can drop the `setNodeRef`, style, and drag attributes onto the
   * outer element, and forward the `listeners` to the drag handle
   * inside the sticky name cell. Without separating ref + listeners
   * this way, the entire row would be a drag target — which would
   * make clicking on any cell (status pill, due date, etc.) trigger a
   * drag instead of opening that cell's editor.
   */
  function SortableRow({ id, children }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });
    // We use a DragOverlay at the DndContext level — the actual moving
    // visual is rendered there, free of the grid + sticky-column layout
    // that would otherwise cause judder.
    //
    // The original row stays at `opacity: 0.4` (visible placeholder) so:
    //   1. The user can see WHERE the row currently "lives" as other
    //      rows slide around it during the drag, and
    //   2. The placeholder doesn't hijack pointer events — at fully
    //      invisible (opacity 0) the row would still occupy its layout
    //      slot and catch the drop hover, preventing the cursor from
    //      reaching the row below. The faded placeholder + the
    //      `pointerEvents: none` rule combine to send the hover to the
    //      ROW underneath, so dropping at a new position works
    //      reliably.
    //
    // `transition` is kept fully — other rows AND the dragged row
    // benefit from the smooth slide. Suppressing it on the dragged
    // row (which we tried earlier) caused the row to snap back to its
    // layout slot whenever the user paused mid-drag.
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.4 : 1,
      willChange: "transform",
      pointerEvents: isDragging ? "none" : undefined,
    };
    return children({ setNodeRef, style, attributes, listeners, isDragging });
  }

  /**
   * Floating preview rendered inside a `<DragOverlay>`. Mirrors the row's
   * first-cell affordances (grip handle + status dot + name) so users
   * can see what they're dragging without the visual weight of every
   * column. Lives in a portal — completely free of the grid layout,
   * which is the whole point: this is what makes the drag follow the
   * cursor smoothly even though the underlying rows use sticky-column
   * CSS grid.
   */
  function TaskDragPreview({ task, status }) {
    if (!task) return null;
    const dotColor = status?.color ?? "#94a3b8";
    const isUnstarted = isUnstartedStatus(status, task.status);
    return (
      <div
        className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl px-4 py-2.5 flex items-center gap-2 min-w-[320px] max-w-[480px] cursor-grabbing"
        style={{ willChange: "transform" }}
      >
        <GripVertical
          size={16}
          className="text-[color:var(--color-ink-muted)] shrink-0"
        />
        <StatusDot color={dotColor} unstarted={isUnstarted} size={14} />
        <span className="text-sm text-[color:var(--color-ink-strong)] truncate">
          {task.name}
        </span>
      </div>
    );
  }

  /** Server cap on `POST /tasks/bulk` (bulk.dto.js `ids.max(500)`). Only the
   *  SELECTED roots count against it — the cascade's descendants don't travel
   *  in the request, so a selection of 10 parents holding 900 subtasks is one
   *  10-id call. */
  const MAX_BULK_IDS = 500;

  /**
   * Bulk-action bar for the List view. Appears only once something is
   * selected, and carries a single destructive action.
   *
   * Counts are spelled out rather than shown as a bare total because the two
   * numbers differ: selecting 2 parents can mean deleting 9 rows, and the user
   * has to see that BEFORE the confirmation, not only inside it. Matches the
   * Task Monitor's bar (`TaskMonitorBulkBar`) visually so the two read as the
   * same affordance.
   */
  function ListBulkBar({ counts, overLimit, busy, onDelete, onClear }) {
    return (
      <div className="flex flex-wrap items-center gap-3 mb-4 px-4 py-2.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
        <span className="text-sm font-medium text-[color:var(--color-ink-strong)] tabular-nums">
          {formatNumber(counts.total)} selected
        </span>
        <span className="text-sm text-[color:var(--color-ink-muted)] tabular-nums">
          {formatNumber(counts.tasks)}{" "}
          {counts.tasks === 1 ? "task" : "tasks"} ·{" "}
          {formatNumber(counts.subtasks)}{" "}
          {counts.subtasks === 1 ? "subtask" : "subtasks"}
        </span>
        {overLimit ? (
          <span className="text-sm text-[color:var(--color-priority-urgent)]">
            Select up to {formatNumber(MAX_BULK_IDS)} rows to delete
            in one go.
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onDelete}
            disabled={busy || overLimit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[13px] bg-[color:var(--color-priority-urgent)] text-white hover:opacity-90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Trash2 size={13} /> Delete
          </button>
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[13px] border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <X size={13} /> Clear
          </button>
        </div>
      </div>
    );
  }

  /**
   * Per-parent SortableContext so a subtask drag never escapes its
   * parent's bucket — extracted here so the parent row JSX doesn't
   * drown in DnD boilerplate.
   */
  function SubtaskSortableZone({ subs, children }) {
    // Subtasks get their OWN SortableContext scoped to their parent's
    // bucket — but they share the LIST VIEW's SINGLE DndContext (owned
    // by SpacePage's list render, alongside every status group's rows).
    //
    // This used to be its own nested <DndContext>. dnd-kit does NOT
    // support nesting DndContext providers: the inner context silently
    // broke the OUTER (parent-row) drag — the row would lift but its
    // neighbours never slid and it never dropped. Nesting SORTABLE
    // contexts under one DndContext IS supported, so the list view owns
    // one DndContext and routes parent, cross-group, and subtask drags
    // through its handlers (`onListDragEnd`) while its grouped
    // `collisionDetection` keeps each drag scoped to legal targets.
    //
    // Memoised sub-ids so the SortableContext sees a stable reference
    // between renders — recreating the array forces dnd-kit to re-measure
    // droppables every frame.
    const subIds = useMemo(() => subs.map((s) => s.id), [subs]);
    return (
      <SortableContext items={subIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    );
  }

  function ListSection({
    groupKey,
    status,
    tasks,
    members,
    columns,
    statuses,
    showSubtasks,
    subtasksByParent,
    expandedParents,
    onToggleParent,
    onAddTask,
    onAddSubtask,
    onUpdateTask,
    onDeleteTask,
    onDuplicateTask,
    onManageStatuses,
    hideHeader,
    emptyText,
    currentUserId,
    workspaceId,
    workspaceName,
    spaceId,
    spaceName,
    listId,
    onOpenDetail,
    onOpenRecurrence,
    // Bulk-selection controller, or null when the viewer can't delete here.
    // One object rather than six props: the checkbox states at three nesting
    // depths all have to answer the same questions, and splitting them apart
    // invites one depth drifting from the others.
    selection,
  }) {
    const [open, setOpen] = useState(true);
    const template = gridTemplate(columns);
    const width = gridMinPx(columns);
    const isOpen = hideHeader ? true : open;

    const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);

    // The DnD context lives on the LIST VIEW parent (see the `view ===
    // 'list'` render in SpacePage) so a drag can CROSS status groups —
    // dnd-kit can't nest DndContexts, so this section only contributes
    // its sortable rows plus this whole-section drop target. The body
    // droppable is what lets a row dragged from another group land here
    // even when this group is EMPTY (no rows to collide with — the same
    // problem the Board view solved for empty columns).
    const { setNodeRef: setSectionDropRef } = useDroppable({
      id: `section:${groupKey}`,
    });
    return (
      // Fill the container width (so the box border reaches the right edge)
      // while `minWidth` preserves the natural column total as the horizontal-
      // scroll threshold. Every section resolves to the same width — the parent
      // width when it's wider than the columns, else the shared `gridMinPx` —
      // so they all still end at the exact same right edge. The trailing
      // Action column (minmax(56px,1fr)) absorbs any slack.
      <section className="mb-6" style={{ width: "100%", minWidth: width }}>
        {hideHeader ? null : (
          // Status pill (TO DO / IN PROGRESS / COMPLETE + count) pinned to the
          // left edge so it stays visible while the row's columns scroll
          // horizontally.
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="sticky left-0 inline-flex items-center gap-2 mb-2 px-2 py-1 -mx-2 rounded-md cursor-pointer w-fit hover:bg-[color:var(--color-card-hover)] transition-colors"
            aria-expanded={open}
          >
            <ChevronDown
              size={14}
              className={
                "transition-transform text-[color:var(--color-ink-muted)] " +
                (open ? "" : "-rotate-90")
              }
            />
            <StatusPill status={status} />
            <span className="text-sm font-medium text-[color:var(--color-ink-muted)] tabular-nums">
              {tasks.length}
            </span>
          </button>
        )}

        {isOpen ? (
          <div
            ref={setSectionDropRef}
            className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
          >
            <div
              className="grid items-center justify-items-start gap-3 px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]"
              style={{ gridTemplateColumns: template }}
            >
              {/* Sticky Task Name column so it stays visible while the rest
               *  of the row scrolls horizontally. `w-full` is critical —
               *  without it the grid's `justify-items-start` lets the sticky
               *  cell shrink to its text width, leaving the rest of the 560px
               *  column transparent and scrolling cells visible behind it.
               *  `pl-4` matches the data rows' inner padding so the header
               *  label aligns vertically with the task names below it. */}
              <span className="sticky left-0 bg-[color:var(--color-canvas)] z-20 flex items-center self-stretch w-full pl-4 pr-3 fd-pinned-col">
                {/* Select-all for THIS status group. Ticking a top-level row
                 *  implies its whole subtree, so the header only has to track
                 *  the group's parent rows — hence `tasks`, not every
                 *  rendered row. `-ml-2` puts it in the same column as the
                 *  row checkboxes below. */}
                {selection ? (
                  <RowSelectCheckbox
                    className="-ml-2 mr-3"
                    checked={selection.groupState(taskIds) === "all"}
                    indeterminate={selection.groupState(taskIds) === "some"}
                    onClick={() => selection.onToggleGroup(taskIds)}
                    ariaLabel={
                      selection.groupState(taskIds) === "all"
                        ? "Clear selection in this group"
                        : "Select all tasks in this group"
                    }
                  />
                ) : null}
                Task Name
              </span>
              {/* Blank gutter cell that aligns with the NAME_GUTTER_W
               *  slot in `gridTemplate`. Every row body needs a
               *  matching empty cell here or the columns shift. */}
              <span aria-hidden="true" />
              {COLUMN_ORDER.filter((c) => columns[c.key]).map((c) => (
                <span key={c.key}>{c.label}</span>
              ))}
              {/* Trailing action column (per-row Duplicate / Delete). Always
               *  plural "Actions" — the column is the row's action affordance
               *  regardless of how many controls it currently shows. Aligned
               *  `justify-self-end` to sit directly above the icons. */}
              <span className="justify-self-end">Actions</span>
            </div>
            {tasks.length === 0 ? (
              // Use the same grid template + sticky Name cell as the data rows
              // so the empty message stays anchored at the left when the user
              // scrolls horizontally — Assignee→Task ID columns remain blank
              // but the row layout matches the rest of the table.
              <div
                className="grid items-center justify-items-start gap-3 px-4 py-3"
                style={{ gridTemplateColumns: template }}
              >
                <div className="sticky left-0 z-20 bg-[color:var(--color-card)] flex items-center min-w-0 w-full self-stretch text-sm text-[color:var(--color-ink-muted)] pl-4 pr-3 fd-pinned-col">
                  {emptyText ?? "No tasks in this status."}
                </div>
              </div>
            ) : (
              // Sortable rows only; the "Add Task" button below stays
              // outside the sortable context so it never becomes a drop
              // target. The owning DndContext (sensors, collision, drag
              // handlers, overlay) lives on the list-view parent so rows
              // can be dropped into OTHER status groups too.
              <SortableContext
                items={taskIds}
                strategy={verticalListSortingStrategy}
              >
                {tasks.map((t) => {
                  const subs = subtasksByParent.get(t.id) ?? [];
                  const expanded = expandedParents.has(t.id);
                  return (
                    <SortableRow key={t.id} id={t.id}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        // `style` (the live drag transform) goes on this outer
                        // wrapper so the WHOLE block — parent row + its expanded
                        // subtask panel — slides together as one unit. But
                        // `setNodeRef` is deliberately NOT here: it lives on the
                        // inner grid row below, so dnd-kit measures a CONSTANT
                        // row-height droppable rect regardless of expand state.
                        // With the ref on this wrapper, an expanded parent
                        // measured as the full tall block, dropping its collision
                        // centre halfway down the subtask list and breaking
                        // `closestCenter` — the row would lift but never drop.
                        // (Same fix the subtask rows already use.)
                        <div
                          style={style}
                          {...attributes}
                          className="border-b border-[color:var(--color-rule-strong)] last:border-b-0 bg-[color:var(--color-card)]"
                        >
                          {/* Parent row — uses Tailwind's named `group/row`
                           *  so the hover-only Trash button (`group-hover/row:`)
                           *  and the sticky-cell background highlight stay
                           *  scoped to this row WITHOUT firing the Avatar
                           *  component's anonymous `group-hover` tooltip,
                           *  which would otherwise pop up whenever the cursor
                           *  touches any cell in the row. */}
                          <div
                            ref={setNodeRef}
                            data-focus-id={t.id}
                            className="group/row grid items-center justify-items-start gap-3 px-4 py-2.5 hover:bg-[color:var(--color-card-hover)]"
                            style={{ gridTemplateColumns: template }}
                          >
                            <div className="sticky left-0 z-20 bg-[color:var(--color-card)] group-hover/row:bg-[color:var(--color-card-hover)] flex items-center gap-1 min-w-0 w-full overflow-hidden self-stretch pl-4 pr-3 fd-pinned-col">
                              {/* Bulk-select. `-ml-2` matches the drag
                               *  handle's own negative margin, so the
                               *  checkbox takes the handle's old slot and
                               *  everything after it shifts by exactly 1rem
                               *  (w-5 + gap-1 − ml-2) — the same shift the
                               *  subtask rows below get, keeping the status
                               *  dots in one vertical line. */}
                              {selection ? (
                                <RowSelectCheckbox
                                  className="-ml-2"
                                  checked={selection.isChecked(t.id)}
                                  onClick={(e) => selection.onRowClick(e, t.id)}
                                  ariaLabel={`Select task ${t.name}`}
                                />
                              ) : null}
                              {/* Drag handle — always visible. Owns the
                               *  sortable listeners so the rest of the row's
                               *  cells stay individually clickable.
                               *  `touch-none` (touch-action: none) is critical
                               *  on touch + trackpad: without it the browser
                               *  steals the gesture to scroll and the drag
                               *  feels glitchy. */}
                              <button
                                type="button"
                                {...listeners}
                                title="Drag to reorder"
                                aria-label="Drag to reorder task"
                                style={{ touchAction: "none" }}
                                className="p-0.5 -ml-2 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-grab active:cursor-grabbing shrink-0"
                              >
                                <GripVertical size={16} />
                              </button>
                              {showSubtasks ? (
                                <button
                                  type="button"
                                  onClick={() => onToggleParent(t.id)}
                                  aria-expanded={expanded}
                                  aria-label={
                                    expanded ? "Hide subtasks" : "Show subtasks"
                                  }
                                  className="p-0.5 -ml-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                >
                                  {expanded ? (
                                    <ChevronDown size={12} />
                                  ) : (
                                    <ChevronRight size={12} />
                                  )}
                                </button>
                              ) : (
                                <span
                                  className="w-4 shrink-0"
                                  aria-hidden="true"
                                />
                              )}
                              <StatusCell
                                current={t.status}
                                statuses={statuses}
                                onCommit={(status) =>
                                  onUpdateTask(t.id, { status })
                                }
                                onManageStatuses={onManageStatuses}
                                disabled={t.canEditStatus === false}
                              />
                              <NameCell
                                value={t.name}
                                onCommit={(name) =>
                                  onUpdateTask(t.id, { name })
                                }
                                onOpenDetail={() => onOpenDetail?.(t.id)}
                              />
                              {showSubtasks && subs.length > 0 ? (
                                <span
                                  className="ml-1 text-[10px] text-[color:var(--color-ink-muted)] inline-flex items-center gap-0.5"
                                  title={`${subs.length} subtask${subs.length === 1 ? "" : "s"}`}
                                >
                                  <GitBranch size={11} />
                                  {subs.length}
                                </span>
                              ) : null}
                            </div>
                            {/* Blank gutter cell — aligns with the NAME_GUTTER_W
                             *  slot in `gridTemplate`. Every row body needs a
                             *  matching empty cell after the sticky Name cell. */}
                            <span aria-hidden="true" />
                            {columns.taskId ? (
                              <span className="text-xs text-[color:var(--color-ink-muted)]">
                                {t.id.slice(0, 8)}
                              </span>
                            ) : null}
                            {columns.status
                              ? (() => {
                                  // Resolve the status row so we can render the
                                  // full pill (name + dot). Unknown statuses fall
                                  // back to a faded grey label so the column never
                                  // shows a broken state.
                                  const s = statuses.find(
                                    (x) => x.name === t.status,
                                  );
                                  return s ? (
                                    <StatusPill status={s} />
                                  ) : (
                                    <span className="text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                                      {t.status}
                                    </span>
                                  );
                                })()
                              : null}
                            {columns.assignee ? (
                              <AssigneeCell
                                assigneeIds={t.assigneeIds ?? []}
                                members={members}
                                currentUserId={currentUserId}
                                workspaceId={workspaceId}
                                workspaceName={workspaceName}
                                spaceId={spaceId}
                                spaceName={spaceName}
                                taskId={t.id}
                                allTasks={tasks}
                                statuses={statuses}
                                onCommit={(assigneeIds) =>
                                  onUpdateTask(t.id, { assigneeIds })
                                }
                                disabled={t.canEditAssignee === false}
                              />
                            ) : null}
                            {columns.startDate ? (
                              <StartDateCell
                                startDate={t.startDate ?? null}
                                dueDate={t.dueDate ?? null}
                                startDateHasTime={t.startDateHasTime ?? false}
                                dueDateHasTime={t.dueDateHasTime ?? false}
                                onCommit={(patch) => onUpdateTask(t.id, patch)}
                                disabled={t.canEditPlanning === false}
                              />
                            ) : null}
                            {columns.startTime ? (
                              <TimeCell
                                field="start"
                                startDate={t.startDate ?? null}
                                dueDate={t.dueDate ?? null}
                                startDateHasTime={t.startDateHasTime ?? false}
                                dueDateHasTime={t.dueDateHasTime ?? false}
                                onCommit={(patch) => onUpdateTask(t.id, patch)}
                                disabled={t.canEditPlanning === false}
                              />
                            ) : null}
                            {columns.dueDate ? (
                              <DueDateCell
                                startDate={t.startDate ?? null}
                                dueDate={t.dueDate ?? null}
                                startDateHasTime={t.startDateHasTime ?? false}
                                dueDateHasTime={t.dueDateHasTime ?? false}
                                completedAt={t.completedAt ?? null}
                                onCommit={(patch) => onUpdateTask(t.id, patch)}
                                disabled={t.canEditPlanning === false}
                              />
                            ) : null}
                            {columns.dueTime ? (
                              <TimeCell
                                field="due"
                                startDate={t.startDate ?? null}
                                dueDate={t.dueDate ?? null}
                                startDateHasTime={t.startDateHasTime ?? false}
                                dueDateHasTime={t.dueDateHasTime ?? false}
                                onCommit={(patch) => onUpdateTask(t.id, patch)}
                                disabled={t.canEditPlanning === false}
                              />
                            ) : null}
                            {columns.priority ? (
                              <PriorityCell
                                priority={t.priority ?? null}
                                onCommit={(priority) =>
                                  onUpdateTask(t.id, { priority })
                                }
                                disabled={t.canEditPlanning === false}
                                completed={isTerminalStatusName(statuses, t.status)}
                              />
                            ) : null}
                            {columns.recurring ? (
                              <RecurringCell
                                state={t.recurring ?? null}
                                schedule={t.recurringSchedule ?? null}
                                canEdit={t.canEditPlanning !== false}
                                onOpen={() =>
                                  onOpenRecurrence?.(t.recurringSourceId ?? t.id)
                                }
                              />
                            ) : null}
                            {columns.comments ? (
                              <CommentsCell
                                taskId={t.id}
                                currentUserId={currentUserId}
                                spaceId={spaceId}
                                initialCount={t.commentCount}
                              />
                            ) : null}
                            {columns.createdBy ? (
                              <CreatedByCell
                                name={t.createdByName ?? null}
                                avatarUrl={t.createdByAvatarUrl ?? null}
                              />
                            ) : null}
                            {columns.dateCreated ? (
                              <ReadOnlyDateCell iso={t.createdAt} />
                            ) : null}
                            {columns.dateUpdated ? (
                              <ReadOnlyDateCell iso={t.updatedAt} />
                            ) : null}
                            {columns.dateDone ? (
                              <ReadOnlyDateCell iso={t.completedAt ?? null} />
                            ) : null}
                            {columns.attachments ? (
                              <TaskAttachmentsCell
                                taskId={t.id}
                                spaceId={spaceId}
                                initialCount={t.attachmentCount}
                              />
                            ) : null}
                            {columns.accountability ? (
                              <AccountabilityCell
                                task={t}
                                members={members}
                                currentUserId={currentUserId}
                                listId={listId}
                                onUpdate={(patch) => onUpdateTask(t.id, patch)}
                                disabled={t.canEditAssignee === false}
                              />
                            ) : null}
                            {columns.proof ? (
                              <ProofRequiredCell
                                value={t.proofRequired}
                                onToggle={(v) =>
                                  onUpdateTask(t.id, { proofRequired: v })
                                }
                                disabled={t.canEditAssignee === false}
                              />
                            ) : null}
                            <div className="justify-self-end inline-flex items-center gap-0.5">
                              {onDuplicateTask ? (
                                <button
                                  type="button"
                                  onClick={() => onDuplicateTask(t)}
                                  title="Duplicate task"
                                  aria-label="Duplicate task"
                                  className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                >
                                  <Copy size={14} />
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => onDeleteTask(t)}
                                title="Delete Task"
                                aria-label="Delete Task"
                                className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Subtasks panel */}
                          {showSubtasks && expanded ? (
                            <div className="bg-[color:var(--color-canvas)]">
                              {subs.length === 0 ? (
                                <div className="pl-12 pr-4 py-2 text-sm italic text-[color:var(--color-ink-muted)]">
                                  No subtasks yet.
                                </div>
                              ) : (
                                // Subtask DnD — its own context per parent so
                                // a subtask drag never escapes its parent's
                                // bucket. Same sparse-numbering scheme as the
                                // top-level reorder; the API endpoint
                                // (`/reorder`) handles both because every
                                // task — parent or sub — uses the same
                                // `sortOrder` field on the Task collection.
                                <SubtaskSortableZone subs={subs}>
                                  {/* SubtaskSortableZone owns the SortableContext
                                   *  + memoised id list internally, so this just
                                   *  renders the row map directly. Drag handling
                                   *  lives on the section's single DndContext. */}
                                  <>
                                    {subs.map((sub) => {
                                      // Sub-subtasks of THIS subtask — used by
                                      // the expand chevron, the count badge, and
                                      // the conditional depth-2 row render below.
                                      const subSubs =
                                        subtasksByParent.get(sub.id) ?? [];
                                      const subExpanded = expandedParents.has(
                                        sub.id,
                                      );
                                      return (
                                        <SortableRow key={sub.id} id={sub.id}>
                                          {({
                                            setNodeRef,
                                            style,
                                            attributes,
                                            listeners,
                                          }) => (
                                            // Outer wrapper carries the VISUAL
                                            // transform (the `style` from useSortable
                                            // contains the live drag translate +
                                            // opacity + pointer-events) plus the
                                            // a11y `attributes`. The whole subtask
                                            // block — row + expanded sub-subtask
                                            // rows + inline-add row — sits inside
                                            // and therefore translates as one unit.
                                            //
                                            // CRITICAL: `setNodeRef` is NOT on this
                                            // outer wrapper — it lives on the inner
                                            // grid row below. Putting the ref here
                                            // makes dnd-kit measure the entire
                                            // expanded block, shifting the active
                                            // item's center down by half the sub-
                                            // subtask block's height and breaking
                                            // `closestCenter` collision matches.
                                            // Keeping the ref on the inner row
                                            // alone makes the bbox a constant
                                            // row-height regardless of expand state,
                                            // so collision math stays stable.
                                            <div style={style} {...attributes}>
                                              <div
                                                // `setNodeRef` is here so dnd-kit measures
                                                // just the visible subtask row. Grid
                                                // template stays in inline style since the
                                                // column widths are computed at runtime.
                                                ref={setNodeRef}
                                                data-focus-id={sub.id}
                                                style={{
                                                  gridTemplateColumns: template,
                                                }}
                                                className="group/row grid items-center justify-items-start gap-3 px-4 py-2 border-t border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] bg-[color:var(--color-canvas)]"
                                              >
                                                {/* Indent lives INSIDE the Name cell (pl-12)
                                                 *  so every other column still lines up
                                                 *  vertically with the parent rows above.
                                                 *  Background matches the subtask row's
                                                 *  `--color-canvas` (not the parent row's
                                                 *  `--color-card`) so there's no visible
                                                 *  colour seam between the frozen Name pane
                                                 *  and the scrolling cells. `w-full` makes
                                                 *  the sticky cell fill the entire 560px
                                                 *  column width. The pl-12 (48px) is a
                                                 *  deliberate +32px step over the parent
                                                 *  row's pl-4 — clearer hierarchy than the
                                                 *  previous pl-8. */}
                                                <div className="sticky left-0 z-20 bg-[color:var(--color-canvas)] group-hover/row:bg-[color:var(--color-card-hover)] flex items-center gap-2 min-w-0 w-full overflow-hidden self-stretch pl-12 pr-3 fd-pinned-col">
                                                  {/* Bulk-select. Disabled +
                                                   *  force-checked while an ancestor is
                                                   *  selected: deleting the parent takes
                                                   *  this row regardless, so an untickable
                                                   *  tick is the honest state — letting the
                                                   *  user "deselect" it would promise
                                                   *  something the cascade won't honour.
                                                   *  `-ml-3` mirrors the drag handle's
                                                   *  margin (see the parent row). */}
                                                  {selection ? (
                                                    <RowSelectCheckbox
                                                      className="-ml-3"
                                                      checked={selection.isChecked(sub.id)}
                                                      disabled={selection.isLocked(sub.id)}
                                                      onClick={(e) =>
                                                        selection.onRowClick(e, sub.id)
                                                      }
                                                      ariaLabel={`Select subtask ${sub.name}`}
                                                      title={
                                                        selection.isLocked(sub.id)
                                                          ? "Deleted with its parent task"
                                                          : undefined
                                                      }
                                                    />
                                                  ) : null}
                                                  {/* Subtask drag handle — sits in the
                                                   *  indent gutter so it doesn't push the
                                                   *  StatusCell out of alignment with the
                                                   *  parent row above. `touchAction:none`
                                                   *  stops the browser from stealing the
                                                   *  gesture for scroll on touch/trackpad. */}
                                                  <button
                                                    type="button"
                                                    {...listeners}
                                                    title="Drag to reorder"
                                                    aria-label="Drag to reorder subtask"
                                                    style={{
                                                      touchAction: "none",
                                                    }}
                                                    className="p-0.5 -ml-3 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-grab active:cursor-grabbing shrink-0"
                                                  >
                                                    <GripVertical size={15} />
                                                  </button>
                                                  {/* Expand chevron — ALWAYS shown when the
                                                   *  global Subtasks toggle is on, mirroring
                                                   *  the parent task rows' chevron. Clicking
                                                   *  it toggles the sub-subtask panel below,
                                                   *  which renders the children (if any) AND
                                                   *  the inline "+ Add Subtask" row — so a
                                                   *  separate per-row dropdown isn't needed.
                                                   *  When `showSubtasks` is off, a fixed-
                                                   *  width spacer holds the layout so the
                                                   *  Name cell still lines up vertically. */}
                                                  {showSubtasks ? (
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        onToggleParent(sub.id)
                                                      }
                                                      aria-expanded={
                                                        subExpanded
                                                      }
                                                      aria-label={
                                                        subExpanded
                                                          ? "Hide sub-subtasks"
                                                          : "Show sub-subtasks"
                                                      }
                                                      className="p-0.5 -ml-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer shrink-0"
                                                    >
                                                      {subExpanded ? (
                                                        <ChevronDown
                                                          size={12}
                                                        />
                                                      ) : (
                                                        <ChevronRight
                                                          size={12}
                                                        />
                                                      )}
                                                    </button>
                                                  ) : (
                                                    <span
                                                      className="w-4 shrink-0"
                                                      aria-hidden="true"
                                                    />
                                                  )}
                                                  <StatusCell
                                                    current={sub.status}
                                                    statuses={statuses}
                                                    onCommit={(status) =>
                                                      onUpdateTask(sub.id, {
                                                        status,
                                                      })
                                                    }
                                                    onManageStatuses={
                                                      onManageStatuses
                                                    }
                                                    disabled={
                                                      sub.canEditStatus ===
                                                      false
                                                    }
                                                  />
                                                  <NameCell
                                                    value={sub.name}
                                                    onCommit={(name) =>
                                                      onUpdateTask(sub.id, {
                                                        name,
                                                      })
                                                    }
                                                    onOpenDetail={() =>
                                                      onOpenDetail?.(sub.id)
                                                    }
                                                  />
                                                  {/* Sub-subtask count badge — same shape as
                                                   *  the parent row's badge. Renders when the
                                                   *  subtask has at least one nested child. */}
                                                  {showSubtasks &&
                                                  subSubs.length > 0 ? (
                                                    <span
                                                      className="ml-1 text-[10px] text-[color:var(--color-ink-muted)] inline-flex items-center gap-0.5 shrink-0"
                                                      title={`${subSubs.length} sub-subtask${subSubs.length === 1 ? "" : "s"}`}
                                                    >
                                                      <GitBranch size={11} />
                                                      {subSubs.length}
                                                    </span>
                                                  ) : null}
                                                </div>
                                                {/* Blank gutter cell — keeps subtask rows
                                                 *  aligned with the header / parent row. */}
                                                <span aria-hidden="true" />
                                                {columns.taskId ? (
                                                  <span className="text-xs text-[color:var(--color-ink-muted)]">
                                                    {sub.id.slice(0, 8)}
                                                  </span>
                                                ) : null}
                                                {columns.status
                                                  ? (() => {
                                                      const s = statuses.find(
                                                        (x) =>
                                                          x.name === sub.status,
                                                      );
                                                      return s ? (
                                                        <StatusPill
                                                          status={s}
                                                        />
                                                      ) : (
                                                        <span className="text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                                                          {sub.status}
                                                        </span>
                                                      );
                                                    })()
                                                  : null}
                                                {columns.assignee ? (
                                                  <AssigneeCell
                                                    assigneeIds={
                                                      sub.assigneeIds ?? []
                                                    }
                                                    members={members}
                                                    currentUserId={
                                                      currentUserId
                                                    }
                                                    workspaceId={workspaceId}
                                                    workspaceName={
                                                      workspaceName
                                                    }
                                                    spaceId={spaceId}
                                                    spaceName={spaceName}
                                                    taskId={sub.id}
                                                    allTasks={tasks}
                                                    statuses={statuses}
                                                    onCommit={(assigneeIds) =>
                                                      onUpdateTask(sub.id, {
                                                        assigneeIds,
                                                      })
                                                    }
                                                    disabled={
                                                      sub.canEditPlanning ===
                                                      false
                                                    }
                                                  />
                                                ) : null}
                                                {columns.startDate ? (
                                                  <StartDateCell
                                                    startDate={
                                                      sub.startDate ?? null
                                                    }
                                                    dueDate={
                                                      sub.dueDate ?? null
                                                    }
                                                    startDateHasTime={
                                                      sub.startDateHasTime ??
                                                      false
                                                    }
                                                    dueDateHasTime={
                                                      sub.dueDateHasTime ??
                                                      false
                                                    }
                                                    onCommit={(patch) =>
                                                      onUpdateTask(
                                                        sub.id,
                                                        patch,
                                                      )
                                                    }
                                                    disabled={
                                                      sub.canEditPlanning ===
                                                      false
                                                    }
                                                  />
                                                ) : null}
                                                {columns.startTime ? (
                                                  <TimeCell
                                                    field="start"
                                                    startDate={
                                                      sub.startDate ?? null
                                                    }
                                                    dueDate={
                                                      sub.dueDate ?? null
                                                    }
                                                    startDateHasTime={
                                                      sub.startDateHasTime ??
                                                      false
                                                    }
                                                    dueDateHasTime={
                                                      sub.dueDateHasTime ??
                                                      false
                                                    }
                                                    onCommit={(patch) =>
                                                      onUpdateTask(
                                                        sub.id,
                                                        patch,
                                                      )
                                                    }
                                                    disabled={
                                                      sub.canEditPlanning ===
                                                      false
                                                    }
                                                  />
                                                ) : null}
                                                {columns.dueDate ? (
                                                  <DueDateCell
                                                    startDate={
                                                      sub.startDate ?? null
                                                    }
                                                    dueDate={
                                                      sub.dueDate ?? null
                                                    }
                                                    startDateHasTime={
                                                      sub.startDateHasTime ??
                                                      false
                                                    }
                                                    dueDateHasTime={
                                                      sub.dueDateHasTime ??
                                                      false
                                                    }
                                                    completedAt={
                                                      sub.completedAt ?? null
                                                    }
                                                    onCommit={(patch) =>
                                                      onUpdateTask(
                                                        sub.id,
                                                        patch,
                                                      )
                                                    }
                                                    disabled={
                                                      sub.canEditPlanning ===
                                                      false
                                                    }
                                                  />
                                                ) : null}
                                                {columns.dueTime ? (
                                                  <TimeCell
                                                    field="due"
                                                    startDate={
                                                      sub.startDate ?? null
                                                    }
                                                    dueDate={
                                                      sub.dueDate ?? null
                                                    }
                                                    startDateHasTime={
                                                      sub.startDateHasTime ??
                                                      false
                                                    }
                                                    dueDateHasTime={
                                                      sub.dueDateHasTime ??
                                                      false
                                                    }
                                                    onCommit={(patch) =>
                                                      onUpdateTask(
                                                        sub.id,
                                                        patch,
                                                      )
                                                    }
                                                    disabled={
                                                      sub.canEditPlanning ===
                                                      false
                                                    }
                                                  />
                                                ) : null}
                                                {columns.priority ? (
                                                  <PriorityCell
                                                    priority={
                                                      sub.priority ?? null
                                                    }
                                                    onCommit={(priority) =>
                                                      onUpdateTask(sub.id, {
                                                        priority,
                                                      })
                                                    }
                                                    disabled={
                                                      sub.canEditPlanning ===
                                                      false
                                                    }
                                                    completed={isTerminalStatusName(
                                                      statuses,
                                                      sub.status,
                                                    )}
                                                  />
                                                ) : null}
                                                {columns.recurring ? (
                                                  <RecurringCell
                                                    state={sub.recurring ?? null}
                                                    schedule={sub.recurringSchedule ?? null}
                                                  />
                                                ) : null}
                                                {columns.comments ? (
                                                  <CommentsCell
                                                    taskId={sub.id}
                                                    currentUserId={
                                                      currentUserId
                                                    }
                                                    spaceId={spaceId}
                                                    initialCount={sub.commentCount}
                                                  />
                                                ) : null}
                                                {columns.createdBy ? (
                                                  <CreatedByCell
                                                    name={
                                                      sub.createdByName ?? null
                                                    }
                                                    avatarUrl={
                                                      sub.createdByAvatarUrl ??
                                                      null
                                                    }
                                                  />
                                                ) : null}
                                                {columns.dateCreated ? (
                                                  <ReadOnlyDateCell
                                                    iso={sub.createdAt}
                                                  />
                                                ) : null}
                                                {columns.dateUpdated ? (
                                                  <ReadOnlyDateCell
                                                    iso={sub.updatedAt}
                                                  />
                                                ) : null}
                                                {columns.dateDone ? (
                                                  <ReadOnlyDateCell
                                                    iso={
                                                      sub.completedAt ?? null
                                                    }
                                                  />
                                                ) : null}
                                                {columns.attachments ? (
                                                  <TaskAttachmentsCell
                                                    taskId={sub.id}
                                                    spaceId={spaceId}
                                                    initialCount={sub.attachmentCount}
                                                  />
                                                ) : null}
                                                {columns.accountability ? (
                                                  <AccountabilityCell
                                                    task={sub}
                                                    members={members}
                                                    currentUserId={currentUserId}
                                                    listId={listId}
                                                    onUpdate={(patch) =>
                                                      onUpdateTask(sub.id, patch)
                                                    }
                                                    disabled={
                                                      sub.canEditAssignee === false
                                                    }
                                                  />
                                                ) : null}
                                                {columns.proof ? (
                                                  <ProofRequiredCell
                                                    value={sub.proofRequired}
                                                    onToggle={(v) =>
                                                      onUpdateTask(sub.id, {
                                                        proofRequired: v,
                                                      })
                                                    }
                                                    disabled={
                                                      sub.canEditAssignee === false
                                                    }
                                                  />
                                                ) : null}
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    onDeleteTask(sub)
                                                  }
                                                  title="Delete subtask"
                                                  aria-label="Delete subtask"
                                                  className="justify-self-end p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                                >
                                                  <Trash2 size={14} />
                                                </button>
                                              </div>
                                              {/* Depth-2 sub-subtasks. Rendered when this
                                               *  depth-1 subtask is expanded. No DnD wrap
                                               *  (sub-subtasks aren't reorderable for now);
                                               *  the row mirrors the subtask row's layout
                                               *  with a deeper `pl-16` indent. */}
                                              {showSubtasks && subExpanded ? (
                                                <>
                                                  {subSubs.map((subSub) => (
                                                    <div
                                                      key={subSub.id}
                                                      data-focus-id={subSub.id}
                                                      className="group/row grid items-center justify-items-start gap-3 px-4 py-2 border-t border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] bg-[color:var(--color-canvas)]"
                                                      style={{
                                                        gridTemplateColumns:
                                                          template,
                                                      }}
                                                    >
                                                      <div className="sticky left-0 z-20 bg-[color:var(--color-canvas)] group-hover/row:bg-[color:var(--color-card-hover)] flex items-center gap-2 min-w-0 w-full overflow-hidden self-stretch pl-32 pr-3 fd-pinned-col">
                                                        {/* Depth-2 has no drag handle, so
                                                         *  the checkbox carries the same
                                                         *  `-ml-3` the handle would have
                                                         *  had — the +1rem shift stays
                                                         *  identical at every depth. */}
                                                        {selection ? (
                                                          <RowSelectCheckbox
                                                            className="-ml-3"
                                                            checked={selection.isChecked(
                                                              subSub.id,
                                                            )}
                                                            disabled={selection.isLocked(
                                                              subSub.id,
                                                            )}
                                                            onClick={(e) =>
                                                              selection.onRowClick(
                                                                e,
                                                                subSub.id,
                                                              )
                                                            }
                                                            ariaLabel={`Select subtask ${subSub.name}`}
                                                            title={
                                                              selection.isLocked(subSub.id)
                                                                ? "Deleted with its parent task"
                                                                : undefined
                                                            }
                                                          />
                                                        ) : null}
                                                        <StatusCell
                                                          current={
                                                            subSub.status
                                                          }
                                                          statuses={statuses}
                                                          onCommit={(status) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              { status },
                                                            )
                                                          }
                                                          onManageStatuses={
                                                            onManageStatuses
                                                          }
                                                          disabled={
                                                            subSub.canEditStatus ===
                                                            false
                                                          }
                                                        />
                                                        <NameCell
                                                          value={subSub.name}
                                                          onCommit={(name) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              { name },
                                                            )
                                                          }
                                                          onOpenDetail={() =>
                                                            onOpenDetail?.(
                                                              subSub.id,
                                                            )
                                                          }
                                                        />
                                                      </div>
                                                      {/* Blank gutter cell — same role as
                                                       *  on parent + subtask rows. */}
                                                      <span aria-hidden="true" />
                                                      {columns.taskId ? (
                                                        <span className="text-xs text-[color:var(--color-ink-muted)]">
                                                          {subSub.id.slice(
                                                            0,
                                                            8,
                                                          )}
                                                        </span>
                                                      ) : null}
                                                      {columns.status
                                                        ? (() => {
                                                            const s =
                                                              statuses.find(
                                                                (x) =>
                                                                  x.name ===
                                                                  subSub.status,
                                                              );
                                                            return s ? (
                                                              <StatusPill
                                                                status={s}
                                                              />
                                                            ) : null;
                                                          })()
                                                        : null}
                                                      {columns.assignee ? (
                                                        <AssigneeCell
                                                          assigneeIds={
                                                            subSub.assigneeIds ??
                                                            []
                                                          }
                                                          members={members}
                                                          currentUserId={
                                                            currentUserId
                                                          }
                                                          workspaceId={
                                                            workspaceId
                                                          }
                                                          workspaceName={
                                                            workspaceName
                                                          }
                                                          spaceId={spaceId}
                                                          spaceName={spaceName}
                                                          taskId={subSub.id}
                                                          allTasks={tasks}
                                                          statuses={statuses}
                                                          onCommit={(
                                                            assigneeIds,
                                                          ) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              { assigneeIds },
                                                            )
                                                          }
                                                          disabled={
                                                            subSub.canEditPlanning ===
                                                            false
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.startDate ? (
                                                        <StartDateCell
                                                          startDate={
                                                            subSub.startDate ??
                                                            null
                                                          }
                                                          dueDate={
                                                            subSub.dueDate ??
                                                            null
                                                          }
                                                          startDateHasTime={
                                                            subSub.startDateHasTime ??
                                                            false
                                                          }
                                                          dueDateHasTime={
                                                            subSub.dueDateHasTime ??
                                                            false
                                                          }
                                                          onCommit={(patch) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              patch,
                                                            )
                                                          }
                                                          disabled={
                                                            subSub.canEditPlanning ===
                                                            false
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.startTime ? (
                                                        <TimeCell
                                                          field="start"
                                                          startDate={
                                                            subSub.startDate ??
                                                            null
                                                          }
                                                          dueDate={
                                                            subSub.dueDate ??
                                                            null
                                                          }
                                                          startDateHasTime={
                                                            subSub.startDateHasTime ??
                                                            false
                                                          }
                                                          dueDateHasTime={
                                                            subSub.dueDateHasTime ??
                                                            false
                                                          }
                                                          onCommit={(patch) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              patch,
                                                            )
                                                          }
                                                          disabled={
                                                            subSub.canEditPlanning ===
                                                            false
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.dueDate ? (
                                                        <DueDateCell
                                                          startDate={
                                                            subSub.startDate ??
                                                            null
                                                          }
                                                          dueDate={
                                                            subSub.dueDate ??
                                                            null
                                                          }
                                                          startDateHasTime={
                                                            subSub.startDateHasTime ??
                                                            false
                                                          }
                                                          dueDateHasTime={
                                                            subSub.dueDateHasTime ??
                                                            false
                                                          }
                                                          completedAt={
                                                            subSub.completedAt ??
                                                            null
                                                          }
                                                          onCommit={(patch) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              patch,
                                                            )
                                                          }
                                                          disabled={
                                                            subSub.canEditPlanning ===
                                                            false
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.dueTime ? (
                                                        <TimeCell
                                                          field="due"
                                                          startDate={
                                                            subSub.startDate ??
                                                            null
                                                          }
                                                          dueDate={
                                                            subSub.dueDate ??
                                                            null
                                                          }
                                                          startDateHasTime={
                                                            subSub.startDateHasTime ??
                                                            false
                                                          }
                                                          dueDateHasTime={
                                                            subSub.dueDateHasTime ??
                                                            false
                                                          }
                                                          onCommit={(patch) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              patch,
                                                            )
                                                          }
                                                          disabled={
                                                            subSub.canEditPlanning ===
                                                            false
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.priority ? (
                                                        <PriorityCell
                                                          priority={
                                                            subSub.priority ??
                                                            null
                                                          }
                                                          onCommit={(
                                                            priority,
                                                          ) =>
                                                            onUpdateTask(
                                                              subSub.id,
                                                              { priority },
                                                            )
                                                          }
                                                          disabled={
                                                            subSub.canEditPlanning ===
                                                            false
                                                          }
                                                          completed={isTerminalStatusName(
                                                            statuses,
                                                            subSub.status,
                                                          )}
                                                        />
                                                      ) : null}
                                                      {columns.recurring ? (
                                                        <RecurringCell
                                                          state={
                                                            subSub.recurring ??
                                                            null
                                                          }
                                                          schedule={subSub.recurringSchedule ?? null}
                                                        />
                                                      ) : null}
                                                      {columns.comments ? (
                                                        <CommentsCell
                                                          taskId={subSub.id}
                                                          currentUserId={
                                                            currentUserId
                                                          }
                                                          spaceId={spaceId}
                                                          initialCount={subSub.commentCount}
                                                        />
                                                      ) : null}
                                                      {columns.createdBy ? (
                                                        <CreatedByCell
                                                          name={
                                                            subSub.createdByName ??
                                                            null
                                                          }
                                                          avatarUrl={
                                                            subSub.createdByAvatarUrl ??
                                                            null
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.dateCreated ? (
                                                        <ReadOnlyDateCell
                                                          iso={subSub.createdAt}
                                                        />
                                                      ) : null}
                                                      {columns.dateUpdated ? (
                                                        <ReadOnlyDateCell
                                                          iso={subSub.updatedAt}
                                                        />
                                                      ) : null}
                                                      {columns.dateDone ? (
                                                        <ReadOnlyDateCell
                                                          iso={
                                                            subSub.completedAt ??
                                                            null
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.attachments ? (
                                                        <TaskAttachmentsCell
                                                          taskId={subSub.id}
                                                          spaceId={spaceId}
                                                          initialCount={subSub.attachmentCount}
                                                        />
                                                      ) : null}
                                                      {columns.accountability ? (
                                                        <AccountabilityCell
                                                          task={subSub}
                                                          members={members}
                                                          currentUserId={currentUserId}
                                                          listId={listId}
                                                          onUpdate={(patch) =>
                                                            onUpdateTask(subSub.id, patch)
                                                          }
                                                          disabled={
                                                            subSub.canEditAssignee === false
                                                          }
                                                        />
                                                      ) : null}
                                                      {columns.proof ? (
                                                        <ProofRequiredCell
                                                          value={subSub.proofRequired}
                                                          onToggle={(v) =>
                                                            onUpdateTask(subSub.id, {
                                                              proofRequired: v,
                                                            })
                                                          }
                                                          disabled={
                                                            subSub.canEditAssignee === false
                                                          }
                                                        />
                                                      ) : null}
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          onDeleteTask(subSub)
                                                        }
                                                        title="Delete sub-subtask"
                                                        aria-label="Delete sub-subtask"
                                                        className="justify-self-end p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                                      >
                                                        <Trash2 size={14} />
                                                      </button>
                                                    </div>
                                                  ))}
                                                  {/* Inline add for a new sub-subtask under
                                                   *  this subtask. Reuses the same component
                                                   *  as the parent's "Add subtask" row but
                                                   *  with `depth={2}` so the indent matches
                                                   *  the sub-subtask rows above (pl-20). */}
                                                  <InlineAddSubtaskRow
                                                    statusColor={status?.color}
                                                    statusUnstarted={isUnstartedStatus(
                                                      status,
                                                      status?.name,
                                                    )}
                                                    onSubmit={(name) =>
                                                      onAddSubtask(sub.id, name)
                                                    }
                                                    depth={2}
                                                  />
                                                </>
                                              ) : null}
                                            </div>
                                          )}
                                        </SortableRow>
                                      );
                                    })}
                                  </>
                                </SubtaskSortableZone>
                              )}
                              <InlineAddSubtaskRow
                                statusColor={status?.color}
                                statusUnstarted={isUnstartedStatus(
                                  status,
                                  status?.name,
                                )}
                                onSubmit={(name) => onAddSubtask(t.id, name)}
                              />
                            </div>
                          ) : null}
                        </div>
                      )}
                    </SortableRow>
                  );
                })}
              </SortableContext>
            )}
            {onAddTask ? (
              <InlineAddTaskRow
                statusColor={status?.color}
                statusUnstarted={isUnstartedStatus(status, status?.name)}
                onSubmit={(name) => onAddTask(name)}
              />
            ) : null}
          </div>
        ) : null}
      </section>
    );
  }

  /**
   * Bottom-of-group quick-add row. Two states:
   *
   *   1. Idle  — renders as a slim "+ Add Task" link, identical
   *              affordance to the old button so the bottom of each
   *              status group still reads as a clear empty-state CTA.
   *   2. Active — clicking the link expands the row into an input
   *              with a status dot on the left and a Save button on
   *              the right. Enter or Save commits the typed name as
   *              the new task; Escape cancels. Stays inside the
   *              group so the typed task lands in the right status
   *              bucket immediately.
   *
   * Matches the ClickUp quick-add pattern from the user's reference:
   * a single click → focused input → typed name → Save / Enter, with
   * no modal interrupt.
   */
  function InlineAddTaskRow({ statusColor, statusUnstarted, onSubmit }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState("");
    // Validation message shown when the user hits Save with no / too-short
    // name. Cleared as soon as they start typing (see the input's onChange).
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
      if (open) inputRef.current?.focus();
    }, [open]);

    function commit() {
      const trimmed = draft.trim();
      // Task name is mandatory (QA). An explicit Save with an empty or
      // too-short name now surfaces a validation message + red field and
      // KEEPS the row open, instead of silently collapsing — matching the
      // 2-character rule the Create Task dialog and inline rename already
      // enforce. (Cancel / Escape still dismiss silently — that's the
      // "didn't mean to open it" path, not a failed save.)
      if (trimmed.length === 0) {
        setError("Task Name is required.");
        inputRef.current?.focus();
        return;
      }
      if (trimmed.length < 2) {
        setError("Task Name must be at least 2 characters.");
        inputRef.current?.focus();
        return;
      }
      const tooLong = taskNameLengthError(trimmed);
      if (tooLong) {
        setError(tooLong);
        inputRef.current?.focus();
        return;
      }
      if (CONTAINS_EMOJI.test(trimmed)) {
        // Keep the input open so the typed value can be corrected in place.
        toast.error("Task Name cannot contain emojis.");
        return;
      }
      // Client-side quality guard — mirrors the server rule in
      // tasks.dto.js so junk names give instant feedback instead of a
      // round-trip 400 (bug 0013372). Keep the input open so the typed
      // value isn't lost.
      if (!isMeaningfulTaskName(trimmed)) {
        toast.error("Task Name must include at least one letter or number.");
        return;
      }
      onSubmit(trimmed);
      // Reset for the next add without re-opening — Save commits and
      // collapses, and the next click on "Add Task" gives a fresh input.
      setDraft("");
      setError(null);
      setOpen(false);
    }

    function cancel() {
      setDraft("");
      setError(null);
      setOpen(false);
    }

    if (!open) {
      return (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full text-left py-2 text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] flex items-center cursor-pointer group/addtask"
        >
          {/* The row sits inside a section deliberately WIDER than the viewport,
           *  so an unpinned label scrolled out of sight and the user had to
           *  scroll back left to add a task (defect DF_017 / Mantis 22153).
           *  Pin the label to the left edge exactly as the group status pill and
           *  every row's name cell already do — the button itself stays
           *  full-width so the whole row remains a click target. The opaque
           *  background is what stops columns showing through underneath. */}
          <span className="sticky left-0 z-20 inline-flex items-center gap-2 self-stretch bg-[color:var(--color-card)] group-hover/addtask:bg-[color:var(--color-card-hover)] pl-4 pr-3 fd-pinned-col">
            <Plus size={12} />
            Add Task
          </span>
        </button>
      );
    }

    return (
      <div className="flex items-center gap-2 px-4 py-2 border-t border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <StatusDot
          color={statusColor ?? "#94a3b8"}
          unstarted={Boolean(statusUnstarted)}
          size={14}
        />
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            // Flag an over-long name WHILE typing. These inputs carry no DOM
            // maxLength by design (see below), so without this the first and
            // only feedback was a rejected save. The unconditional setError
            // still clears on a valid keystroke — React bails out when the
            // value is already null.
            setError(taskNameLengthError(next, "Task Name"));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          // Blur DISMISSES an empty row (collapses back to "+ Add Task")
          // so a row you never typed into doesn't leave the Save / Cancel
          // controls stranded in the list. It never auto-COMMITS: a blur
          // with text typed keeps the row open — silently creating a task
          // on a stray click, or discarding typed work, both felt unsafe.
          // Save / Enter and Cancel / Escape stay the only ways to commit
          // or discard typed text. (The Save / Cancel buttons already use
          // onMouseDown + preventDefault, so clicking them never blurs the
          // input and this handler can't race the button click.)
          onBlur={() => {
            if (!draft.trim()) cancel();
          }}
          /* Soft limit: no DOM maxLength — the create save is capped by the DTO. */
          placeholder="Task name"
          // `max-w-md` caps the input's growth at ~448px so the
          // Cancel / Save buttons sit RIGHT AFTER the input instead
          // of being pushed to the far right edge of the row. The row
          // itself still spans the full table width (so the bottom
          // border lines up with the header), but the interactive
          // controls cluster on the left where the eye is already
          // tracking the new task title.
          aria-invalid={error ? true : undefined}
          className={
            "flex-1 min-w-0 max-w-md bg-transparent outline-none text-sm text-[color:var(--color-ink-strong)] placeholder:text-[color:var(--color-ink-subtle)] rounded " +
            (error
              ? "ring-1 ring-[color:var(--color-priority-urgent)] px-2 py-0.5"
              : "border-none")
          }
        />
        <button
          type="button"
          // onMouseDown (not onClick) so the input's blur doesn't fire
          // and steal focus before the cancel registers. Same trick on
          // the Save button.
          onMouseDown={(e) => {
            e.preventDefault();
            cancel();
          }}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer shrink-0"
        >
          Cancel
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            commit();
          }}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer shrink-0"
        >
          Save
        </button>
        {error ? (
          <span
            role="alert"
            className="text-xs font-medium text-[color:var(--color-priority-urgent)] shrink-0"
          >
            {error}
          </span>
        ) : null}
        {/* Spacer absorbs the remaining row width so the cluster
         *  above stays left-aligned. Without this, the table's full
         *  width was getting attributed to the input's flex-1 and the
         *  buttons would slide all the way to the right edge. */}
        <div className="flex-1" />
      </div>
    );
  }

  /**
   * Subtask quick-add row — mirrors `InlineAddTaskRow` but indented to
   * align with the subtask cells above.
   *
   * Idle state:  "+ Add Subtask"
   * Active state: status dot · input · Cancel · Save
   *
   * This row used to carry a second "Break Down" action beside the quick-add,
   * opening an SOP-driven bulk-subtask dialog. That entry point was removed on
   * request — it was the only way to reach `TaskBreakdownDialog`, so the popup
   * is now unreachable from the product.
   *
   * The status dot mirrors the PARENT task's status because a brand
   * new subtask inherits the parent's status on the backend
   * (`addSubtask` does this) — showing the parent's pip on the left
   * is an accurate preview of where the subtask will land.
   */

  function InlineAddSubtaskRow({
    statusColor,
    statusUnstarted,
    onSubmit,
    /** Visual indent. `1` = subtask under a parent task (default `pl-12`);
     *  `2` = sub-subtask under a subtask (`pl-20`). Matches the data
     *  row's `pl-8` / `pl-16` indent + the 4 extra units the existing
     *  inline-add row already uses. */
    depth = 1,
  }) {
    // Depth → indent map. Mirrors the data rows above:
    //   depth 1 (subtask)        → pl-12 (matches the depth-1 cell padding)
    //   depth 2 (sub-subtask)    → pl-32 (matches the depth-2 cell padding)
    const indentClass = depth === 2 ? "pl-32" : "pl-12";
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState("");
    // Subtask name is mandatory too — same validation as InlineAddTaskRow.
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
      if (open) inputRef.current?.focus();
    }, [open]);

    function commit() {
      const trimmed = draft.trim();
      // Mandatory name (QA): an empty / too-short Save surfaces a validation
      // message + red field and keeps the row open, matching InlineAddTaskRow.
      if (trimmed.length === 0) {
        setError("Subtask Name is required.");
        inputRef.current?.focus();
        return;
      }
      if (trimmed.length < 2) {
        setError("Subtask Name must be at least 2 characters.");
        inputRef.current?.focus();
        return;
      }
      const tooLong = taskNameLengthError(trimmed, "Subtask Name");
      if (tooLong) {
        setError(tooLong);
        inputRef.current?.focus();
        return;
      }
      if (CONTAINS_EMOJI.test(trimmed)) {
        // Keep the input open so the typed value can be corrected in place.
        toast.error("Task Name cannot contain emojis.");
        return;
      }
      // Same junk-name guard as the task quick-add (bug 0013372).
      if (!isMeaningfulTaskName(trimmed)) {
        toast.error("Subtask Name must include at least one letter or number.");
        return;
      }
      onSubmit(trimmed);
      setDraft("");
      setError(null);
      setOpen(false);
    }

    function cancel() {
      setDraft("");
      setError(null);
      setOpen(false);
    }

    if (!open) {
      return (
        <div className="border-t border-[color:var(--color-rule)] flex">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={`flex-1 text-left ${indentClass} pr-4 py-2 text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] flex items-center gap-2 cursor-pointer`}
          >
            <Plus size={11} />
            Add Subtask
          </button>
        </div>
      );
    }

    return (
      <div
        className={`border-t border-[color:var(--color-rule)] flex items-center gap-2 ${indentClass} pr-4 py-2 bg-[color:var(--color-canvas)]`}
      >
        <StatusDot
          color={statusColor ?? "#94a3b8"}
          unstarted={Boolean(statusUnstarted)}
          size={13}
        />
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            // Flag an over-long name WHILE typing. These inputs carry no DOM
            // maxLength by design (see below), so without this the first and
            // only feedback was a rejected save. The unconditional setError
            // still clears on a valid keystroke — React bails out when the
            // value is already null.
            setError(taskNameLengthError(next, "Subtask Name"));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          // Blur dismisses an empty row (collapses to "+ Add Subtask");
          // never auto-commits. Same contract as InlineAddTaskRow — the
          // buttons' onMouseDown + preventDefault keep a Save / Cancel
          // click from blurring the input and racing this handler.
          onBlur={() => {
            if (!draft.trim()) cancel();
          }}
          /* Soft limit: no DOM maxLength — the create save is capped by the DTO. */
          placeholder="Subtask name"
          // Capped width — same rationale as `InlineAddTaskRow`. Keeps
          // the Cancel / Save buttons immediately after the input
          // instead of stranded at the row's right edge.
          aria-invalid={error ? true : undefined}
          className={
            "flex-1 min-w-0 max-w-md bg-transparent outline-none text-sm text-[color:var(--color-ink-strong)] placeholder:text-[color:var(--color-ink-subtle)] rounded " +
            (error
              ? "ring-1 ring-[color:var(--color-priority-urgent)] px-2 py-0.5"
              : "border-none")
          }
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cancel();
          }}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer shrink-0"
        >
          Cancel
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            commit();
          }}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer shrink-0"
        >
          Save
        </button>
        {error ? (
          <span
            role="alert"
            className="text-xs font-medium text-[color:var(--color-priority-urgent)] shrink-0"
          >
            {error}
          </span>
        ) : null}
        <div className="flex-1" />
      </div>
    );
  }

  // ---------- Board column + card (DnD-aware) ----------

  /** Plain visual for a task card. Used inside the column AND inside the
   *  DragOverlay — keeping it presentation-only avoids registering a second
   *  useDraggable hook with the same id (which dnd-kit silently breaks on). */
  /**
   * Compact, read-only card body — used inside the DragOverlay so the
   * "ghost" that follows the cursor mid-drag stays cheap and doesn't
   * register a second set of `useDraggable` hooks for the same id (a
   * silent dnd-kit footgun). The interactive card lives in
   * `BoardCardInteractive` below.
   */
  function BoardCardView({ task, assignees, dragging }) {
    return (
      <div
        className={
          "rounded-md border border-[color:var(--color-rule)] p-3 bg-[color:var(--color-card)] cursor-grab active:cursor-grabbing select-none shadow-sm " +
          (dragging ? "opacity-100 shadow-lg" : "")
        }
      >
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
          {task.name}
        </p>
        <div className="mt-2 flex items-center justify-between">
          <AvatarStack
            members={assignees}
            totalCount={(task.assigneeIds ?? []).length}
          />
          <PriorityChip priority={task.priority ?? null} />
        </div>
      </div>
    );
  }

  /**
   * Interactive card — the version users actually see in each column.
   *
   * Mirrors the List view's per-cell interactivity:
   *   - Click the name → opens the full task-detail dialog
   *   - Date chip → opens the same date popover the List/Gantt use
   *   - Assignee picker → opens the same picker (search + invite)
   *   - Priority pill → opens the same priority menu
   *   - Hover-only Pencil "open task" button (top-right)
   *   - "X subtasks" expander row on parents — toggles the nested
   *     subtask sub-cards beneath
   *
   * Subtask sub-cards reuse this same component with `isSubtask`, so
   * every action that works on a parent works on its subtasks too.
   *
   * `dragHandleProps` is the bag of `{...listeners, ...attributes}`
   * from `useDraggable`. We attach it ONLY to the name / empty area so
   * clicks on the interactive cells don't start a drag.
   */
  function BoardCardInteractive({
    task,
    members,
    statuses,
    currentUserId,
    workspaceId,
    workspaceName,
    spaceId,
    spaceName,
    allTasks,
    isSubtask = false,
    /** Column-visibility map driven by the toolbar's Customize pill.
     *  Each `columns.<key>` boolean decides whether the corresponding
     *  chip renders on the card. Same shape the List view consumes,
     *  so toggling Assignee / Date / Priority / etc. in either view
     *  affects both — and the Customize panel stays one source of
     *  truth. Defaults to the historic shipping set (assignee + dates
     *  + priority) so callers that haven't wired `columns` through yet
     *  keep their existing cards. */
    columns = {
      assignee: true,
      startDate: true,
      dueDate: true,
      priority: true,
    },
    // Subtask expander state — only parents consume these.
    hasSubtasks = false,
    expanded = false,
    subtaskCount = 0,
    onToggleSubtasks,
    onUpdateTask,
    onOpenDetail,
    onDeleteTask,
    /** Optional — when provided (top-level cards only), shows a one-click
     *  "Duplicate task" button in the action cluster. */
    onDuplicateTask,
    /** Optional — when provided, the card shows a hover "+ Add subtask"
     *  button in its action cluster. Used to create depth-1 children
     *  (on parent cards) or depth-2 children (on subtask cards). The
     *  parent's id is passed back so the caller knows what to nest
     *  under. */
    onAddSubtask,
    /** Opens the recurrence editor for this card's task (top-level tasks and
     *  subtasks alike). The Repeat chip renders only when this is provided. */
    onOpenRecurrence,
    dragHandleProps,
  }) {
    // Pre-compute each chip's visibility once so the JSX below stays
    // readable. The Board view only supports the chips in
    // `BOARD_ALLOWED_COLUMN_KEYS` — Start time, Due time, Created,
    // Updated, Done, and Task ID toggles are filtered out of the
    // Customize panel for Board, so we don't need to read them here.
    // `showDateChip` consolidates the two date toggles — BoardDateChip
    // itself decides whether to show a range or a single date based on
    // what's actually set on the task.
    const showAssignee = Boolean(columns.assignee);
    const showStartDate = Boolean(columns.startDate);
    const showDueDate = Boolean(columns.dueDate);
    const showDateChip = showStartDate || showDueDate;
    const showPriority = Boolean(columns.priority);
    // Repeat chip — TOP-LEVEL cards only (only top-level tasks can repeat; a
    // parent's recurrence clones its subtasks). Subtask cards don't wire the
    // open-recurrence handler, so the chip is hidden there.
    const showRecurring = Boolean(columns.recurring) && Boolean(onOpenRecurrence);
    const showComments = Boolean(columns.comments);
    // "Created by" is intentionally NOT shown on Board cards — it duplicated
    // the Assignee avatar whenever the creator was also the assignee, reading
    // as the assignee twice. It remains available on the List and Gantt views.
    // Comments lives in its own row below the primary chips so the
    // count + click-to-open thread sits visually apart from the chip
    // strip. Rendered only when its toggle is on.
    const showMetaRow = showComments;
    // Whether the primary chip row renders at all — when every primary
    // toggle is off we skip rendering the row entirely.
    const showChipRow =
      showAssignee || showDateChip || showPriority || showRecurring;
    // Helper — stop drag from kicking in when the user clicks on an
    // interactive control inside the card. `useDraggable` listens at
    // the wrapper level, so any click that survives bubbles up.
    function stopDrag(e) {
      e.stopPropagation();
    }
    return (
      // Named Tailwind group (`group/card`) so the hover-only action
      // cluster below can fade in on card-hover WITHOUT triggering the
      // Avatar component's anonymous `group-hover` tooltip — which
      // matches the closest `.group` ancestor and would otherwise fire
      // every time the cursor touches the date / priority / anywhere on
      // the card. Each child uses `group-hover/card:` to opt back in.
      <div
        className={
          "group/card rounded-md border bg-[color:var(--color-card)] shadow-sm " +
          (isSubtask
            ? "border-[color:var(--color-rule)] p-2.5 ml-4"
            : "border-[color:var(--color-rule)] p-3")
        }
      >
        {/* Header row — drag handle covers the Name area so users can
         *  grab the card from anywhere in the title strip. `pan-x pan-y`
         *  (not `none`) so a touch SWIPE still scrolls the board — the
         *  delay-based TouchSensor distinguishes a press-and-hold (drag)
         *  from a swipe (scroll), so we no longer need to block panning. */}
        <div
          {...(dragHandleProps ?? {})}
          className="flex items-start gap-2 cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: "pan-x pan-y" }}
        >
          {/* Visible drag grip — always shown (not hover-gated) so it's
           *  obvious the card can be dragged (reorder within a column /
           *  move between columns). The whole title row is the handle. */}
          <span
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-[color:var(--color-ink-subtle)]"
          >
            <GripVertical size={14} />
          </span>
          <div onPointerDown={stopDrag} onClick={stopDrag}>
            <StatusCell
              current={task.status}
              statuses={statuses}
              onCommit={(status) => onUpdateTask?.(task.id, { status })}
              disabled={task.canEditStatus === false}
            />
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail?.(task.id);
            }}
            onPointerDown={stopDrag}
            disabled={!onOpenDetail}
            className={
              "flex-1 text-left text-sm font-medium truncate bg-transparent border-0 p-0 " +
              (onOpenDetail ? "cursor-pointer " : "") +
              (isSubtask
                ? "text-[color:var(--color-ink)]"
                : "text-[color:var(--color-ink-strong)]")
            }
          >
            {task.name}
          </button>
          {/* Hover action cluster — Pencil opens detail; Plus adds a
           *  subtask; Trash deletes the task (parent OR subtask — same
           *  confirmation dialog handles both, the delete cascades to
           *  children for parents). Uses the named `group-hover/card`
           *  so it stays scoped to this card, unlike the anonymous
           *  group-hover the Avatar tooltip uses. */}
          <span
            className="inline-flex items-center gap-0.5 shrink-0"
            onPointerDown={stopDrag}
            onClick={stopDrag}
          >
            {/* Open + Add-subtask stay hover-only to keep the card clean;
             *  Delete is always visible. */}
            {onOpenDetail || onAddSubtask ? (
              <span className="inline-flex items-center gap-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity">
                {onOpenDetail ? (
                  <button
                    type="button"
                    onClick={() => onOpenDetail(task.id)}
                    // Pencil icon → "Edit task" (matches the icon + the fully
                    // editable detail panel). Was "Open task", which read as
                    // wrong against a pencil (QA). The card body / task-name
                    // click keeps "Open task" — that's a plain open, not an edit.
                    title="Edit task"
                    aria-label="Edit task"
                    className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    <Pencil size={12} />
                  </button>
                ) : null}
                {onAddSubtask ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Auto-expand so the freshly created child shows up
                      // immediately. Skip the toggle when the card is
                      // already expanded (which it is for sub-cards by
                      // virtue of being rendered).
                      if (!expanded) onToggleSubtasks?.();
                      onAddSubtask(task.id);
                    }}
                    title={isSubtask ? "Add sub-subtask" : "Add subtask"}
                    aria-label={isSubtask ? "Add sub-subtask" : "Add subtask"}
                    className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    <Plus size={12} />
                  </button>
                ) : null}
              </span>
            ) : null}
            {onDuplicateTask ? (
              <button
                type="button"
                onClick={() => onDuplicateTask(task)}
                title="Duplicate task"
                aria-label="Duplicate task"
                className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                <Copy size={12} />
              </button>
            ) : null}
            {onDeleteTask ? (
              <button
                type="button"
                onClick={() => onDeleteTask(task)}
                title={isSubtask ? "Delete subtask" : "Delete Task"}
                aria-label={isSubtask ? "Delete subtask" : "Delete Task"}
                className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                <Trash2 size={12} />
              </button>
            ) : null}
          </span>
        </div>

        {/* Primary chip row — Assignee / Date / Priority. Each chip is
         *  individually gated on the matching Customize toggle, so a
         *  fully-off Customize state collapses the row to nothing.
         *  Each control stops pointerdown propagation so opening a
         *  popover doesn't also pick up the card for dragging. */}
        {showChipRow ? (
          <div
            className="mt-2 flex items-center gap-2 flex-wrap"
            onPointerDown={stopDrag}
            onClick={stopDrag}
          >
            {showAssignee ? (
              <AssigneeCell
                assigneeIds={task.assigneeIds ?? []}
                members={members}
                currentUserId={currentUserId}
                workspaceId={workspaceId}
                workspaceName={workspaceName}
                spaceId={spaceId}
                spaceName={spaceName}
                taskId={task.id}
                allTasks={allTasks}
                statuses={statuses}
                onCommit={(assigneeIds) =>
                  onUpdateTask?.(task.id, { assigneeIds })
                }
                disabled={task.canEditAssignee === false}
              />
            ) : null}
            {/* Compact date range chip — single chip showing
             *  "DD/MM/YYYY → DD/MM/YYYY" when both dates set, single
             *  date otherwise. Clicking opens the same date popover
             *  the List view uses (defaults to the Due field). When
             *  only one of Start / Due is toggled on, we pass `null`
             *  for the other so the chip renders just the visible
             *  field. */}
            {showDateChip ? (
              <BoardDateChip
                startDate={showStartDate ? (task.startDate ?? null) : null}
                dueDate={showDueDate ? (task.dueDate ?? null) : null}
                startDateHasTime={task.startDateHasTime ?? false}
                dueDateHasTime={task.dueDateHasTime ?? false}
                completedAt={task.completedAt ?? null}
                onCommit={(patch) => onUpdateTask?.(task.id, patch)}
              />
            ) : null}
            {showPriority ? (
              <PriorityCell
                priority={task.priority ?? null}
                onCommit={(priority) => onUpdateTask?.(task.id, { priority })}
                disabled={task.canEditPlanning === false}
                completed={isTerminalStatusName(statuses, task.status)}
              />
            ) : null}
            {showRecurring ? (
              <RecurringCell
                state={task.recurring ?? null}
                schedule={task.recurringSchedule ?? null}
                canEdit={task.canEditPlanning !== false}
                onOpen={() => onOpenRecurrence?.(task.recurringSourceId ?? task.id)}
              />
            ) : null}
          </div>
        ) : null}

        {/* Comments row — count of comments on this task + click-to-
         *  open thread. Lives below the primary chip strip so it
         *  doesn't compete with assignee / date / priority for space.
         *  Hidden by default, toggled on via the Customize panel. */}
        {showMetaRow ? (
          <div
            className="mt-2 flex items-center gap-x-3 gap-y-1 flex-wrap text-[12px] text-[color:var(--color-ink-muted)]"
            onPointerDown={stopDrag}
            onClick={stopDrag}
          >
            {showComments ? (
              <CommentsCell
                taskId={task.id}
                currentUserId={currentUserId}
                spaceId={spaceId}
                initialCount={task.commentCount}
              />
            ) : null}
          </div>
        ) : null}

        {/* Subtask expander — parents only, when at least one subtask
         *  exists AND the global Subtasks toggle is on (`hasSubtasks`
         *  is gated on both at the caller). */}
        {hasSubtasks ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSubtasks?.();
            }}
            onPointerDown={stopDrag}
            className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {subtaskCount} subtask{subtaskCount === 1 ? "" : "s"}
          </button>
        ) : null}
      </div>
    );
  }

  /**
   * Compact "DD/MM/YYYY → DD/MM/YYYY" chip for the Board card. Opens
   * the same `TaskDatePopover` the List/Gantt use, so date editing on
   * a Board card flows through the same code path. When only a single
   * date is set we delegate to the shared `DueChip` so the overdue /
   * completed colour cues stay consistent with every other surface
   * showing a task date.
   */
  function BoardDateChip({
    startDate,
    dueDate,
    startDateHasTime = false,
    dueDateHasTime = false,
    completedAt,
    onCommit,
  }) {
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    // Board cards show DATE ONLY (no time-of-day) so the compact card stays
    // tidy and a date-only task never reads as "12:00 AM". The time, when
    // the user set one, is still shown in the task detail panel.
    const startLabel = startDate ? formatDate(startDate) : null;
    const dueLabel = dueDate ? formatDate(dueDate) : null;
    const hasRange =
      Boolean(startLabel) && Boolean(dueLabel) && startLabel !== dueLabel;
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs cursor-pointer text-[color:var(--color-ink-muted)]"
          aria-label="Set start / due date"
        >
          {hasRange ? (
            <>
              <Calendar size={12} />
              <span className="tabular-nums">
                {startLabel} → {dueLabel}
              </span>
            </>
          ) : (
            // Single-date (or unset) — defer to the shared chip so
            // overdue / completed colouring matches the List/Gantt.
            <DueChip
              iso={dueDate ?? startDate ?? null}
              completedAt={completedAt}
            />
          )}
        </button>
        <TaskDatePopover
          open={open}
          triggerRef={triggerRef}
          startDate={startDate}
          dueDate={dueDate}
          startDateHasTime={startDateHasTime}
          dueDateHasTime={dueDateHasTime}
          initialField={startDate && !dueDate ? "start" : "due"}
          onCommit={onCommit}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  /**
   * Draggable wrapper around `BoardCardInteractive` for top-level
   * parent tasks. dnd-kit's listeners go to the title-row only (via
   * `dragHandleProps`) so clicking a cell inside the card doesn't
   * start a drag. Subtask sub-cards skip this wrapper — they're not
   * column-draggable; status changes go through the StatusCell on the
   * sub-card itself.
   */
  function BoardCard({
    task,
    members,
    statuses,
    currentUserId,
    workspaceId,
    workspaceName,
    spaceId,
    spaceName,
    allTasks,
    columns,
    hasSubtasks,
    expanded,
    subtaskCount,
    onToggleSubtasks,
    onUpdateTask,
    onOpenDetail,
    onOpenRecurrence,
    onDeleteTask,
    onDuplicateTask,
    /** Add-subtask handler — was previously dropped here, which made the
     *  "+" hover icon disappear on parent cards (subtasks under this
     *  parent skipped this wrapper and got the prop directly, so the
     *  icon only showed on subtask cards). Forwarding it so parent cards
     *  get the same Pencil + Plus + Trash hover cluster the subtask
     *  cards have. */
    onAddSubtask,
  }) {
    const data = { taskId: task.id, fromStatus: task.status };
    // `useSortable` (not plain `useDraggable`) so a card is BOTH draggable
    // AND a drop target within its column's SortableContext — that's what
    // lets cards reorder inside a column. Dropping on a card in ANOTHER
    // column still changes status (handled in `onBoardDragEnd`).
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: `task:${task.id}`, data });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.3 : undefined,
    };
    return (
      <div ref={setNodeRef} style={style}>
        <BoardCardInteractive
          task={task}
          members={members}
          statuses={statuses}
          currentUserId={currentUserId}
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          spaceId={spaceId}
          spaceName={spaceName}
          allTasks={allTasks}
          columns={columns}
          hasSubtasks={hasSubtasks}
          expanded={expanded}
          subtaskCount={subtaskCount}
          onToggleSubtasks={onToggleSubtasks}
          onUpdateTask={onUpdateTask}
          onOpenDetail={onOpenDetail}
          onOpenRecurrence={onOpenRecurrence}
          onDeleteTask={onDeleteTask}
          onDuplicateTask={onDuplicateTask}
          onAddSubtask={onAddSubtask}
          dragHandleProps={{ ...listeners, ...attributes }}
        />
      </div>
    );
  }

  function BoardColumn({
    status,
    tasks,
    members,
    statuses,
    currentUserId,
    workspaceId,
    workspaceName,
    spaceId,
    spaceName,
    allTasks,
    /** Column-visibility map driven by the toolbar's Customize pill.
     *  Threaded down to each card so toggles affect every card in the
     *  column simultaneously. */
    columns,
    subtasksByParent = new Map(),
    showSubtasks = true,
    expandedParents = new Set(),
    onToggleParent,
    onUpdateTask,
    onOpenDetail,
    onOpenRecurrence,
    onDeleteTask,
    /** One-click duplicate for TOP-LEVEL cards only (undefined for subtask
     *  sub-cards, which are rendered directly below without this prop). */
    onDuplicateTask,
    /** Adds a subtask (or sub-subtask) under the given parent id.
     *  Wired through to each card's hover "+" button so the Board view
     *  can create depth-1 + depth-2 children inline. */
    onAddSubtask,
    /** Creates a new TOP-LEVEL task in this column's status bucket.
     *  Wired to the `InlineAddTaskRow` below the card list so the
     *  Board view has the same per-status quick-add affordance the
     *  List view exposes (matches ClickUp's inline add pattern). */
    onAddTask,
  }) {
    const data = { statusName: status.name };
    const { setNodeRef, isOver } = useDroppable({
      id: `column:${status.name}`,
      data,
    });
    return (
      <div
        ref={setNodeRef}
        className={
          // Note: `overflow-hidden` was removed here so the Avatar hover
          // tooltip on a Board card near the top edge of the column
          // isn't clipped. We compensate by rounding the header's
          // top corners directly (`rounded-t-lg` below), which gives
          // the same visual result as clipping the parent.
          //
          // Responsive width — set via the inline `style` below to one
          // third of the visible board width minus the two 1rem gaps between
          // the three, so exactly THREE columns show at a time and they
          // grow/shrink with the screen. (Inline rather than a Tailwind
          // `w-[calc(...)]` arbitrary value: the `/` in the calc collides
          // with Tailwind's opacity-modifier syntax, so the class is
          // silently dropped at build time.) `shrink-0` stops the flex
          // container compressing the columns; the parent owns the
          // horizontal scroll, so a fourth+ status scrolls into view
          // instead of squishing.
          // Mobile / tablet: hold a readable ~280px minimum so the inline
          // 1/3-width calc below can't crush columns on a narrow screen — the
          // board (parent) scrolls horizontally instead. Reset on desktop
          // (lg:min-w-0) so ≥1024px keeps the exact 3-columns-per-screen calc.
          "shrink-0 min-w-[280px] lg:min-w-0 rounded-lg border bg-[color:var(--color-card)] transition-colors " +
          (isOver
            ? "border-[color:var(--color-accent)] outline outline-1 outline-[color:var(--color-accent)]"
            : "")
        }
        // One-third of the visible board width minus the two 1rem inter-
        // column gaps → exactly three columns per screen, fluid with size.
        style={{ width: "calc((100% - 2rem) / 3)" }}
      >
        <div className="px-4 py-3 rounded-t-lg bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] flex items-center gap-2">
          <StatusPill status={status} />
          <span className="ml-auto text-sm text-[color:var(--color-ink-muted)]">
            {tasks.length}
          </span>
        </div>
        <div className="p-3 space-y-2 min-h-[80px]">
          {tasks.length === 0 ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] py-4 text-center">
              No tasks.
            </p>
          ) : (
            <SortableContext
              items={tasks.map((t) => `task:${t.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {tasks.map((t) => {
                const subs = subtasksByParent.get(t.id) ?? [];
                const hasSubs = showSubtasks && subs.length > 0;
                const expanded = expandedParents.has(t.id);
                return (
                  <div key={t.id} className="space-y-2">
                    <BoardCard
                      task={t}
                      members={members}
                      statuses={statuses}
                      currentUserId={currentUserId}
                      workspaceId={workspaceId}
                      workspaceName={workspaceName}
                      spaceId={spaceId}
                      spaceName={spaceName}
                      allTasks={allTasks}
                      columns={columns}
                      hasSubtasks={hasSubs}
                      expanded={expanded}
                      subtaskCount={subs.length}
                      onToggleSubtasks={() => onToggleParent?.(t.id)}
                      onUpdateTask={onUpdateTask}
                      onOpenDetail={onOpenDetail}
                      onOpenRecurrence={onOpenRecurrence}
                      onDeleteTask={onDeleteTask}
                      onDuplicateTask={onDuplicateTask}
                      onAddSubtask={onAddSubtask}
                    />
                    {/* Subtask sub-cards — rendered when the parent is
                     *  expanded. Not draggable across columns (their
                     *  status follows the same pickers as the parent
                     *  card; only the status pill on the sub-card
                     *  changes their bucket). Receive the same `columns`
                     *  map so toggles affect parent + subtask chips in
                     *  lock-step.
                     *
                     *  Each subtask card can ALSO expand to show its
                     *  sub-subtasks (depth 2) — same toggle state
                     *  (`expandedParents`) keyed by the subtask's id. */}
                    {hasSubs && expanded
                      ? subs.map((sub) => {
                          const subSubs = subtasksByParent.get(sub.id) ?? [];
                          const subHasChildren =
                            showSubtasks && subSubs.length > 0;
                          const subExpanded = expandedParents.has(sub.id);
                          return (
                            <div key={sub.id} className="space-y-2">
                              <BoardCardInteractive
                                task={sub}
                                isSubtask
                                members={members}
                                statuses={statuses}
                                currentUserId={currentUserId}
                                workspaceId={workspaceId}
                                workspaceName={workspaceName}
                                spaceId={spaceId}
                                spaceName={spaceName}
                                allTasks={allTasks}
                                columns={columns}
                                hasSubtasks={subHasChildren}
                                expanded={subExpanded}
                                subtaskCount={subSubs.length}
                                onToggleSubtasks={() =>
                                  onToggleParent?.(sub.id)
                                }
                                onUpdateTask={onUpdateTask}
                                onOpenDetail={onOpenDetail}
                                onDeleteTask={onDeleteTask}
                                onAddSubtask={onAddSubtask}
                              />
                              {subHasChildren && subExpanded
                                ? subSubs.map((subSub) => (
                                    <BoardCardInteractive
                                      key={subSub.id}
                                      task={subSub}
                                      isSubtask
                                      members={members}
                                      statuses={statuses}
                                      currentUserId={currentUserId}
                                      workspaceId={workspaceId}
                                      workspaceName={workspaceName}
                                      spaceId={spaceId}
                                      spaceName={spaceName}
                                      allTasks={allTasks}
                                      columns={columns}
                                      onUpdateTask={onUpdateTask}
                                      onOpenDetail={onOpenDetail}
                                      onDeleteTask={onDeleteTask}
                                    />
                                  ))
                                : null}
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })}
            </SortableContext>
          )}
          {/* Per-column quick-add row — same `InlineAddTaskRow` the List
           *  view uses at the bottom of each status section, so the
           *  Board has the matching "+ Add Task" affordance. The typed
           *  name lands as a top-level task in THIS column's status. */}
          {onAddTask ? (
            <InlineAddTaskRow
              statusColor={status?.color}
              statusUnstarted={isUnstartedStatus(status, status?.name)}
              onSubmit={(name) => onAddTask(name)}
            />
          ) : null}
        </div>
      </div>
    );
  }

  // ---------- Toolbar pills ----------

  const DUE_RANGE_OPTIONS = [
    { value: "all", label: "Any time" },
    { value: "overdue", label: "Overdue" },
    { value: "today", label: "Today" },
    { value: "this_week", label: "This week" },
    { value: "this_month", label: "This month" },
    { value: "no_due", label: "No due date" },
  ];

  function FilterPill({
    members,
    filterAssignees,
    setFilterAssignees,
    filterPriorities,
    setFilterPriorities,
    filterDueRange,
    setFilterDueRange,
    activeCount,
  }) {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    usePortalCoords(open, triggerRef, setCoords);
    useDismissOn(open, triggerRef, menuRef, () => setOpen(false));

    function toggleAssignee(id) {
      const next = new Set(filterAssignees);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setFilterAssignees(next);
    }
    function togglePriority(p) {
      const next = new Set(filterPriorities);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      setFilterPriorities(next);
    }
    function clearAll() {
      setFilterAssignees(new Set());
      setFilterPriorities(new Set());
      setFilterDueRange("all");
    }

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border cursor-pointer " +
            // Only the ACTIVE state (filters applied) gets the accent
            // highlight. The inactive state matches the sibling toolbar
            // buttons (Group / Customise) — a subtle rule border — instead of
            // the bare `border` (currentColor in Tailwind 4), which read as a
            // permanently "focused/active" dark outline.
            (activeCount > 0
              ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]"
              : "border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]")
          }
        >
          {/* Label and icon size match the `ToolButton` filter trigger in
           *  components/table/DataTable.jsx, which every table page in the
           *  product uses — the two read as different controls otherwise
           *  (DF_031 / Mantis 22241). The pill's own geometry stays as it is
           *  so it still lines up with its Group / Customise siblings. */}
          <FilterIcon size={15} />
          Filters
          {activeCount > 0 ? (
            <span className="ml-0.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] bg-[color:var(--color-accent)] text-white">
              {activeCount}
            </span>
          ) : null}
        </button>
        {open && coords
          ? createPortal(
              <div
                ref={menuRef}
                style={{
                  position: "fixed",
                  top: coords.top,
                  bottom: coords.bottom,
                  left: coords.left,
                  maxHeight: coords.maxHeight,
                  overflowY: "auto",
                  zIndex: 60,
                }}
                className="w-72 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] max-h-[28rem] overflow-auto"
              >
                <div className="px-3 py-2 flex items-center justify-between border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                  <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                    Filters
                  </span>
                  {activeCount > 0 ? (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="text-xs text-[color:var(--color-priority-urgent)] hover:underline cursor-pointer"
                    >
                      Clear All
                    </button>
                  ) : null}
                </div>

                <div className="p-3 border-b border-[color:var(--color-rule)]">
                  <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2 mt-0">
                    Assignee
                  </h3>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterAssignees.has("__unassigned__")}
                        onChange={() => toggleAssignee("__unassigned__")}
                      />
                      <Avatar name={null} />
                      <span className="text-[color:var(--color-ink-muted)]">
                        Unassigned
                      </span>
                    </label>
                    {members.map((m) => (
                      <label
                        key={m.userId}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterAssignees.has(m.userId)}
                          onChange={() => toggleAssignee(m.userId)}
                        />
                        {/* Filter row already shows the name beside the
                         *  avatar — no need for a duplicate hover chip. */}
                        <Avatar name={m.fullName} showTooltip={false} />
                        <span className="truncate">{m.fullName}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="p-3 border-b border-[color:var(--color-rule)]">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                    Priority
                  </p>
                  <div className="space-y-1">
                    {TASK_PRIORITIES.map((p) => (
                      <label
                        key={p}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterPriorities.has(p)}
                          onChange={() => togglePriority(p)}
                        />
                        <Flag
                          size={12}
                          fill={PRIORITY_META[p].color}
                          strokeWidth={0}
                        />
                        <span style={{ color: PRIORITY_META[p].color }}>
                          {PRIORITY_META[p].label}
                        </span>
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterPriorities.has("none")}
                        onChange={() => togglePriority("none")}
                      />
                      <Flag
                        size={12}
                        className="text-[color:var(--color-ink-subtle)]"
                      />
                      <span className="text-[color:var(--color-ink-muted)]">
                        No priority
                      </span>
                    </label>
                  </div>
                </div>

                <div className="p-3">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                    Due date
                  </p>
                  <div className="space-y-1">
                    {DUE_RANGE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="due-range"
                          checked={filterDueRange === opt.value}
                          onChange={() => setFilterDueRange(opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  }

  function GroupModePill({ mode, setMode, dir, setDir }) {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const [sub, setSub] = useState(null);
    usePortalCoords(open, triggerRef, setCoords);
    useDismissOn(open, triggerRef, menuRef, () => {
      setOpen(false);
      setSub(null);
    });
    useEffect(() => {
      if (!open) setSub(null);
    }, [open]);
    const label =
      mode === "status" ? "Status" : mode === "priority" ? "Priority" : "None";
    const dirLabel = dir === "asc" ? "Ascending" : "Descending";
    function reset() {
      setMode("none");
      setOpen(false);
      setSub(null);
    }
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <Layers size={12} /> Group: {label}
        </button>
        {open && coords && mode === "none"
          ? createPortal(
              <div
                ref={menuRef}
                style={{
                  position: "fixed",
                  top: coords.top,
                  bottom: coords.bottom,
                  left: coords.left,
                  maxHeight: coords.maxHeight,
                  overflowY: "auto",
                  zIndex: 60,
                }}
                className="w-56 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] py-1"
              >
                {["status", "priority"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setMode(m);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                  >
                    {m === "status" ? <Layers size={14} /> : <Flag size={14} />}
                    {m === "status" ? "Status" : "Priority"}
                  </button>
                ))}
              </div>,
              document.body,
            )
          : null}
        {open && coords && mode !== "none"
          ? createPortal(
              <div
                ref={menuRef}
                style={{
                  position: "fixed",
                  top: coords.top,
                  left: coords.left,
                  zIndex: 60,
                }}
                className="w-[320px] rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] p-3"
              >
                <div className="text-sm font-medium text-[color:var(--color-ink-muted)] mb-2">
                  Group by
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <button
                      type="button"
                      onClick={() =>
                        setSub((s) => (s === "mode" ? null : "mode"))
                      }
                      className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-sm border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                    >
                      <span className="flex items-center gap-1.5">
                        <Layers size={12} /> {label}
                      </span>
                      <ChevronDown size={12} />
                    </button>
                    {sub === "mode" ? (
                      <div className="absolute z-10 top-full left-0 mt-1 w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl">
                        {["status", "priority"].map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => {
                              setMode(m);
                              setSub(null);
                            }}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                          >
                            <span className="flex items-center gap-1.5">
                              {m === "status" ? (
                                <Layers size={12} />
                              ) : (
                                <Flag size={12} />
                              )}
                              {m === "status" ? "Status" : "Priority"}
                            </span>
                            {m === mode ? (
                              <Check
                                size={14}
                                className="text-[color:var(--color-accent)]"
                              />
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <div className="relative flex-1">
                    <button
                      type="button"
                      onClick={() =>
                        setSub((s) => (s === "dir" ? null : "dir"))
                      }
                      className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-sm border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                    >
                      <span>{dirLabel}</span>
                      <ChevronDown size={12} />
                    </button>
                    {sub === "dir" ? (
                      <div className="absolute z-10 top-full left-0 mt-1 w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl">
                        {["asc", "desc"].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => {
                              setDir(d);
                              setSub(null);
                            }}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                          >
                            <span className="flex items-center gap-1.5">
                              {d === "asc" ? (
                                <ArrowUp size={12} />
                              ) : (
                                <ArrowDown size={12} />
                              )}
                              {d === "asc" ? "Ascending" : "Descending"}
                            </span>
                            {d === dir ? (
                              <Check
                                size={14}
                                className="text-[color:var(--color-accent)]"
                              />
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={reset}
                    title="Remove grouping"
                    className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] cursor-pointer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  }

  function SubtasksPill({ showSubtasks, onToggle }) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title={showSubtasks ? "Hide subtasks" : "Show subtasks"}
        className={
          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border cursor-pointer " +
          (showSubtasks
            ? "border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
            : "")
        }
      >
        <Layers size={12} /> Subtasks {showSubtasks ? "" : "(hidden)"}
      </button>
    );
  }

  function CustomizePill({
    columns,
    setColumns,
    /** Optional whitelist of column keys this view supports — the
     *  panel filters its Shown / Hidden rows down to this set when
     *  provided. Used on the Board view, which only renders six of the
     *  catalogue's columns as chips (Assignee, Start date, Due date,
     *  Priority, Comments — plus the always-on Task Name). Pass `null`
     *  / omit to keep the full catalogue (List view default). */
    allowedKeys = null,
  }) {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    usePortalCoords(open, triggerRef, setCoords);
    useDismissOn(open, triggerRef, menuRef, () => setOpen(false));

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
        >
          <Settings2 size={12} /> Customise
        </button>
        {open && coords
          ? createPortal(
              <CustomizePanel
                menuRef={menuRef}
                coords={coords}
                columns={columns}
                setColumns={setColumns}
                allowedKeys={allowedKeys}
              />,
              document.body,
            )
          : null}
      </>
    );
  }

  /** Shown / Hidden columns panel with toggle switches.
   *  Task Name is always shown (locked). Other columns split dynamically by
   *  current visibility — toggling moves them between sections instantly.
   *
   *  When `allowedKeys` is supplied, the panel restricts itself to that
   *  subset of `COLUMN_ORDER`. This is how the Board view exposes a
   *  smaller, view-appropriate customize menu without forking the panel
   *  or the underlying `columns` state. */
  function CustomizePanel({
    menuRef,
    coords,
    columns,
    setColumns,
    allowedKeys = null,
  }) {
    const orderedColumns = allowedKeys
      ? COLUMN_ORDER.filter((c) => allowedKeys.has(c.key))
      : COLUMN_ORDER;
    const shown = orderedColumns.filter((c) => columns[c.key]);
    const hidden = orderedColumns.filter((c) => !columns[c.key]);
    function toggle(key) {
      setColumns({ ...columns, [key]: !columns[key] });
    }
    function hideAll() {
      // Scope "Hide all" to the keys the panel is currently surfacing —
      // never wipe out toggles for columns the user can't see here
      // (e.g. don't hide Date created from the List view just because
      // the Board's Hide all was clicked).
      const next = { ...columns };
      for (const c of orderedColumns) next[c.key] = false;
      setColumns(next);
    }
    function showAll() {
      // Counterpart to hideAll, scoped the same way — enable every column this
      // panel currently surfaces so the user restores the full view in one
      // click. (Task Name is always shown and isn't in orderedColumns.)
      const next = { ...columns };
      for (const c of orderedColumns) next[c.key] = true;
      setColumns(next);
    }
    return (
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          top: coords.top,
          bottom: coords.bottom,
          left: coords.left,
          maxHeight: coords.maxHeight,
          overflowY: "auto",
          zIndex: 60,
        }}
        className="w-72 max-h-[480px] overflow-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
          <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
            Shown
          </span>
          <button
            type="button"
            onClick={hideAll}
            className="text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
          >
            Hide All
          </button>
        </div>
        <div className="py-1">
          {/* Task Name is always pinned at the top of Shown — can't be toggled. */}
          <ColumnRow
            label="Task Name"
            checked
            locked
            onToggle={() => undefined}
          />
          {shown.map((c) => (
            <ColumnRow
              key={c.key}
              label={c.label}
              checked
              onToggle={() => toggle(c.key)}
            />
          ))}
        </div>

        {hidden.length > 0 ? (
          <>
            <div className="flex items-center justify-between px-3 py-2 border-y border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
              <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                Hidden
              </span>
              <button
                type="button"
                onClick={showAll}
                className="text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
              >
                Show All
              </button>
            </div>
            <div className="py-1">
              {hidden.map((c) => (
                <ColumnRow
                  key={c.key}
                  label={c.label}
                  checked={false}
                  onToggle={() => toggle(c.key)}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  function ColumnRow({ label, checked, locked, onToggle }) {
    return (
      <div
        className={
          "flex items-center justify-between gap-3 px-3 py-2 text-sm " +
          (locked
            ? "text-[color:var(--color-ink-subtle)]"
            : "cursor-pointer hover:bg-[color:var(--color-card-hover)]")
        }
        onClick={locked ? undefined : onToggle}
      >
        <span className="truncate">{label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={`Toggle ${label}`}
          disabled={locked}
          onClick={(e) => {
            e.stopPropagation();
            if (!locked) onToggle();
          }}
          className={
            "relative inline-flex h-4 w-7 items-center rounded-full transition-colors shrink-0 cursor-pointer " +
            (checked
              ? "bg-[color:var(--color-accent)]"
              : "bg-[color:var(--color-rule-strong)]") +
            (locked ? " opacity-60 cursor-not-allowed" : "")
          }
        >
          <span
            className={
              "inline-block h-3 w-3 rounded-full bg-white shadow transition-transform " +
              (checked ? "translate-x-3.5" : "translate-x-0.5")
            }
          />
        </button>
      </div>
    );
  }

  // ---------- Overview view ----------
  //
  // The legacy OverviewView computed KPIs from the CURRENT list's task
  // array (the `tasks` prop is the visible list's tasks only), which
  // surfaced misleading totals as soon as the Space grew past one
  // List. The new shell is fed by the whole-Space aggregator at
  // `GET /spaces/:id/overview` — see `flowdesk-api/src/modules/spaces/
  // space-overview.service.js`. SpacePage owns the React Query call
  // itself (`overviewQuery`) and passes the payload + a single
  // click-to-open-task handler down. Toolbar filters (Assignees /
  // Priorities / Closed pill) intentionally do NOT thread through —
  // the subtitle explains the decoupling.

  function OverviewView({
    overview,
    isLoading,
    isError,
    errorMessage,
    spaceName,
    onOpenTask,
  }) {
    const scopeListName = overview?.scope?.listName ?? null;
    const subtitle = (
      <p className="text-xs text-[color:var(--color-ink-muted)]">
        {/* "including subtasks" is load-bearing copy, not decoration: the task
          counts here roll up EVERY task in scope (space-overview.service
          counts by listId with no parentTaskId filter), whereas the List /
          Board status headers count top-level tasks only. Stating it stops the
          "Overview total doesn't match the list" confusion (QA). The old
          "toolbar filters don't apply here" clause was dropped — those
          controls are now hidden on this tab (see the sub-toolbar) rather
          than shown-but-inert.

          The scope half of this sentence must stay truthful: the Overview is
          now scoped to the open List, so claiming "the whole Space"
          unconditionally was wrong — and it is what made the Space-wide
          member total look like a broken List count. */}
        {scopeListName
          ? `Showing a summary of ${scopeListName}, including subtasks. Members and recent activity are Space-wide.`
          : 'Showing a summary of the whole Space, including subtasks.'}
      </p>
    );

    if (isLoading) {
      return (
        <div className="space-y-4">
          {subtitle}
          <div className="rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
            Loading overview for {spaceName ?? "this Space"}…
          </div>
        </div>
      );
    }

    if (isError || !overview) {
      return (
        <div className="space-y-4">
          {subtitle}
          <div className="rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
            {errorMessage ?? "Couldn’t load the Space overview."}
          </div>
        </div>
      );
    }

    if (overview.tasks.total === 0) {
      return (
        <div className="space-y-4">
          {subtitle}
          <div className="rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
            No tasks in this Space yet. Create your first list and add tasks to
            see the Overview.
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {subtitle}
        <SpaceOverviewKpiStrip overview={overview} />
        <SpaceOverviewBreakdowns overview={overview} />
        <SpaceOverviewProjects overview={overview} />
        <SpaceOverviewWorkload overview={overview} />
        <SpaceOverviewLists overview={overview} onOpenTask={onOpenTask} />
        <SpaceOverviewActivity overview={overview} />
      </div>
    );
  }

  // ---------- Gantt view ----------

  const DAY_MS = 24 * 60 * 60 * 1000;

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  // ── Gantt date helpers (also used by the Team Tasks page; keep in sync). ─

  /** Format an ISO timestamp as 12-hour "HH:MM AM/PM" — matching the
   *  canonical `formatTime()` in `lib/format.js` so a time displayed
   *  here reads the same as a time displayed anywhere else in the
   *  product (Members table, Reports, Audit log, etc.).
   *
   *  Returns `null` when the timestamp is exactly midnight, since most
   *  date-only tasks are stored at 00:00 and rendering "12:00 AM" next
   *  to every row would be visual noise — callers fall back to an
   *  em-dash or omit the time entirely.
   *
   *  `hasTime` overrides that: when the user EXPLICITLY set a time of
   *  day (the task's `*HasTime` flag), an intentional "12:00 AM" is a
   *  real value and is shown even though it sits at midnight. */
  function fmtIsoTime(iso, hasTime = false) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    // Midnight is judged in the person's display zone — the zone the time
    // is shown in — not the browser's.
    const local = DateTime.fromJSDate(d).setZone(getDisplayTimeZone() || "local");
    if (!hasTime && local.hour === 0 && local.minute === 0) return null;
    // Shared formatter: person's zone and 12/24-hour clock.
    return formatTime(d);
  }

  function fmtIsoDateTime(iso, hasTime = false) {
    if (!iso) return "—";
    const dt = formatDateTime(iso);
    if (!dt) return "—";
    // `fmtIsoTime` returns null for midnight (unless `hasTime`) — date-
    // only callers (e.g. a task stored at 00:00) get just the date so we
    // don't print a noisy "12:00 AM" next to every row.
    return fmtIsoTime(iso, hasTime) ? dt : formatDate(iso);
  }

  /** ISO week number (Mon-leading). Used to label week strips above the
   *  day columns — e.g. "W19 · May 10–16". */
  function isoWeekNumber(date) {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / DAY_MS + 1) / 7);
  }

  /** Resolve a task's effective scheduling window for the Gantt:
   *   - both dates set → returns the literal span
   *   - only one date set → returns a one-day span on that date (so
   *     the task still appears on the timeline as a single-day bar)
   *   - neither → returns null and the caller excludes the task.
   *  Always returns Date objects snapped to midnight so day-grid math
   *  works without fractional pixels. */
  function ganttSpan(task) {
    if (!task.startDate && !task.dueDate) return null;
    const start = task.startDate ?? task.dueDate;
    const end = task.dueDate ?? task.startDate;
    let s = startOfDay(new Date(start));
    let e = startOfDay(new Date(end));
    // Guard against an admin who set startDate AFTER dueDate — swap so
    // the bar still renders left-to-right instead of imploding to width 0.
    if (e < s) [s, e] = [e, s];
    return { start: s, end: e };
  }

  // `GanttDateEditor` and its `isoToLocalDatetimeInput` /
  // `localDatetimeInputToIso` helpers used to live here. They were
  // removed when the Gantt was switched to reuse the same
  // `TaskDatePopover` the List view uses, so the two pages now share
  // a single source of truth for the date-picker UX (preset chips,
  // range-highlight calendar, time-slot column).

  /** Curated colour palette for the bar / chip colour picker. Mirrors
   *  the 2026-05-18 blue analytics palette used on the dashboards, plus
   *  a curated set of accent hues for variety and the standard priority
   *  colours so admins can match a bar to its priority by hand. */
  const BAR_COLOR_PALETTE = [
    "#02367B", // Deep Navy (brand)
    "#006CA5", // Ocean
    "#0496C7", // Sky Cyan
    "#04BADE", // Bright Cyan
    "#10B981", // emerald (status: complete)
    "#6366F1", // indigo
    "#A855F7", // purple
    "#EC4899", // pink
    "#F59E0B", // amber
    "#F97316", // orange
    "#EF4444", // red (also urgent priority)
    "#64748B", // slate
    "#1F2937", // ink
  ];

  /** Small colour-picker popover anchored to a trigger element. Shows a
   *  grid of preset colours plus a "Default" tile that clears the
   *  custom colour and lets the bar fall back to its priority/status
   *  colour. Reused by the Gantt left-pane swatch and could be reused
   *  elsewhere (e.g. List view row dot) later. */
  function GanttColorPicker({ anchorEl, currentColor, onPick, onClose }) {
    const popoverRef = useRef(null);
    // Position the popover under the trigger element. Same pattern as
    // TaskDatePopover but smaller (we only need room for a 7x2 grid).
    const [coords, setCoords] = useState(null);
    useLayoutEffect(() => {
      if (!anchorEl) return;
      const r = anchorEl.getBoundingClientRect();
      const popoverW = 232;
      const rightGutter = 16;
      let left = r.left;
      if (left + popoverW > window.innerWidth - rightGutter) {
        left = Math.max(8, window.innerWidth - rightGutter - popoverW);
      }
      setCoords({ top: r.bottom + 6, left });
    }, [anchorEl]);

    // Esc + click-outside to close.
    useEffect(() => {
      function handleKey(e) {
        if (e.key === "Escape") {
          // Claim the keystroke so an enclosing overlay (the task detail
          // dialog) doesn't close in the same press — Escape peels one layer
          // at a time. See the matching guard in `useDismissOn`.
          e.preventDefault();
          onClose();
        }
      }
      function handleClick(e) {
        if (!popoverRef.current) return;
        if (popoverRef.current.contains(e.target)) return;
        if (anchorEl?.contains(e.target)) return;
        onClose();
      }
      document.addEventListener("keydown", handleKey);
      document.addEventListener("mousedown", handleClick);
      return () => {
        document.removeEventListener("keydown", handleKey);
        document.removeEventListener("mousedown", handleClick);
      };
    }, [onClose, anchorEl]);

    if (!coords) return null;

    return createPortal(
      <div
        ref={popoverRef}
        role="dialog"
        aria-label="Choose bar colour"
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          zIndex: 60,
          width: 232,
        }}
        className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl p-3"
      >
        <p className="text-[12px] uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
          Bar colour
        </p>
        <div className="grid grid-cols-7 gap-1.5">
          {/* "Default" tile — clears the custom colour. Render first
           *  so admins can quickly reset to the priority/status default
           *  without scanning the palette. */}
          <button
            type="button"
            onClick={() => onPick(null)}
            title="Default (use priority / status colour)"
            aria-label="Default colour — clear custom"
            aria-pressed={!currentColor}
            className={
              "h-6 w-6 rounded-md border bg-[color:var(--color-card)] flex items-center justify-center text-[10px] text-[color:var(--color-ink-muted)] hover:border-[color:var(--color-accent)] cursor-pointer " +
              (!currentColor
                ? "border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)]"
                : "border-[color:var(--color-rule)]")
            }
          >
            ✕
          </button>
          {BAR_COLOR_PALETTE.map((c) => {
            const selected =
              currentColor && currentColor.toLowerCase() === c.toLowerCase();
            return (
              <button
                key={c}
                type="button"
                onClick={() => onPick(c)}
                title={c}
                aria-label={`Use ${c}`}
                aria-pressed={selected}
                className={
                  "h-6 w-6 rounded-md border motion-safe:hover:scale-110 transition-transform cursor-pointer " +
                  (selected
                    ? "border-[color:var(--color-ink-strong)] ring-2 ring-[color:var(--color-ink-strong)]/30"
                    : "border-[color:var(--color-rule)]")
                }
                style={{ backgroundColor: c }}
              />
            );
          })}
        </div>
      </div>,
      document.body,
    );
  }

  /** Two-pane Gantt — Name + Dates columns on the left, scrolling
   *  timeline on the right. Header carries a week strip (week number plus
   *  date range) above day cells; weekends are hatched, today gets an
   *  accent badge in the day cell and a vertical guide through the body.
   *  Tasks group by status with the colour configured on the Space, each
   *  group collapsible.
   *
   *  **Bar logic**: each task renders as a horizontal bar that spans from
   *  its `startDate` to its `dueDate` (inclusive of both end days). Tasks
   *  with only one date set still appear as a one-day bar on that date,
   *  so a quick "due Friday" task isn't invisible just because the admin
   *  never set a start. The bar's colour is the priority colour when set,
   *  otherwise the status colour. */
  function GanttView({
    tasks,
    /** Full task list including subtasks — passed to interactive cells
     *  (AssigneeCell) so they can resolve subtask context. */
    allTasks,
    statuses,
    members = [],
    /** Column-visibility map (which left-pane columns render). Driven
     *  by the toolbar's Customize pill — Gantt has no in-pane add-
     *  column button, so we only consume the value here. */
    columns = {},
    /** Subtask plumbing — mirrors the List view's setup so the two
     *  panels stay in lock-step. When `showSubtasks` is on AND a parent
     *  appears in `expandedParents`, its subtasks render as indented
     *  rows directly beneath it (with their own bars, drag handles,
     *  status circles, assignee / priority / date cells — every action
     *  the parent rows support). */
    subtasksByParent = new Map(),
    showSubtasks = true,
    expandedParents = new Set(),
    onToggleParent,
    /** Hover-Trash button on subtask rows. Optional — without it the
     *  rows still render, they just can't be deleted from the Gantt. */
    onDeleteTask,
    /** Open the full task-detail dialog. Wires the hover-only Pencil
     *  icon next to each row's name — same affordance the List view
     *  exposes via NameCell. */
    onOpenDetail,
    /** Open the recurrence editor for a task — drives the left pane's
     *  "Repeat" column cell (top-level rows only). */
    onOpenRecurrence,
    /** Inline "+ Add subtask" hover button on parent rows. Optional —
     *  parent rows still render their chevron/count without it. */
    onAddSubtask,
    /** Inline "+ Add Task" row at the bottom of each status group.
     *  Same `InlineAddTaskRow` the List + Board views use, so all
     *  three views share one quick-add affordance. Optional — the
     *  group still renders without the add-row when this is omitted. */
    onAddTask,
    currentUserId,
    workspaceId,
    workspaceName,
    spaceId,
    spaceName,
    onUpdateTask,
    /** Drag-to-reorder rows in the left pane — same reorder mutation the
     *  List view uses. Optional: without it the grips don't render and
     *  rows stay drag-locked. */
    onReorderTasks,
    /** Ref the parent owns so the SUB-TOOLBAR can trigger "jump to today".
     *  The scroll logic closes over this component's pane ref + date range,
     *  so it can't live in the parent — instead we publish the current
     *  closure into `todayHandleRef.current` and the toolbar calls it. The
     *  button used to float over the timeline header and kept covering a
     *  week / month label (QA); the toolbar has no such collision. */
    todayHandleRef,
  }) {
    const statusByName = new Map();
    for (const s of statuses) statusByName.set(s.name, s);

    // Group tasks by status, preserving the Space's configured order so
    // the section order matches every other view. We show EVERY task
    // (including unscheduled ones), so users can pick a row in TO DO
    // that has no dates yet and assign start/due directly from the
    // Gantt — see the "Set dates" affordance below.
    const groups = useMemo(() => {
      const out = [];
      const orderMap = new Map();
      for (const s of statuses) {
        orderMap.set(s.name, { key: s.name, status: s, tasks: [] });
        out.push(orderMap.get(s.name));
      }
      // Tasks in unknown statuses (rare, e.g. status renamed) get their
      // own bucket at the end so they're still visible.
      for (const t of tasks) {
        let bucket = orderMap.get(t.status);
        if (!bucket) {
          bucket = {
            key: t.status,
            status: {
              name: t.status,
              color: "#94a3b8",
              category: "not_started",
            },
            tasks: [],
          };
          orderMap.set(t.status, bucket);
          out.push(bucket);
        }
        bucket.tasks.push(t);
      }
      // Sort each bucket by sortOrder so a drag-reorder (which rewrites
      // sortOrder VALUES, not array order) repositions rows immediately —
      // the optimistic update shows before the refetch lands. Mirrors the
      // List view's `bySortOrder` ordering.
      for (const b of out) b.tasks.sort(bySortOrder);
      return out.filter((g) => g.tasks.length > 0);
    }, [tasks, statuses]);

    // ── Left-pane drag-to-reorder (same model as the List view) ─────────
    // Rows reorder WITHIN their own sibling set: a top-level row among its
    // status group's tasks, a subtask among its parent's subtasks. The
    // right-pane bars re-sort automatically because both panes iterate the
    // same `sortOrder`-sorted `visibleRows`.
    const ganttSortSensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: { distance: 4, tolerance: 5, delay: 0 },
      }),
      // Touch: delay-based so a horizontal swipe scrolls the Gantt timeline
      // instead of grabbing a row; press-and-hold (200ms) starts a drag.
      useSensor(TouchSensor, {
        activationConstraint: { delay: 200, tolerance: 8 },
      }),
    );
    const [activeReorderId, setActiveReorderId] = useState(null);
    const reorderSiblingSet = useMemo(() => {
      const m = new Map();
      for (const g of groups) {
        const ids = new Set(g.tasks.map((t) => t.id));
        for (const t of g.tasks) m.set(t.id, ids);
      }
      for (const subs of subtasksByParent.values()) {
        const ids = new Set(subs.map((s) => s.id));
        for (const s of subs) m.set(s.id, ids);
      }
      return m;
    }, [groups, subtasksByParent]);
    const activeReorderTask = useMemo(() => {
      if (!activeReorderId) return null;
      for (const g of groups) {
        const hit = g.tasks.find((t) => t.id === activeReorderId);
        if (hit) return hit;
      }
      for (const subs of subtasksByParent.values()) {
        const hit = subs.find((s) => s.id === activeReorderId);
        if (hit) return hit;
      }
      return null;
    }, [activeReorderId, groups, subtasksByParent]);
    // Restrict drop candidates to the dragged row's own sibling set so a
    // drag can't cross status groups (that would be a status change, not a
    // reorder) or jump between parents.
    const reorderCollision = useMemo(
      () => (args) => {
        const sibs = reorderSiblingSet.get(args.active.id);
        const droppableContainers = sibs
          ? args.droppableContainers.filter((c) => sibs.has(c.id))
          : args.droppableContainers;
        return closestCenter({ ...args, droppableContainers });
      },
      [reorderSiblingSet],
    );
    function onReorderDragEnd(event) {
      setActiveReorderId(null);
      const { active, over } = event;
      if (!over || !onReorderTasks) return;
      if (active.id === over.id) return;
      // Top-level row reorder — within its status group.
      for (const g of groups) {
        const oi = g.tasks.findIndex((t) => t.id === active.id);
        if (oi < 0) continue;
        const ni = g.tasks.findIndex((t) => t.id === over.id);
        if (ni < 0) return;
        const updates = computeReorderUpdates(
          arrayMove(g.tasks, oi, ni),
          active.id,
        );
        if (updates.length > 0) onReorderTasks(updates);
        return;
      }
      // Subtask reorder — within its parent's bucket.
      for (const subs of subtasksByParent.values()) {
        const oi = subs.findIndex((s) => s.id === active.id);
        if (oi < 0) continue;
        const ni = subs.findIndex((s) => s.id === over.id);
        if (ni < 0) return;
        const updates = computeReorderUpdates(
          arrayMove(subs, oi, ni),
          active.id,
        );
        if (updates.length > 0) onReorderTasks(updates);
        return;
      }
    }

    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
    const toggleGroup = (key) =>
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });

    // The date-editor popover. We use the same `TaskDatePopover` here
    // that the List view uses — presets (Today / Tomorrow / Next week /
    // 2 weeks…) on the left, calendar with range-highlight on the
    // right, time-slot column on demand. One popover instance lives at
    // the GanttView level and is anchored to whichever trigger most
    // recently opened it (a bar, the left-pane Dates button, or an
    // empty-row click).
    //
    // We store the trigger element in STATE rather than a ref so the
    // strict `react-hooks/refs` lint doesn't flag the assignment as
    // "ref access during render" (the ref-write would otherwise be
    // statically detected inside the openEditor closure even though
    // it's only called from event handlers). The popover gets a
    // memoised `{current: triggerEl}` shim that's stable when
    // `triggerEl` doesn't change, so its useLayoutEffect doesn't loop.
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [popoverTriggerEl, setPopoverTriggerEl] = useState(null);
    const popoverTriggerRef = useMemo(
      () => ({ current: popoverTriggerEl }),
      [popoverTriggerEl],
    );
    function openEditor(triggerEl, taskId) {
      if (!onUpdateTask) return;
      setPopoverTriggerEl(triggerEl);
      setEditingTaskId(taskId);
    }

    // Bar colour-picker state. Lives at the GanttView level (one
    // picker for the whole view) and is anchored to the swatch
    // button on whichever row was last clicked.
    const [colorPickerTaskId, setColorPickerTaskId] = useState(null);
    const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);
    function openColorPicker(triggerEl, taskId) {
      if (!onUpdateTask) return;
      setColorPickerAnchorEl(triggerEl);
      setColorPickerTaskId(taskId);
    }

    // ── Sticky horizontal scrollbar at the viewport bottom ───────────
    // The user wants the horizontal scrollbar to behave like a footer
    // pinned to the bottom of the browser viewport — visible no matter
    // how far they scroll vertically through long task lists.
    //
    // `position: sticky` proved unreliable here (it depends on a
    // parent's overflow chain and only sticks while scrolling THROUGH
    // the element). Instead we use `position: fixed` anchored to the
    // viewport bottom, with the LEFT and WIDTH measured from the right
    // pane via `getBoundingClientRect` so the fixed bar aligns
    // perfectly with the timeline column. A ResizeObserver re-measures
    // on every layout change.
    //
    // - `rightPaneRef`        → the actual timeline scroll container.
    // - `footerScrollbarRef`  → the fixed scrollbar mirror.
    // - `syncingScrollRef`    → guard against scroll-event ping-pong.
    // - `footerBounds`        → { left, width } in viewport pixels.
    const rightPaneRef = useRef(null);
    const footerScrollbarRef = useRef(null);
    const syncingScrollRef = useRef(false);
    const [footerBounds, setFooterBounds] = useState(null);

    // ── Infinite-scroll bounds ────────────────────────────────────────
    // `pastDays` / `futureDays` control how far the timeline extends in
    // either direction. They start at one year each and grow by another
    // year whenever the user scrolls within EDGE_DAYS of an edge (see
    // the onScroll handler on the right pane). Bumping these triggers a
    // re-render with a wider date range, and the scrollLeft-compensation
    // useLayoutEffect (below the timeline render) snaps the viewport
    // back so the visible bar doesn't appear to jump.
    const PAST_EXTEND = 365;
    const FUTURE_EXTEND = 365;
    const EDGE_DAYS = 14;
    const [pastDays, setPastDays] = useState(365);
    const [futureDays, setFutureDays] = useState(365);
    const extendingRef = useRef(false);

    // Viewport state — drives the off-screen indicator chevrons.
    // When a task's bar sits entirely outside the visible canvas, a
    // button at the viewport edge lets the user jump to it without
    // dragging the scrollbar. `clientWidth = 0` until first measure;
    // the chevrons guard on that to avoid rendering one per row on
    // initial paint.
    const [viewport, setViewport] = useState({ scrollLeft: 0, clientWidth: 0 });
    const viewportRafRef = useRef(null);

    useEffect(() => {
      const el = rightPaneRef.current;
      if (!el) return;
      const measure = () => {
        setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth });
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    const onTimelineScroll = (e) => {
      const el = e.currentTarget;
      // rAF-throttled viewport state update for the off-screen chevrons.
      if (viewportRafRef.current == null) {
        viewportRafRef.current = requestAnimationFrame(() => {
          viewportRafRef.current = null;
          setViewport({
            scrollLeft: el.scrollLeft,
            clientWidth: el.clientWidth,
          });
        });
      }
      if (extendingRef.current) return;
      const threshold = (DAY_W ?? 56) * EDGE_DAYS;
      const remainingRight = el.scrollWidth - el.scrollLeft - el.clientWidth;
      if (el.scrollLeft < threshold) {
        extendingRef.current = true;
        setPastDays((p) => p + PAST_EXTEND);
        requestAnimationFrame(() => {
          extendingRef.current = false;
        });
        return;
      }
      if (remainingRight < threshold) {
        extendingRef.current = true;
        setFutureDays((f) => f + FUTURE_EXTEND);
        requestAnimationFrame(() => {
          extendingRef.current = false;
        });
      }
    };

    // Measure the right pane on mount and on every viewport / layout
    // change. Using a ResizeObserver on the pane catches layout shifts
    // from the sidebar collapsing, the page resizing, or anything that
    // shifts the timeline column.
    useEffect(() => {
      if (!onUpdateTask) return;
      const el = rightPaneRef.current;
      if (!el) return;

      const measure = () => {
        const rect = el.getBoundingClientRect();
        setFooterBounds({ left: rect.left, width: rect.width });
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      // Also re-measure on window resize, because resizing the window
      // can change the pane's left edge (when the sidebar / layout
      // reflows) without triggering a ResizeObserver on the pane
      // itself.
      window.addEventListener("resize", measure);
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", measure);
      };
    }, [onUpdateTask]);

    // ── Drag-to-reschedule ────────────────────────────────────────────
    // `dragPreview` is the bit React DOES need to know about during the
    // drag: which task, which mode (`move` / `resize-start` /
    // `resize-end`), and how many DAYS the user has dragged so far. The
    // bar's left / width is derived from the original span + this delta,
    // so the bar visually follows the cursor while the underlying task
    // hasn't been mutated yet — the mutation fires once on pointerup.
    //
    // The mutable capture-state for the move/up listeners (start pixel,
    // original dates, mode) lives in CLOSURE variables inside
    // `startBarDrag`. We don't need a `useRef` for it: each pointerdown
    // creates a fresh closure with its own state, and the matching
    // pointerup tears down those listeners — they can't outlive the
    // gesture.
    const [dragPreview, setDragPreview] = useState(null);

    // ── Drag-to-create on empty rows ──────────────────────────────────
    // Two pieces of state for the "click or drag to schedule" pattern
    // on unscheduled task rows:
    //
    //   - `hoverRow` tracks the cursor's horizontal position INSIDE
    //     the timeline pane of an empty row. Drives the dotted
    //     cursor-line + "Click or drag to schedule" tooltip that
    //     follows the cursor so users discover the click target.
    //     Cleared on pointer-leave.
    //
    //   - `dragCreate` tracks an active drag-create gesture. While
    //     it's non-null we render a translucent ghost bar between the
    //     drag's origin column and the cursor's current column —
    //     same visual style as a real bar so it's clear what'll
    //     commit on release. Snapped to whole days in both visuals
    //     and commit.
    const [hoverRow, setHoverRow] = useState(null);
    const [dragCreate, setDragCreate] = useState(null);

    /** Returns a new Date that's `n` days after `d`, preserving the
     *  time-of-day component. `setDate` is the right knob here — it
     *  doesn't touch hours / minutes, so a task originally scheduled at
     *  09:00 stays at 09:00 after a drag of +2 days. */
    function addDaysPreservingTime(d, n) {
      const next = new Date(d);
      next.setDate(next.getDate() + n);
      return next;
    }

    /** Start a drag from the bar body or a resize handle. We attach
     *  pointermove + pointerup listeners on `window` rather than on the
     *  bar itself so the drag keeps tracking even if the cursor leaves
     *  the bar's bounding box mid-gesture. Listeners are torn down on
     *  pointerup so we don't leak them. */
    function startBarDrag(e, task, mode) {
      // Only respond to the primary mouse button / single-touch.
      if (e.button !== undefined && e.button !== 0) return;
      if (!onUpdateTask) return; // read-only views skip the drag entirely

      e.preventDefault();
      e.stopPropagation();

      // Raw dates (with their time components) — what we'll mutate on
      // commit. If a task has only one date set, the missing side
      // mirrors the present one so dragging still makes sense.
      const startRaw = task.startDate ? new Date(task.startDate) : null;
      const dueRaw = task.dueDate ? new Date(task.dueDate) : null;
      const originalStart = startRaw ?? dueRaw;
      const originalEnd = dueRaw ?? startRaw;
      if (!originalStart || !originalEnd) return;

      // Closure-captured gesture state. Each pointerdown spins up a
      // fresh capture object and a fresh pair of listeners; pointerup
      // tears them down. Nothing outlives the gesture, so there's no
      // need for a `useRef`.
      const captured = {
        task,
        mode,
        originalStart,
        originalEnd,
        // Was either date previously null? We use this on commit to
        // decide whether to send a value for that field — if the task
        // had no startDate, dragging the bar should set it (because
        // the user is now scheduling that edge), not invent one from
        // thin air.
        hadStart: Boolean(startRaw),
        hadDue: Boolean(dueRaw),
        startPxX: e.clientX,
        // Bar element for anchoring the date popover on a click-
        // without-drag. Captured here because the pointerup `ev`
        // doesn't carry a currentTarget — Web standards strip it
        // outside the original event-loop turn.
        barEl: e.currentTarget,
        moved: false,
      };

      function onMove(ev) {
        const dxPx = ev.clientX - captured.startPxX;
        // 2px gesture threshold — just enough to ignore a wobbly click,
        // but tight enough that any deliberate move engages drag mode
        // straight away. Earlier 4px felt sticky.
        if (Math.abs(dxPx) > 2) captured.moved = true;
        // Carry the RAW pixel offset (not pre-rounded days) so the bar
        // visually follows the cursor smoothly. Day snapping only kicks
        // in for the left-pane date label and at commit time on
        // pointerup — see below.
        setDragPreview({
          taskId: captured.task.id,
          mode: captured.mode,
          dxPx,
        });
      }

      function onUp(ev) {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);

        const dxPx = ev.clientX - captured.startPxX;
        const dxDays = Math.round(dxPx / DAY_W);

        // Click-without-drag → open the editor instead. Mode === 'move'
        // is the only one that falls through; resize handles always
        // commit (clicking the edge with no movement is a no-op).
        if (!captured.moved && captured.mode === "move") {
          // Click without drag → open the shared `TaskDatePopover`
          // anchored to the bar that was clicked. Lets the user pick
          // dates and times via the same presets / calendar / time
          // column the List view uses.
          openEditor(captured.barEl ?? e.target, captured.task.id);
        } else if (captured.moved && dxDays !== 0) {
          // Commit the drag.
          let newStart = captured.originalStart;
          let newEnd = captured.originalEnd;
          if (captured.mode === "move") {
            newStart = addDaysPreservingTime(captured.originalStart, dxDays);
            newEnd = addDaysPreservingTime(captured.originalEnd, dxDays);
          } else if (captured.mode === "resize-start") {
            newStart = addDaysPreservingTime(captured.originalStart, dxDays);
            // Don't let the user invert the range by dragging start past
            // end — clamp to end. Same logic the popover validates.
            if (newStart > captured.originalEnd)
              newStart = captured.originalEnd;
          } else if (captured.mode === "resize-end") {
            newEnd = addDaysPreservingTime(captured.originalEnd, dxDays);
            if (newEnd < captured.originalStart)
              newEnd = captured.originalStart;
          }
          // Decide which fields to actually send. Move drags both, so
          // both go. Resize-start only writes startDate (and seeds it
          // if it was previously null — the user is committing a new
          // start by dragging from the placeholder). Same for resize-
          // end → dueDate.
          const patch = {};
          if (captured.mode === "move" || captured.mode === "resize-start") {
            patch.startDate = newStart.toISOString();
          }
          if (captured.mode === "move" || captured.mode === "resize-end") {
            patch.dueDate = newEnd.toISOString();
          }
          onUpdateTask(captured.task.id, patch);
        }

        setDragPreview(null);
      }

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }

    /** Map a pixel X (relative to the timeline-pane left edge) into
     *  the index of the day-column it falls into. Negative values
     *  before the timeline start are clamped to 0; values past the
     *  end are clamped to the last column. */
    function pxToDayIndex(px) {
      const idx = Math.floor(px / DAY_W);
      if (idx < 0) return 0;
      if (idx > days.length - 1) return days.length - 1;
      return idx;
    }

    /** Day-column index → Date at midnight for that column. */
    function dayIndexToDate(idx) {
      return new Date(min.getTime() + idx * DAY_MS);
    }

    /** Start a drag-to-create gesture on an UNSCHEDULED task row.
     *  Captures the click origin in pixel coordinates and attaches
     *  window listeners so the gesture keeps tracking even if the
     *  cursor leaves the row. On pointerup:
     *    - if the cursor never moved (or moved < 3px), commit a
     *      single-day task on the clicked column (start = due = that
     *      day at midnight).
     *    - otherwise commit a range spanning the dragged columns,
     *      lowest column → highest column. */
    function startDragCreate(e, task, rowRect) {
      if (e.button !== undefined && e.button !== 0) return;
      if (!onUpdateTask) return;

      e.preventDefault();
      e.stopPropagation();
      setHoverRow(null); // hide the hover cursor-line during the drag

      const originXClient = e.clientX;
      const originXLocal = e.clientX - rowRect.left;
      const originDayIdx = pxToDayIndex(originXLocal);
      const rowEl = e.currentTarget;
      const captured = {
        taskId: task.id,
        originDayIdx,
        currentDayIdx: originDayIdx,
        moved: false,
        // Captured for the click-without-drag fallback so the popover
        // anchors to the row the user actually pressed on.
        rowEl,
      };
      setDragCreate({ ...captured });

      function onMove(ev) {
        const dxClient = ev.clientX - originXClient;
        if (Math.abs(dxClient) > 3) captured.moved = true;
        const localX = ev.clientX - rowRect.left;
        captured.currentDayIdx = pxToDayIndex(localX);
        setDragCreate({ ...captured });
      }

      function onUp() {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setDragCreate(null);

        if (!captured.moved) {
          // Click without drag — open the shared `TaskDatePopover`
          // for this task, anchored to the row. Mirrors the List
          // view: the user picks dates via the presets / calendar
          // rather than committing a one-day task automatically.
          openEditor(captured.rowEl, captured.taskId);
          return;
        }

        // Drag → commit a range across the dragged columns.
        const a = captured.originDayIdx;
        const b = captured.currentDayIdx;
        const startIdx = Math.min(a, b);
        const endIdx = Math.max(a, b);
        const newStart = dayIndexToDate(startIdx);
        const newDue = dayIndexToDate(endIdx);

        onUpdateTask(captured.taskId, {
          startDate: newStart.toISOString(),
          dueDate: newDue.toISOString(),
        });
      }

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }

    // Empty-state early return USED to live here. It was moved to just
    // before the main `return` below so it sits AFTER every hook in this
    // component — otherwise React saw a different hook order the moment
    // the first task arrived (the `useRef` / `useLayoutEffect` calls
    // further down would have been skipped on the no-tasks first render
    // and registered on the second), which corrupts hook state and can
    // surface as "Rendered more hooks than during the previous render".

    const today = startOfDay(new Date());
    let min = today;
    let max = today;
    // Range covers the union of every task's start..due window, padded so
    // bars don't crash into the left or right edges. Tasks without dates
    // contribute nothing here.
    for (const t of tasks) {
      const span = ganttSpan(t);
      if (!span) continue;
      if (span.start < min) min = span.start;
      if (span.end > max) max = span.end;
    }
    min = new Date(min.getTime() - 7 * DAY_MS);
    max = new Date(max.getTime() + 7 * DAY_MS);

    // True infinite-scroll: the active window starts at ±1 year around
    // today and grows by another year whenever the user scrolls within
    // EDGE_DAYS of either edge (handled by `onScroll` below). Tasks
    // outside the active window still expand it. We never truncate.
    const pastFloor = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - pastDays,
    );
    const futureCeil = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + futureDays,
    );
    if (max < futureCeil) max = futureCeil;
    if (min > pastFloor) min = pastFloor;

    // Snap to Mon→Sun so the week strip lands on whole columns.
    while (min.getDay() !== 1) min = new Date(min.getTime() - DAY_MS);
    while (max.getDay() !== 0) max = new Date(max.getTime() + DAY_MS);

    const days = [];
    for (let d = new Date(min); d <= max; d = new Date(d.getTime() + DAY_MS)) {
      days.push(new Date(d));
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      const start = days[i];
      const end = days[Math.min(i + 6, days.length - 1)];
      // Canonical date format (DD/MM/YYYY) — the strip already implies
      // the surrounding year via the Gantt's visible date range, so we
      // drop the year on the week banner to keep it compact: DD/MM–DD/MM.
      // Keeps the format consistent with every other date in the app
      // (see `lib/format.js`).
      const fmtDM = (d) =>
        `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
      weeks.push({
        key: `w-${i}`,
        span: Math.min(7, days.length - i),
        label: `W${isoWeekNumber(start)} · ${fmtDM(start)}–${fmtDM(end)}`,
      });
    }

    // Month/year banner — stitches consecutive day columns into runs
    // of the same calendar month so the user can always tell which
    // year they're looking at while scrolling through the infinite
    // past/future canvas. Without this, the week strip's DD/MM-only
    // labels feel disorienting once you scroll past your current year.
    const GANTT_MONTH_NAMES = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthBands = [];
    if (days.length > 0) {
      let runStart = 0;
      let runMonth = days[0].getMonth();
      let runYear = days[0].getFullYear();
      for (let i = 1; i < days.length; i++) {
        const m = days[i].getMonth();
        const y = days[i].getFullYear();
        if (m !== runMonth || y !== runYear) {
          monthBands.push({
            key: `m-${runStart}`,
            span: i - runStart,
            label: `${GANTT_MONTH_NAMES[runMonth]} ${runYear}`,
          });
          runStart = i;
          runMonth = m;
          runYear = y;
        }
      }
      monthBands.push({
        key: `m-${runStart}`,
        span: days.length - runStart,
        label: `${GANTT_MONTH_NAMES[runMonth]} ${runYear}`,
      });
    }

    const DAY_W = 56;
    // Task Name column — widened so longer task names aren't truncated to
    // "Website F…" once the row's indent + chevron/status chrome is taken out.
    const NAME_W = 360;
    // Per-column widths in the left pane. Match the List view's
    // COLUMN_ORDER widths so cells line up if both views are open
    // side-by-side and admins flip between them.
    const START_DATE_W = 130;
    const DUE_DATE_W = 130;
    const ASSIGNEE_W = 120;
    const PRIORITY_W = 110;
    const RECURRING_W = 120;
    const CREATED_BY_W = 170;
    const ROW_H = 36;
    // Three tiers: month/year banner, week strip, day labels — 28px
    // each. Bumped from the original 56 (two tiers) so the year is
    // visible at the top of the scroll surface while infinite-scrolling.
    const HEADER_H = 84;
    const todayIso = today.toISOString().slice(0, 10);

    // Which optional columns to render — driven by the shared
    // `useTaskColumns` state via the Customize popover. Same toggles
    // the List view consumes, so turning Start Date / Due Date /
    // Assignee / Priority on in either view reflects in both.
    const showStartDate = Boolean(columns.startDate);
    const showDueDate = Boolean(columns.dueDate);
    const showAssignee = Boolean(columns.assignee);
    const showPriority = Boolean(columns.priority);
    // Repeat column — interactive only on top-level rows (subtasks can't
    // recur); the handler gates whether the column renders at all.
    const showRecurring = Boolean(columns.recurring) && Boolean(onOpenRecurrence);
    const showCreatedBy = Boolean(columns.createdBy);

    // Total left-pane width — Name is always shown; every other
    // column is conditional. Width is computed each render so the
    // header row, group separators and body rows all stay aligned.
    const LEFT_PANE_W =
      NAME_W +
      (showStartDate ? START_DATE_W : 0) +
      (showDueDate ? DUE_DATE_W : 0) +
      (showAssignee ? ASSIGNEE_W : 0) +
      (showPriority ? PRIORITY_W : 0) +
      (showRecurring ? RECURRING_W : 0) +
      (showCreatedBy ? CREATED_BY_W : 0);

    function xForDateTime(iso) {
      const d = new Date(iso);
      return ((d.getTime() - min.getTime()) / DAY_MS) * DAY_W;
    }
    function xForToday() {
      return ((today.getTime() - min.getTime()) / DAY_MS) * DAY_W;
    }
    // Centre today's column in the timeline viewport — backs the "Today"
    // jump button so the user can return to the current date from
    // anywhere on the (±1-year) canvas.
    function scrollToToday() {
      const el = rightPaneRef.current;
      if (!el) return;
      // Fixed-duration rAF tween — smooth at any distance (native smooth
      // scroll stutters across this wide canvas). Today's column is always
      // far from the edges, so this never trips the infinite-scroll extend.
      smoothScrollLeftTo(el, xForToday() + DAY_W / 2 - el.clientWidth / 2);
    }

    // Publish the CURRENT closure to the parent's ref so the sub-toolbar's
    // "Today" button scrolls this canvas. No dependency array on purpose:
    // `scrollToToday` closes over the live date range, so re-publishing each
    // render keeps the handle fresh instead of pinning a stale closure.
    // Nulled on unmount so the toolbar no-ops when Gantt isn't mounted.
    useEffect(() => {
      if (!todayHandleRef) return undefined;
      todayHandleRef.current = scrollToToday;
      return () => {
        todayHandleRef.current = null;
      };
    });

    // Build the flat row list the left + right panes both iterate over.
    // Subtasks slot in as deeper-depth rows directly beneath their
    // parent when (a) the global `showSubtasks` toggle is on, AND (b)
    // the user has expanded that specific parent. Matches the List
    // view's per-parent expand/collapse behaviour exactly — toggling
    // in either view stays in sync because both read the same
    // `expandedParents` state from SpacePage.
    //
    // Nesting goes 3 levels: Task (depth 0) → Subtask (depth 1) →
    // Sub-subtask (depth 2). The recursive `pushTaskTree` walker
    // handles all depths uniformly so adding deeper nesting later
    // doesn't fork the row builder.
    const MAX_GANTT_DEPTH = 2;
    const visibleRows = [];
    function pushTaskTree(t, g, depth, parentId) {
      const subs = subtasksByParent.get(t.id) ?? [];
      visibleRows.push({
        kind: "task",
        task: t,
        group: g,
        depth,
        parentId,
        subtaskCount: subs.length,
      });
      if (
        showSubtasks &&
        expandedParents.has(t.id) &&
        depth < MAX_GANTT_DEPTH
      ) {
        for (const sub of subs) {
          pushTaskTree(sub, g, depth + 1, t.id);
        }
      }
    }
    for (const g of groups) {
      visibleRows.push({ kind: "group", group: g });
      if (!collapsedGroups.has(g.key)) {
        for (const t of g.tasks) {
          pushTaskTree(t, g, 0, null);
        }
        // Per-group "+ Add Task" row — mirrors the InlineAddTaskRow at
        // the bottom of each List/Board section so all three views
        // share one quick-add affordance. Skipped when the parent
        // didn't pass `onAddTask` so a read-only Gantt embed stays
        // clean.
        if (onAddTask) {
          visibleRows.push({ kind: "add-task", group: g });
        }
      }
    }

    const TIMELINE_W = days.length * DAY_W;

    // Centre today on first mount so the user doesn't land a year in
    // the past. Subsequent re-renders (zoom, task patches, range
    // extensions) skip this so the viewport doesn't yank around.
    const hasScrolledRef = useRef(false);
    useLayoutEffect(() => {
      if (hasScrolledRef.current) return;
      const el = rightPaneRef.current;
      if (!el) return;
      const todayX = ((today.getTime() - min.getTime()) / DAY_MS) * DAY_W;
      const visibleWidth = el.clientWidth;
      el.scrollLeft = Math.max(0, todayX - visibleWidth / 2);
      hasScrolledRef.current = true;
    });

    // When the past edge extends, `min` shifts to an earlier date and
    // every existing bar moves further to the right inside the wider
    // canvas. Compensate `scrollLeft` by the pixel delta right before
    // paint so the visible bar stays put — without this, the user
    // appears to teleport every time the timeline grows.
    const prevMinRef = useRef(min);
    useLayoutEffect(() => {
      const prev = prevMinRef.current;
      if (prev.getTime() === min.getTime()) return;
      const el = rightPaneRef.current;
      if (el) {
        const shiftDays = Math.round((prev.getTime() - min.getTime()) / DAY_MS);
        if (shiftDays > 0) el.scrollLeft += shiftDays * DAY_W;
      }
      prevMinRef.current = min;
    }, [min, DAY_W]);

    // Empty-state branch — placed AFTER every hook above so React sees a
    // stable hook order across the "no tasks" → "tasks arrive" transition.
    if (tasks.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
          No tasks yet. Create one from the List view and it&apos;ll appear
          here.
        </div>
      );
    }

    return (
      <div className="relative rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] flex overflow-x-auto overflow-y-hidden lg:overflow-x-hidden min-h-full view-hscroll">
        {/* NOTE: the "Jump to today" button used to float here, absolutely
         *  positioned over the header. It kept covering header text (QA, three
         *  times): the timeline header scrolls beneath a button pinned to the
         *  card, so a week label or a month band's label would land under it
         *  depending on the visible range — no offset or size could fix that.
         *  It now lives in the sub-toolbar next to Filter / Customise (see
         *  `todayHandleRef`), matching how the Task Monitor Gantt does it. */}
        {/* Left pane: Name + Dates + optional Assignee / Priority. The
         *  pane width grows as toggles are added so cells stay aligned
         *  with their headers. */}
        <div
          className="shrink-0 border-r border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
          style={{ width: LEFT_PANE_W }}
        >
          <div
            className="flex items-end bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] text-sm font-semibold text-[color:var(--color-ink-muted)]"
            style={{ height: HEADER_H }}
          >
            <div className="px-3 py-2" style={{ width: NAME_W }}>
              Task Name
            </div>
            {showStartDate ? (
              <div className="px-3 py-2" style={{ width: START_DATE_W }}>
                Start Date
              </div>
            ) : null}
            {showDueDate ? (
              <div className="px-3 py-2" style={{ width: DUE_DATE_W }}>
                Due Date
              </div>
            ) : null}
            {showAssignee ? (
              <div className="px-3 py-2" style={{ width: ASSIGNEE_W }}>
                Assignee
              </div>
            ) : null}
            {showPriority ? (
              <div className="px-3 py-2" style={{ width: PRIORITY_W }}>
                Priority
              </div>
            ) : null}
            {showRecurring ? (
              <div className="px-3 py-2" style={{ width: RECURRING_W }}>
                Repeat
              </div>
            ) : null}
            {showCreatedBy ? (
              <div className="px-3 py-2" style={{ width: CREATED_BY_W }}>
                Created by
              </div>
            ) : null}
            {/* Column toggling lives on the toolbar's Customize pill —
             *  no per-pane "+ Add column" button. Keeps one trigger for
             *  the same panel and avoids the popover competing with the
             *  bar's resize / drag affordances right next to it. */}
          </div>
          <DndContext
            sensors={ganttSortSensors}
            collisionDetection={reorderCollision}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={(e) => setActiveReorderId(e.active.id)}
            onDragCancel={() => setActiveReorderId(null)}
            onDragEnd={onReorderDragEnd}
          >
            <SortableContext
              items={visibleRows
                .filter((r) => r.kind === "task")
                .map((r) => r.task.id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleRows.map((row) => {
                if (row.kind === "add-task") {
                  // Per-group quick-add row in the left pane. The right pane
                  // renders a matching empty strip at the same height so the
                  // two panes stay aligned.
                  return (
                    <div
                      key={`add-${row.group.key}`}
                      className="flex items-center border-b border-[color:var(--color-rule)]"
                      style={{ height: ROW_H }}
                    >
                      <div style={{ width: NAME_W }}>
                        <InlineAddTaskRow
                          statusColor={row.group.status?.color}
                          statusUnstarted={isUnstartedStatus(
                            row.group.status,
                            row.group.status?.name,
                          )}
                          onSubmit={(name) =>
                            onAddTask?.(row.group.status?.name, name)
                          }
                        />
                      </div>
                      {/* Empty filler cells under the optional columns so the
                       *  separator extends the full width of the left pane. */}
                      {showStartDate ? (
                        <div style={{ width: START_DATE_W }} />
                      ) : null}
                      {showDueDate ? (
                        <div style={{ width: DUE_DATE_W }} />
                      ) : null}
                      {showAssignee ? (
                        <div style={{ width: ASSIGNEE_W }} />
                      ) : null}
                      {showPriority ? (
                        <div style={{ width: PRIORITY_W }} />
                      ) : null}
                      {showRecurring ? (
                        <div style={{ width: RECURRING_W }} />
                      ) : null}
                    </div>
                  );
                }
                if (row.kind === "group") {
                  const collapsed = collapsedGroups.has(row.group.key);
                  return (
                    <div
                      key={`name-${row.group.key}`}
                      className="flex items-center bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]"
                      style={{ height: ROW_H }}
                    >
                      {/* Status group header — chevron + pill + count.
                       *  Visually identical to the List view's status
                       *  section header (`ListSection`): `ChevronDown`
                       *  rotating `-90°` when collapsed, `StatusPill`, and
                       *  a `text-sm text-ink-muted` count immediately to
                       *  the right of the pill. Keeps the two views feeling
                       *  like the same product when you flip between them. */}
                      <button
                        type="button"
                        onClick={() => toggleGroup(row.group.key)}
                        className="flex items-center gap-2 px-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] h-full"
                        style={{ width: NAME_W }}
                        aria-expanded={!collapsed}
                        title={
                          collapsed
                            ? "Expand status group"
                            : "Collapse status group"
                        }
                      >
                        <ChevronDown
                          size={14}
                          className={
                            "shrink-0 transition-transform text-[color:var(--color-ink-muted)] " +
                            (collapsed ? "-rotate-90" : "")
                          }
                        />
                        <StatusPill status={row.group.status} />
                        <span className="text-sm text-[color:var(--color-ink-muted)]">
                          {row.group.tasks.length}
                        </span>
                      </button>
                      {/* Empty filler cells under each optional column
                       *  header so the group separator extends the full
                       *  width of the left pane. Each column's filler is
                       *  conditional on the same visibility flag the
                       *  header uses. */}
                      {showStartDate ? (
                        <div style={{ width: START_DATE_W }} />
                      ) : null}
                      {showDueDate ? (
                        <div style={{ width: DUE_DATE_W }} />
                      ) : null}
                      {showAssignee ? (
                        <div style={{ width: ASSIGNEE_W }} />
                      ) : null}
                      {showPriority ? (
                        <div style={{ width: PRIORITY_W }} />
                      ) : null}
                      {showRecurring ? (
                        <div style={{ width: RECURRING_W }} />
                      ) : null}
                    </div>
                  );
                }
                const t = row.task;
                const status = statusByName.get(t.status) ?? row.group.status;
                // Apply the live drag preview to the LEFT pane too, so the
                // "Dates" column reads the same as the bar position while
                // the user is dragging. Without this, the bar moves but the
                // date label stays frozen on the old value until pointerup
                // — feels broken.
                const linkedPreview =
                  dragPreview?.taskId === t.id ? dragPreview : null;
                let previewStart = t.startDate ? new Date(t.startDate) : null;
                let previewDue = t.dueDate ? new Date(t.dueDate) : null;
                if (linkedPreview && (previewStart || previewDue)) {
                  // Snap the raw pixel offset to whole days for the LEFT-
                  // pane label and the row tooltip — the bar itself moves
                  // smoothly, but date text reads in whole days. Same
                  // snap is used at commit time on pointerup.
                  const dxDays = Math.round(linkedPreview.dxPx / DAY_W);
                  const baseStart = previewStart ?? previewDue;
                  const baseDue = previewDue ?? previewStart;
                  if (linkedPreview.mode === "move") {
                    // Only shift the dates the task currently has —
                    // dragging shouldn't invent a missing one. Matches the
                    // commit logic in `startBarDrag`.
                    previewStart = previewStart
                      ? addDaysPreservingTime(baseStart, dxDays)
                      : null;
                    previewDue = previewDue
                      ? addDaysPreservingTime(baseDue, dxDays)
                      : null;
                  } else if (linkedPreview.mode === "resize-start") {
                    // Resize-start always SETS startDate, even on a task
                    // that previously only had a dueDate (the user is now
                    // declaring "this is when I'll start").
                    const base = previewStart ?? previewDue;
                    if (base) {
                      let candidate = addDaysPreservingTime(base, dxDays);
                      if (previewDue && candidate > previewDue)
                        candidate = previewDue;
                      previewStart = candidate;
                    }
                  } else if (linkedPreview.mode === "resize-end") {
                    // Symmetric: resize-end SETS dueDate even on a task
                    // that previously only had a startDate.
                    const base = previewDue ?? previewStart;
                    if (base) {
                      let candidate = addDaysPreservingTime(base, dxDays);
                      if (previewStart && candidate < previewStart)
                        candidate = previewStart;
                      previewDue = candidate;
                    }
                  }
                }
                // Build a "DD/MM → DD/MM" range when both dates are set;
                // Row hover tooltip — surfaces both dates at a glance,
                // including time component when set. Built from the
                // PREVIEW values so the tooltip stays in sync with the
                // bar's position during drag-to-reschedule.
                const tooltipParts = [];
                if (previewStart)
                  tooltipParts.push(
                    `Start ${fmtIsoDateTime(previewStart.toISOString(), t.startDateHasTime ?? false)}`,
                  );
                if (previewDue)
                  tooltipParts.push(
                    `Due ${fmtIsoDateTime(previewDue.toISOString(), t.dueDateHasTime ?? false)}`,
                  );
                // Row hierarchy — parents are depth 0, subtasks depth 1,
                // sub-subtasks depth 2. The depth drives left padding on
                // the Name cell so each level sits visually nested under
                // its parent (matches the List view's indent scheme).
                const isSubtask = row.depth >= 1;
                // Allow further nesting only when we haven't hit the cap.
                // depth 0 → can have subtasks, depth 1 → can have sub-
                // subtasks, depth 2 → leaf.
                const canHaveChildren = row.depth < MAX_GANTT_DEPTH;
                const hasSubtasks =
                  canHaveChildren &&
                  (row.subtaskCount ?? 0) > 0 &&
                  showSubtasks;
                const expanded = expandedParents.has(t.id);
                return (
                  <SortableRow key={t.id} id={t.id}>
                    {({ setNodeRef, style, attributes, listeners }) => (
                      // `style` (drag transform) rides the outer wrapper so the
                      // whole row slides as one; `setNodeRef` lives on the inner
                      // constant-height row so dnd-kit measures a stable rect
                      // (same split the List rows use). The right-pane bar for
                      // this row stays put during the drag and re-aligns on drop.
                      <div style={style} {...attributes}>
                        <div
                          ref={setNodeRef}
                          // Named `group/row` so the hover-action cluster stays
                          // scoped to this row and doesn't trigger the Avatar
                          // component's anonymous `group-hover` tooltip on every
                          // cell hover.
                          className="group/row flex items-center border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
                          style={{ height: ROW_H }}
                          title={`${t.name}${tooltipParts.length ? ` — ${tooltipParts.join(" · ")}` : ""}`}
                        >
                          <div
                            className={
                              "flex items-center gap-2 px-3 " +
                              (row.depth === 2
                                ? "pl-20"
                                : row.depth === 1
                                  ? "pl-12"
                                  : "pl-3")
                            }
                            style={{ width: NAME_W }}
                          >
                            {/* Drag handle — always-visible grip that owns the
                             *  sortable listeners so the rest of the row stays
                             *  individually clickable. `touch-none` stops the
                             *  browser stealing the gesture to scroll. */}
                            <button
                              type="button"
                              {...listeners}
                              title="Drag to reorder"
                              aria-label="Drag to reorder task"
                              style={{ touchAction: "none" }}
                              className="p-0.5 -ml-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-grab active:cursor-grabbing shrink-0"
                            >
                              <GripVertical size={14} />
                            </button>
                            {/* Expand / collapse chevron — renders on any row that
                             *  can have children (depth 0 or 1) when it actually
                             *  has at least one child and the global Subtasks
                             *  toggle is on. Otherwise a fixed-width spacer keeps
                             *  the rest of the row aligned with rows that do have
                             *  a chevron. */}
                            {hasSubtasks ? (
                              <button
                                type="button"
                                onClick={() => onToggleParent?.(t.id)}
                                aria-expanded={expanded}
                                aria-label={
                                  expanded ? "Hide subtasks" : "Show subtasks"
                                }
                                className="p-0.5 -ml-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                              >
                                {expanded ? (
                                  <ChevronDown size={12} />
                                ) : (
                                  <ChevronRight size={12} />
                                )}
                              </button>
                            ) : (
                              <span
                                className="w-4 shrink-0"
                                aria-hidden="true"
                              />
                            )}

                            {/* Status indicator — same `StatusCell` used in the
                             *  List view: a hollow circle whose border colour
                             *  matches the task's current status, with a click-
                             *  to-open menu of every status the Space has
                             *  configured (grouped by category). Picking a new
                             *  status fires the same `onUpdateTask` mutation,
                             *  so the bar's status colour / row grouping
                             *  refresh instantly via optimistic update. */}
                            <StatusCell
                              current={t.status}
                              statuses={statuses}
                              onCommit={(nextStatusName) =>
                                onUpdateTask?.(t.id, { status: nextStatusName })
                              }
                              disabled={t.canEditStatus === false}
                            />

                            {/* Color swatch — clickable button that opens the
                             *  bar-colour picker. Same colour resolution as the
                             *  bar itself: custom → priority → status. */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openColorPicker(e.currentTarget, t.id);
                              }}
                              disabled={!onUpdateTask}
                              title={
                                onUpdateTask
                                  ? t.color
                                    ? "Change bar colour"
                                    : "Set bar colour"
                                  : undefined
                              }
                              aria-label={
                                onUpdateTask
                                  ? `Change bar colour for ${t.name}`
                                  : undefined
                              }
                              className={
                                "h-3 w-3 rounded-full shrink-0 border border-[color:var(--color-rule)] transition-transform " +
                                (onUpdateTask
                                  ? "cursor-pointer motion-safe:hover:scale-125 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
                                  : "cursor-default")
                              }
                              style={{
                                backgroundColor:
                                  t.color ??
                                  (t.priority
                                    ? PRIORITY_META[t.priority].color
                                    : status.color),
                              }}
                            />
                            {/* No subtask glyph here — the `pl-12` indent on the
                             *  Name cell already reads as "this row is nested
                             *  under the parent above" without needing a chain
                             *  icon competing with the status dot + colour swatch
                             *  for space. */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenDetail?.(t.id);
                              }}
                              disabled={!onOpenDetail}
                              title={onOpenDetail ? "Open task" : undefined}
                              className={
                                "text-sm truncate text-left bg-transparent border-0 p-0 " +
                                (onOpenDetail ? "cursor-pointer " : "") +
                                (isSubtask
                                  ? "text-[color:var(--color-ink)]"
                                  : "text-[color:var(--color-ink-strong)]")
                              }
                            >
                              {t.name}
                            </button>
                            {/* "X subtasks" hint — parents only, GitBranch icon +
                             *  count, identical to the List view's parent row.
                             *  Lives inside the Name cell so it scrolls with the
                             *  task name and never overflows the column. */}
                            {hasSubtasks ? (
                              <span
                                className="ml-1 text-[10px] text-[color:var(--color-ink-muted)] inline-flex items-center gap-0.5"
                                title={`${row.subtaskCount} subtask${row.subtaskCount === 1 ? "" : "s"}`}
                              >
                                <GitBranch size={11} />
                                {row.subtaskCount}
                              </span>
                            ) : null}
                            {/* Hover-only row actions — right-aligned cluster that
                             *  fades in on row hover. Mirrors the ClickUp pattern
                             *  the user pointed to: a Pencil for "open task" and
                             *  a Plus for "add subtask" on parents, plus a Trash
                             *  on subtasks (parents are deleted from the detail
                             *  dialog so a single click here can't wipe a whole
                             *  subtree). */}
                            <span className="ml-auto inline-flex items-center gap-0.5">
                              {/* Open + Add-subtask stay hover-only; Delete is always
                               *  visible. */}
                              {onOpenDetail ||
                              (canHaveChildren && onAddSubtask) ? (
                                <span className="inline-flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                  {onOpenDetail ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenDetail(t.id);
                                      }}
                                      // Pencil icon → "Edit task" (see the Board card note).
                                      title="Edit task"
                                      aria-label="Edit task"
                                      className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                    >
                                      <Pencil size={12} />
                                    </button>
                                  ) : null}
                                  {canHaveChildren && onAddSubtask ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Auto-expand the parent so the freshly added
                                        // subtask appears immediately — otherwise the
                                        // user would click "+" and see nothing visible
                                        // change until they manually expanded.
                                        if (!expandedParents.has(t.id))
                                          onToggleParent?.(t.id);
                                        onAddSubtask(t.id);
                                      }}
                                      title={
                                        row.depth === 0
                                          ? "Add subtask"
                                          : "Add sub-subtask"
                                      }
                                      aria-label={
                                        row.depth === 0
                                          ? "Add subtask"
                                          : "Add sub-subtask"
                                      }
                                      className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                    >
                                      <Plus size={12} />
                                    </button>
                                  ) : null}
                                </span>
                              ) : null}
                              {onDeleteTask ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteTask(t);
                                  }}
                                  title={
                                    isSubtask ? "Delete subtask" : "Delete Task"
                                  }
                                  aria-label={
                                    isSubtask ? "Delete subtask" : "Delete Task"
                                  }
                                  className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                                >
                                  <Trash2 size={12} />
                                </button>
                              ) : null}
                            </span>
                          </div>
                          {/* Start Date / Due Date — separate columns, each
                           *  uses the same interactive cell the List view does.
                           *  Clicking opens the cell's own date popover.
                           *
                           *  When the user is dragging the bar in the right
                           *  pane, we substitute the previewed dates here so the
                           *  cells track the cursor in real time. Once the drag
                           *  ends, `previewStart` / `previewDue` go back to
                           *  null and the cells render the committed values. */}
                          {showStartDate ? (
                            <div
                              className="px-3 inline-flex items-center"
                              style={{ width: START_DATE_W }}
                            >
                              <StartDateCell
                                startDate={
                                  previewStart
                                    ? previewStart.toISOString()
                                    : (t.startDate ?? null)
                                }
                                dueDate={
                                  previewDue
                                    ? previewDue.toISOString()
                                    : (t.dueDate ?? null)
                                }
                                startDateHasTime={t.startDateHasTime ?? false}
                                dueDateHasTime={t.dueDateHasTime ?? false}
                                onCommit={(patch) =>
                                  onUpdateTask?.(t.id, patch)
                                }
                                disabled={t.canEditPlanning === false}
                              />
                            </div>
                          ) : null}
                          {showDueDate ? (
                            <div
                              className="px-3 inline-flex items-center"
                              style={{ width: DUE_DATE_W }}
                            >
                              <DueDateCell
                                startDate={
                                  previewStart
                                    ? previewStart.toISOString()
                                    : (t.startDate ?? null)
                                }
                                dueDate={
                                  previewDue
                                    ? previewDue.toISOString()
                                    : (t.dueDate ?? null)
                                }
                                startDateHasTime={t.startDateHasTime ?? false}
                                dueDateHasTime={t.dueDateHasTime ?? false}
                                completedAt={t.completedAt ?? null}
                                onCommit={(patch) =>
                                  onUpdateTask?.(t.id, patch)
                                }
                                disabled={t.canEditPlanning === false}
                              />
                            </div>
                          ) : null}

                          {/* Optional Assignee column — uses the same
                           *  interactive `AssigneeCell` the List view uses, so
                           *  clicking opens the assignee picker (search + add /
                           *  invite) and commits via the same callback. */}
                          {showAssignee ? (
                            <div
                              className="px-3 inline-flex items-center"
                              style={{ width: ASSIGNEE_W }}
                            >
                              <AssigneeCell
                                assigneeIds={t.assigneeIds ?? []}
                                members={members}
                                currentUserId={currentUserId}
                                workspaceId={workspaceId}
                                workspaceName={workspaceName}
                                spaceId={spaceId}
                                spaceName={spaceName}
                                taskId={t.id}
                                allTasks={allTasks ?? tasks}
                                statuses={statuses}
                                onCommit={(assigneeIds) =>
                                  onUpdateTask?.(t.id, { assigneeIds })
                                }
                                disabled={t.canEditAssignee === false}
                              />
                            </div>
                          ) : null}

                          {/* Optional Priority column — uses the same
                           *  interactive `PriorityCell` the List view uses,
                           *  so the dropdown menu / chip styling matches. */}
                          {showPriority ? (
                            <div
                              className="px-3 inline-flex items-center"
                              style={{ width: PRIORITY_W }}
                            >
                              <PriorityCell
                                priority={t.priority ?? null}
                                onCommit={(priority) =>
                                  onUpdateTask?.(t.id, { priority })
                                }
                                disabled={t.canEditPlanning === false}
                                completed={isTerminalStatusName(statuses, t.status)}
                              />
                            </div>
                          ) : null}
                          {/* Optional Repeat column — interactive on top-level
                           *  rows only (only top-level tasks can repeat; a
                           *  parent's recurrence clones its subtasks). Subtask
                           *  rows show a read-only dash. */}
                          {showRecurring ? (
                            <div
                              className="px-3 inline-flex items-center"
                              style={{ width: RECURRING_W }}
                            >
                              <RecurringCell
                                state={t.recurring ?? null}
                                schedule={t.recurringSchedule ?? null}
                                canEdit={t.canEditPlanning !== false}
                                onOpen={
                                  row.depth === 0
                                    ? () => onOpenRecurrence?.(t.id)
                                    : undefined
                                }
                              />
                            </div>
                          ) : null}
                          {showCreatedBy ? (
                            <div
                              className="px-3 inline-flex items-center"
                              style={{ width: CREATED_BY_W }}
                            >
                              <CreatedByCell
                                name={t.createdByName ?? null}
                                avatarUrl={t.createdByAvatarUrl ?? null}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </SortableRow>
                );
              })}
            </SortableContext>
            <DragOverlay
              dropAnimation={{
                duration: 180,
                easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
              }}
            >
              {activeReorderTask ? (
                <TaskDragPreview
                  task={activeReorderTask}
                  status={statusByName.get(activeReorderTask.status) ?? null}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Right pane: timeline. The native horizontal scrollbar is
         *  hidden — users interact with the FIXED scrollbar pinned to
         *  the viewport bottom (rendered via portal further down).
         *  Wheel / trackpad / programmatic horizontal scroll still
         *  works here; the onScroll handler mirrors `scrollLeft` into
         *  the fixed footer so the two stay locked. */}
        <div
          ref={rightPaneRef}
          onScroll={(e) => {
            // Step 1 — mirror scrollLeft into the fixed footer scrollbar.
            if (!syncingScrollRef.current) {
              syncingScrollRef.current = true;
              if (footerScrollbarRef.current) {
                footerScrollbarRef.current.scrollLeft =
                  e.currentTarget.scrollLeft;
              }
              requestAnimationFrame(() => {
                syncingScrollRef.current = false;
              });
            }
            // Step 2 — infinite-scroll detection. When the user
            // approaches either edge, extend the active range so the
            // canvas can keep growing in both directions.
            onTimelineScroll(e);
          }}
          className="flex-1 min-w-[80vw] lg:min-w-0 overflow-x-auto view-hscroll"
        >
          <div style={{ minWidth: TIMELINE_W, position: "relative" }}>
            {/* Header — month/year banner + week strip + day cells */}
            <div
              className="bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] sticky top-0 z-10"
              style={{ height: HEADER_H }}
            >
              <div
                className="flex border-b border-[color:var(--color-rule)]"
                style={{ height: HEADER_H / 3 }}
              >
                {monthBands.map((m) => (
                  <div
                    key={m.key}
                    className="shrink-0 flex items-center px-3 text-xs font-semibold text-[color:var(--color-ink-strong)] border-r border-[color:var(--color-rule)] truncate bg-[color:var(--color-canvas)]"
                    style={{ width: m.span * DAY_W }}
                    title={m.label}
                  >
                    {m.label}
                  </div>
                ))}
              </div>
              <div
                className="flex border-b border-[color:var(--color-rule)]"
                style={{ height: HEADER_H / 3 }}
              >
                {weeks.map((w) => (
                  <div
                    key={w.key}
                    className="shrink-0 flex items-center justify-center px-2 text-[12px] font-semibold text-[color:var(--color-ink-muted)] border-r border-[color:var(--color-rule)] truncate"
                    style={{ width: w.span * DAY_W }}
                    title={w.label}
                  >
                    {w.label}
                  </div>
                ))}
              </div>
              <div className="flex" style={{ height: HEADER_H / 3 }}>
                {days.map((d) => {
                  const iso = d.toISOString().slice(0, 10);
                  const isToday = iso === todayIso;
                  return (
                    <div
                      key={iso}
                      className={
                        "shrink-0 flex items-center justify-center gap-1 text-[12px] border-r border-[color:var(--color-rule)] " +
                        (isToday
                          ? "font-semibold text-[color:var(--color-accent)]"
                          : "text-[color:var(--color-ink-muted)]")
                      }
                      style={{ width: DAY_W }}
                    >
                      <span className="leading-none">
                        {d.toLocaleDateString("en-GB", { weekday: "short" })}
                      </span>
                      {isToday ? (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--color-priority-urgent)] text-white text-[10px] font-semibold tabular-nums">
                          {d.getDate()}
                        </span>
                      ) : (
                        <span className="tabular-nums">{d.getDate()}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div className="relative">
              <div
                className="absolute top-0 bottom-0 w-px bg-[color:var(--color-priority-urgent)] z-[1] pointer-events-none"
                style={{ left: xForToday() + DAY_W / 2 }}
                aria-hidden="true"
              />

              {visibleRows.map((row) => {
                if (row.kind === "group") {
                  return (
                    <div
                      key={`tl-${row.group.key}`}
                      className="bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]"
                      style={{ height: ROW_H }}
                    />
                  );
                }
                if (row.kind === "add-task") {
                  // Empty timeline strip aligned with the left-pane
                  // `InlineAddTaskRow`. No bar to render — the row is a
                  // pure quick-add affordance, not a scheduled task.
                  return (
                    <div
                      key={`tl-add-${row.group.key}`}
                      className="border-b border-[color:var(--color-rule)]"
                      style={{ height: ROW_H }}
                    />
                  );
                }
                const t = row.task;
                const status = statusByName.get(t.status) ?? row.group.status;
                // Bar / chip colour resolution, highest priority first:
                //   1. Custom task colour (`t.color`) — admin override
                //      via the swatch button next to the task name.
                //   2. Priority colour — when no custom colour set.
                //   3. Status colour — default Space colour for that
                //      task's current status.
                const barColor =
                  t.color ??
                  (t.priority ? PRIORITY_META[t.priority].color : status.color);

                // Compute the bar's pixel coordinates from the task's
                // start/due window. `ganttSpan` returns null when the
                // task has neither date set — we render a "Set dates"
                // placeholder for those rows instead of a bar.
                const span = ganttSpan(t);

                // Drag preview for THIS row — lifted out of the bar IIFE
                // so the editor popover (rendered after `barNode`) can
                // read the same chipStart / chipDue and mirror them
                // into its inputs while the user is dragging.
                const preview =
                  dragPreview?.taskId === t.id ? dragPreview : null;

                // Snapped previewed dates — used by:
                //   1. The floating chip ABOVE the bar (live readout).
                //   2. The popup editor's input sync (when it's open).
                //   3. The bar's hover title for completeness.
                let chipStart = t.startDate ? new Date(t.startDate) : null;
                let chipDue = t.dueDate ? new Date(t.dueDate) : null;
                if (preview && (chipStart || chipDue)) {
                  const dxDaysSnap = Math.round(preview.dxPx / DAY_W);
                  const baseS = chipStart ?? chipDue;
                  const baseD = chipDue ?? chipStart;
                  if (preview.mode === "move") {
                    chipStart = chipStart
                      ? addDaysPreservingTime(baseS, dxDaysSnap)
                      : null;
                    chipDue = chipDue
                      ? addDaysPreservingTime(baseD, dxDaysSnap)
                      : null;
                  } else if (preview.mode === "resize-start") {
                    const base = chipStart ?? chipDue;
                    if (base) {
                      let c = addDaysPreservingTime(base, dxDaysSnap);
                      if (chipDue && c > chipDue) c = chipDue;
                      chipStart = c;
                    }
                  } else if (preview.mode === "resize-end") {
                    const base = chipDue ?? chipStart;
                    if (base) {
                      let c = addDaysPreservingTime(base, dxDaysSnap);
                      if (chipStart && c < chipStart) c = chipStart;
                      chipDue = c;
                    }
                  }
                }
                const chipText = (() => {
                  const s = chipStart
                    ? formatDateTime(chipStart.toISOString())
                    : null;
                  const d = chipDue
                    ? formatDateTime(chipDue.toISOString())
                    : null;
                  if (s && d && s !== d) return `${s} → ${d}`;
                  return d ?? s ?? "";
                })();

                const barNode = span
                  ? (() => {
                      const startX = xForDateTime(span.start.toISOString());
                      // `+ DAY_W` because the end day is INCLUSIVE — a
                      // task due on May 15 should fill all of May 15's
                      // column, not stop at its left edge.
                      const endX = xForDateTime(span.end.toISOString()) + DAY_W;
                      let barLeft = startX;
                      let barWidth = Math.max(endX - startX, 16);
                      const isOverdue =
                        t.dueDate &&
                        span.end < today &&
                        status.category !== "done" &&
                        status.category !== "closed";

                      // Apply the drag preview offset so the bar follows
                      // the cursor SMOOTHLY (pixel-by-pixel — no day
                      // snapping during the gesture). Day snapping only
                      // applies to the left-pane date label and to the
                      // committed mutation on pointerup; the bar itself
                      // tracks the cursor exactly.
                      //   move         → shift both edges
                      //   resize-start → shift only the left edge,
                      //                  width grows/shrinks
                      //   resize-end   → shift only the right edge,
                      //                  width grows/shrinks
                      // (`preview` was already computed at the row level
                      // so the editor popup outside this IIFE can read
                      // it too — see the chip / popup-sync use below.)
                      if (preview) {
                        const dxPx = preview.dxPx;
                        if (preview.mode === "move") {
                          barLeft = startX + dxPx;
                        } else if (preview.mode === "resize-start") {
                          const newLeft = Math.min(startX + dxPx, endX - 16);
                          barWidth = Math.max(endX - newLeft, 16);
                          barLeft = newLeft;
                        } else if (preview.mode === "resize-end") {
                          const newEndX = Math.max(endX + dxPx, startX + 16);
                          barWidth = Math.max(newEndX - startX, 16);
                        }
                      }

                      const tipParts = [];
                      if (t.startDate)
                        tipParts.push(
                          `Start ${fmtIsoDateTime(t.startDate, t.startDateHasTime ?? false)}`,
                        );
                      if (t.dueDate)
                        tipParts.push(
                          `Due ${fmtIsoDateTime(t.dueDate, t.dueDateHasTime ?? false)}`,
                        );
                      if (isOverdue) tipParts.push("Overdue");
                      const barTitle = `${t.name}${tipParts.length ? " — " + tipParts.join(" · ") : ""} · Drag to reschedule, click to edit time`;

                      // Resize edges are 8px hit-targets pinned to the
                      // left and right of the bar (wider than before so
                      // they're easier to grab). They only render when
                      // the bar is wide enough to actually grab both
                      // edges (< 28px gets just a single "move"
                      // affordance to avoid overlapping handles).
                      const showResizeEdges =
                        barWidth >= 28 && Boolean(onUpdateTask);
                      const draggable = Boolean(onUpdateTask);

                      return (
                        <div
                          onPointerDown={(e) => {
                            // Only the body initiates a `move` — resize
                            // edges (`ew-resize`) stop propagation so
                            // they don't bubble up and become a move.
                            startBarDrag(e, t, "move");
                          }}
                          className={
                            "absolute top-1/2 -translate-y-1/2 rounded-md flex items-center px-2 transition-[filter] focus-within:outline-none focus-within:ring-2 focus-within:ring-[color:var(--color-accent)] select-none " +
                            (draggable
                              ? "hover:brightness-110 cursor-grab active:cursor-grabbing"
                              : "cursor-default")
                          }
                          style={{
                            left: barLeft,
                            width: barWidth,
                            height: ROW_H - 12,
                            backgroundColor: barColor,
                            // While dragging, drop the slight
                            // transparency so the moving bar reads as
                            // "active" — and pop a small shadow.
                            opacity: preview ? 1 : 0.9,
                            // Overdue red outline removed — bars now read
                            // purely by their priority/status colour.
                            // Drag-preview keeps its lift shadow.
                            boxShadow: preview
                              ? "0 4px 10px rgba(0,0,0,0.18)"
                              : "none",
                            // `pan-x pan-y` (not `none`) so a touch swipe still
                            // scrolls the timeline — the delay-based TouchSensor
                            // tells a press-and-hold reschedule from a swipe.
                            touchAction: "pan-x pan-y",
                          }}
                          title={barTitle}
                          aria-label={barTitle}
                          role={draggable ? "slider" : "img"}
                        >
                          {/* Live readout chip during drag — sits above
                           *  the bar and shows the SNAPPED dates that
                           *  will commit on release. Visible without
                           *  needing the popover open. */}
                          {preview && chipText ? (
                            <span className="absolute -top-7 left-1/2 -translate-x-1/2 z-30 whitespace-nowrap rounded-md bg-[color:var(--color-ink-strong)] text-[color:var(--color-canvas)] text-[12px] font-medium px-2 py-1 shadow-md pointer-events-none">
                              {chipText}
                            </span>
                          ) : null}

                          {/* Left resize edge — grabs `startDate`. */}
                          {showResizeEdges ? (
                            <span
                              onPointerDown={(e) => {
                                startBarDrag(e, t, "resize-start");
                              }}
                              aria-label="Drag to change start date"
                              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-l-md hover:bg-black/15"
                            />
                          ) : null}

                          {/* Task-name label inside the bar removed —
                           *  the row's name column already shows it, so
                           *  the bar reads as a pure date span. */}

                          {/* Right resize edge — grabs `dueDate`. */}
                          {showResizeEdges ? (
                            <span
                              onPointerDown={(e) => {
                                startBarDrag(e, t, "resize-end");
                              }}
                              aria-label="Drag to change due date"
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-r-md hover:bg-black/15"
                            />
                          ) : null}
                        </div>
                      );
                    })()
                  : (() => {
                      /* Unscheduled row — the whole timeline is the
                       *  click/drag target. Hovering shows a vertical
                       *  cursor line + "Click or drag to schedule"
                       *  tooltip; clicking commits a one-day task on
                       *  that column; dragging commits a range. The
                       *  surface is `position: absolute, inset: 0` so
                       *  it covers the full row width on top of the
                       *  weekend stripes. */
                      const isThisRowDragCreating = dragCreate?.taskId === t.id;
                      const hoverX =
                        hoverRow?.taskId === t.id ? hoverRow.x : null;
                      // Snap the hover line to the centre of the day
                      // column the cursor is over, so the visual
                      // indicator matches what'll be committed.
                      const hoverDayIdx =
                        hoverX !== null ? pxToDayIndex(hoverX) : null;
                      const hoverLineLeft =
                        hoverDayIdx !== null
                          ? hoverDayIdx * DAY_W + DAY_W / 2
                          : null;

                      // Drag-create ghost — translucent bar spanning
                      // the drag range, snapped to whole-day columns.
                      let ghostLeft = 0;
                      let ghostWidth = 0;
                      if (isThisRowDragCreating) {
                        const a = dragCreate.originDayIdx;
                        const b = dragCreate.currentDayIdx;
                        const startIdx = Math.min(a, b);
                        const endIdx = Math.max(a, b);
                        ghostLeft = startIdx * DAY_W;
                        ghostWidth = (endIdx - startIdx + 1) * DAY_W;
                      }

                      const draggable = Boolean(onUpdateTask);

                      return (
                        <>
                          {/* Click / drag capture surface */}
                          <div
                            onPointerMove={(e) => {
                              if (isThisRowDragCreating) return;
                              if (!draggable) return;
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setHoverRow({
                                taskId: t.id,
                                x: e.clientX - rect.left,
                              });
                            }}
                            onPointerLeave={() => {
                              if (!isThisRowDragCreating) setHoverRow(null);
                            }}
                            onPointerDown={(e) => {
                              if (!draggable) return;
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              startDragCreate(e, t, rect);
                            }}
                            className={
                              "absolute inset-0 z-[1] " +
                              (draggable
                                ? "cursor-crosshair"
                                : "cursor-default")
                            }
                            aria-label={
                              draggable
                                ? `Click or drag on this row to schedule ${t.name}`
                                : `${t.name} — no dates set`
                            }
                            role={draggable ? "button" : undefined}
                          />

                          {/* Drag-create ghost bar */}
                          {isThisRowDragCreating ? (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 rounded-md flex items-center px-2 pointer-events-none z-[2]"
                              style={{
                                left: ghostLeft,
                                width: ghostWidth,
                                height: ROW_H - 12,
                                backgroundColor: barColor,
                                opacity: 0.7,
                                boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
                              }}
                              aria-hidden="true"
                            >
                              {ghostWidth > 60 ? (
                                <span className="text-[12px] font-medium text-white truncate leading-none drop-shadow-sm">
                                  {t.name}
                                </span>
                              ) : null}
                            </div>
                          ) : null}

                          {/* Hover indicator — vertical line + tooltip */}
                          {hoverLineLeft !== null && !isThisRowDragCreating ? (
                            <>
                              <div
                                className="absolute top-0 bottom-0 w-px bg-[color:var(--color-accent)] pointer-events-none z-[2] opacity-70"
                                style={{ left: hoverLineLeft }}
                                aria-hidden="true"
                              />
                              <div
                                className="absolute -top-7 z-[3] whitespace-nowrap rounded-md bg-[color:var(--color-ink-strong)] text-[color:var(--color-canvas)] text-[12px] font-medium px-2 py-1 shadow-md pointer-events-none"
                                style={{
                                  left: Math.max(4, hoverLineLeft - 70),
                                }}
                              >
                                Click or drag to schedule
                              </div>
                            </>
                          ) : null}
                        </>
                      );
                    })();

                // Precompute the off-screen chevron flags so the JSX
                // below reads cleanly. Both chevrons use CSS sticky
                // for positioning so the browser pins them at the
                // viewport edges without per-scroll JS updates.
                const chevBarLeftPx = span
                  ? xForDateTime(span.start.toISOString())
                  : 0;
                const chevBarRightPx = span
                  ? xForDateTime(span.end.toISOString()) + DAY_W
                  : 0;
                const showLeftChevron =
                  span &&
                  viewport.clientWidth > 0 &&
                  chevBarRightPx <= viewport.scrollLeft;
                const showRightChevron =
                  span &&
                  viewport.clientWidth > 0 &&
                  chevBarLeftPx >= viewport.scrollLeft + viewport.clientWidth;

                return (
                  <div
                    key={`tl-${t.id}`}
                    className="relative flex items-center border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
                    style={{ height: ROW_H }}
                  >
                    {/* Sticky-left off-screen indicator chevron. */}
                    {showLeftChevron ? (
                      <button
                        type="button"
                        onClick={() => {
                          rightPaneRef.current?.scrollTo({
                            left: Math.max(0, chevBarLeftPx - 40),
                            behavior: "smooth",
                          });
                        }}
                        title={`Jump to ${t.name}`}
                        aria-label={`Jump to ${t.name}`}
                        className="sticky shrink-0 z-10 flex items-center justify-center h-6 w-6 ml-1.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] cursor-pointer shadow-sm"
                        style={{ left: 6 }}
                      >
                        <ChevronLeft size={14} />
                      </button>
                    ) : null}
                    <div className="flex-1" />
                    {showRightChevron ? (
                      <button
                        type="button"
                        onClick={() => {
                          rightPaneRef.current?.scrollTo({
                            left: Math.max(0, chevBarLeftPx - 40),
                            behavior: "smooth",
                          });
                        }}
                        title={`Jump to ${t.name}`}
                        aria-label={`Jump to ${t.name}`}
                        className="sticky shrink-0 z-10 flex items-center justify-center h-6 w-6 mr-1.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] cursor-pointer shadow-sm"
                        style={{ right: 6 }}
                      >
                        <ChevronRight size={14} />
                      </button>
                    ) : null}
                    {/* The bar (or "Set dates" placeholder). It's
                     *  absolutely positioned so the flex chevrons
                     *  above don't push it sideways. */}
                    {barNode}
                    {/* The date-editor popover used to render here,
                     *  one per row. It now lives ONCE at the GanttView
                     *  level (below) and is anchored to whichever
                     *  trigger opened it — exact same `TaskDatePopover`
                     *  the List view uses, so the two pages feel
                     *  identical when adding dates. */}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Single TaskDatePopover, shared by every trigger inside the
         *  Gantt: a clicked bar, the left-pane Dates button, or the
         *  empty-row click target. `editingTask` is resolved from the
         *  task list so optimistic updates flow into the popover
         *  automatically. Commits go through `onUpdateTask` which the
         *  parent has wired to the existing task-update mutation, so
         *  the bar repositions immediately on each date pick. */}
        <TaskDatePopover
          open={editingTaskId !== null}
          triggerRef={popoverTriggerRef}
          startDate={
            editingTaskId
              ? ((allTasks ?? tasks).find((t) => t.id === editingTaskId)
                  ?.startDate ?? null)
              : null
          }
          dueDate={
            editingTaskId
              ? ((allTasks ?? tasks).find((t) => t.id === editingTaskId)
                  ?.dueDate ?? null)
              : null
          }
          startDateHasTime={
            editingTaskId
              ? ((allTasks ?? tasks).find((t) => t.id === editingTaskId)
                  ?.startDateHasTime ?? false)
              : false
          }
          dueDateHasTime={
            editingTaskId
              ? ((allTasks ?? tasks).find((t) => t.id === editingTaskId)
                  ?.dueDateHasTime ?? false)
              : false
          }
          initialField="start"
          onCommit={(patch) => {
            if (editingTaskId) onUpdateTask?.(editingTaskId, patch);
          }}
          onClose={() => setEditingTaskId(null)}
        />

        {/* Bar-colour picker — anchored to the row swatch the user
         *  clicked. Commits via the same `onUpdateTask` callback as
         *  every other Gantt edit, so the bar's new colour flows back
         *  through the optimistic update and the swatch / bar refresh
         *  instantly. */}
        {colorPickerTaskId ? (
          <GanttColorPicker
            anchorEl={colorPickerAnchorEl}
            currentColor={
              (allTasks ?? tasks).find((t) => t.id === colorPickerTaskId)
                ?.color ?? null
            }
            onPick={(nextColor) => {
              onUpdateTask?.(colorPickerTaskId, { color: nextColor });
              setColorPickerTaskId(null);
              setColorPickerAnchorEl(null);
            }}
            onClose={() => {
              setColorPickerTaskId(null);
              setColorPickerAnchorEl(null);
            }}
          />
        ) : null}

        {/* Fixed-to-viewport horizontal scrollbar (mirrors ClickUp's
         *  pattern). Rendered via portal to `document.body` so no
         *  parent's `overflow: hidden` can clip it. Visual is
         *  deliberately minimal — no border, no shadow, no opaque
         *  background — so it reads as a native scrollbar pinned to
         *  the viewport bottom rather than a separate UI panel.
         *  Position glues it exactly under the timeline column via
         *  the measured bounds. */}
        {footerBounds
          ? createPortal(
              <div
                ref={footerScrollbarRef}
                onScroll={(e) => {
                  if (!syncingScrollRef.current) {
                    syncingScrollRef.current = true;
                    if (rightPaneRef.current) {
                      rightPaneRef.current.scrollLeft =
                        e.currentTarget.scrollLeft;
                    }
                    requestAnimationFrame(() => {
                      syncingScrollRef.current = false;
                    });
                  }
                  // Mirror the right-pane infinite-scroll detection so
                  // dragging the fixed footer bar near an edge also
                  // extends the canvas.
                  onTimelineScroll(e);
                }}
                className="fixed z-30 overflow-x-auto"
                style={{
                  bottom: 0,
                  left: footerBounds.left,
                  width: footerBounds.width,
                }}
                aria-label="Timeline horizontal scrollbar"
              >
                <div
                  style={{ width: TIMELINE_W, height: 12 }}
                  aria-hidden="true"
                />
              </div>,
              document.body,
            )
          : null}
      </div>
    );
  }

  // ---------- Page ----------

  export function SpacePage() {
    const { spaceId, listId } = useParams();
    const navigate = useNavigate();
    const [view, setView] = useState("list");
    const userId = useAuthStore((s) => s.user?.id);
    const userRole = useAuthStore((s) => s.user?.role);
    // Statuses are tenant-wide and managed in the Workflows page (Company
    // Admin only). For non-admins the "Manage statuses" link is hidden so
    // the picker doesn't dangle a CTA the user can't act on.
    const canManageWorkflow = userRole === "company_admin";
    const onManageStatuses = canManageWorkflow
      ? () => navigate("/workflows")
      : null;
    // Favourites — split into two collections so the star at the top of
    // the page is context-aware. When the URL pins a list
    // (`/app/spaces/:spaceId/lists/:listId`), the star toggles the
    // LIST's favourite. Otherwise (Space overview / board / gantt / no
    // list selected) it toggles the SPACE's favourite. Previously the
    // star always wrote to the Space set even on a list URL, so users
    // who tried to pin a single list ended up with the whole space
    // pinned alongside it. See `useFavoriteLists` + `useFavoriteList
    // Meta` in space-prefs.js for the persistence shape.
    const spaceFavorites = useFavoriteSpaces(userId);
    const listFavorites = useFavoriteLists(userId);
    const [, setListFavMeta] = useFavoriteListMeta(userId);
    const favoriteScope = listId ? "list" : "space";
    const isFavorite =
      favoriteScope === "list"
        ? listFavorites.has(listId)
        : spaceId
          ? spaceFavorites.has(spaceId)
          : false;
    const qc = useQueryClient();
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [detailTaskId, setDetailTaskId] = useState(null);
    // Task whose Repeat/recurrence editor is open — driven by the list's
    // "Repeat" column cell (click the icon to set / edit recurrence inline).
    const [recurrenceTaskId, setRecurrenceTaskId] = useState(null);
    const [statusEditorOpen, setStatusEditorOpen] = useState(false);
    const [sopUploadOpen, setSopUploadOpen] = useState(false);
    // Header "+ Create Task" → opens the 3-method popup (Manual / From
    // SOP / AI from upload). Scoped to the current listId.
    const [createTaskOpen, setCreateTaskOpen] = useState(false);
    // Handle published by GanttView so the sub-toolbar's "Today" button can
    // scroll its canvas. Null whenever the Gantt isn't mounted, so the button
    // (which only renders on that view) safely no-ops.
    const ganttTodayRef = useRef(null);
    const [sopPreview, setSopPreview] = useState(null);
    /** Top-level task currently being broken down via the SOP-library /
     *  upload / manual workflow. Null when the dialog is closed. */

    const spaceQuery = useQuery({
      queryKey: ["space", spaceId],
      queryFn: async () => {
        const res = await api.get(`/spaces/${spaceId}`);
        return spaceSchema.parse(res.data);
      },
      enabled: Boolean(spaceId),
      retry: false,
    });
    const space = spaceQuery.data ?? null;

    // Auto-accept a Space invitation opened from the email button. The
    // `space_invitation` email's "Accept Invitation" button lands here with
    // `?acceptInvite=1`. A directly-invited (often cross-department) member
    // holds a PENDING SpaceMember row, so `/spaces/:id` 404s until they
    // accept — which otherwise only happens from the in-app Inbox. Firing
    // the accept here makes the email button actually join them, then we
    // strip the param and let the Space load. `respond()` is idempotent — it
    // no-ops when there's no pending invite (already accepted / not invited).
    const [searchParams, setSearchParams] = useSearchParams();
    const wantsInviteAccept = searchParams.get("acceptInvite") === "1";
    // Seed `true` when the param is present so the stale-space bounce below
    // waits for the accept to resolve instead of firing on the initial 404.
    const [acceptingInvite, setAcceptingInvite] = useState(wantsInviteAccept);
    const inviteAcceptFiredRef = useRef(false);
    useEffect(() => {
      if (!spaceId) return;
      if (!wantsInviteAccept) return;
      if (inviteAcceptFiredRef.current) return;
      inviteAcceptFiredRef.current = true;
      (async () => {
        try {
          await api.post(`/spaces/${spaceId}/members/respond`, {
            action: "accept",
          });
          toast.success("Invitation accepted.");
          // Await the invalidations so the Space refetch settles BEFORE we
          // clear `acceptingInvite` — otherwise the bounce effect could see a
          // still-erroring query and eject the just-joined member.
          await Promise.all([
            qc.invalidateQueries({ queryKey: ["space", spaceId] }),
            qc.invalidateQueries({ queryKey: ["spaces"] }),
            qc.invalidateQueries({ queryKey: ["workspaces"] }),
            qc.invalidateQueries({ queryKey: ["notifications", "me"] }),
            qc.invalidateQueries({ queryKey: ["notifications", "unread-count"] }),
          ]);
        } catch (err) {
          toast.error(extractApiError(err).message);
        } finally {
          // One-shot: drop the param so a refresh / back-nav doesn't re-fire.
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete("acceptInvite");
              return next;
            },
            { replace: true },
          );
          setAcceptingInvite(false);
        }
      })();
    }, [spaceId, wantsInviteAccept, qc, setSearchParams]);

    // Stale space URL — the space was deleted (by another user, or in
    // another tab) and the React Query refetch came back 404. Bounce
    // out to the app home so the user lands somewhere live instead of
    // being stuck on the "Space not found" empty state with a manual
    // "Back to Home" link. This is the LOCAL recovery that replaces
    // the old `window.location.reload()` hard-reload in
    // `RealtimeInvalidator` — the global reload was destructive (lost
    // unsaved state for every other user whenever ANYONE edited a
    // space). The new realtime path invalidates `['space', spaceId]`
    // softly; if the refetched query errors, this effect handles the
    // user-stranded edge case for the one tab that was viewing the
    // deleted space.
    useEffect(() => {
      if (!spaceId) return;
      if (!spaceQuery.isError) return;
      // Hold the bounce while an email-driven invite accept is in flight —
      // the 404 is expected (pending invite) and clears once we accept.
      if (acceptingInvite) return;
      navigate("/app", { replace: true });
    }, [spaceId, spaceQuery.isError, acceptingInvite, navigate]);

    const listQuery = useQuery({
      queryKey: ["list", spaceId, listId],
      queryFn: async () => {
        const res = await api.get(`/spaces/${spaceId}/lists/${listId}`);
        return listSummary.parse(res.data);
      },
      enabled: Boolean(spaceId && listId),
      retry: false,
    });
    const list = listQuery.data ?? null;

    // Folders (Projects) in this Space — used to surface the current List's
    // parent Project in the page heading (Space → Project → List).
    const foldersQuery = useQuery({
      queryKey: ["folders", spaceId],
      queryFn: async () => {
        const res = await api.get(`/spaces/${spaceId}/folders`);
        return folderListResponse.parse(res.data);
      },
      enabled: Boolean(spaceId),
    });
    const currentProject = list?.folderId
      ? ((foldersQuery.data?.data ?? []).find((f) => f.id === list.folderId) ??
        null)
      : null;

    /** What this page calls itself: the Project name when the open list sits
     *  in one, else the list's own name, else the Space. The <h1>, the
     *  favourite star's stored label and its toast ALL read this so they can't
     *  disagree. They used to: the heading showed the Project ("Project1")
     *  while the star saved the raw list name, so favouriting from a project
     *  page filled the sidebar with identical "Tasks List" entries that
     *  matched nothing the user had clicked (QA). */
    const headerLabel = currentProject
      ? currentProject.name
      : (list?.name ?? space?.name ?? "");

    // Stale list URL — the list was deleted (or never existed). Bounce to the
    // space root, which will pick another list or show the empty state.
    // BUT if the SPACE itself is gone too (spaceQuery errored — e.g. the whole
    // Space was just deleted), do NOT bounce back to the deleted space: that
    // re-mounts it, its space-error effect navigates to /app, the route
    // re-renders, the list query errors again… and the two effects ping-pong
    // for a few seconds (the "page blinks on delete" bug). Let the
    // spaceQuery.isError effect above take it to /app in one hop instead.
    useEffect(() => {
      if (!spaceId || !listId) return;
      if (!listQuery.isError) return;
      if (spaceQuery.isError) return;
      navigate(`/app/spaces/${spaceId}`, { replace: true });
    }, [spaceId, listId, listQuery.isError, spaceQuery.isError, navigate]);

    // Lists in this space — drives the auto-redirect to the first list when
    // the URL is just /app/spaces/:spaceId (e.g. just-created spaces). Every
    // Space has at least one List ("List", auto-created on Space create).
    const listsQuery = useQuery({
      queryKey: ["lists", spaceId],
      queryFn: async () => {
        const res = await api.get(`/spaces/${spaceId}/lists`);
        return listListResponse.parse(res.data);
      },
      enabled: Boolean(spaceId),
    });
    // Auto-redirect from /app/spaces/:spaceId → /app/spaces/:spaceId/lists/:listId
    // once the lists query has resolved. Depending on `listsQuery.data` (rather
    // than a fresh fallback array each render) keeps the effect from firing on
    // every render, which previously caused the page to flicker for a couple
    // of seconds right after creating a new Space.
    useEffect(() => {
      if (!spaceId) return;
      if (listId) return;
      if (listsQuery.isLoading) return;
      const first = listsQuery.data?.data?.[0];
      if (first)
        navigate(`/app/spaces/${spaceId}/lists/${first.id}`, { replace: true });
    }, [spaceId, listId, listsQuery.isLoading, listsQuery.data, navigate]);

    const tasksQuery = useQuery({
      queryKey: ["tasks", listId],
      queryFn: async () => {
        const res = await api.get(`/lists/${listId}/tasks`);
        return taskListResponse.parse(res.data);
      },
      enabled: Boolean(listId),
    });
    const tasks = tasksQuery.data?.data ?? [];

    // Deep-link support — a notification/email link of the form
    // `…/lists/:listId?task=:taskId` opens that task's (or subtask's)
    // detail panel once the list has loaded. The ref guard fires it once
    // so the user can close the panel without it springing back open.
    const [deepLinkParams, setDeepLinkParams] = useSearchParams();
    const deepLinkOpenedRef = useRef(false);
    useEffect(() => {
      if (deepLinkOpenedRef.current) return;
      const wanted = deepLinkParams.get("task");
      if (!wanted) return;
      if (!tasks.some((t) => t.id === wanted)) return;
      deepLinkOpenedRef.current = true;
      setDetailTaskId(wanted);
    }, [deepLinkParams, tasks]);

    // Aggregator that backs the Overview tab. It USED to be Space-wide
    // unconditionally, which meant two Lists in one Project — and two
    // Projects in one Space — all reported the same merged KPIs. It is now
    // scoped to the open List, matching the List / Board / Gantt tabs, so
    // each List reports only its own work. `staleTime: 30s` keeps the
    // request quiet during rapid view-switching without holding stale
    // numbers for too long. The shape isn't passed through a zod schema
    // (yet) — kept loose so the response can evolve without a coordinated
    // frontend release.
    //
    // `listId` is in the key as well as the request: without it React Query
    // would hand List B the numbers it cached for List A while B's fetch is
    // still in flight — the same cross-List merge, just briefer.
    const overviewQuery = useQuery({
      queryKey: ["space-overview", spaceId, listId ?? "all"],
      queryFn: async () => {
        const res = await api.get(`/spaces/${spaceId}/overview`, {
          params: listId ? { listId } : undefined,
        });
        return res.data;
      },
      enabled: Boolean(spaceId),
      staleTime: 30_000,
    });

    // Assignee picker roster — scoped to who can actually open THIS Space
    // (its department members, admins, the owner, invited collaborators), NOT
    // the whole workspace. Other departments would otherwise appear here and
    // could be assigned tasks they can't see.
    const membersQuery = useQuery({
      queryKey: ["assignable-members", space?.id],
      queryFn: async () => {
        const res = await api.get(`/spaces/${space?.id}/assignable-members`);
        return workspaceMembersResponse.parse(res.data);
      },
      enabled: Boolean(space?.id),
    });

    // For a private Space, the assignee picker shows only people who can
    // actually see the Space: the creator, anyone explicitly invited via
    // the Sharing dialog, plus workspace admins (always have access).
    // Public Spaces fall back to the full workspace member list.
    const spaceMembersQuery = useQuery({
      queryKey: ["space-members", space?.id],
      queryFn: async () => {
        const res = await api.get(`/spaces/${space?.id}/members`);
        return spaceMembersResponse.parse(res.data);
      },
      // Enabled for ALL spaces (not just private) so the member-profile
      // panel can show each member's EFFECTIVE access level inside this Space
      // (the per-Space override / inherited level) instead of their broader
      // workspace access. Public spaces still derive `members` from the
      // workspace roster below; this only feeds the access-level map.
      enabled: Boolean(space?.id),
    });
    // Memoise the raw workspace-members list so it keeps a stable reference
    // across renders — otherwise downstream useMemos / queries see a fresh
    // array on every render and cascade extra work.
    const allWorkspaceMembers = useMemo(
      () => membersQuery.data?.data ?? [],
      [membersQuery.data],
    );
    // Effective per-Space access level by user — the per-Space override (or
    // the inherited level on a public Space) from /spaces/:id/members. The
    // member-profile panel reads this so a "View only" Space member doesn't
    // display as "Edit" just because their workspace access is Edit.
    const spaceAccessByUser = useMemo(() => {
      const map = new Map();
      for (const sm of spaceMembersQuery.data?.data ?? []) {
        if (sm.accessLevel) map.set(sm.userId, sm.accessLevel);
      }
      return map;
    }, [spaceMembersQuery.data]);
    const members = useMemo(() => {
      // Override each member's accessLevel with their effective Space access
      // (falling back to workspace access) so it stays consistent with the
      // Sharing & Permissions dialog wherever `members` is consumed. The Space
      // OWNER is always "Full edit" — mirrors the backend's owner promotion
      // (effectiveAccessLevel) and guards the case where the owner has no
      // explicit /spaces/:id/members row to read from.
      const ownerId = space?.createdById ? String(space.createdById) : null;
      const withSpaceAccess = (m) => ({
        ...m,
        accessLevel:
          ownerId && String(m.userId) === ownerId
            ? "full"
            : spaceAccessByUser.get(m.userId) ?? m.accessLevel,
      });
      if (!space?.isPrivate) return allWorkspaceMembers.map(withSpaceAccess);
      const allowed = new Set();
      if (space.createdById) allowed.add(space.createdById);
      for (const m of allWorkspaceMembers) {
        if (m.role === "admin") allowed.add(m.userId);
        // Externals are pre-vetted by the backend (their grant already covers
        // this Space), so they stay assignable even on a private Space.
        if (m.external) allowed.add(m.userId);
      }
      // Pending invitees aren't assignable yet — they can't see the Space
      // until they Accept in their Inbox. Only count 'accepted' rows.
      for (const sm of spaceMembersQuery.data?.data ?? []) {
        if (sm.status === "accepted") allowed.add(sm.userId);
      }
      return allWorkspaceMembers
        .filter((m) => allowed.has(m.userId))
        .map(withSpaceAccess);
    }, [allWorkspaceMembers, space, spaceMembersQuery.data, spaceAccessByUser]);

    // Server-supplied display info for EVERY assignee across the loaded tasks,
    // keyed by userId. Provided via AssigneeDisplayContext so the assignee
    // avatars resolve even for members outside the scoped roster above (fixes
    // the anonymous "+1"). Deduped; first occurrence wins.
    const assigneeDisplayIndex = useMemo(() => {
      const map = new Map();
      for (const t of tasks) {
        for (const a of t.assignees ?? []) {
          if (a?.userId && !map.has(a.userId)) map.set(a.userId, a);
        }
      }
      return map;
    }, [tasks]);

    const statuses = space?.taskStatuses ?? [];

    // ----- Filter / Customize state (Phase 3) -----
    // Default: no filters, all columns visible. Filters are AND across
    // categories and OR within a category (multi-select).
    // Persisted per user + list (see useTaskFilters) so the filter survives
    // a refresh and each list keeps its own. Stored as arrays; bridged here
    // to the Set-based API the FilterPill and matchesFilters already use.
    // The setters are only ever called with a direct value (a Set, or the
    // due-range string) — see FilterPill — so no functional-updater path is
    // needed. Switching lists changes listId → useJsonObject re-reads that
    // list's saved filter automatically.
    const [storedFilters, setStoredFilters] = useTaskFilters(userId, listId);
    const filterAssignees = useMemo(
      () => new Set(storedFilters.assignees),
      [storedFilters.assignees],
    );
    const filterPriorities = useMemo(
      () => new Set(storedFilters.priorities),
      [storedFilters.priorities],
    );
    const filterDueRange = storedFilters.dueRange;
    const setFilterAssignees = useCallback(
      (nextSet) =>
        setStoredFilters((p) => ({ ...p, assignees: Array.from(nextSet) })),
      [setStoredFilters],
    );
    const setFilterPriorities = useCallback(
      (nextSet) =>
        setStoredFilters((p) => ({ ...p, priorities: Array.from(nextSet) })),
      [setStoredFilters],
    );
    const setFilterDueRange = useCallback(
      (nextRange) => setStoredFilters((p) => ({ ...p, dueRange: nextRange })),
      [setStoredFilters],
    );
    // Free-text task-name search (client-side, current list only). Applied in
    // matchesFilters so every task view (List / Board) honours the shared
    // sub-toolbar search box. Deliberately NOT part of activeFilterCount — the
    // box is always visible, not a toggle inside the Filter popover.
    const [filterQuery, setFilterQuery] = useState("");
    // Column visibility — persisted per-user via localStorage so toggles
    // in the Customize panel survive reloads + tab switches. (Was a plain
    // useState before, which reset every time the user navigated away.)
    //
    // The List view and the Gantt view keep INDEPENDENT column-visibility
    // state: the List view's table benefits from a richer default set
    // (Assignee / Start date / Due date / Priority), while the Gantt's
    // left pane defaults to just Due date because the timeline bars
    // already carry start/end / colour cues. Each view has its own
    // localStorage key so toggling Priority in the Gantt doesn't
    // unexpectedly affect the List view.
    const [rawColumns, setColumns] = useTaskColumns(userId);
    const [rawGanttColumns, setGanttColumns] = useGanttColumns(userId);
    // Personal (owner-only) Spaces have no collaborators, so Assignee and
    // Created By are meaningless there — force those columns hidden across
    // List / Board / Gantt regardless of the saved preference, and drop them
    // from the Customize popover + Task Detail dialog below.
    const isPersonalSpace = Boolean(space?.isPersonal);
    const columns = isPersonalSpace
      ? { ...rawColumns, assignee: false, createdBy: false }
      : rawColumns;
    const ganttColumns = isPersonalSpace
      ? { ...rawGanttColumns, assignee: false, createdBy: false }
      : rawGanttColumns;
    // Phase 4 — grouping + subtasks.

    const [groupMode, setGroupMode] = useState("status");
    const [groupDir, setGroupDir] = useState("asc");
    const [showSubtasks, setShowSubtasks] = useState(true);
    const [expandedParents, setExpandedParents] = useState(new Set());
    function toggleParent(id) {
      const next = new Set(expandedParents);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setExpandedParents(next);
    }

    // Deep-link "scroll to task" — arriving with `?focusTask=<id>` (e.g.
    // from the Team Tasks list) switches to List view, expands the task's
    // ancestor chain so a nested subtask is actually rendered, then scrolls
    // it into view and flashes it. Distinct from `?task=<id>` above, which
    // opens the detail panel (used by notification links). Fires once.
    // Holds the focusTask id already handled for the CURRENT presence of the
    // param (NOT a plain "done" boolean). Reset to null whenever the param is
    // absent — a fresh load, or just after a flash strips it — so the NEXT
    // focusTask fires even without a remount. That's the "already on this list
    // and I click another task notification" case: same route, no unmount, so a
    // latching boolean would block every click after the first.
    const focusedTaskRef = useRef(null);
    useEffect(() => {
      const wanted = deepLinkParams.get("focusTask");
      if (!wanted) {
        focusedTaskRef.current = null;
        return;
      }
      if (tasks.length === 0) return;
      // Already flashed THIS param instance — guards StrictMode / task-refetch
      // re-runs. Set LATE (in tryScroll) so StrictMode's cleanup→re-run still
      // reaches the flash.
      if (focusedTaskRef.current === wanted) return;
      const byId = new Map(tasks.map((t) => [String(t.id), t]));
      if (!byId.has(String(wanted))) return;
      // NB: do NOT mark "done" here. React StrictMode (dev) runs effects
      // mount→cleanup→mount; setting the ref now would let the cleanup
      // cancel the in-flight scroll poll and then short-circuit the second
      // run — which is exactly why it only worked after a manual refresh.
      // We mark it done inside tryScroll, AFTER the scroll actually lands.

      setView("list");
      // Walk up the parent chain and expand every ancestor so the target
      // row (which may be a deep subtask) is mounted before we scroll.
      const ancestors = [];
      let cur = byId.get(String(wanted));
      while (cur?.parentTaskId && byId.has(String(cur.parentTaskId))) {
        ancestors.push(String(cur.parentTaskId));
        cur = byId.get(String(cur.parentTaskId));
      }
      if (ancestors.length > 0) {
        setShowSubtasks(true);
        setExpandedParents((prev) => {
          const next = new Set(prev);
          for (const a of ancestors) next.add(a);
          return next;
        });
      }
      // The expanded rows render asynchronously, and on SPA navigation the
      // List can take several frames longer to mount than on a hard refresh
      // (which is why it only worked after a manual refresh before). So poll
      // for the row instead of guessing a single delay: retry until it
      // appears, scroll + flash, then re-scroll once after layout settles in
      // case late-rendering rows above it shift the position.
      let cancelled = false;
      const timers = [];
      let attempts = 0;
      const scrollTo = (el) =>
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.querySelector(`[data-focus-id="${wanted}"]`);
        if (el) {
          scrollTo(el);
          // Blink/flash the row for ~4s so the user can locate it. Shared with
          // every other deep-link surface via lib/focusFlash. NOTE: we do NOT
          // keep the flash-cancel handle — stripping `focusTask` below re-runs
          // this effect, and cancelling in the cleanup would kill the flash we
          // just started. The WAAPI flash self-ends (~4s) and is GC'd with the
          // element on unmount, so there's nothing to leak.
          flashElement(el);
          // Re-scroll once after layout settles (late rows above can shift it).
          timers.push(
            setTimeout(() => {
              if (!cancelled) scrollTo(el);
            }, 350),
          );
          focusedTaskRef.current = wanted; // mark THIS id done, only after a real scroll
          // Strip the transient param (replace, no history entry) so a refresh
          // or Back nav doesn't re-flash and the URL stays clean. The re-run
          // this triggers no-ops (focusedTaskRef is now set); the flash already
          // running on the element is imperative (WAAPI) and unaffected.
          const stripped = new URLSearchParams(deepLinkParams);
          stripped.delete("focusTask");
          setDeepLinkParams(stripped, { replace: true });
          return;
        }
        if (attempts++ < 40) timers.push(setTimeout(tryScroll, 120)); // up to ~5s
      };
      timers.push(setTimeout(tryScroll, 100));
      return () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      };
    }, [deepLinkParams, tasks]);

    const activeFilterCount =
      filterAssignees.size +
      filterPriorities.size +
      (filterDueRange === "all" ? 0 : 1);

    function matchesFilters(t) {
      // Free-text name search (client-side): trimmed, case-insensitive
      // substring on the task name. Driven by the sub-toolbar SearchField.
      const q = filterQuery.trim().toLowerCase();
      if (q && !(t.name ?? "").toLowerCase().includes(q)) return false;
      if (filterAssignees.size > 0) {
        const ids = t.assigneeIds ?? [];
        // A task matches the filter if ANY of its assignees is selected, OR if
        // the user explicitly filtered for "Unassigned" and the task has none.
        const matchedSomeone = ids.some((id) => filterAssignees.has(id));
        const matchedUnassigned =
          ids.length === 0 && filterAssignees.has("__unassigned__");
        if (!matchedSomeone && !matchedUnassigned) return false;
      }
      if (filterPriorities.size > 0) {
        const p = t.priority ?? "none";
        if (!filterPriorities.has(p)) return false;
      }
      if (filterDueRange !== "all") {
        if (filterDueRange === "no_due") {
          if (t.dueDate) return false;
        } else {
          if (!t.dueDate) return false;
          // Calendar-day offset in the tenant display timezone (0 = today,
          // <0 = overdue) so date-only dues bucket on the day everyone sees.
          const offset = dayOffsetFromToday(t.dueDate);
          if (offset == null) return false;
          if (filterDueRange === "overdue") {
            // Shared definition — a task with an explicit due TIME is overdue
            // the moment that time passes, not only from the next calendar
            // day. This branch used the day offset alone, so a task due 09:00
            // today was still "not overdue" at 17:00 while the API and the
            // team stats both counted it (part of DF_013 / Mantis 22120).
            if (!isPastDueNow(t, offset)) return false;
          } else if (filterDueRange === "today") {
            if (offset !== 0) return false;
          } else if (filterDueRange === "this_week") {
            if (offset < 0 || offset > 6) return false;
          } else if (filterDueRange === "this_month") {
            if (offset < 0 || offset > 30) return false;
          }
        }
      }
      return true;
    }

    // Search should also match SUBTASKS. A task is kept if it matches the active
    // filters/search OR one of its descendants does — so a matching subtask
    // under a non-matching parent isn't hidden with the parent. Ancestor
    // inclusion only kicks in while a search query is active, so the structured
    // filters (assignee / priority / due) keep their per-task behaviour.
    // `searchAncestorIds` = the parents pulled in this way, so search can
    // auto-expand them and reveal the matching subtask.
    const hasSearchQuery = filterQuery.trim().length > 0;
    const { keepIds, searchAncestorIds } = useMemo(() => {
      const byId = new Map(tasks.map((t) => [t.id, t]));
      const keep = new Set();
      const ancestors = new Set();
      for (const t of tasks) {
        if (!matchesFilters(t)) continue;
        keep.add(t.id);
        if (hasSearchQuery) {
          let cur = t;
          while (cur?.parentTaskId) {
            keep.add(cur.parentTaskId);
            ancestors.add(cur.parentTaskId);
            cur = byId.get(cur.parentTaskId);
          }
        }
      }
      return { keepIds: keep, searchAncestorIds: ancestors };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasks, filterAssignees, filterPriorities, filterDueRange, filterQuery]);

    // While searching, force subtasks visible + auto-expand the ancestors of
    // matches so a matching subtask actually renders (subtasks are otherwise
    // gated behind the Subtasks toggle + the parent being expanded).
    const effectiveShowSubtasks = showSubtasks || hasSearchQuery;
    const effectiveExpandedParents = hasSearchQuery
      ? new Set([...expandedParents, ...searchAncestorIds])
      : expandedParents;

    // Statuses to render — every status (including any 'closed'-category
    // ones) is always shown now that the Closed toggle has been removed.
    const displayedStatuses = statuses;

    // Top-level vs subtasks. Subtasks are always carried in `tasks` but only
    // rendered when `showSubtasks` is on.
    const topLevelTasks = useMemo(
      () => tasks.filter((t) => !t.parentTaskId),
      [tasks],
    );
    const subtasksByParent = useMemo(() => {
      const map = new Map();
      for (const t of tasks) {
        if (!t.parentTaskId) continue;
        if (!keepIds.has(t.id)) continue;
        let bucket = map.get(t.parentTaskId);
        if (!bucket) {
          bucket = [];
          map.set(t.parentTaskId, bucket);
        }
        bucket.push(t);
      }
      // Sort each parent's subtasks by sortOrder so an optimistic drag
      // reorder repositions them immediately (instead of snapping back).
      for (const bucket of map.values()) bucket.sort(bySortOrder);
      return map;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasks, filterAssignees, filterPriorities, filterDueRange, filterQuery]);

    /** Synthetic SpaceTaskStatus for non-status group modes — gives ListSection
     *  a pill colour + label without bespoke header rendering per mode. */
    const groups = useMemo(() => {
      // Sort by sortOrder so an optimistic drag reorder (which only
      // rewrites sortOrder values) repositions the row immediately rather
      // than snapping back. `.filter` already returns a fresh array, so
      // the in-place `.sort` is safe.
      const visibleParents = topLevelTasks
        .filter((t) => keepIds.has(t.id))
        .sort(bySortOrder);

      if (groupMode === "none") {
        return [
          {
            key: "none:all",
            pill: { category: "active", name: "All", color: "#94a3b8" },
            parents: visibleParents,
          },
        ];
      }

      if (groupMode === "status") {
        const ordered =
          groupDir === "asc"
            ? displayedStatuses
            : [...displayedStatuses].reverse();
        return ordered.map((s) => ({
          key: `status:${s.name}`,
          pill: s,
          parents: visibleParents.filter((t) => t.status === s.name),
        }));
      }

      // priority
      const out = [];
      const priorityOrder =
        groupDir === "asc"
          ? TASK_PRIORITIES
          : [...TASK_PRIORITIES].slice().reverse();
      for (const p of priorityOrder) {
        out.push({
          key: `priority:${p}`,
          pill: {
            category: "active",
            name: PRIORITY_META[p].label,
            color: PRIORITY_META[p].color,
          },
          parents: visibleParents.filter((t) => t.priority === p),
        });
      }
      const noPriority = {
        key: "priority:none",
        pill: {
          category: "not_started",
          name: "No priority",
          color: "#94a3b8",
        },
        parents: visibleParents.filter((t) => !t.priority),
      };
      if (groupDir === "asc") out.push(noPriority);
      else out.unshift(noPriority);
      return out;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      groupMode,
      groupDir,
      topLevelTasks,
      displayedStatuses,
      filterAssignees,
      filterPriorities,
      filterDueRange,
      filterQuery,
    ]);

    // Backwards compat for Board view (only groups by status).
    const groupedByStatus = useMemo(() => {
      const map = new Map();
      for (const s of displayedStatuses) map.set(s.name, []);
      for (const t of topLevelTasks) {
        if (!map.has(t.status)) continue;
        if (!keepIds.has(t.id)) continue;
        map.get(t.status)?.push(t);
      }
      // Sort each column by sortOrder so a within-column drag-reorder
      // (which rewrites sortOrder VALUES, not array order) repositions the
      // card immediately — same `bySortOrder` ordering the List/Gantt use.
      for (const arr of map.values()) arr.sort(bySortOrder);
      return map;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      displayedStatuses,
      topLevelTasks,
      filterAssignees,
      filterPriorities,
      filterDueRange,
      filterQuery,
    ]);

    const createMutation = useMutation({
      mutationFn: async (input) => {
        if (!listId) throw new Error("No list context.");
        const res = await api.post(`/lists/${listId}/tasks`, input);
        return res.data;
      },
      onSuccess: (_data, input) => {
        void qc.invalidateQueries({ queryKey: ["tasks", listId] });
        // Keep the Space Overview tab's whole-Space counts in sync — it's a
        // separate always-mounted query, so without this its totals stay
        // stale until staleTime lapses. Prefix key matches the active space.
        void qc.invalidateQueries({ queryKey: ["space-overview"] });
        // Auto-expand the parent so the user sees the freshly added subtask.
        if (input.parentTaskId) {
          setExpandedParents((prev) => {
            const next = new Set(prev);
            next.add(input.parentTaskId);
            return next;
          });
        }
      },
      onError: (err) => {
        // Plan-cap hit (maxTasks): the global API interceptor already shows the
        // single amber "upgrade your plan" warning, so suppress our own toast.
        if (isPlanLimitError(err)) return;
        toast.error(extractApiError(err).message);
      },
    });

    const updateMutation = useMutation({
      mutationFn: async (input) => {
        if (!listId) throw new Error("No list context.");
        await api.patch(`/lists/${listId}/tasks/${input.id}`, input.patch);
      },
      // Optimistic update: write the patch into the cache before the network
      // call so list/board cells (assignee, due date, status, priority, etc.)
      // flip instantly. Without this, a reassign or status change waits ~200ms
      // for the round-trip + refetch and feels broken — particularly visible on
      // the assignee picker right after inviting a new member.
      onMutate: async (input) => {
        await qc.cancelQueries({ queryKey: ["tasks", listId] });
        const prev = qc.getQueryData(["tasks", listId]);
        qc.setQueryData(["tasks", listId], (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((t) =>
              t.id === input.id ? { ...t, ...input.patch } : t,
            ),
          };
        });
        return { prev };
      },
      onError: (err, _vars, context) => {
        if (context?.prev) {
          qc.setQueryData(["tasks", listId], context.prev);
        }
        // Dedupe so a blocked edit shows ONE toast, not a pile-up. A 403 is a
        // permission denial → reuse the shared id so it collapses with any
        // frontend pre-check toast for the same action; other errors dedupe by
        // message so rapid repeats (several cells) don't stack either.
        const { message } = extractApiError(err);
        const id =
          err?.response?.status === 403
            ? EDIT_DENIED_TOAST_ID
            : `task-update:${message}`;
        toast.error(message, { id });
      },
      onSettled: (_data, _err, variables) => {
        void qc.invalidateQueries({ queryKey: ["tasks", listId] });
        // Status / due-date / assignee / priority edits all flow through
        // here (incl. board drag-to-column) — refresh the Overview counts.
        void qc.invalidateQueries({ queryKey: ["space-overview"] });
        // Refresh the open task's Activity timeline with the change just made.
        void qc.invalidateQueries({ queryKey: ["task-history"] });
        // A status change can award/reverse points — refresh the Leaderboard /
        // Performance / My-Day points too, so they update without a manual
        // reload even when the realtime socket isn't connected.
        if (variables?.patch?.status !== undefined) invalidateGamification(qc);
      },
    });

    /**
     * Centralised task / subtask edit. Validates the name client-side so an
     * inline rename to fewer than 2 characters shows the SAME message as the
     * Create Task dialog ("Task Name must be at least 2 characters.")
     * instead of the backend's generic "Validation failed." — then applies
     * the patch (optimistically) via `updateMutation`.
     */
    function commitTaskUpdate(id, patch) {
      if (
        patch &&
        Object.prototype.hasOwnProperty.call(patch, "name") &&
        String(patch.name ?? "").trim().length < 2
      ) {
        toast.error("Task Name must be at least 2 characters.");
        return;
      }
      // Same reasoning as the 2-character rule above, for the other end of
      // the range. Callers that own an input (NameCell, the quick-add and
      // subtask composers) flag this themselves so the text stays on screen
      // to be trimmed; this backstop covers every other path into a rename.
      if (patch && Object.prototype.hasOwnProperty.call(patch, "name")) {
        const tooLong = taskNameLengthError(patch.name);
        if (tooLong) {
          toast.error(tooLong);
          return;
        }
      }
      updateMutation.mutate({ id, patch });
    }

    /**
     * Drag-and-drop reorder mutation for the List view. The List view's
     * SortableContext fires this when the user drops a task at a new
     * position within the same status group. The client owns the sparse
     * sortOrder numbering scheme (see `computeReorderUpdates` below) so
     * a typical drag only writes ONE row, not the whole bucket.
     *
     * Optimistic update reorders the React Query cache in place so the
     * row settles into its new slot immediately; on error we restore
     * the previous order and surface a toast.
     */
    const reorderMutation = useMutation({
      mutationFn: async (updates) => {
        if (!listId) throw new Error("No list context.");
        if (!Array.isArray(updates) || updates.length === 0) return;
        await api.post(`/lists/${listId}/tasks/reorder`, { updates });
      },
      onMutate: async (updates) => {
        await qc.cancelQueries({ queryKey: ["tasks", listId] });
        const prev = qc.getQueryData(["tasks", listId]);
        // Splice the new sortOrders into the cached list. The actual
        // visual ordering is computed off `sortOrder` on every render
        // (see the `groups` useMemo), so updating these values is
        // enough to flip the row's slot without rebuilding the array.
        qc.setQueryData(["tasks", listId], (old) => {
          if (!old) return old;
          const map = new Map(updates.map((u) => [u.taskId, u.sortOrder]));
          return {
            ...old,
            data: old.data.map((t) =>
              map.has(t.id) ? { ...t, sortOrder: map.get(t.id) } : t,
            ),
          };
        });
        return { prev };
      },
      onError: (err, _vars, context) => {
        if (context?.prev) {
          qc.setQueryData(["tasks", listId], context.prev);
        }
        toast.error(extractApiError(err).message);
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: ["tasks", listId] });
      },
    });

    // Board DnD: MouseSensor keeps the desktop behaviour exactly — a small
    // activation distance so plain clicks on a card don't trigger a drag.
    // TouchSensor is delay-based so a horizontal SWIPE scrolls the board
    // (columns overflow on mobile) while a press-and-hold starts a drag.
    const sensors = useSensors(
      useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
      useSensor(TouchSensor, {
        activationConstraint: { delay: 200, tolerance: 8 },
      }),
    );
    const [activeBoardTask, setActiveBoardTask] = useState(null);
    function onBoardDragStart(event) {
      const data = event.active.data.current;
      if (!data) return;
      const t = tasks.find((x) => x.id === data.taskId) ?? null;
      setActiveBoardTask(t);
    }
    function onBoardDragEnd(event) {
      setActiveBoardTask(null);
      const drag = event.active.data.current;
      const drop = event.over?.data.current;
      if (!drag || !drop) return;
      // The drop target is EITHER a column (carries `statusName`) or
      // another card (carries that card's `fromStatus` + `taskId`).
      const targetStatus = drop.statusName ?? drop.fromStatus;
      if (targetStatus == null) return;
      const overTaskId = drop.taskId ?? null;

      if (drag.fromStatus !== targetStatus) {
        // Cross-column → change status (optimistic, then persist).
        qc.setQueryData(["tasks", listId], (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((t) =>
              t.id === drag.taskId ? { ...t, status: targetStatus } : t,
            ),
          };
        });
        updateMutation.mutate({
          id: drag.taskId,
          patch: { status: targetStatus },
        });
        return;
      }

      // Same column → reorder by sortOrder (cards dropped on a sibling
      // card). `groupedByStatus` is sorted by sortOrder so the move maps
      // straight onto the same sparse-renumber the List/Gantt use.
      if (!overTaskId || overTaskId === drag.taskId) return;
      const colTasks = groupedByStatus.get(targetStatus) ?? [];
      const oldIdx = colTasks.findIndex((t) => t.id === drag.taskId);
      const newIdx = colTasks.findIndex((t) => t.id === overTaskId);
      if (oldIdx < 0 || newIdx < 0) return;
      const updates = computeReorderUpdates(
        arrayMove(colTasks, oldIdx, newIdx),
        drag.taskId,
      );
      if (updates.length > 0) reorderMutation.mutate(updates);
    }

    // List view DnD — ONE context spanning every group so a row can be
    // dragged ACROSS groups (TO DO → IN PROGRESS, Urgent → High, …), not
    // just reordered inside its own. Lives here rather than in ListSection
    // because dnd-kit does not support nested DndContexts — a per-section
    // context physically confined every drag to its own status group.
    // Sensor tuning matches the old per-section setup: `distance: 4` picks
    // the drag up as soon as the grip handle moves without firing on a
    // plain click; `tolerance: 5` absorbs post-activation micro-movements.
    const listSensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: { distance: 4, tolerance: 5, delay: 0 },
      }),
      // Touch: delay-based so a horizontal swipe scrolls the List instead of
      // grabbing a row; press-and-hold (200ms) still starts a drag-reorder.
      useSensor(TouchSensor, {
        activationConstraint: { delay: 200, tolerance: 8 },
      }),
    );
    // Active drag id powers the DragOverlay. Holding the task object would
    // go stale if the tasks array remounted mid-drag; the id stays valid.
    const [listDragId, setListDragId] = useState(null);
    // True when the LAST drop crossed into another group — read by the
    // DragOverlay's dropAnimation keyframes (a ref, not state, because the
    // animation config is consulted right after onDragEnd runs and must
    // see the fresh value without waiting for a re-render).
    const listCrossDropRef = useRef(false);
    // The dragged row can be a top-level task OR a subtask — resolve the
    // floating preview from whichever bucket holds the id.
    const listDragTask = useMemo(() => {
      if (!listDragId) return null;
      const parent = topLevelTasks.find((t) => t.id === listDragId);
      if (parent) return parent;
      for (const subs of subtasksByParent.values()) {
        const hit = subs.find((s) => s.id === listDragId);
        if (hit) return hit;
      }
      return null;
    }, [listDragId, topLevelTasks, subtasksByParent]);
    const listDragStatus = listDragTask
      ? (statuses.find((s) => s.name === listDragTask.status) ?? null)
      : null;

    // Visible top-level ids + a subtask→siblings lookup powering the
    // grouped collision detection below.
    const listParentIdSet = useMemo(
      () => new Set(groups.flatMap((g) => g.parents.map((t) => t.id))),
      [groups],
    );
    const listSubSiblingSet = useMemo(() => {
      const map = new Map();
      for (const subs of subtasksByParent.values()) {
        const ids = new Set(subs.map((s) => s.id));
        for (const s of subs) map.set(s.id, ids);
      }
      return map;
    }, [subtasksByParent]);

    // Collision rules:
    //   - a top-level row may drop on any top-level row (ANY group) or on
    //     another group's body (`section:<key>` droppable — how a drop
    //     into an EMPTY group resolves). The row's OWN group body is
    //     excluded so in-group reorders always resolve to a sibling row,
    //     never the tall container whose centre sits mid-section.
    //   - a subtask stays confined to its sibling bucket: parents and each
    //     parent's subtasks live in separate SortableContexts, so without
    //     this filter a parent dragged past an expanded parent's subtask
    //     rows would resolve `over` to the wrong context and no-op.
    const listCollision = useMemo(
      () => (args) => {
        const activeId = args.active.id;
        if (listParentIdSet.has(activeId)) {
          const ownGroup = groups.find((g) =>
            g.parents.some((t) => t.id === activeId),
          );
          const ownSectionId = ownGroup ? `section:${ownGroup.key}` : null;
          const droppableContainers = args.droppableContainers.filter(
            (c) =>
              listParentIdSet.has(c.id) ||
              (String(c.id).startsWith("section:") && c.id !== ownSectionId),
          );
          return closestCenter({ ...args, droppableContainers });
        }
        const siblings = listSubSiblingSet.get(activeId);
        const allowed = siblings ? (id) => siblings.has(id) : () => true;
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) =>
            allowed(c.id),
          ),
        });
      },
      [groups, listParentIdSet, listSubSiblingSet],
    );

    function onListDragStart(event) {
      listCrossDropRef.current = false;
      setListDragId(event.active.id);
    }

    function onListDragCancel() {
      setListDragId(null);
    }

    function onListDragEnd(event) {
      setListDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // Subtask reorder — within its sibling bucket (unchanged semantics).
      for (const subs of subtasksByParent.values()) {
        const sOld = subs.findIndex((s) => s.id === active.id);
        if (sOld < 0) continue;
        const sNew = subs.findIndex((s) => s.id === over.id);
        if (sNew < 0) return;
        const updates = computeReorderUpdates(
          arrayMove(subs, sOld, sNew),
          active.id,
        );
        if (updates.length > 0) reorderMutation.mutate(updates);
        return;
      }

      // Top-level row: same group → reorder; ANOTHER group → change the
      // grouped field (status / priority), mirroring the Board's
      // cross-column behaviour. Position inside the target group is not
      // applied — same as the Board, the row lands by its sortOrder and
      // the server rejects a change the user isn't allowed to make (the
      // updateMutation's deduped 403 toast covers the denial).
      const sourceGroup = groups.find((g) =>
        g.parents.some((t) => t.id === active.id),
      );
      if (!sourceGroup) return;
      const overId = String(over.id);
      const targetGroup = overId.startsWith("section:")
        ? groups.find((g) => `section:${g.key}` === overId)
        : groups.find((g) => g.parents.some((t) => t.id === over.id));
      if (!targetGroup) return;

      if (targetGroup.key === sourceGroup.key) {
        const pOld = sourceGroup.parents.findIndex((t) => t.id === active.id);
        const pNew = sourceGroup.parents.findIndex((t) => t.id === over.id);
        if (pOld < 0 || pNew < 0) return;
        const updates = computeReorderUpdates(
          arrayMove(sourceGroup.parents, pOld, pNew),
          active.id,
        );
        if (updates.length > 0) reorderMutation.mutate(updates);
        return;
      }

      listCrossDropRef.current = true;
      if (groupMode === "status") {
        commitTaskUpdate(active.id, { status: targetGroup.pill.name });
      } else if (groupMode === "priority") {
        const p = targetGroup.key.slice("priority:".length);
        commitTaskUpdate(active.id, { priority: p === "none" ? null : p });
      }
    }

    const deleteMutation = useMutation({
      mutationFn: async (id) => {
        if (!listId) throw new Error("No list context.");
        await api.delete(`/lists/${listId}/tasks/${id}`);
      },
      onSuccess: () => {
        toast.success("Task deleted successfully.");
        setTaskToDelete(null);
        void qc.invalidateQueries({ queryKey: ["tasks", listId] });
        void qc.invalidateQueries({ queryKey: ["space-overview"] });
      },
      onError: (err) => toast.error(extractApiError(err).message),
    });

    // Duplicate task. The action opens the Duplicate Task modal (pick one or more
    // destination Lists across Projects/Spaces); the modal owns the POST to
    // `/lists/:listId/tasks/:id/duplicate` with `destinationListIds`. Gated in the
    // UI on `canModify` (edit/full — the same level the server's task.create check
    // requires) so a view/comment-only member never sees the control.
    const { canModify: canModifySpace } = useSpaceAccess(spaceId);
    const canDuplicateTask = canModifySpace;
    const [duplicateTarget, setDuplicateTarget] = useState(null);

    /* ── Bulk delete (List view) ───────────────────────────────────────
     * Multi-select + one destructive action, on top of the EXISTING
     * `POST /tasks/bulk` endpoint (`op: 'delete'`), which already scopes by
     * tenant and re-authorises every id server-side.
     *
     * Gate: `canModifySpace` — the viewer's effective Space access resolved by
     * useSpaceAccess. That is the canonical client mirror of the server's
     * floor for this op (`task.delete`, which the access matrix grants to
     * edit/full and denies to comment/view). No Work-Role names appear here,
     * deliberately: the delete floor is an ACCESS-LEVEL rule, and hardcoding
     * "Manager"/"Team Leader" would both duplicate the resolver and go stale
     * the moment the matrix changes. The hook fails open while loading; the
     * server is the enforcing authority either way.
     */
    const canBulkDelete = canModifySpace;

    // Child index over the UNFILTERED task list. `subtasksByParent` is
    // filtered by the active search/filters, but the server cascade deletes
    // every descendant regardless of what the filter hides — counting off the
    // filtered index would understate the damage in the confirmation.
    const allChildrenByParent = useMemo(() => {
      const map = new Map();
      for (const t of tasks) {
        if (!t.parentTaskId) continue;
        const key = String(t.parentTaskId);
        let bucket = map.get(key);
        if (!bucket) {
          bucket = [];
          map.set(key, bucket);
        }
        bucket.push(t);
      }
      return map;
    }, [tasks]);

    const topLevelTaskIds = useMemo(
      () => new Set(topLevelTasks.map((t) => String(t.id))),
      [topLevelTasks],
    );
    const isTopLevelTask = useCallback(
      (id) => topLevelTaskIds.has(String(id)),
      [topLevelTaskIds],
    );

    const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    // Shift+click anchor. State, not a ref: it is READ during render (the
    // selection controller is memoised on it), and a ref read in render is
    // exactly what the react-hooks lint forbids. It only ever changes
    // alongside the selection, so it costs no extra render.
    const [selectAnchor, setSelectAnchor] = useState(null);

    // Every RENDERED row id in visual order — what a Shift+click range walks.
    // Collapsed parents contribute only themselves: their subtasks aren't on
    // screen, and a range must never sweep in rows the user can't see.
    const orderedRowIds = useMemo(() => {
      const out = [];
      const pushSubtree = (parentId) => {
        if (!effectiveShowSubtasks) return;
        if (!effectiveExpandedParents.has(parentId)) return;
        for (const sub of subtasksByParent.get(parentId) ?? []) {
          out.push(String(sub.id));
          pushSubtree(sub.id);
        }
      };
      for (const g of groups) {
        for (const t of g.parents) {
          out.push(String(t.id));
          pushSubtree(t.id);
        }
      }
      return out;
    }, [
      groups,
      subtasksByParent,
      effectiveShowSubtasks,
      effectiveExpandedParents,
    ]);

    // Drop selected ids that have left the view (filter/search change, a
    // refetch, or another user's delete arriving over the socket) so the bar
    // never acts on rows nobody can see. A row hidden only because its parent
    // is collapsed stays selected — it's still in `tasks`, and collapsing a
    // parent shouldn't silently shrink a pending selection.
    useEffect(() => {
      setSelectedTaskIds((prev) => {
        if (prev.size === 0) return prev;
        const present = new Set(tasks.map((t) => String(t.id)));
        let changed = false;
        const next = new Set();
        for (const id of prev) {
          if (present.has(id)) next.add(id);
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, [tasks]);

    // A different List is a different table — never carry a selection across.
    useEffect(() => {
      setSelectedTaskIds(new Set());
      setSelectAnchor(null);
    }, [listId]);

    const clearSelection = useCallback(() => {
      setSelectedTaskIds(new Set());
      setSelectAnchor(null);
    }, []);

    // Rows a selected ANCESTOR already covers: shown ticked but disabled.
    const lockedTaskIds = useMemo(
      () => collectDescendants(selectedTaskIds, allChildrenByParent),
      [selectedTaskIds, allChildrenByParent],
    );

    const bulkCounts = useMemo(
      () =>
        selectionCounts(selectedTaskIds, allChildrenByParent, isTopLevelTask),
      [selectedTaskIds, allChildrenByParent, isTopLevelTask],
    );

    const bulkDeleteMutation = useMutation({
      mutationFn: async (ids) => {
        const res = await api.post("/tasks/bulk", { ids, op: "delete" });
        return res.data;
      },
      onSuccess: (res) => {
        const failed = res?.failed ?? [];
        const ok = res?.succeeded?.length ?? 0;
        if (ok > 0) {
          toast.success(
            `Deleted ${bulkCounts.total} item${bulkCounts.total === 1 ? "" : "s"}.`,
          );
        } else {
          // Delete is all-or-nothing server-side, so a non-empty `failed` with
          // nothing succeeded means the WHOLE batch was refused — surface the
          // first reason rather than a generic error.
          toast.error(
            failed[0]?.reason
              ? `Nothing was deleted — ${failed[0].reason}`
              : "Nothing was deleted.",
          );
        }
        setBulkDeleteOpen(false);
        clearSelection();
        void qc.invalidateQueries({ queryKey: ["tasks", listId] });
        void qc.invalidateQueries({ queryKey: ["space-overview"] });
      },
      onError: (err) => toast.error(extractApiError(err).message),
    });

    /** Controller handed to every ListSection — see the `selection` prop. */
    const listSelection = useMemo(() => {
      if (!canBulkDelete) return null;
      const asId = (id) => String(id);
      return {
        isChecked: (id) =>
          selectedTaskIds.has(asId(id)) || lockedTaskIds.has(asId(id)),
        isLocked: (id) => lockedTaskIds.has(asId(id)),
        groupState: (ids) => groupCheckState(ids, selectedTaskIds),
        onRowClick: (event, id) => {
          const rowId = asId(id);
          // An ancestor already owns this row — its box is disabled, but guard
          // anyway so a keyboard activation can't desync the model.
          if (lockedTaskIds.has(rowId)) return;
          setSelectedTaskIds((prev) => {
            let next;
            const range =
              event?.shiftKey && selectAnchor != null
                ? rangeIdsBetween(orderedRowIds, selectAnchor, rowId)
                : [rowId];
            if (range.length > 1) {
              next = applyRange(prev, range, !prev.has(rowId));
            } else {
              next = new Set(prev);
              if (next.has(rowId)) next.delete(rowId);
              else next.add(rowId);
            }
            return prunedSelection(next, allChildrenByParent);
          });
          setSelectAnchor(rowId);
        },
        onToggleGroup: (ids) => {
          const all = groupCheckState(ids, selectedTaskIds) === "all";
          setSelectedTaskIds((prev) => {
            const next = applyRange(prev, ids.map(asId), !all);
            return prunedSelection(next, allChildrenByParent);
          });
          setSelectAnchor(null);
        },
      };
    }, [
      canBulkDelete,
      selectedTaskIds,
      lockedTaskIds,
      orderedRowIds,
      allChildrenByParent,
      selectAnchor,
    ]);

    const statusEditorMutation = useMutation({
      mutationFn: async (next) => {
        if (!space) throw new Error("No space loaded.");
        await api.patch(`/workspaces/${space.workspaceId}/spaces/${space.id}`, {
          taskStatuses: next,
        });
      },
      onSuccess: () => {
        toast.success("Task statuses updated successfully.");
        setStatusEditorOpen(false);
        void qc.invalidateQueries({ queryKey: ["space", spaceId] });
        void qc.invalidateQueries({ queryKey: ["spaces", space?.workspaceId] });
      },
      onError: (err) => toast.error(extractApiError(err).message),
    });

    function defaultStatusName() {
      const first =
        statuses.find((s) => s.category === "not_started") ?? statuses[0];
      return first?.name ?? "";
    }

    function addTask(statusName, name) {
      if (!listId) {
        toast.info("Open a List to add tasks.");
        return;
      }
      const status = statusName ?? defaultStatusName();
      if (!status) {
        toast.error("This Space has no statuses configured.");
        return;
      }
      // The inline quick-add (per status group) passes the typed name
      // through. The legacy header "+ Add Task" button and any other
      // call without an explicit name falls back to "Untitled task" so
      // its behaviour is unchanged.
      const trimmed = typeof name === "string" ? name.trim() : "";
      createMutation.mutate({ name: trimmed || "Untitled task", status });
    }

    function addSubtask(parentTaskId, name) {
      if (!listId) return;
      const parent = tasks.find((t) => t.id === parentTaskId);
      const status = parent?.status ?? defaultStatusName();
      if (!status) return;
      // Same pattern as addTask: inline quick-add passes the typed
      // name; legacy callers without a name still get the placeholder
      // "Untitled subtask" so old behaviour stays intact.
      const trimmed = typeof name === "string" ? name.trim() : "";
      createMutation.mutate({
        name: trimmed || "Untitled subtask",
        status,
        parentTaskId,
      });
    }

    // ── Sticky horizontal scrollbar for the List view ────────────────
    // When the column set on the List exceeds the viewport width, the
    // native scrollbar lives at the BOTTOM of the page content — users
    // have to scroll all the way down to drag it horizontally. We
    // mirror the Gantt's pattern instead: an `overflow-x-auto` wrapper
    // owns the horizontal scroll, its native bar is hidden, and a
    // fixed-to-viewport-bottom mirror tracks the same scrollLeft. The
    // mirror is anchored under the wrapper's left edge with a width
    // that matches it, so visually it reads as one scrollbar pinned to
    // the foot of the page.
    //
    // - `listScrollRef`       → the inner overflow-x wrapper around the
    //                           List view's content.
    // - `listFooterRef`       → the fixed mirror scrollbar.
    // - `listSyncingRef`      → guard against scroll-event ping-pong
    //                           between the wrapper and the mirror.
    // - `listFooterBounds`    → { left, width, contentWidth } in
    //                           viewport pixels. `contentWidth` is the
    //                           inner spacer's width — drives whether
    //                           the mirror even needs to render.
    const listScrollRef = useRef(null);
    const listFooterRef = useRef(null);
    const listSyncingRef = useRef(false);
    const [listFooterBounds, setListFooterBounds] = useState(null);
    // Board view gets the same sticky-footer scrollbar treatment as the
    // List (a mirror pinned to the viewport bottom) so a tall board's
    // columns stay horizontally scrollable without scrolling down to the
    // foot. Same ref/guard/bounds trio, scoped to the board wrapper.
    const boardScrollRef = useRef(null);
    const boardFooterRef = useRef(null);
    const boardSyncingRef = useRef(false);
    const [boardFooterBounds, setBoardFooterBounds] = useState(null);

    useEffect(() => {
      if (view !== "list") {
        setListFooterBounds(null);
        return undefined;
      }
      const el = listScrollRef.current;
      if (!el) return undefined;

      let raf = 0;
      const measure = () => {
        raf = 0;
        // `scrollWidth` reflects the widest child; when it equals
        // `clientWidth` there's nothing to scroll and we skip the mirror
        // entirely so we don't sit a useless empty bar over the view.
        const rect = el.getBoundingClientRect();
        const overflows = el.scrollWidth > el.clientWidth + 1;
        // Bail out when the measured bounds are unchanged. `measure` is driven
        // by a MutationObserver on this wrapper's SUBTREE, so if we always set a
        // fresh object the re-render mutates the subtree, the observer re-fires,
        // and we spin forever ("Maximum update depth exceeded"). Returning the
        // previous reference on an equal measure lets React skip the re-render
        // and breaks the feedback loop. Rounded to whole pixels so sub-pixel
        // jitter (from a scrollbar appearing/disappearing) can't re-trigger it.
        setListFooterBounds((prev) => {
          const next = overflows
            ? {
                left: Math.round(rect.left),
                width: Math.round(rect.width),
                contentWidth: Math.round(el.scrollWidth),
              }
            : null;
          if (!prev && !next) return prev;
          if (
            prev &&
            next &&
            prev.left === next.left &&
            prev.width === next.width &&
            prev.contentWidth === next.contentWidth
          )
            return prev;
          return next;
        });
      };
      // rAF-coalesce bursts of observer callbacks into one measure per frame,
      // and defer the read until AFTER the mutation's layout has settled
      // (reading scrollWidth synchronously inside a MutationObserver can catch
      // a half-applied layout).
      const scheduleMeasure = () => {
        if (raf) return;
        raf = requestAnimationFrame(measure);
      };

      measure();

      // ResizeObserver on the wrapper catches viewport / sidebar / vertical-
      // scrollbar width changes (the wrapper's own box changes).
      const ro = new ResizeObserver(scheduleMeasure);
      ro.observe(el);

      // A ResizeObserver on the wrapper does NOT fire when only its CHILDREN's
      // width changes (the wrapper is a fixed-size overflow container). The old
      // code observed `el.firstElementChild` to compensate, but that node is
      // captured once — when a data refetch, grouping change, or the initial
      // loading→content swap REPLACES the inner nodes, the observer was left
      // watching a detached element and never re-measured, so the bar silently
      // stuck (visible-then-gone, "needs refresh"). A MutationObserver on the
      // whole subtree re-measures on ANY structural change regardless of node
      // identity, which is what makes recovery reliable.
      const mo = new MutationObserver(scheduleMeasure);
      mo.observe(el, { childList: true, subtree: true });

      window.addEventListener("resize", scheduleMeasure);
      return () => {
        if (raf) cancelAnimationFrame(raf);
        ro.disconnect();
        mo.disconnect();
        window.removeEventListener("resize", scheduleMeasure);
      };
    }, [view, columns, listId, tasks.length]);

    // Board mirror-scrollbar bounds — same measuring logic as the List
    // effect above, keyed on the board wrapper and re-run when the column
    // count (status list) changes, since that drives the board's width.
    useEffect(() => {
      if (view !== "board") {
        setBoardFooterBounds(null);
        return undefined;
      }
      const el = boardScrollRef.current;
      if (!el) return undefined;
      let raf = 0;
      const measure = () => {
        raf = 0;
        const rect = el.getBoundingClientRect();
        const overflows = el.scrollWidth > el.clientWidth + 1;
        // Same anti-feedback-loop guard as the List mirror above: bail when the
        // bounds are unchanged so the MutationObserver-driven re-measure can't
        // spin the component ("Maximum update depth exceeded").
        setBoardFooterBounds((prev) => {
          const next = overflows
            ? {
                left: Math.round(rect.left),
                width: Math.round(rect.width),
                contentWidth: Math.round(el.scrollWidth),
              }
            : null;
          if (!prev && !next) return prev;
          if (
            prev &&
            next &&
            prev.left === next.left &&
            prev.width === next.width &&
            prev.contentWidth === next.contentWidth
          )
            return prev;
          return next;
        });
      };
      const scheduleMeasure = () => {
        if (raf) return;
        raf = requestAnimationFrame(measure);
      };
      measure();
      const ro = new ResizeObserver(scheduleMeasure);
      ro.observe(el);
      // See the List effect above — a MutationObserver on the subtree is what
      // makes the mirror recover when columns/cards are swapped out from under
      // it (node replacement the wrapper's ResizeObserver can't see).
      const mo = new MutationObserver(scheduleMeasure);
      mo.observe(el, { childList: true, subtree: true });
      window.addEventListener("resize", scheduleMeasure);
      return () => {
        if (raf) cancelAnimationFrame(raf);
        ro.disconnect();
        mo.disconnect();
        window.removeEventListener("resize", scheduleMeasure);
      };
    }, [view, columns, listId, tasks.length, displayedStatuses.length]);

    if (spaceQuery.isLoading) {
      return (
        <div className="p-8 text-[color:var(--color-ink-muted)]">
          Loading space…
        </div>
      );
    }
    if (!space) {
      return (
        <div className="p-8">
          <p className="text-[color:var(--color-ink-muted)]">
            {spaceQuery.isError
              ? extractApiError(spaceQuery.error).message
              : "Space not found."}{" "}
            <Link
              to="/app"
              className="text-[color:var(--color-accent-dim)] hover:text-[color:var(--color-ink-strong)]"
            >
              Back to Home
            </Link>
          </p>
        </div>
      );
    }

    // View / comment access cannot create tasks (matches the server matrix:
    // task.create is false for both). Hide the add-task affordance entirely so a
    // view-only user never opens the row and then hits a delayed 403.
    const canCreateTask =
      space.myAccessLevel !== "view" && space.myAccessLevel !== "comment";

    return (
      <AssigneeDisplayContext.Provider value={assigneeDisplayIndex}>
      <StatusDeniedMsgContext.Provider
        value={
          space.myAccessLevel === "view"
            ? VIEW_STATUS_DENIED_MSG
            : STATUS_DENIED_MSG
        }
      >
      <div className="flex flex-col h-full">
        {/* Header — Space identity strip. Icon tile + name + owner +
         *  favourite star. The icon tile is sized to a 36px square so it
         *  reads as a proper avatar; the name uses the display face at
         *  text-lg so it anchors the page like every other module H1.
         *  The "· Created by" caption sits as a separate slate row under
         *  the title for cleaner hierarchy than the old inline run-on.
         *  Functionality is unchanged — same favourite-toggle button,
         *  same DOM tree, only className adjustments. */}
        <div className="px-6 pt-5 pb-3 border-b border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
          {/* Breadcrumb — Dashboard › Space Name, via the shared component
           *  so it matches the other module pages exactly. */}
          <Breadcrumb current={space.name} />
          <div className="flex items-center gap-3">
            <span
              className="h-9 w-9 rounded-lg text-white flex items-center justify-center text-base font-semibold shadow-sm shrink-0"
              style={{
                backgroundColor: space.color || "var(--color-accent-dim)",
              }}
            >
              {space.name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1
                  // `min-w-0` makes the `truncate` actually take effect: a flex
                  // child keeps its content width (default min-width: auto)
                  // without it, so a very long project / list name overflowed
                  // and broke the header layout instead of ellipsising (QA).
                  // `title` surfaces the full name on hover now that it clips.
                  title={headerLabel}
                  className="min-w-0 font-display text-lg font-semibold text-[color:var(--color-ink-strong)] truncate"
                >
                  {headerLabel}
                </h1>
                <button
                  type="button"
                  onClick={() => {
                    if (!spaceId) return;
                    if (favoriteScope === "list") {
                      // Toggle the LIST favourite, not the parent space.
                      if (!listId || !list) return;
                      const wasFavorite = listFavorites.has(listId);
                      listFavorites.toggle(listId);
                      // Keep the meta map in sync so the Favourites sidebar
                      // can render a working name + parent-space link
                      // without having to fetch every space's lists. Wipe
                      // the entry when un-pinning so stale meta doesn't
                      // accumulate as users churn lists.
                      if (wasFavorite) {
                        setListFavMeta((prev) => {
                          const next = { ...prev };
                          delete next[listId];
                          return next;
                        });
                      } else {
                        setListFavMeta((prev) => ({
                          ...prev,
                          [listId]: {
                            // Store what the HEADING says, not the raw list
                            // name — on a project page those differ, and the
                            // sidebar renders this value verbatim.
                            name: headerLabel,
                            spaceId,
                            spaceName: space?.name ?? null,
                            // Parent Project id when the label above IS the
                            // Project's name — lets a Project rename cascade
                            // to this favourite (renameFavoriteFolderMeta).
                            // Null when the label is the list's own name.
                            folderId: currentProject?.id ?? null,
                          },
                        }));
                      }
                      toast.success(
                        wasFavorite
                          ? `${headerLabel} removed from Favourites.`
                          : `${headerLabel} added to Favourites.`,
                      );
                      return;
                    }
                    const wasFavorite = spaceFavorites.has(spaceId);
                    spaceFavorites.toggle(spaceId);
                    toast.success(
                      wasFavorite
                        ? `${space.name} removed from Favourites.`
                        : `${space.name} added to Favourites.`,
                    );
                  }}
                  title={
                    isFavorite
                      ? `Remove ${favoriteScope === "list" ? "list" : "space"} from Favourites`
                      : `Add ${favoriteScope === "list" ? "list" : "space"} to Favourites`
                  }
                  aria-pressed={isFavorite}
                  className={
                    "inline-flex h-7 w-7 items-center justify-center rounded-md cursor-pointer transition-colors " +
                    (isFavorite
                      ? "text-[color:var(--color-warning)] hover:bg-[color:var(--color-warning-bg)]"
                      : "text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-warning)] hover:bg-[color:var(--color-card-hover)]")
                  }
                >
                  <Star
                    size={15}
                    fill={isFavorite ? "currentColor" : "none"}
                    strokeWidth={isFavorite ? 1 : 2}
                  />
                </button>
              </div>
              {/* Caption row — when a List is open it shows BOTH the List's
               *  actual creator AND the Space owner, separately:
               *  "List · Created by <list author> · Space owner <space owner>".
               *  At Space level (no List) it's just "Created by <space owner>".
               *  Each name prefers the server-resolved `createdByName`
               *  (resolved across ALL tenant users, so an admin creator with no
               *  workspace_members row still shows a name), then the workspace
               *  member roster, then "Unknown". */}
              {(currentProject && list) ||
              list?.createdById ||
              space.createdById ? (
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)] min-w-0">
                  {currentProject && list ? (
                    <>
                      <LayoutList size={12} className="shrink-0" />
                      <span className="font-medium text-[color:var(--color-ink)] truncate">
                        {list.name}
                      </span>
                    </>
                  ) : null}
                  {currentProject &&
                  list &&
                  (list.createdById || space.createdById) ? (
                    <span
                      aria-hidden="true"
                      className="text-[color:var(--color-ink-subtle)]"
                    >
                      ·
                    </span>
                  ) : null}
                  {/* List creator — the ACTUAL author of the open List (a List
                   *  created by another user shows THAT user, the reported
                   *  bug). Only shown when a List is open. */}
                  {list?.createdById ? (
                    <span className="truncate">
                      Created by{" "}
                      <span className="font-medium text-[color:var(--color-ink)]">
                        {list.createdByName ??
                          allWorkspaceMembers.find(
                            (m) => m.userId === list.createdById,
                          )?.fullName ??
                          "Unknown"}
                      </span>
                    </span>
                  ) : null}
                  {/* Separator between the List creator and the Space owner. */}
                  {list?.createdById && space.createdById ? (
                    <span
                      aria-hidden="true"
                      className="text-[color:var(--color-ink-subtle)]"
                    >
                      ·
                    </span>
                  ) : null}
                  {/* Space owner — labelled "Space owner" when a List is open
                   *  (so it reads as distinct from the List creator above), or
                   *  the sole "Created by" line at Space level. */}
                  {space.createdById ? (
                    <span className="truncate">
                      {list?.createdById ? "Space owner " : "Created by "}
                      <span className="font-medium text-[color:var(--color-ink)]">
                        {space.createdByName ??
                          allWorkspaceMembers.find(
                            (m) => m.userId === space.createdById,
                          )?.fullName ??
                          "Unknown"}
                      </span>
                    </span>
                  ) : null}
                </p>
              ) : null}
            </div>
            {/* The Filter funnel icon used to sit here, but it duplicated
             *  the existing "Filter" pill in the sub-toolbar (which is
             *  the real, wired-up filter UI). Removed so the page header
             *  carries only the affordances it actually drives — Space
             *  name, owner, favourite star. */}
          </div>

          {/* View tabs — segmented pill with accent-tinted active state.
           *  Refreshed from the previous canvas-grey active fill to an
           *  accent pill so the current view jumps out at a glance and
           *  matches the chip-filter language used elsewhere in the
           *  admin chrome (Plans page, All Users page). Same buttons,
           *  same onClick handlers — only className changes. */}
          <div className="mt-4 flex flex-wrap items-center gap-1">
            {[
              { key: "overview", label: "Overview", icon: Eye },
              { key: "list", label: "List", icon: LayoutList },
              { key: "board", label: "Board", icon: LayoutGrid },
              { key: "gantt", label: "Gantt", icon: GanttChart },
              { key: "files", label: "Files", icon: Paperclip },
            ].map(({ key, label, icon: Icon }) => {
              const active = view === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  className={
                    "inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm cursor-pointer transition-colors " +
                    (active
                      ? "bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-semibold border border-[color:var(--color-accent-border)]"
                      : "text-[color:var(--color-ink-muted)] border border-transparent hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]")
                  }
                >
                  <Icon size={14} strokeWidth={active ? 2.25 : 1.75} />
                  {label}
                </button>
              );
            })}
            {/* "+ View" was a no-op placeholder for a custom-view
             *  feature that isn't wired up yet. Removed so users don't
             *  click a button that does nothing. When custom views land
             *  this is where the trigger goes. */}
            {/* Overview has no sub-toolbar (its Group / Subtasks / Filter /
             *  Customise controls don't apply to the read-only KPI summary),
             *  so its Create Task lives HERE on the tab row, right-aligned.
             *  List / Board / Gantt keep Create Task in the sub-toolbar with
             *  the rest of the task actions. */}
            {view === "overview" ? (
              <button
                type="button"
                onClick={() => setCreateTaskOpen(true)}
                disabled={!listId}
                className="ml-auto inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-[13px] bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Plus size={12} /> Create Task
              </button>
            ) : null}
          </div>

          {/* Sub-toolbar — task-view controls (Group / Subtasks / Search /
           *  Filter / Customise / Create Task). Hidden on the Files tab (its own
           *  actions) AND on the Overview tab: Overview is a read-only whole-
           *  Space KPI summary that none of these affect, and showing them there
           *  — the inert Subtasks toggle, a lone Create Task — read as broken /
           *  out of place (QA). Create tasks from the List / Board tabs. */}
          {view !== "files" && view !== "overview" ? (
            <div className="mt-2 flex items-center gap-2 flex-wrap pb-3">
              <GroupModePill
                mode={groupMode}
                setMode={setGroupMode}
                dir={groupDir}
                setDir={setGroupDir}
              />
              <SubtasksPill
                showSubtasks={showSubtasks}
                onToggle={() => setShowSubtasks((v) => !v)}
              />

              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {/* Client-side task-name search over the loaded list. Filters the
                 *  same `matchesFilters` pipeline the assignee / priority / due
                 *  pills use, so List + Board honour it live with no backend call. */}
                <div className="w-full sm:w-80">
                  <SearchField
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    onClear={() => setFilterQuery("")}
                    placeholder="Search tasks or subtasks"
                    aria-label="Search tasks or subtasks"
                    className="h-8 text-[13px]"
                  />
                </div>
                {/* Jump-to-today — Gantt only. Lives here rather than floating
                 *  over the timeline header, where it kept covering a week or
                 *  month label whatever offset it was given (QA). Calls the
                 *  handle GanttView publishes; no-ops if the canvas isn't
                 *  mounted yet. */}
                {view === "gantt" ? (
                  <button
                    type="button"
                    onClick={() => ganttTodayRef.current?.()}
                    title="Jump to today"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
                  >
                    <Calendar size={12} /> Today
                  </button>
                ) : null}
                <FilterPill
                  members={members}
                  filterAssignees={filterAssignees}
                  setFilterAssignees={setFilterAssignees}
                  filterPriorities={filterPriorities}
                  setFilterPriorities={setFilterPriorities}
                  filterDueRange={filterDueRange}
                  setFilterDueRange={setFilterDueRange}
                  activeCount={activeFilterCount}
                />
                {/* Customize pill — toggles column visibility for the
                 *  currently active view. List and Gantt keep independent
                 *  column-visibility state (see `useTaskColumns` /
                 *  `useGanttColumns`), so the pill routes through the
                 *  matching pair based on `view`. Board shares the List
                 *  state but restricts the panel's row set to the chips it
                 *  can actually render (`BOARD_ALLOWED_COLUMN_KEYS`).
                 *  Gantt similarly restricts to the four left-pane cells
                 *  it knows how to render (`GANTT_ALLOWED_COLUMN_KEYS`)
                 *  so admins aren't presented with toggles that wouldn't
                 *  take effect on the active view. */}
                <CustomizePill
                  columns={view === "gantt" ? ganttColumns : columns}
                  setColumns={view === "gantt" ? setGanttColumns : setColumns}
                  allowedKeys={(() => {
                    const base =
                      view === "board"
                        ? BOARD_ALLOWED_COLUMN_KEYS
                        : view === "gantt"
                          ? GANTT_ALLOWED_COLUMN_KEYS
                          : null;
                    if (!isPersonalSpace) return base;
                    // Personal Space — never offer Assignee / Created By as
                    // toggles (they're force-hidden above).
                    const keys = (
                      base ? [...base] : COLUMN_ORDER.map((c) => c.key)
                    ).filter((k) => k !== "assignee" && k !== "createdBy");
                    return new Set(keys);
                  })()}
                />
                {/* Create Task — opens the 3-method dialog (Manual / From
                 *  SOP / AI from upload). Replaces the old single-click
                 *  "+ Add Task" header button so the SOP + AI flows are
                 *  discoverable. The per-column "+ Add Task" inline button
                 *  still lives at the bottom of each status group for
                 *  fast name-only quick-create. */}
                <button
                  type="button"
                  onClick={() => setCreateTaskOpen(true)}
                  disabled={!listId}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[13px] bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Plus size={12} /> Create Task
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Body — `scrollbar-thin` styling so the vertical bar is
         *  visible on Windows 11 / macOS where overlay scrollbars
         *  fade out by default. Without this the page reads as
         *  "no scrollbar present" even when content overflows. */}
        <div
          className={
            "flex-1 overflow-y-auto overflow-x-hidden p-6 " +
            "[scrollbar-width:thin] [scrollbar-color:rgba(143,161,188,0.55)_transparent] " +
            "[&::-webkit-scrollbar]:w-2.5 " +
            "[&::-webkit-scrollbar-button]:hidden " +
            "[&::-webkit-scrollbar-track]:bg-transparent " +
            "[&::-webkit-scrollbar-thumb]:bg-[rgba(143,161,188,0.55)] " +
            "[&::-webkit-scrollbar-thumb]:rounded-full " +
            "[&::-webkit-scrollbar-thumb:hover]:bg-[rgba(143,161,188,0.85)]"
          }
        >
          {view === "list" ? (
            <>
            {/* Bulk bar sits OUTSIDE the horizontal-scroll wrapper below, so
             *  it stays put while the columns scroll sideways — the Delete
             *  button must never slide off-screen with a selection live. */}
            {listSelection && selectedTaskIds.size > 0 ? (
              <ListBulkBar
                counts={bulkCounts}
                overLimit={selectedTaskIds.size > MAX_BULK_IDS}
                busy={bulkDeleteMutation.isPending}
                onDelete={() => setBulkDeleteOpen(true)}
                onClear={clearSelection}
              />
            ) : null}
            {/* Inner overflow-x wrapper owns horizontal scroll for the
              * List view. Its native scrollbar is hidden — the visible
              * one is the fixed mirror portalled to the viewport bottom
              * (see the `listFooterBounds` portal below). That keeps the
              * bar reachable without scrolling to the page foot, which
              * is the whole point of the sticky-footer treatment. */}
            <div
              ref={listScrollRef}
              onScroll={(e) => {
                if (listSyncingRef.current) return;
                listSyncingRef.current = true;
                if (listFooterRef.current) {
                  listFooterRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
                requestAnimationFrame(() => {
                  listSyncingRef.current = false;
                });
              }}
              className="overflow-x-auto view-hscroll"
            >
              {!listId ? (
                listsQuery.isLoading ? (
                  <p className="text-sm text-[color:var(--color-ink-muted)]">
                    Loading list…
                  </p>
                ) : (
                  <p className="text-sm text-[color:var(--color-ink-muted)]">
                    Select a list to view tasks.
                  </p>
                )
              ) : (
                // Single DndContext across ALL groups so a drag can cross
                // from one status group into another (drop = status
                // change). Per-group reorder still works — same-group
                // drops route to the sortOrder path in `onListDragEnd`.
                <DndContext
                  sensors={listSensors}
                  collisionDetection={listCollision}
                  measuring={DND_MEASURING}
                  modifiers={[restrictToVerticalAxis]}
                  onDragStart={onListDragStart}
                  onDragEnd={onListDragEnd}
                  onDragCancel={onListDragCancel}
                >
                  {groups.map((g) => (
                    <ListSection
                      key={g.key}
                      groupKey={g.key}
                      status={g.pill}
                      tasks={g.parents}
                      members={members}
                      columns={columns}
                      statuses={statuses}
                      showSubtasks={effectiveShowSubtasks}
                      subtasksByParent={subtasksByParent}
                      expandedParents={effectiveExpandedParents}
                      onToggleParent={toggleParent}
                      onAddTask={
                        canCreateTask
                          ? (name) => {
                              // For status grouping, pre-fill the section's status.
                              // For other groupings, fall back to default status.
                              if (groupMode === "status") addTask(g.pill.name, name);
                              else addTask(undefined, name);
                            }
                          : undefined
                      }
                      onAddSubtask={addSubtask}
                      onUpdateTask={commitTaskUpdate}
                      onDeleteTask={(t) => setTaskToDelete(t)}
                      onDuplicateTask={
                        canDuplicateTask ? (t) => setDuplicateTarget(t) : undefined
                      }
                      onManageStatuses={onManageStatuses}
                      hideHeader={groupMode === "none"}
                      emptyText={
                        groupMode === "none" ? "No tasks yet." : undefined
                      }
                      currentUserId={userId ?? null}
                      workspaceId={space?.workspaceId ?? null}
                      workspaceName="this workspace"
                      spaceId={space?.id ?? null}
                      spaceName={space?.name ?? "this Space"}
                      listId={listId}
                      onOpenDetail={(taskId) => setDetailTaskId(taskId)}
                      onOpenRecurrence={(taskId) =>
                        setRecurrenceTaskId(taskId)
                      }
                      selection={listSelection}
                    />
                  ))}
                  {/* Floating ghost — renders OUTSIDE the grid so it
                   *  follows the cursor smoothly without fighting the
                   *  sticky-column / CSS-grid layout. The held row
                   *  collapses to opacity 0, leaving a placeholder slot
                   *  that other rows can slide around.
                   *
                   *  Drop animation is CONDITIONAL: a same-group reorder
                   *  keeps the settle-into-slot slide, but a cross-group
                   *  drop fades out in place instead. dnd-kit's default
                   *  drop animation slides the ghost back to the ACTIVE
                   *  row's rect — which, on a cross-group drop, is still
                   *  the row's OLD slot in the source group — so the task
                   *  visibly bounced backward before the optimistic
                   *  update snapped it into its new group. (The Board's
                   *  overlay avoids the same artefact with
                   *  `dropAnimation={null}`.) */}
                  <DragOverlay
                    dropAnimation={{
                      duration: 180,
                      easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                      keyframes({ transform }) {
                        if (listCrossDropRef.current) {
                          return [
                            {
                              transform: CSS.Transform.toString(
                                transform.initial,
                              ),
                              opacity: 1,
                            },
                            {
                              transform: CSS.Transform.toString(
                                transform.initial,
                              ),
                              opacity: 0,
                            },
                          ];
                        }
                        return [
                          {
                            transform: CSS.Transform.toString(
                              transform.initial,
                            ),
                          },
                          {
                            transform: CSS.Transform.toString(transform.final),
                          },
                        ];
                      },
                    }}
                  >
                    <TaskDragPreview
                      task={listDragTask}
                      status={listDragStatus}
                    />
                  </DragOverlay>
                </DndContext>
              )}
            </div>
            </>
          ) : view === "board" ? (
            <DndContext
              sensors={sensors}
              /**
               * `closestCorners` is the dnd-kit recommended collision
               * strategy for kanban-style boards: it picks the droppable
               * whose closest corner is nearest the dragged item, which
               * works regardless of column height. The previous default
               * (`rectIntersection`) silently failed when dragging into
               * EMPTY columns — empty columns sized to their tiny "No
               * tasks." placeholder, so the dragged card's virtual rect
               * never intersected them and `event.over` stayed `null`,
               * making `onBoardDragEnd` short-circuit.
               */
              collisionDetection={closestCorners}
              onDragStart={onBoardDragStart}
              onDragEnd={onBoardDragEnd}
            >
              {/* Horizontal-flex board so each column keeps a generous
               *  fixed width (`w-96` on the column itself = 384px)
               *  regardless of how many statuses are configured. When
               *  more statuses exist than fit in the viewport, this
               *  container horizontally scrolls — the previous CSS-Grid
               *  layout capped columns at 1/2/4 and squashed them at 5+.
               *
               *  IMPORTANT: no `overflow-y-hidden` here. Browsers force
               *  overflow-y to `auto` when overflow-x is auto, but
               *  explicitly setting overflow-y to `hidden` ALSO clips
               *  the dnd-kit DragOverlay's hit-test region — which made
               *  card drops between columns silently fail. Letting the
               *  vertical axis compute as `auto` keeps DnD happy and the
               *  board's columns are sized by their content anyway so
               *  vertical scroll never actually engages. */}
              <div
                ref={boardScrollRef}
                onScroll={(e) => {
                  if (boardSyncingRef.current) return;
                  boardSyncingRef.current = true;
                  if (boardFooterRef.current) {
                    boardFooterRef.current.scrollLeft =
                      e.currentTarget.scrollLeft;
                  }
                  requestAnimationFrame(() => {
                    boardSyncingRef.current = false;
                  });
                }}
                // Native horizontal bar hidden — the visible one is the
                // fixed mirror pinned to the viewport bottom (portal below),
                // so the columns are reachable without scrolling to the foot
                // of a tall board.
                className="flex gap-4 overflow-x-auto pb-3 view-hscroll"
              >
                {displayedStatuses.map((s) => (
                  <BoardColumn
                    key={s.name}
                    status={s}
                    tasks={groupedByStatus.get(s.name) ?? []}
                    members={members}
                    statuses={displayedStatuses}
                    currentUserId={userId ?? null}
                    workspaceId={space?.workspaceId ?? null}
                    workspaceName="this workspace"
                    spaceId={space?.id ?? null}
                    spaceName={space?.name ?? "this Space"}
                    allTasks={tasks}
                    // Column-visibility map — same shared state the
                    // List view consumes (`useTaskColumns`), wired
                    // through to the Customize pill in the toolbar. A
                    // toggle in either view (List or Board) flips the
                    // same chips on / off everywhere they live.
                    columns={columns}
                    // Subtask plumbing — same shared state the List
                    // and Gantt consume, so expand/collapse stays in
                    // sync as the user flips between views.
                    subtasksByParent={subtasksByParent}
                    showSubtasks={showSubtasks}
                    expandedParents={expandedParents}
                    onToggleParent={toggleParent}
                    onUpdateTask={commitTaskUpdate}
                    onOpenDetail={(taskId) => setDetailTaskId(taskId)}
                    onOpenRecurrence={(taskId) => setRecurrenceTaskId(taskId)}
                    onDeleteTask={(t) => setTaskToDelete(t)}
                    onDuplicateTask={
                      canDuplicateTask ? (t) => setDuplicateTarget(t) : undefined
                    }
                    onAddSubtask={(parentId) => addSubtask(parentId)}
                    // Per-column quick-add — drops the typed task into
                    // THIS status bucket so the user doesn't have to
                    // pick a status from a dialog after typing.
                    onAddTask={canCreateTask ? (name) => addTask(s.name, name) : undefined}
                  />
                ))}
              </div>
              <DragOverlay dropAnimation={null}>
                {activeBoardTask ? (
                  <BoardCardView
                    task={activeBoardTask}
                    dragging
                    assignees={(activeBoardTask.assigneeIds ?? [])
                      .map((id) => members.find((m) => m.userId === id))
                      .filter((m) => m)}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : view === "gantt" ? (
            <GanttView
              tasks={topLevelTasks}
              allTasks={tasks}
              statuses={displayedStatuses}
              members={members}
              columns={ganttColumns}
              // Subtask plumbing — same state the List view consumes,
              // so a parent expanded on either view stays expanded on
              // the other when the user flips between them.
              subtasksByParent={subtasksByParent}
              showSubtasks={showSubtasks}
              expandedParents={expandedParents}
              onToggleParent={toggleParent}
              todayHandleRef={ganttTodayRef}
              onDeleteTask={(t) => setTaskToDelete(t)}
              onOpenDetail={(taskId) => setDetailTaskId(taskId)}
              onOpenRecurrence={(taskId) => setRecurrenceTaskId(taskId)}
              onAddSubtask={(parentId) => addSubtask(parentId)}
              // Per-group quick-add: typed name lands as a top-level
              // task in the group's status bucket — matches the per-
              // status quick-add on the List and Board views.
              onAddTask={canCreateTask ? (statusName, name) => addTask(statusName, name) : undefined}
              currentUserId={userId ?? null}
              workspaceId={space?.workspaceId}
              workspaceName="this workspace"
              spaceId={spaceId}
              spaceName={space?.name ?? "this Space"}
              onUpdateTask={commitTaskUpdate}
              onReorderTasks={(updates) => reorderMutation.mutate(updates)}
            />
          ) : view === "files" ? (
            <SpaceFilesView
              spaceId={space?.id ?? null}
              // Scoped to the open List so two Projects in one Space stop
              // showing each other's files. Space-level files (filed under no
              // List) still appear, in their own labelled group.
              listId={listId ?? null}
            />
          ) : (
            <OverviewView
              overview={overviewQuery.data ?? null}
              isLoading={overviewQuery.isLoading}
              isError={overviewQuery.isError}
              errorMessage={
                overviewQuery.isError
                  ? extractApiError(overviewQuery.error).message
                  : null
              }
              spaceName={space?.name ?? null}
              onOpenTask={(taskId) => setDetailTaskId(taskId)}
            />
          )}
        </div>

        <TaskStatusEditor
          open={statusEditorOpen}
          initial={statuses.length > 0 ? statuses : DEFAULT_SPACE_TASK_STATUSES}
          onClose={() => setStatusEditorOpen(false)}
          onApply={(next) => statusEditorMutation.mutate(next)}
        />

        <TaskDetailDialog
          task={tasks.find((t) => t.id === detailTaskId) ?? null}
          members={members}
          statuses={statuses}
          currentUserId={userId ?? null}
          spaceId={spaceId}
          listId={listId ?? null}
          workspaceId={space?.workspaceId ?? null}
          workspaceName="this workspace"
          spaceName={space?.name ?? "this Space"}
          isPersonal={isPersonalSpace}
          allTasks={tasks}
          onClose={() => setDetailTaskId(null)}
          onUpdate={(patch) => {
            if (!detailTaskId) return;
            commitTaskUpdate(detailTaskId, patch);
          }}
        />

        {/* Recurrence editor opened from the list's "Repeat" column. Rendered
         *  at the page root (NOT inside a task-detail backdrop) so its clicks
         *  don't bubble into a click-to-close overlay. Self-fetches the rule
         *  by taskId; the title uses the task's name. */}
        <RecurrenceDialog
          open={Boolean(recurrenceTaskId)}
          taskId={recurrenceTaskId}
          taskName={
            tasks.find((t) => t.id === recurrenceTaskId)?.name ?? "this task"
          }
          onClose={() => setRecurrenceTaskId(null)}
        />

        <CreateTaskDialog
          open={createTaskOpen}
          listId={listId ?? null}
          onClose={() => setCreateTaskOpen(false)}
        />

        <SopUploadDialog
          open={sopUploadOpen}
          listId={listId ?? null}
          onClose={() => setSopUploadOpen(false)}
          onGenerated={(sop, preview) => {
            setSopUploadOpen(false);
            // Upload now returns the saved SOP WITHOUT running AI (decoupled so
            // the request never exceeds the proxy timeout). When there's no
            // preview yet, open the review dialog in a "pending generation"
            // state — it runs POST /:id/generate-tasks itself and shows the
            // loading + Regenerate handling. A ready preview (e.g. blueprint
            // reuse) still opens directly.
            setSopPreview(
              preview ?? {
                sopId: sop.id,
                sopTitle: sop.title,
                tasks: [],
                pendingGeneration: true,
              },
            );
          }}
        />
        <SopPreviewDialog
          open={sopPreview !== null}
          listId={listId ?? null}
          preview={sopPreview}
          onClose={() => setSopPreview(null)}
        />
        {/* TaskBreakdownDialog was mounted here. Its only trigger was the
         *  "Break Down" button on the add-subtask row, which has been removed,
         *  so the dialog could never be opened again. Left unmounted rather
         *  than kept as dead state. */}

        <ConfirmDialog
          open={taskToDelete !== null}
          title="Delete Task"
          message={
            taskToDelete
              ? `This will permanently delete "${
                  taskToDelete.name.length > 80
                    ? taskToDelete.name.slice(0, 80).trimEnd() + "…"
                    : taskToDelete.name
                }". This action cannot be undone.`
              : ""
          }
          confirmLabel="Delete"
          variant="danger"
          confirming={deleteMutation.isPending}
          onCancel={() => setTaskToDelete(null)}
          onConfirm={() => {
            if (taskToDelete) deleteMutation.mutate(taskToDelete.id);
          }}
        />

        {/* Bulk delete. The message spells out BOTH numbers because they can
         *  differ sharply from what was ticked — selecting one parent can
         *  destroy a dozen rows, and "Delete 1 task?" would be a lie. The
         *  subtask figure counts every descendant, including ones the current
         *  filter or a collapsed parent is hiding. */}
        <ConfirmDialog
          open={bulkDeleteOpen}
          title="Delete selected tasks"
          message={
            <>
              This will permanently delete{" "}
              <strong>
                {formatNumber(bulkCounts.tasks)}{" "}
                {bulkCounts.tasks === 1 ? "task" : "tasks"}
              </strong>{" "}
              and{" "}
              <strong>
                {formatNumber(bulkCounts.subtasks)}{" "}
                {bulkCounts.subtasks === 1 ? "subtask" : "subtasks"}
              </strong>
              {bulkCounts.subtasks > 0
                ? " (every subtask under a selected task goes with it, including any hidden by the current filter)"
                : ""}
              , along with their comments, time entries, attachments and
              recurring schedules. This action cannot be undone.
            </>
          }
          confirmLabel={`Delete ${formatNumber(bulkCounts.total)} ${
            bulkCounts.total === 1 ? "item" : "items"
          }`}
          variant="danger"
          confirming={bulkDeleteMutation.isPending}
          onCancel={() => setBulkDeleteOpen(false)}
          onConfirm={() => {
            // `selectedTaskIds` is kept pruned, so it already holds only the
            // roots — the server cascades to the rest.
            if (selectedTaskIds.size > 0) {
              bulkDeleteMutation.mutate([...selectedTaskIds]);
            }
          }}
        />

        {/* Duplicate Task → one or more destination Lists (any Project/Space in
         *  this tenant). Opens from the per-row/card Duplicate action. */}
        <DuplicateTaskModal
          open={!!duplicateTarget}
          onClose={() => setDuplicateTarget(null)}
          sourceTask={duplicateTarget}
          // The SOURCE list is the task's OWN list — not the URL `listId`, which is
          // undefined on a Space Overview / list-less route (e.g. the company-admin
          // panel) and would otherwise make the backend look the task up in the
          // wrong list ("Task not found").
          sourceListId={duplicateTarget?.listId ?? listId}
          workspaceId={space?.workspaceId ?? null}
          // Scope destinations to the current space's department — don't surface
          // other departments' projects/lists in the picker.
          sourceDepartmentId={space?.departmentId ?? null}
        />

        {/* Fixed-to-viewport horizontal scrollbar for the List view —
         *  portalled to <body> so no parent's `overflow: hidden` (header,
         *  sidebar, dialog wrappers) can clip it. Only renders when the
         *  table actually overflows horizontally and we're on the List
         *  view, so it never sits idle over Board / Gantt / Overview. */}
        {view === "list" && listFooterBounds
          ? createPortal(
              // Mirror horizontal scrollbar — fixed to the viewport bottom
              // so the user can scroll columns without hunting for the
              // native bar at the foot of the table content. Explicit
              // `overflow-x-scroll` + styled thumb/track so the bar is
              // ALWAYS visible (Windows 11 / macOS overlay scrollbars
              // otherwise fade out and read as "missing"). Background tint
              // + top border anchor it visually as a separate strip
              // pinned to the page foot.
              <div
                ref={listFooterRef}
                onScroll={(e) => {
                  if (listSyncingRef.current) return;
                  listSyncingRef.current = true;
                  if (listScrollRef.current) {
                    listScrollRef.current.scrollLeft =
                      e.currentTarget.scrollLeft;
                  }
                  requestAnimationFrame(() => {
                    listSyncingRef.current = false;
                  });
                }}
                className={
                  "fixed z-30 overflow-x-scroll overflow-y-hidden " +
                  "bg-[color:var(--color-card)] border-t border-[color:var(--color-rule)] " +
                  "[scrollbar-width:auto] [scrollbar-color:rgba(143,161,188,0.6)_transparent] " +
                  "[&::-webkit-scrollbar]:h-3 " +
                  "[&::-webkit-scrollbar-button]:hidden " +
                  "[&::-webkit-scrollbar-track]:bg-transparent " +
                  "[&::-webkit-scrollbar-thumb]:bg-[rgba(143,161,188,0.6)] " +
                  "[&::-webkit-scrollbar-thumb]:rounded-full " +
                  "[&::-webkit-scrollbar-thumb:hover]:bg-[rgba(143,161,188,0.85)]"
                }
                style={{
                  bottom: 0,
                  left: listFooterBounds.left,
                  width: listFooterBounds.width,
                  height: 14,
                }}
                aria-label="List horizontal scrollbar"
              >
                <div
                  style={{ width: listFooterBounds.contentWidth, height: 1 }}
                  aria-hidden="true"
                />
              </div>,
              document.body,
            )
          : null}

        {/* Board mirror scrollbar — same fixed-to-viewport-bottom treatment
         *  as the List, so a tall board's columns stay horizontally
         *  scrollable without scrolling down to the foot. */}
        {view === "board" && boardFooterBounds
          ? createPortal(
              <div
                ref={boardFooterRef}
                onScroll={(e) => {
                  if (boardSyncingRef.current) return;
                  boardSyncingRef.current = true;
                  if (boardScrollRef.current) {
                    boardScrollRef.current.scrollLeft =
                      e.currentTarget.scrollLeft;
                  }
                  requestAnimationFrame(() => {
                    boardSyncingRef.current = false;
                  });
                }}
                className={
                  "fixed z-30 overflow-x-scroll overflow-y-hidden " +
                  "bg-[color:var(--color-card)] border-t border-[color:var(--color-rule)] " +
                  "[scrollbar-width:auto] [scrollbar-color:rgba(143,161,188,0.6)_transparent] " +
                  "[&::-webkit-scrollbar]:h-3 " +
                  "[&::-webkit-scrollbar-button]:hidden " +
                  "[&::-webkit-scrollbar-track]:bg-transparent " +
                  "[&::-webkit-scrollbar-thumb]:bg-[rgba(143,161,188,0.6)] " +
                  "[&::-webkit-scrollbar-thumb]:rounded-full " +
                  "[&::-webkit-scrollbar-thumb:hover]:bg-[rgba(143,161,188,0.85)]"
                }
                style={{
                  bottom: 0,
                  left: boardFooterBounds.left,
                  width: boardFooterBounds.width,
                  height: 14,
                }}
                aria-label="Board horizontal scrollbar"
              >
                <div
                  style={{ width: boardFooterBounds.contentWidth, height: 1 }}
                  aria-hidden="true"
                />
              </div>,
              document.body,
            )
          : null}
      </div>
      </StatusDeniedMsgContext.Provider>
      </AssigneeDisplayContext.Provider>
    );
  }
